/**
 * Dutch (Nederlands) locale dictionary.
 *
 * Typed against the English dictionary so missing keys break the build.
 * When you add a string to `en.ts`, TypeScript will require you to add the
 * Dutch translation here too.
 */

import type { Dictionary } from "./en";
import { marketingNl } from "./marketing";
import { projectSetupNl } from "./project-setup";

export const nl: Dictionary = {
  // Zie en.ts.
  previewNotice: {
    readyTitle: "Preview staat klaar",
    readyMessage: "{target} draait en kan worden geopend.",
    failedTitle: "Preview is niet gestart",
    failedMessage: "{target} is niet gestart: {reason}.",
  },
  // Zie en.ts.
  prPreview: {
    title: "Pull request controleren",
    subtitle: "Zo komen de pull request en de commit op de git-host te staan. Pas aan waar nodig en bevestig.",
    loading: "Pull request voorbereiden\u2026",
    loadFailed: "De pull request kon niet worden voorbereid.",
    target: "{repo} \u2190 naar {base}",
    unverified: "Geopend zonder geslaagde verificatie: bovenaan de omschrijving komt een melding met jouw reden.",
    titleLabel: "Titel",
    descriptionLabel: "Omschrijving",
    descriptionPlaceholder: "Waarom is deze wijziging nodig, waar moeten reviewers op letten\u2026",
    tabPullRequest: "Pull request",
    tabCommit: "Commit-bericht",
    branchLine: "wil samenvoegen in {base}",
    cancel: "Annuleren",
    confirm: "Pull request openen",
    opening: "Openen\u2026",
  },
  // Zie en.ts.
  sandboxRunList: {
    loading: "Verificaties laden\u2026",
    empty: "Nog geen verificaties.",
    open: "Open run",
    showAll: "Toon {n} oudere",
    olderVersion: "oudere versie",
    olderVersionHint: "Deze run verifieerde een oudere versie van deze wijziging, niet de huidige.",
  },
  agentSandbox: {
    title: "Agent-sandbox",
    intro: "Hoeveel de codeer-agent van dit project mag doen binnen zijn container. Je kunt alleen strenger zijn dan wat het platform toestaat, nooit ruimer — een verkeerde waarde hier beknot dus hooguit je eigen project.",
    disabledNotice: "De agent-sandbox staat op dit deployment uit. Deze waarden worden bewaard, maar doen nog niets: de agent draait nu op de server zelf. Je FixControl-beheerder kan dit aanzetten.",
    shell: "Shell",
    shellHint: "Mag de agent commando's draaien in zijn container?",
    shellLevel: {
      none: "Geen shell",
      readonly: "Wel draaien, niet bewerken",
      full: "Volledig",
    },
    network: "Netwerk",
    networkHint: "Hoe ver de container naar buiten mag. Let op: alleen 'geen' en 'open' worden door Docker zelf afgedwongen.",
    networkLevel: {
      none: "Geen netwerk",
      anthropic: "Alleen het model",
      registries: "Ook package-registries",
      open: "Open",
    },
    writeScope: "Schrijfruimte",
    writeScopeHint: "Waar de agent mag schrijven. Afgedwongen door de mount, niet door de toollijst.",
    writeScopeLevel: {
      none: "Alleen lezen",
      worktree: "Alleen de eigen checkout",
      worktree_and_tmp: "Checkout en /tmp",
    },
    maxRuntimeMs: "Looptijd (ms)",
    maxCommands: "Max. commando's",
    cpus: "CPU's",
    memoryMb: "Geheugen (MB)",
    pids: "Processen",
    inherit: "Overnemen ({value})",
    inheritHint: "Leeg laten betekent: overnemen van het platform. De grijze waarde onder een veld is wat een run daadwerkelijk krijgt.",
    clamped: "beperkt tot",
    clampNotice: "Het platformplafond is lager dan wat dit project vraagt. De opgeslagen wens blijft staan — wordt het plafond later verhoogd, dan geldt jouw waarde alsnog.",
    saved: "Opgeslagen",
    loadFailed: "Kon het agent-sandboxbeleid niet laden",
    saveFailed: "Opslaan mislukt",
    notANumber: "{field} is geen getal",
  },
  // Publieke marketingpagina-copy (use-cases, integraties, pricing). Zie
  // ./marketing — NL valt terug op EN bij ontbrekende sleutels.
  ...marketingNl,
  // Copy van de projectinrichting, één fragment per track (./project-setup).
  ...projectSetupNl,
  // Ledenpagina: de weigering bij de laatste projectbeheerder. Zie en.ts.
  projectMembersGuard: {
    lastAdmin: "Dit is de laatste projectbeheerder. Maak eerst iemand anders projectbeheerder, of vraag een beheerder van je organisatie om deze wijziging te doen.",
  },
  // Gedeelde datatable-kolomkoppen + maker-attributielabels. Zie en.ts.
  datatable: {
    created: "Aangemaakt",
    updated: "Gewijzigd",
    createdBy: "Aangemaakt door",
    resizeColumn: "Kolombreedte aanpassen",
    resizeHint: "Sleep om te verbreden · dubbelklik om te herstellen",
    resetWidths: "Kolombreedtes herstellen",
    actor: {
      system: "Systeem",
      device: "API",
      source: {
        slack: "Slack",
        email: "E-mail",
        api: "API",
        intake: "Intakeformulier",
        split: "Issue gesplitst",
        seed: "Demodata",
      },
    },
  },
  common: {
    loading: "Laden…",
    save: "Opslaan",
    cancel: "Annuleren",
    delete: "Verwijderen",
    edit: "Bewerken",
    create: "Aanmaken",
    close: "Sluiten",
    open: "Openen",
    yes: "Ja",
    no: "Nee",
    search: "Zoeken",
    back: "Terug",
    next: "Volgende",
    submit: "Versturen",
    error: "Er is iets misgegaan.",
    failedToLoad: "Laden is mislukt",
    tryAgain: "Opnieuw proberen",
    empty: "Niets te tonen",
    filters: {
      project: "Project",
      allProjects: "Alle projecten",
      clearProject: "Projectfilter wissen",
      projectUnnamed: "Project {id} (naam niet beschikbaar)",
    },
    providers: {
      email: "E-mail",
      webhook: "Webhook",
      local: "Lokaal",
      ssh: "SSH",
    },
    roles: {
      admin: "Beheerder",
      manager: "Manager",
      operator: "Operator",
    },
  },

  language: {
    label: "Taal",
    switchTo: "Wissel naar {name}",
  },

  nav: {
    sidebarLabel: "Hoofdnavigatie",
    overview: "Overzicht",
    work: "Werk",
    aiResults: "AI-resultaten",
    context: "Context",
    agents: "Agents",
    system: "Systeem",
    fixcontrol: "FixControl",
    aiOperations: "AI-operaties",
    aiOperationsMetrics: "Operationele metrics",
    platform: "Platform",
    dashboard: "Dashboard",
    inbox: "Inbox",
    notifications: "Meldingen",
    approvals: "Goedkeuringen",
    issues: "Issues",
    customerDirectory: "Contacten",
    missions: "AI-Missies",
    projects: "Projecten",
    workspaces: "Werksessies",
    patches: "Patches",
    sandboxRuns: "Sandbox-runs",
    followUps: "Opvolgingen",
    knowledge: "Kennisbank",
    codebasePosture: "Codebase-gezondheid",
    memory: "AI-geheugen",
    tenants: "Organisaties",
    aiTeam: "AI Team",
    skills: "Skills",
    governance: "Governance",
    governanceHierarchy: "Beleid & bereik",
    orchestrationConsole: "Orkestratie",
    runs: "Agent-runs",
    commandCenter: "Command Center",
    settings: "Instellingen",
    customers: "Klanten",
    signups: "Aanmeldingen",
    adminSettings: "Instellingen",
    patchDelivery: "Patch-aflevering",
    gitProvidersHealth: "Git-providers gezondheid",
    office365Health: "Office 365 gezondheid",
    knowledgeEmbeddings: "Kennis embeddings",
    brandExplorer: "Brand Explorer",
    internalDocs: "Documentatie",
    aiCapacity: "AI-capaciteit",
    newIssue: "Nieuwe issue",
    signOut: "Uitloggen",
    activeAgents: "{count} actief",
    agentsRunning: "{count} agent actief",
    agentsRunningPlural: "{count} agents actief",
    taskLabels: {
      support: {
        dashboard: "Overzicht",
        inbox: "Klantvragen",
        issues: "Tickets",
        knowledge: "Kennis",
      },
      engineering: {
        issues: "Problemen",
        missions: "AI-onderzoeken",
        patches: "Voorgestelde wijzigingen",
        sandboxRuns: "Controles & reviews",
        workspaces: "Werksessies",
      },
    },
    profileHint: {
      text: "Je ziet nu alle onderdelen. Kies een werkprofiel voor een opgeruimde navigatie die bij jouw taak past.",
      action: "Werkprofiel kiezen",
      dismiss: "Verbergen",
    },
  },

  breadcrumb: {
    back: "Terug",
    integrations: "Kanalen",
  },

  topbar: {
    dashboard: "Dashboard",
    live: "Live",
    searchPlaceholder: "Zoek of spring naar…",
    commandCenter: "Command Center",
    openCommandCenter: "Command Center openen (AI-assistent)",
    openNavigation: "Navigatie openen",
    openHelp: "Hulp openen voor deze pagina",
    supportMode: "Support-modus",
    supportModeDetail: "— je bekijkt {name} als FixControl-medewerker. Acties worden geregistreerd op deze organisatie.",
    backToCustomers: "Terug naar klanten",
    stopSupport: "Support stoppen",
    stopSupportTitle: "Terug naar je eigen organisatie ({name})",
  },

  help: {
    drawerLabel: "Hulp",
    title: "Hulp",
    browseTitle: "Alle onderwerpen",
    close: "Hulp sluiten",
    backToTopic: "Terug naar onderwerp",
    openExternal: "Open in nieuw tabblad",
    openExternalTitle: "Open dit artikel in de losse documentatiesite",
    browseAll: "Bekijk alle onderwerpen",
    sectionEmpty: "(geen pagina's)",
    loading: "Laden…",
    empty: "Geen documentatie gevonden.",
    audienceUser: "Gebruikershandleiding",
    audienceAdmin: "Beheerdersgids",
    readMore: "Meer lezen",
    /**
     * Eén zin per begrip dat dit product niet van het scherm kan houden.
     * Geschreven voor iemand die de architectuur nooit gelezen heeft en dat
     * ook nooit hoeft: wat het is, in termen van zijn eigen doel. De lange
     * versie staat in de hulplade.
     */
    explain: {
      validationStrategy: "Waar FixControl bewijst dat een wijziging werkt vóórdat iemand goedkeurt — op de machines van FixControl zelf, of in jouw omgeving als de wijziging systemen nodig heeft die alleen jij kunt bereiken.",
      environmentSettings: "Hoe je applicatie start. FixControl leidt dit af uit de repository; zet het hier als jij het beter weet, en elke controle start het vanaf dan op jouw manier.",
      /**
       * De VELDEN van de omgevingsinstellingen, één zin per veld. Elk zegt
       * wat het veld doet én wat er gebeurt als je het verandert — dat tweede
       * laat een formulier meestal aan de verbeelding van de lezer over, en
       * die lezer heeft hier nog nooit van een container gehoord.
       */
      envStartCommand: "Hoe je applicatie wordt gestart zodra alles geïnstalleerd is. FixControl heeft dit ingevuld vanuit de run die echt werkte; pas het alleen aan als daarmee het verkeerde onderdeel van je project startte.",
      envTargetName: "Een naam voor deze manier van starten, zodat de rest van FixControl ernaar kan verwijzen — bijvoorbeeld de voorbeeldlink. Alleen jij ziet hem, en hernoemen verandert niets aan hoe de applicatie draait.",
      envCommand: "Het ene commando dat je applicatie start, precies zoals je het zelf zou typen. Klopt het niet, dan mislukt de controle doordat de applicatie nooit antwoordt; laat je het staan, dan start de volgende controle hem zoals de laatste geslaagde run.",
      envPort: "Het nummer waarop je applicatie luistert binnen haar eigen machine. Het staat nooit open op internet — FixControl verbindt van binnenuit. Staat hier het verkeerde nummer, dan wacht de controle voor een deur waar niets achter zit en mislukt ze.",
      envReadinessPath: "De pagina die FixControl opent om te bepalen dat je applicatie draait — vaak / of een statuspagina. Kies er een die pas antwoordt als de applicatie echt klaar is: een pagina die te vroeg antwoordt laat een mislukte start geslaagd lijken.",
      envReadinessTimeout: "Hoeveel seconden je applicatie mag doen over dat eerste antwoord. Te laag en een trage maar gezonde start geldt als mislukt; te hoog en een écht kapotte start laat elke controle voor niets wachten.",
      envVars: "Instellingen die je applicatie uit haar omgeving leest, één NAAM=waarde per regel, bij elke start gezet. Zet hier nooit wachtwoorden of sleutels: die worden bewaard precies zoals je ze typt. De waarden die FixControl zelf aanmaakt of uit je eigen systemen leest, staan hierboven en hoef je niet in te vullen.",
      envSystemPackages: "Extra software die de machine nodig heeft om je code te bouwen — bijvoorbeeld de clientbibliotheek van PostgreSQL. Wat je toevoegt komt bovenop wat er al staat; iets weglaten verwijdert niets. Een naam die niet op de goedgekeurde lijst staat wordt geweigerd met de reden erbij, nooit stilletjes geïnstalleerd.",
      envInstallSteps: "Extra commando's die draaien ná de voorbereidende stappen hierboven, voor een project dat nog iets nodig heeft voordat het gebouwd kan worden. Ze draaien op volgorde bij elke controle en maken het wachten langer, dus laat dit leeg tenzij een controle er aantoonbaar op stukliep.",
      envNotes: "Alles wat FixControl verder moet weten om uit te zoeken hoe dit project draait, in je eigen woorden. Het wordt gelezen zodra de omgeving opnieuw wordt uitgezocht, en nooit uitgevoerd.",
      envSaveAndVerify: "Opslaan bewaart je instellingen voor de volgende controle. Opslaan en verifiëren start je applicatie meteen één keer om te bewijzen dat de instellingen werken — de enige manier om daarachter te komen vóórdat een echte wijziging ervan afhangt.",
      preview: "Een draaiende kopie van je applicatie met de voorgestelde wijziging erin, op een privé-link die verloopt. Klik er zelf doorheen voordat je goedkeurt.",
      browserVerification: "FixControl opent de draaiende kopie in een browser en legt vast wat het zag — zodat \"het werkt\" een schermafbeelding en een antwoordende pagina is, geen bewering.",
      reproduction: "Als een controle mislukt, kijkt FixControl rond in de omgeving waarin het misging en schrijft op wat het vond, voordat het het opnieuw probeert.",
      fcAgent: "Een kleine dienst die je in je eigen omgeving draait. Die belt naar FixControl, nooit andersom, zodat FixControl een wijziging kan controleren tegen systemen die niet vanaf internet bereikbaar zijn.",
      approvalRouting: "Wie er wordt gevraagd voordat er iets gebeurt, en voor welk soort wijziging. Risicovollere wijzigingen vragen zwaardere mensen; niets bereikt een klant of productie zonder dat het antwoord is vastgelegd.",
      agentsVsSkills: "Agents zijn de werkers — elk met een taak en een reglement. Skills zijn de dingen die een werker kan. Geef een agent een skill en hij kan het; neem hem weg en hij kan het niet.",
      sandbox: "Een wegwerpkopie van je project, afgesloten van internet en van je systemen. Elke voorgestelde wijziging wordt daar eerst geprobeerd, en niets erin overleeft de controle.",
      aiCapacity: "Of de AI-leveranciers achter FixControl werk weigeren — saldo op, te veel verzoeken, of meer gevraagd dan een model in één keer kan lezen — en wie daar last van heeft.",
    },
  },

  tenantSwitcher: {
    heading: "Wissel van workspace",
    selectTenant: "Kies een workspace",
    noTenant: "Geen workspace",
    empty: "Nog geen workspaces.",
    manage: "Workspaces beheren",
    switching: "Bezig met wisselen…",
    home: "Thuis",
    platform: "Platform",
    blocked: "Geblokkeerd",
    deleting: "Wordt verwijderd",
    roleLabel: "Jouw rol: {role}",
    errorTitle: "Wisselen van workspace is niet gelukt",
    dismiss: "Sluiten",
    multiTab: "Wisselen verandert de workspace in élk geopend tabblad.",
    errors: {
      not_a_member: "Je bent geen lid van die workspace.",
      tenant_blocked: "Die workspace is geblokkeerd. Vraag je platformbeheerder om deze te deblokkeren.",
      tenant_deleting: "Die workspace wordt verwijderd en is niet meer te openen.",
      session_revoked: "Je sessie is niet langer geldig voor die workspace. Log opnieuw in.",
      two_factor_reauth_required: "Die workspace vereist tweefactorverificatie. Log opnieuw in met je code.",
      two_factor_setup_required: "Die workspace vereist tweefactorauthenticatie. Stel die eerst in.",
      user_inactive: "Je account is niet actief. Neem contact op met een beheerder.",
      rate_limited: "Te vaak gewisseld in korte tijd. Wacht even en probeer het opnieuw.",
      csrf_origin_mismatch: "Het verzoek is om veiligheidsredenen geblokkeerd. Herlaad de pagina en probeer het opnieuw.",
      unknown: "Wisselen van workspace is mislukt. Probeer het opnieuw.",
    },
  },

  userMenu: {
    openMenu: "Gebruikersmenu openen",
    profile: "Profiel",
    changePassword: "Wachtwoord wijzigen",
    docs: "Documentatie",
    accountSettings: "Accountinstellingen",
    signOut: "Uitloggen",
  },

  pages: {
    hrDemo: {
      eyebrow: "HR-governancedemo",
      title: "Veilige HR-servicedesk",
      subtitle: "Identiteit, bronnen, persoonlijke data, beleid, review en audit worden server-side afgedwongen.",
      tenantLabel: "Organisatie",
      projectLabel: "project",
      scenarioGeneral: "Algemeen beleid",
      scenarioPersonal: "Persoonlijk",
      scenarioConfidential: "Vertrouwelijk",
      scenarioConflict: "Bronconflict",
      questionLabel: "Vraag van medewerker",
      submit: "Vraag indienen",
      submitting: "Veilig verwerken…",
      submitFailed: "De vraag kon niet worden verwerkt.",
      boardLoadFailed: "Het HR-bord kon niet worden geladen.",
      boardLoading: "HR-bord laden…",
      policyDecision: "Beleidsbesluit",
      sources: "Bronnen",
      sourceVersion: "versie {version} · {status}",
      noAuthoritativeSource: "Geen geldige definitieve bron.",
      sourceConflict: "Bronconflict",
      caseLabel: "Zaak {id}",
      personalData: "Minimale systeemgegevens: {hours} verlofuren, peildatum {asOf}.",
      salaryRule: "Salarisregel: {rule}.",
      versionLabel: "versie {version}",
      reviewWork: "Beoordelingswerk",
      noReviewTasks: "Geen zichtbare beoordelingstaken.",
      auditTimeline: "Privacybewuste audittijdlijn",
      noAuditEvents: "Geen zichtbare auditgebeurtenissen.",
      casesTitle: "Zaken",
      noCases: "Geen zichtbare zaken.",
      answersTitle: "Antwoorden",
      noAnswers: "Geen zichtbare antwoorden.",
      correctionsTitle: "Open correcties",
      assignedTo: "Toegewezen aan {user}",
      unassigned: "Nog niet toegewezen",
      assignLoad: "Toewijzen…",
      assignAction: "Wijs toe",
      escalateAction: "Escaleren",
      resolveAction: "Oplossen",
      resolutionPlaceholder: "Oplossing (verplicht om af te ronden)",
      requestApproval: "Ter goedkeuring aanbieden",
      approve: "Goedkeuren",
      reject: "Afwijzen",
      deliver: "Leveren",
      fourEyesNote: "Vier ogen: een tweede beoordelaar moet deze goedkeuring nemen.",
      reportIncorrect: "Fout melden",
      reportReasonPlaceholder: "Waarom klopt dit antwoord niet?",
      correctAction: "Correctie publiceren",
      retractAction: "Intrekken",
      correctedTextPlaceholder: "Gecorrigeerde antwoordtekst",
      correctionReasonPlaceholder: "Reden van correctie / intrekking",
      actionDone: "Uitgevoerd — het bord hieronder toont de nieuwe status.",
      actionFailed: "De actie is niet uitgevoerd.",
      settingsTitle: "HR-governance (projectinstelling)",
      settingsEnabled: "Ingeschakeld voor dit project",
      settingsThreshold: "Betrouwbaarheidsdrempel voor automatisch antwoorden",
      save: "Opslaan",
      settingsSaved: "Instellingen opgeslagen.",
      /**
       * Leesbare labels bij de opgeslagen HR-waarden. De waarden zelf
       * (zaakstatus, gevoeligheid, classificatie, beleidsbesluit, reden)
       * veranderen niet: dit is uitsluitend presentatie. Eén platte lijst,
       * omdat dezelfde waarde overal hetzelfde betekent (een DRAFT-bron en een
       * DRAFT-antwoord zijn allebei een concept); een onbekende waarde valt
       * terug op de rauwe code, zodat een nieuwe enumwaarde zichtbaar
       * onvertaald is in plaats van stil verkeerd gelabeld.
       */
      labels: {
        // Zaakstatus
        NEW: "Nieuw",
        TRIAGED: "Getrieerd",
        WAITING_FOR_INFORMATION: "Wacht op informatie",
        WAITING_FOR_HUMAN_REVIEW: "Wacht op menselijke beoordeling",
        IN_REVIEW: "In beoordeling",
        ANSWER_PROPOSED: "Antwoord voorgesteld",
        APPROVAL_REQUIRED: "Goedkeuring nodig",
        RESOLVED: "Afgerond",
        ESCALATED: "Geëscaleerd",
        BLOCKED: "Geblokkeerd",
        CANCELLED: "Geannuleerd",
        // Antwoord-, goedkeurings- en correctiestatus
        GENERATED: "Gegenereerd",
        DRAFT: "Concept",
        REVIEW_REQUIRED: "Beoordeling nodig",
        APPROVED: "Goedgekeurd",
        DELIVERED: "Geleverd",
        UNDER_REVIEW: "In herbeoordeling",
        CORRECTED: "Gecorrigeerd",
        SUPERSEDED: "Vervangen",
        RETRACTED: "Ingetrokken",
        PENDING: "In afwachting",
        REJECTED: "Afgewezen",
        EXPIRED: "Verlopen",
        INVALIDATED: "Vervallen",
        CONSUMED: "Gebruikt",
        OPEN: "Open",
        ANSWERED: "Beantwoord",
        // Gevoeligheid
        NORMAL: "Normaal",
        SENSITIVE: "Gevoelig",
        HIGHLY_RESTRICTED: "Streng afgeschermd",
        // Gegevensclassificatie
        INTERNAL: "Intern",
        PERSONAL: "Persoonlijk",
        CONFIDENTIAL_HR: "Vertrouwelijk HR",
        PAYROLL_CONFIDENTIAL: "Vertrouwelijke salarisgegevens",
        MEDICAL_RESTRICTED: "Medisch, afgeschermd",
        LEGAL_RESTRICTED: "Juridisch, afgeschermd",
        // Soort vraag
        GENERAL_POLICY: "Algemeen beleid",
        PERSONAL_INFORMATION: "Persoonlijke gegevens",
        PERSONAL_CALCULATION: "Persoonlijke berekening",
        PROCEDURAL_REQUEST: "Procedurevraag",
        SENSITIVE_EMPLOYEE_RELATIONS: "Gevoelige arbeidsrelatie",
        MEDICAL_OR_ABSENCE: "Medisch of verzuim",
        PAYROLL: "Salarisadministratie",
        LEGAL_OR_COMPLIANCE: "Juridisch of compliance",
        UNKNOWN: "Onbekend",
        // Beleidsbesluit
        AUTO_ANSWER_ALLOWED: "Automatisch antwoord toegestaan",
        DRAFT_ONLY: "Alleen als concept",
        HUMAN_REVIEW_REQUIRED: "Menselijke beoordeling nodig",
        ESCALATION_REQUIRED: "Escalatie nodig",
        DENIED: "Geweigerd",
        CLARIFICATION_REQUIRED: "Verduidelijking nodig",
        // Soort beoordelingstaak
        SENSITIVE_CASE: "Gevoelige zaak",
        ANSWER_APPROVAL: "Antwoord ter goedkeuring",
        INCORRECT_ANSWER: "Als onjuist gemeld",
        CONNECTOR_FAILURE: "Koppeling niet bereikbaar",
        // Bronstatus
        AUTHORITATIVE: "Definitief",
        SUPPORTING: "Ondersteunend",
        DEPRECATED: "Vervallen bron",
        // Redenen
        SOURCE_CONFLICT: "Bronconflict",
        SOURCE_NOT_FOUND: "Geen definitieve bron",
        SOURCE_EXPIRED: "Bron verlopen",
        LOW_CONFIDENCE: "Betrouwbaarheid onder de drempel",
        PERSONAL_IMPACT: "Persoonlijke salarisimpact",
        CURRENT_AUTHORITATIVE_SOURCE: "Geldige definitieve bron",
        SAFE_DEFAULT: "Veilige standaardroute",
        DATA_SCOPE_VIOLATION: "Buiten je eigen gegevens",
        CONNECTOR_NOT_ALLOWLISTED: "Koppeling niet toegestaan",
        EXTERNAL_CONNECTOR_UNAVAILABLE: "Bronsysteem niet bereikbaar",
        SOURCE_METADATA_INCOMPLETE: "Onvolledige brongegevens",
        EXPIRED_OR_INACTIVE_SOURCE_PRESENT: "Verlopen of inactieve bron aanwezig",
        AUTHORITATIVE_RULE_CONFLICT: "Definitieve bronnen spreken elkaar tegen",
        DRAFT_CONTRADICTS_AUTHORITATIVE: "Concept spreekt de definitieve bron tegen",
      },
    },
    customers: {
      eyebrow: "Klantcontext",
      indexTitle: "Contacten",
      indexSubtitle: "De klanten en contactpersonen achter gegoverneerde klantcommunicatie.",
      searchPlaceholder: "Zoek klanten…",
      statusActive: "Actief",
      statusArchived: "Gearchiveerd",
      statusAll: "Alle",
      newCustomer: "Nieuwe klant",
      createTitle: "Nieuwe klant",
      nameLabel: "Klantnaam",
      contactLegend: "Eerste contactpersoon (optioneel)",
      firstName: "Voornaam",
      lastName: "Achternaam",
      email: "E-mail",
      contactRole: "Rol",
      create: "Aanmaken",
      cancel: "Annuleren",
      save: "Opslaan",
      colName: "Klant",
      colStatus: "Status",
      colContacts: "Contacten",
      colUpdated: "Bijgewerkt",
      empty: "Nog geen klanten",
      emptyHint: "Maak hier een klant aan, of vanuit Slack met /fixcontrol customer create.",
      loadError: "Klanten konden niet worden geladen.",
      notFound: "Klant niet gevonden.",
      backToList: "Alle klanten",
      detailEyebrow: "Klant",
      rename: "Naam wijzigen",
      archive: "Archiveren",
      reactivate: "Heractiveren",
      syncAction: "Synchroniseer naar supportdesk",
      syncOk: "Gesynchroniseerd naar de supportdesk.",
      syncNotConnected: "Er is geen supportdesk-connector geconfigureerd.",
      contactsTitle: "Contactpersonen",
      contactsEmpty: "Nog geen contactpersonen.",
      addContact: "Contactpersoon toevoegen",
      editContact: "Bewerken",
      contactArchived: "gearchiveerd",
      communicationsTitle: "Communicatie",
      communicationsEmpty: "Nog geen gegoverneerde communicatie voor deze klant.",
      communicationsHint: "Stel er een op vanuit Slack met /fixcontrol email draft — elke verzending gaat door beleid en menselijke goedkeuring.",
      commSubjectFallback: "(geen onderwerp)",
      commStatus: {
        draft: "Concept",
        pending_approval: "Wacht op goedkeuring",
        approved: "Goedgekeurd",
        sent: "Verzonden",
        failed: "Verzending mislukt",
        rejected: "Afgewezen",
        discarded: "Vervallen",
      },
      toastCreated: "Klant aangemaakt.",
      toastUpdated: "Klant bijgewerkt.",
      toastContactAdded: "Contactpersoon toegevoegd.",
      toastContactUpdated: "Contactpersoon bijgewerkt.",
    },
    issues: {
      eyebrow: "Beslis-engine",
      title: "Issues",
      subtitle: "Scannen, beslissen, uitrollen. Acties zijn inline — detailpagina alleen bij meer context.",
      newIssue: "Nieuwe issue",
      open: "Open",
      resolved: "Opgelost",
      all: "Alle",
      shown: "zichtbaar",
      searchPlaceholder: "Zoek op titel, key, organisatie, impact…",
      empty: "Niets gevonden.",
      emptyHint: "Wis filters of zoekopdracht om de hele wachtrij te zien.",
      exportCsv: "Exporteer CSV",
      filters: {
        project: "Project",
        allProjects: "Alle projecten",
        status: "Status",
        allStatuses: "Alle statussen",
        allPriorities: "Alle prioriteiten",
      },
      headers: {
        key: "Key",
        issue: "Issue",
        project: "Project",
        status: "Status",
        priority: "Prioriteit",
        actions: "Acties",
      },
    },
    patches: {
      eyebrow: "Werk",
      title: "Patches",
      subtitle: "Elke code-patch voor {company} — wacht op review, goedgekeurd, toegepast, afgewezen of gearchiveerd.",
      subtitleNoTenant: "Elke code-patch — wacht op review, goedgekeurd, toegepast, afgewezen of gearchiveerd.",
      pending: "Wacht op review",
      approved: "Goedgekeurd",
      applied: "Toegepast",
      rejected: "Afgewezen",
      total: "Totaal",
      empty: "Geen patches die aan dit filter voldoen.",
      // Zie en.ts.
      emptyAll: "Nog geen patches.",
      emptyHint: "Een patch verschijnt hier zodra een agent een codewijziging voorstelt voor een issue. Start een missie vanuit een issue om er een te krijgen.",
      emptyAction: "Issues openen",
      filteredEmptyHint: "Wis het filter om alle patches te zien.",
      fileCount: "{count} bestand",
      fileCountPlural: "{count} bestanden",
      workspaceLink: "werkruimte",
      untitledWorkspace: "Naamloze werkruimte",
      cols: { patch: "Patch", version: "Versie", status: "Status", verification: "Verificatie" },
      verification: { notRun: "niet gedraaid" },
      rowAction: {
        approve: "Goedkeuren",
        reject: "Afwijzen",
        archive: "Archiveren",
      },
      searchPlaceholder: "Zoek patches…",
      columnsMenu: "Kolommen",
      splitView: {
        enable: "Zijpaneel tonen",
        disable: "Zijpaneel verbergen",
        detail: "Patchdetails",
        resize: "Breedte van het patchpaneel aanpassen",
        emptyTitle: "Kies een patch om te beoordelen",
        emptyHint: "Klik op een rij om hier de samenvatting, bestanden en beslisgeschiedenis te laden.",
      },
      bulk: {
        approve: "Goedkeuren",
        reject: "Afwijzen",
        archive: "Archiveren",
        rejectTitle: "Geselecteerde patches afwijzen?",
        rejectBodyOne: "1 patch wordt afgewezen.",
        rejectBodyMany: "{count} patches worden afgewezen.",
        archiveTitle: "Geselecteerde patches archiveren?",
        archiveBodyOne: "1 patch verdwijnt uit de wachtrijen. Hij blijft onder Archief bewaard, met runs en geschiedenis.",
        archiveBodyMany: "{count} patches verdwijnen uit de wachtrijen. Ze blijven onder Archief bewaard, met runs en geschiedenis.",
      },
      // Zie en.ts.
      actionError: {
        approve: "Goedkeuren lukte niet: {reason}",
        reject: "Afwijzen lukte niet: {reason}",
        archive: "Archiveren lukte niet: {reason}",
        gateOpen: "er wacht nog een goedkeuring op deze patch. Wijs hem af, dan wordt die goedkeuring meteen afgehandeld.",
        notArchivable: "de patch is al afgerond.",
        unknown: "de server weigerde het.",
      },
      preview: {
        start: "Preview starten",
        starting: "Preview start op…",
        open: "Preview openen",
        dismiss: "Sluiten",
        startFailed: "Kon de preview niet starten.",
        openFailed: "Kon de preview niet openen.",
        gone: "Deze preview draait niet meer.",
      },
      filter: {
        pending: "Te reviewen",
        approved: "Goedgekeurd",
        applied: "Toegepast",
        rejected: "Afgewezen",
        archived: "Archief",
        all: "Alle",
      },
      status: {
        draft: "Concept",
        approved: "Goedgekeurd",
        rejected: "Afgewezen",
        applied: "Toegepast",
        changes_requested: "Wijzigingen aangevraagd",
        archived: "Gearchiveerd",
        superseded: "Vervangen",
      },
      supersededHint: "Er bestaat een nieuwere versie van deze patch in dezelfde werkruimte.",
    },
    knowledge: {
      eyebrow: "Context",
      title: "Kennisbank",
      subtitle: "Documenten, FAQ's en referenties die de agents kunnen raadplegen bij triage.",
      addSource: "Bron toevoegen",
      empty: "Nog geen bronnen.",
      emptyHint: "Voeg een document toe, plak een URL of upload een tekstbestand (Markdown, TXT, JSON of CSV) om de agents context te geven.",
      inspectButton: "Codebase inspecteren",
      inspectButtonNoCodebase: "Codebase inspecteren",
      inspectTooltip: "Genereer technische documentatie uit een gekoppelde codebase",
      scrapeButton: "URL scrapen",
      stats: {
        sources: "Bronnen",
        sourcesSub: "{count} geïndexeerd",
        chunks: "Chunks",
        chunksSub: "Doorzoekbare eenheden",
        codebaseDocs: "Codebase-docs",
        codebaseDocsSub: "Gegenereerd door inspect",
        learnings: "Leringen",
        learningsSub: "Cases vastgelegd",
        windev: "WINDEV",
        windevSub: "Platform-bronnen",
        coverage: "Dekking",
        coverageSub: "Geïndexeerd percentage",
      },
      filters: {
        searchPlaceholder: "Zoek titels, tags, referenties…",
        allTenants: "Alle organisaties",
        allTypes: "Alle typen",
        allCodebases: "Alle codebases",
        general: "Algemeen",
        windev: "WINDEV",
        codebase: "Codebase-docs",
        learning: "System Learning",
        allScopes: "Alle scopes",
        scopeTenant: "Organisatiebreed",
        scopeProject: "Project",
        scopeCodebase: "Codebase",
        allProjects: "Alle projecten",
      },
      scope: {
        column: "Scope",
        tenant: "Organisatiebreed",
        project: "Project",
        codebase: "Codebase",
        tenantHint: "Beschikbaar binnen de hele organisatie.",
        projectHint: "Beschikbaar voor agents die aan dit project werken.",
        codebaseHint: "Gegenereerd uit of gekoppeld aan een codebase.",
        tenantWideWarning: "Organisatiebrede bronnen kunnen in alle projecten beschikbaar zijn. Gebruik projectniveau voor projectspecifieke documentatie.",
      },
      sourcesPanelTitle: "Bronnen",
      sourcesPanelSub: "Klik om te inspecteren — gebruik het prullenbakicoon om te verwijderen",
      table: {
        title: "Titel",
        tenant: "Organisatie",
        kind: "Type",
        chunks: "Chunks",
        status: "Status",
        moreTags: "+{count}",
        attemptHint: "Poging {index} van {total} op deze melding. Aparte patchruns met een eigen uitkomst, geen duplicaat.",
      },
      noSources: "Nog geen kennis",
      noSourcesHint: "Kennis is wat de agents lezen voordat ze antwoorden of een wijziging voorstellen: je documentatie, een gescrapete site, of wat FixControl uit een gekoppelde codebase heeft geleerd. Zonder kennis werken ze alleen met de tekst van de melding.",
      noSourcesAsk: "Welke kennis moet ik FixControl als eerste geven?",
      filteredEmpty: "Niets voldoet aan deze filters",
      filteredEmptyHint: "Wis de zoekterm of de filters op type, bereik, project en codebase om alles te zien.",
      deleteOne: {
        confirmTitle: "Deze kennisbron verwijderen?",
        confirmLabel: "Bron verwijderen",
        confirmBody: "De bron en haar chunks worden verwijderd, dus agents halen er niets meer uit op. Antwoorden die haar al citeerden houden hun vastgelegde bronvermelding.",
      },
      bulkDelete: {
        confirmTitle: "Geselecteerde bronnen verwijderen?",
        confirmLabel: "Bronnen verwijderen",
        confirmBody: "Hiermee verwijder je definitief {count} kennisbron(nen) en alle bijbehorende chunks. Dit kan niet ongedaan worden gemaakt.",
        resultDeleted: "{count} bron(nen) verwijderd.",
        resultSkipped: "{count} overgeslagen (niet gevonden in deze organisatie).",
      },
    },
    memory: {
      eyebrow: "Context",
      title: "AI-geheugen",
      subtitle: "Markdown-regels die elke agent moet volgen. Globale regels gelden overal; projectregels komen daar bovenop.",
      tabGlobal: "Globaal",
      tabProject: "Project",
      saved: "Opgeslagen",
      saving: "Opslaan…",
    },
    workspace: {
      eyebrow: "Werk",
      title: "Werksessies",
      subtitle: "AI-ondersteunde codewijzigingssessies.",
      patchPanel: "Patch",
      activity: "Activiteit",
      chat: "Chat",
      noPatch: "Nog geen patch.",
      runDev: "Developer starten",
      reRunDev: "Opnieuw met feedback",
      newSession: "Nieuwe code-sessie",
      searchPlaceholder: "Zoek werkruimtes…",
      empty: "Geen werkruimtes gevonden",
      emptyHint: "Een werkruimte ontstaat zodra een agent aan een issue begint te werken. Start een missie vanuit een issue om er een te krijgen.",
      emptyAction: "Issues openen",
      shownOf: "Toont {visible} van {total} werkruimtes",
      headers: {
        title: "Titel",
        issue: "Issue",
        status: "Status",
        updated: "Bijgewerkt",
      },
      rename: "Hernoemen",
      renameTitle: "Werkruimte hernoemen",
      deleteTitle: "Werkruimte verwijderen",
      deleteConfirm: "{name} verwijderen? De werkruimte verdwijnt uit de lijst en een beheerder kan haar terugzetten. De patches, goedkeuringsbeslissingen, sandbox-verdicten en audithistorie blijven bewaard.",
      createTitle: "Nieuwe code-sessie",
      createSubtitle: "Kies een project en beschrijf wat je nodig hebt.",
      createFields: {
        project: "Project",
        sessionTitle: "Sessietitel",
        sessionTitlePlaceholder: "bijv. Fix payment-metadata",
        linkedIssue: "Gekoppelde issue",
        linkedIssuePlaceholder: "bijv. BIZ-29",
        instruction: "Instructie",
        instructionPlaceholder: "Beschrijf de codewijziging die je nodig hebt…",
      },
      noProjects: "Nog geen projecten.",
      createOneFirst: "Maak er eerst een aan",
      starting: "Bezig met starten…",
      startSession: "Sessie starten",
      contextHint: {
        codebase: "Codebase",
        source: "Bron",
        repo: "Repo",
        path: "Pad",
        mode: "Modus",
      },
      status: {
        drafting: "Aan het schrijven",
        ready_for_review: "Klaar voor review",
        approved: "Goedgekeurd",
        rejected: "Afgewezen",
        applied: "Toegepast",
      },
      bulk: {
        delete: "Verwijderen",
        deleteConfirmTitle: "Geselecteerde werkruimtes verwijderen?",
        deleteConfirmLabel: "Werksessies verwijderen",
        deleteConfirmBody: "Dit verbergt {count} werkruimte(s) uit de lijst. Een beheerder kan ze terugzetten; hun patches, goedkeuringsbeslissingen, sandbox-verdicten en audithistorie blijven bewaard.",
        result: "{ok} verwijderd",
        resultPartial: "{ok} verwijderd, {failed} mislukt",
      },
    },
    sandboxRuns: {
      eyebrow: "Verificatie",
      title: "Sandbox runs",
      subtitle: "Elke keer dat FixControl een voorgestelde wijziging bouwde en testte tegen een wegwerpkopie van je project — wat het uitvoerde en wat het bewees.",
      empty: "Nog geen sandbox runs — die verschijnen zodra een patch is geverifieerd.",
      // Zie en.ts.
      emptyHint: "Een run verschijnt hier zodra een patch is geverifieerd tegen de omgeving van een project. Verifieer de omgeving van een project onder Code & omgevingen om de eerste te krijgen.",
      emptyAction: "Projecten openen",
      filteredEmpty: "Geen runs die aan deze filters voldoen.",
      filteredEmptyHint: "Wis het status- of projectfilter om alle runs te zien.",
      archivedEmpty: "Het archief is leeg.",
      archivedEmptyHint: "Archiveer afgeronde runs vanuit de lijst (selecteer ze en kies Archiveren) om de lijst overzichtelijk te houden. Gearchiveerde runs blijven hier te openen.",
      row: { archive: "Archiveren", restore: "Herstellen" },
      filter: { all: "Alle", passed: "Geslaagd", failed: "Mislukt", running: "Actief", errored: "Fout/time-out", cancelled: "Geannuleerd", archived: "Gearchiveerd" },
      cols: { status: "Status", run: "Run", language: "Taal", lane: "Waar het draaide", steps: "Stappen", duration: "Duur", created: "Aangemaakt" },
      lane: { local: "Bij FixControl", remote: "In je eigen cluster" },
      noPatch: "geen patch gekoppeld",
      patchRef: "patch v{version}",
      patchRefId: "patch {id}",
      stepsSummary: "{passed}/{total} geslaagd",
      // Rundetail — zie en.ts.
      detail: {
        cancel: "Run annuleren",
        // Niet "Opnieuw draaien": deze run start nooit opnieuw — zijn oordeel
        // is bewijs waar de apply-poort naar verwijst. Er begint een nieuwe
        // verificatie van dezelfde patch, gekoppeld aan deze.
        rerun: "Opnieuw verifiëren",
        loading: "Deze run wordt geladen…",
        loadFailed: "Kon deze run niet laden: {message}",
        cancelled: "Run geannuleerd.",
        cancelFailed: "Kon deze run niet annuleren: {message}",
        archive: "Archiveren",
        restore: "Herstellen",
        archived: "Run gearchiveerd. Je vindt hem terug onder Gearchiveerd.",
        archiveFailed: "Kon deze run niet archiveren: {message}",
        restored: "Run hersteld uit het archief.",
        restoreFailed: "Kon deze run niet herstellen: {message}",
        archivedBanner: "Deze run staat in het archief. Uitkomst, logs en bewijs zijn ongewijzigd.",
        rerunRequested: "Er is een nieuwe verificatie gestart.",
        rerunReused: "Er loopt al een verificatie van precies deze patchversie — die volgen we.",
        rerunFailed: "Kon geen nieuwe verificatie starten: {message}",
        attemptOf: "poging {index} van {total}",
        // Zie en.ts.
        patchSuperseded: "Deze run verifieerde v{current}. Het AI-team schreef daarna v{newer} \u2014 opnieuw verifiëren gaat wéér over v{current}.",
        patchSupersededLink: "Open v{version}",
        newerRunPassed: "Een latere verificatie van deze patch is geslaagd.",
        newerRunFailed: "Er is een latere verificatie van deze patch; die is niet geslaagd.",
        newerRunRunning: "Er loopt een latere verificatie van deze patch.",
        newerRunLink: "Open de nieuwste run",
        technicalDetails: "Technische details",
        artifacts: "Bestanden uit de run",
        steps: "Wat de controle heeft uitgevoerd",
        stepLog: "Uitvoer van {name}",
        stepLogTail: "Laatste regels — de volledige uitvoer verschijnt zodra de controle klaar is.",
        noSteps: "Er zijn nog geen stappen gemeld.",
        notExecuted: "Deze controle is nooit gedraaid: {reason}",
        notExecutedTitle: "Deze controle is nooit gedraaid",
        notExecutedTechnical: "Toon de technische reden (Engels)",
        /**
         * De twee Windows-toolchains. De weigering van de runtime noemt een
         * TypeScript-interface; deze zeggen wat voor machine het project
         * nodig heeft en of die er al is.
         */
        notExecutedReason: {
          dotnetframework: "Dit project is gebouwd met .NET Framework. Het compileert tegen Windows-only assemblies, dus FixControl kan het alleen verifiëren op een Windows-buildmachine in je eigen omgeving — die is nog niet gekoppeld.",
          windev: "Dit project is gebouwd met WINDEV. Het compileert en test alleen via een gelicentieerde WINDEV-installatie, dus FixControl kan het alleen verifiëren op een Windows-machine in je eigen omgeving met zo'n installatie — die is nog niet gekoppeld.",
        },
        notExecutedNext: {
          dotnetframework: "Koppel een Windows-buildmachine onder Instellingen → Infrastructuur. Tot die tijd leest FixControl dit project en stelt het wijzigingen voor, maar beweert het nooit dat ze geverifieerd zijn.",
          windev: "Koppel een Windows-machine met WINDEV onder Instellingen → Infrastructuur. Tot die tijd leest FixControl dit project en stelt het wijzigingen voor, maar beweert het nooit dat ze geverifieerd zijn.",
        },
        runError: "De controle stopte met een fout: {error}",
        profileNotes: "Wat de repositoryscan opmerkte",
        profileNotesUntranslated: "Geschreven door de scanner tijdens het plannen van deze controle. Technisch, alleen Engels, en hier bewaard voor wie de omgeving van dit project afstemt.",
        evidenceTitle: "Validatiebewijs",
        strategyFromProject: "projectinstelling",
        none: "—",
        runId: "Run-id",
        patchValue: "v{version} · {status}",
        tiles: {
          language: "Taal",
          workspace: "Werkruimte",
          patch: "Patch",
          duration: "Duur",
          lane: "Waar het draaide",
          strategy: "Validatie",
        },
        fields: {
          executor: "Executor",
          fingerprint: "Fingerprint",
          cluster: "Cluster",
          namespace: "Namespace",
          agent: "Agent",
          operation: "Operatie",
          appStart: "Start van de applicatie",
        },
        reproduction: {
          title: "Wat FixControl vond in de mislukte testomgeving",
          lead: "FixControl heeft deze fout van binnenuit de testomgeving bekeken waarin hij optrad, daar {n} commando('s) uitgevoerd en concludeerde:",
          note: "De commando's draaiden in de eigen sandbox van deze run, die daarna direct is vernietigd. De bevinding is een waarneming die de volgende poging meeweegt — het is geen wijziging, en er is niets toegepast.",
          finding: "Bevinding",
          cause: "Waarschijnlijke oorzaak",
          direction: "Voorgestelde richting",
          confidence: "Zekerheid",
          /** Hoeveel gewicht de bevinding heeft, als zin — de opgeslagen
           *  waarde is high/medium/low en een lezer hoort geen enum te
           *  vertalen naar een oordeel. */
          confidenceValue: {
            high: "Het bewijs ondersteunt dit goed.",
            medium: "Het bewijs wijst deze kant op, maar is niet sluitend.",
            low: "Dit is een zwakke lezing van dun bewijs — beschouw het als aanwijzing, niet als antwoord.",
          },
          transcript: "Toon de uitgevoerde commando's",
          noFinding: "FixControl heeft in de mislukte testomgeving gekeken en kwam niet tot een conclusie.",
          commands: "{n} commando('s)",
          exit: "exitcode {code}",
          outputTruncated: "… uitvoer afgekapt op de limiet",
          /** Waarom een commando niets opleverde. De opgeslagen uitkomsten
           *  zijn ran/timeout/refused/cancelled/infrastructure. */
          outcome: {
            ran: "uitgevoerd",
            timeout: "duurde te lang en is gestopt",
            refused: "niet uitgevoerd — een limiet was al bereikt",
            cancelled: "de run is geannuleerd",
            infrastructure: "de testomgeving kon dit niet uitvoeren",
          },
          optIn: {
            title: "Laat FixControl mislukkingen onderzoeken in de testomgeving",
            body: "Als een controle mislukt, kan FixControl rondkijken in dezelfde afgeschermde testomgeving waarin het misging — een configuratiebestand lezen, één mislukte test opnieuw draaien met meer uitvoer, de testdatabase vragen wat erin staat — en de volgende poging schrijven op basis van wat het zag, in plaats van op de laatste regels van een log. Die omgeving heeft geen internet en geen toegang tot jouw systemen, wordt daarna direct weggegooid, en alles wat FixControl er deed wordt hier vastgelegd zodat je het kunt nalezen.",
            cost: "Het kost ongeveer een minuut extra op een run die toch al mislukt was, en niets op een run die slaagt.",
            enable: "Zet dit aan",
            enabling: "Bezig met aanzetten…",
            enabled: "Aan — de volgende mislukte controle wordt onderzocht.",
            failed: "Kon dit niet aanzetten ({status}). Er is niets gewijzigd.",
            noPermission: "Alleen beheerders en managers kunnen dit aanzetten.",
          },
        },
        appStart: {
          title: "Startte de applicatie?",
          verified: "Gestart en antwoordde",
          failed: "Startte niet",
          notAttempted: "Niet gestart",
          unsupported: "Hier niet geverifieerd",
          target: "Wat er gestart is",
          answered: "Antwoordde",
          budget: "Mocht duren",
          took: "Duurde",
          note: "De tests kunnen slagen terwijl de applicatie niet meer opstart. Dit is de controle die die twee uit elkaar houdt.",
          /**
           * Het gevolg van elk antwoord, hardop. "Startte niet" alleen nodigt
           * uit om het af te wegen tegen een groene testrun; het is geen
           * afweging, het is wat de uitslag bepaalt.
           */
          consequence: {
            verified: "De applicatie kwam op en antwoordde op haar eigen adres, met deze wijziging erin.",
            failed: "FixControl kon de applicatie met deze wijziging niet starten. Geslaagde tests bewijzen niet dat het werkt — beschouw deze run als rood.",
            notAttempted: "Niemand heeft gecontroleerd of de applicatie nog start. De tests zeggen daar niets over.",
            unsupported: "Deze omgeving geeft niets op om te starten, dus er viel niets te controleren. De tests zeggen niets over of de applicatie draait.",
          },
        },
        preview: "Bekijk live",
        previewFailed: "Kon geen voorbeeld openen: {message}",
        previewTitle: "Live voorbeeld van deze patch",
        previewOpen: "Open de applicatie in een nieuw tabblad",
        previewNote: "Een tijdelijke omgeving met deze patch erin. Hij stopt vanzelf en is niet je productiesysteem.",
        previewStarting: "De omgeving wordt gestart…",
        previewStartingHint: "We halen de images op, starten de services en wachten tot de applicatie antwoordt. Zodra dat lukt verschijnt hier de knop om hem te openen.",
        previewElapsed: "{seconds}s bezig",
        previewExpiresIn: "Stopt automatisch over {minutes} min.",
        previewStartFailed: "De omgeving is niet gestart. Er draait nu niets om te bekijken.",
        previewStartTimeout: "De omgeving is nog steeds bezig met starten. Hij start door op de server — herlaad de pagina om verder te kijken.",
        previewRetry: "Opnieuw proberen",
        environmentBlock: {
          title: "Deze run heeft de patch niet beoordeeld",
          body: "{reason}. De omgeving is wat faalde, dus niets hier zegt iets over de patch — en daarom is er geen revisie gestart.",
          /** Onbekend is géén bewijs dat de omgeving faalde; alleen dat niets
           *  het geclassificeerd heeft. Dan noemen we de stap en laten we in
           *  het midden waar het aan lag. */
          bodyUnknown: "De oorzaak kon niet worden vastgesteld. Deze run zegt daarom niet of het aan de omgeving of aan de patch lag — en er is geen revisie gestart.",
          bodyUnknownStep: "De run stopte bij {step}. De oorzaak kon niet worden vastgesteld, dus deze run zegt niet of het aan de omgeving of aan de patch lag — en er is geen revisie gestart.",
          verdictCaveat: "Dit zijn nieuwe fouten, geen bewezen regressies: de omgeving faalde in deze run, dus een test die nooit is afgerond telt als nieuw.",
          openProject: "Open de omgeving van het project",
          noProjectTitle: "Deze run had geen project",
          noProjectBody: "De melding is niet aan een project gekoppeld, en het project draagt de geverifieerde testomgeving — er was dus niets om deze wijziging in te starten. Koppel de melding op haar werksessie aan een project en start de verificatie opnieuw.",
        },
        regressionVerdict: {
          headPassed: "Tests geslaagd — regressiebewust beoordeeld",
          headFailed: "Tests gefaald — regressiebewust beoordeeld",
          // Twee verschillende niet-uitslagen: de ene suite werd gestopt
          // voordat ze iets kon zeggen, de andere sprak zonder iets gedraaid
          // te hebben.
          headNeverRan: "De tests hebben nooit gedraaid — geen uitslag",
          headRanNothing: "De teststap draaide, maar selecteerde geen enkele test",
          headUiUnsupported: "UI-tests konden in deze testomgeving niet draaien",
          headUiNotRun: "De UI-tests van de patch zijn niet uitgevoerd",
          note: "Fouten die er al waren blokkeren de patch niet; alleen regressies en gemiste acceptatietests doen dat.",
          whyPassed: "{preexisting} bestaande fout(en) bleven staan, 0 regressies.",
          whyPassedFixed: "{preexisting} bestaande fout(en) bleven staan, 0 regressies, {fixed} opnieuw groen.",
          whyNeverRan: "De testsuite heeft nooit een uitslag gegeven — een eerdere verplichte stap stopte de run, dus de tellers hieronder zijn leeg en niet schoon.",
          whyRanNothing: "De teststap draaide en gaf exit 0, maar voerde geen enkele test uit — het testcommando zag de module van deze wijziging niet, dus deze run bewijst niets over de patch.",
          whyRegressions: "{count} nieuwe fout(en) door deze wijziging veroorzaakt.",
          whyUnparsed: "Een teststap faalde zonder leesbare test-ids — we houden het daarom op gefaald.",
          unparsedStep: "De stap die faalde:",
          unparsedExit: "exit {code}",
          whyAcceptanceAbsent: "Een opgegeven acceptatietest heeft niet gedraaid.",
          whyAcceptanceFailed: "Een opgegeven acceptatietest faalt nog steeds.",
          whyFixedNothing: "Geen nieuwe fouten, maar de patch heeft niets opgelost van wat hij moest oplossen.",
          whyGeneric: "De acceptatiepoort is niet gehaald.",
          whyUiUnsupported: "De patch levert UI-tests mee, en deze omgeving kon er geen browser voor starten: {reason}. Dat zegt niets over de code; er is een testomgeving met een browser nodig.",
          whyUiNotRun: "De patch levert UI-tests mee, en geen daarvan is uitgevoerd: {reason}.",
          preexisting: "Bestaand:",
          regressions: "Regressies:",
          newlyFixed: "Opnieuw groen:",
          acceptance: "Acceptatie:",
          acceptanceValue: "{passed}/{total} geslaagd",
          acceptanceFailed: ", {count} gefaald",
          acceptanceAbsent: ", {count} niet gedraaid",
          acceptanceSkipped: ", {count} overgeslagen",
          listRegressions: "Regressies (nieuw)",
          listUnattributed: "Nieuwe fouten (oorzaak onbekend)",
          listPreexisting: "Bestaand (niet door deze patch)",
          listNewlyFixed: "Opnieuw groen",
          acceptanceTargets: "Acceptatietests",
          status: { passed: "geslaagd", failed: "gefaald", skipped: "overgeslagen", absent: "niet gedraaid" },
        },
      },
      bulk: {
        cancel: "Annuleren",
        cancelConfirmTitle: "Geselecteerde runs annuleren?",
        cancelConfirmLabel: "Runs annuleren",
        cancelConfirmBody: "Dit stopt {count} run(s) die nog bezig zijn. Afgeronde runs blijven ongemoeid.",
        result: "{ok} geannuleerd",
        resultPartial: "{ok} geannuleerd, {failed} mislukt",
        archive: "Archiveren",
        archiveConfirmTitle: "Geselecteerde runs archiveren?",
        archiveConfirmLabel: "Runs archiveren",
        archiveConfirmBody: "Dit verplaatst {count} run(s) naar het archief. Runs die nog bezig zijn worden overgeslagen. Uitkomst, logs en bewijs blijven bewaard en de run blijft te openen.",
        archiveResult: "{ok} gearchiveerd",
        archiveResultSkipped: "{ok} gearchiveerd, {skipped} overgeslagen (nog bezig)",
        archiveResultPartial: "{ok} gearchiveerd, {failed} mislukt",
        restore: "Herstellen",
        restoreResult: "{ok} hersteld",
        restoreResultPartial: "{ok} hersteld, {failed} mislukt",
      },
    },
    projects: {
      eyebrow: "Projecten",
      indexEyebrow: "Werk",
      indexTitle: "Projecten",
      indexSubtitle: "Elk project is één werkgebied — klantenservice, een app die je onderhoudt — met eigen verbindingen, goedkeuringen en mensen.",
      newProject: "Nieuw project",
      searchPlaceholder: "Zoek projecten…",
      modeFilter: { all: "Alle" },
      cardLabels: {
        codebases: "Codebases",
        primary: "Primair",
        platforms: "Platforms",
        mode: "Modus",
        updated: "Bijgewerkt",
      },
      guardrails: "{count} guardrail",
      guardrailsPlural: "{count} guardrails",
      empty: {
        title: "Nog geen projecten",
        hint: "Een project is één systeem waar FixControl voor zorgt: waar het werk binnenkomt, in welke code het leeft, en wie een wijziging goedkeurt. Maak er één en de rest van de app heeft iets om mee te werken.",
        ask: "Wat heb ik nodig om een project op te zetten?",
      },
      countLabel: "{count} project",
      countLabelPlural: "{count} projecten",
      delete: {
        title: "Project verwijderen",
        confirm: "\"{name}\" verwijderen? Het project is niet meer beschikbaar voor nieuw werk en een beheerder kan het terugzetten. De audithistorie, goedkeuringsbeslissingen en deployment-bewijzen blijven bewaard, en bestaande sessies behouden hun opgeloste context.",
        cancel: "Annuleren",
        confirmButton: "Verwijderen",
      },
      bulk: {
        delete: "Verwijderen",
        deleteConfirmTitle: "Geselecteerde projecten verwijderen?",
        deleteConfirmLabel: "Projecten verwijderen",
        deleteConfirmBody: "Dit maakt {count} project(en) niet meer beschikbaar voor nieuw werk. Een beheerder kan ze terugzetten; hun audithistorie, goedkeuringsbeslissingen en deployment-bewijzen blijven bewaard.",
        result: "{ok} verwijderd",
        resultPartial: "{ok} verwijderd, {failed} mislukt",
      },
      create: {
        title: "Nieuw project",
        subtitle: "Definieer een herbruikbaar werkruimteprofiel met codebases en integraties.",
        nameLabel: "Projectnaam",
        namePlaceholder: "bijv. POS Backend",
        descriptionLabel: "Beschrijving",
        descriptionPlaceholder: "Korte beschrijving…",
        primaryCodebaseLabel: "Primaire codebase",
        primaryCodebaseNone: "Geen (later toevoegen)",
        supportDeskLabel: "Support desk",
        supportDeskHint: "Behandelt alleen support-tickets — nieuwe tickets worden aangemaakt en naar de tracker gemirrord, maar er start geen remediation-mission automatisch. Escaleer naar engineering wanneer nodig.",
        helperText: "Je kunt na aanmaak meer codebases, integraties, tags en guardrails toevoegen.",
        cancel: "Annuleren",
        createButton: "Project aanmaken",
        creating: "Aanmaken…",
        failed: "Project aanmaken mislukt",
      },
      defaultSubtitle: "Projectconfiguratie",
      notFound: "Project niet gevonden",
      pickWorkspace: "Kies een werkruimte om dit project te openen.",
      backToProjects: "Terug naar projecten",
      back: "Terug",
      patchHistory: "Patch-historie",
      saveChanges: "Wijzigingen opslaan",
      savingChanges: "Opslaan...",
      changesSaved: "Opgeslagen",
      sections: {
        general: "Algemeen",
        executionMode: "Execution mode",
        tags: "Tags",
        codebases: "Codebases",
        codebasesHelp: "Code-repositories en mappen waar agents in dit project op werken.",
        knowledgeSources: "Kennisbronnen",
        knowledgeHelp: "Contextbronnen gekoppeld aan de codebases van dit project.",
        guardrails: "Guardrails",
        guardrailsHelp: "Regels die agents moeten volgen wanneer ze in dit project werken.",
      },
      fields: {
        name: "Naam",
        description: "Beschrijving",
        descriptionPlaceholder: "Waar is dit project voor?",
        addTag: "Tag toevoegen...",
        addGuardrail: "Guardrail toevoegen...",
        add: "Toevoegen",
        defaultBadge: "standaard",
        setAsDefault: "Als standaard instellen",
        noCodebases: "Er is nog geen codebase aan dit project gekoppeld. Gebruik ‘Codebase toevoegen…’ om er een te koppelen.",
        analyses: "Analyses",
        technicalDocs: "Technische docs",
        procedures: "Procedures",
        general: "Algemeen",
        chunksSuffix: "chunks",
      },
      projectKnowledge: {
        title: "Projectkennis",
        help: "Beschikbaar voor agents die aan dit project werken.",
        empty: "Nog geen projectspecifieke kennis. Voeg documentatie toe, scrape een URL of inspecteer een gekoppelde codebase zodat agents de juiste context hebben.",
        addSource: "Projectbron toevoegen",
        scrapeUrl: "Project-URL scrapen",
        inspect: "Projectcodebases inspecteren",
        viewAll: "Alle kennis voor dit project bekijken",
        includeGlobal: "Organisatiebreed tonen",
        readOnly: "Je hebt geen rechten om kennis voor dit project te beheren.",
        stats: {
          projectSources: "Projectbronnen",
          codebaseDocs: "Codebase-docs",
          scrapedUrls: "Gescrapete URL's",
          learnings: "Learnings",
          coverage: "Geïndexeerd",
        },
        /* De blokjes zijn filters: klik er één aan en de lijst eronder toont
         * precies de bronnen die erin geteld zijn. */
        filter: {
          showing: "{label} · {count}",
          clear: "Toon alles",
          none: "Geen bronnen in deze categorie.",
          allIndexed: "Alle bronnen zijn geïndexeerd.",
          notIndexed: "Niet geïndexeerd",
          more: "…en nog {count}",
        },
        groups: {
          project: "Projectbronnen",
          codebase: "Codebase-docs",
          tenant: "Organisatiebreed",
        },
      },
    },
  },

  patchDetail: {
    eyebrow: "Patch",
    noFiles: "Deze patch wijzigt geen bestanden.",
    reviewRail: "Reviewcontext",
    whatChanged: "Wat deze wijziging doet",
    noSummary: "Voor deze patch is nog geen samenvatting geschreven.",
    refresh: {
      label: "Controleren",
      busy: "Controleren…",
      tooltip: "Vraag de code-host wat de staat van deze pull request echt is. Normaal komt die staat binnen via een webhook; hiermee corrigeer je hem als die nooit is aangekomen.",
      unchanged: "ongewijzigd",
      failed: "De leveringsstaat kon niet worden gecontroleerd.",
    },
    ask: {
      button: "Waarom?",
      heading: "Vraag iets over dit bestand",
      close: "Sluiten",
      placeholder: "Waarom is dit gewijzigd? Wat gaat er stuk als ik het weglaat?",
      submit: "Vragen",
      thinking: "Het dossier van deze patch wordt gelezen…",
      failed: "Die vraag kon nu niet worden beantwoord.",
      empty: "Er kwam geen antwoord terug.",
      disclaimer:
        "Gelezen uit het dossier van deze patch — de opdracht, de samenvatting van de engineer, de reviewmotivatie en deze diff. Het licht toe; het beslist niets en maakt geen deel uit van het auditspoor.",
      suggestion: {
        why: "Waarom is dit bestand gewijzigd?",
        risk: "Wat is hier riskant aan?",
        revert: "Wat gaat er stuk als ik dit bestand weglaat?",
      },
    },
    discarded: {
      title: "De pull request is gesloten zonder te mergen",
      body: "Er is niets van deze patch in de codebase terechtgekomen, ook al staat hij als geleverd. Wijs hem af om dat vast te leggen, of open vanuit de werkruimte een nieuwe pull request.",
      reject: "Patch afwijzen",
      noPermission: "Iemand met goedkeuringsrechten moet deze patch afwijzen.",
    },
    decision: {
      title: "Beslissing & levering",
      prompt: "Keur deze patch goed om hem aan de codebase te leveren, of wijs hem af om hem terug te sturen.",
      approve: "Goedkeuren",
      reject: "Afwijzen",
      failed: "De beslissing kon niet worden vastgelegd.",
      noPermission: "Je hebt geen rechten om patches goed te keuren of af te wijzen.",
    },
    browser: {
      reviewProgress: "{done}/{total} bekeken",
      filterPlaceholder: "Bestanden filteren…",
      clearFilter: "Filter wissen",
      noMatches: "Geen bestanden komen overeen met dit filter.",
      unified: "Unified",
      sideBySide: "Naast elkaar",
      wrap: "Lange regels afbreken",
      expand: "Uitvergroten",
      collapse: "Volledig scherm verlaten",
      inOverlay: "Deze diff staat op volledig scherm.",
      prevFile: "Vorig bestand",
      nextFile: "Volgend bestand",
      copyPath: "Pad kopiëren",
      copied: "Gekopieerd",
      markReviewed: "Markeer als bekeken",
      reviewed: "Bekeken",
      kindFullFile: "heel bestand",
      kindUnified: "gewijzigde delen",
      noDiff: "Geen diff om te tonen.",
      hintNav: "bestand",
      hintReviewed: "bekeken",
      hintSplit: "naast elkaar",
      hintExpand: "volledig scherm",
    },
    verify: {
      title: "Verificatie & preview",
      fact: {
        ok: "geverifieerd",
        blocked: "niet vrijgegeven",
        unknown: "nog niet geverifieerd",
      },
      gateOk: "De sandbox heeft deze patch geverifieerd.",
      gateBlocked: "Verificatie heeft deze patch niet vrijgegeven.",
      gateUnknown: "Deze patch is nog niet geverifieerd.",
      viewRun: "Bekijk de sandbox-run ({status})",
      runStatus: {
        pending: "in wachtrij",
        provisioning: "voorbereiden",
        running: "bezig",
        passed: "geslaagd",
        failed: "gefaald",
        timeout: "time-out",
        error: "fout",
        cancelled: "geannuleerd",
        unknown: "onbekend",
      },
      // Zie en.ts.
      runsHeading: "Sandbox-verificaties",
      runsForVersion: "{n} daarvan gingen over v{version}, de versie op deze pagina.",
      runsNoneForVersion: "Geen van deze ging over v{version} \u2014 de versie op deze pagina is nog niet door de sandbox geweest.",
      verifyNow: "Verifieer in de sandbox",
      verifyAgain: "Opnieuw verifiëren",
      verifying: "Starten…",
      verifyFailed: "De verificatie kon niet worden gestart.",
      runInFlight: "Er loopt een sandbox-run voor deze patch.",
      previewHeading: "Uitproberen",
      startPreview: "Preview starten",
      previewOpening: "Starten…",
      previewRetry: "Opnieuw proberen",
      previewStarting: "De preview start op…",
      previewElapsed: "{seconds}s",
      previewExpires: "Nog {minutes} min beschikbaar.",
      openPreview: "Open de draaiende wijziging",
      previewHint: "Draait de applicatie met deze patch toegepast, op de omgeving die de sandbox heeft geverifieerd.",
      previewNeedsPass: "Een preview draait de patch als live applicatie en wordt daarom pas aangeboden zodra de sandbox hem heeft geverifieerd.",
      previewFailed: "De preview kon niet worden gestart.",
      previewStillStarting: "Nog steeds bezig met starten — herlaad de pagina om verder te kijken.",
      environmentsHeading: "Testomgevingen",
    },
    breadcrumbVersion: "Patch v{version}",
    backToPatches: "Patches",
    loading: "Patch laden…",
    notFound: "Patch niet gevonden of je hebt geen toegang.",
    loadFailed: "Patch-historie laden mislukt.",
    newerExistsTitle: "Er bestaat een nieuwere patch voor deze issue",
    newerExistsBody: "Je bekijkt v{current}. De laatste in deze keten is v{latest}.",
    openLatest: "Open laatste patch",
    subtitleSingle: "v{version} · {count} bestand · aangemaakt {date}",
    subtitlePlural: "v{version} · {count} bestanden · aangemaakt {date}",
    openWorkspace: "Werkruimte openen",
    openIssue: "Issue {key} openen",
    meta: {
      status: "Status",
      version: "Versie",
      cycle: "Cyclus",
      created: "Aangemaakt",
      verification: "Verificatie",
      delivery: "Levering",
      notDelivered: "niet geleverd",
      outcome: {
        none: "niet geleverd",
        open: "open, nog niet gemerged",
        landed: "gemerged",
        discarded: "gesloten zonder merge",
        manual: "handmatig toepassen",
      },
      churn: "Churn",
      files: "Bestanden",
    },
    rejectedTitle: "Deze patch is afgewezen",
    rejectedBody: "Maak een vervolgpatch op basis van de CTO-feedback hieronder.",
    fixAndRetry: "Fix & opnieuw",
    starting: "Bezig met starten…",
    retryFailedDefault: "Opnieuw starten mislukt",
    retryMissingSuffix: " (ontbreekt: {fields})",
    latestCtoDecision: "Laatste CTO-beslissing: {label}",
    originatingPrompt: "Oorspronkelijke prompt",
    cycleHistory: "Cyclushistorie",
    chainCount: "{count} patches in keten",
    iterationCount: "{count} iteratie",
    iterationCountPlural: "{count} iteraties",
    inProgress: "Bezig",
    qaPrefix: "QA: {id}",
    viewing: "In beeld",
    openLink: "Openen →",
    ongoing: "loopt nog",
    noCycleHistory: "Geen cyclushistorie vastgelegd voor deze patch.",
    filesInPatch: "Bestanden in deze patch",
    fileCount: "{count} bestand",
    fileCountPlural: "{count} bestanden",
    expandAll: "Alles uitvouwen",
    collapseAll: "Alles inklappen",
    cto: {
      approved: "Goedgekeurd",
      changesRequested: "Wijzigingen aangevraagd",
      rejected: "Afgewezen",
    },
    delivery: {
      title: "Levering",
      notDelivered: "Nog niet geleverd",
      approveFirst: "Keur deze patch goed om hem aan de codebase te leveren.",
      deliveredAt: "Geleverd {date}",
      openPr: "Pull request openen",
      opening: "Bezig met openen…",
      viewPr: "Pull request bekijken",
      openFailedDefault: "Kon de pull request niet openen.",
      override: {
        title: "Verificatie is niet geslaagd",
        hint: "Je kunt de pull request alsnog openen op eigen risico. Niemand heeft deze wijziging geverifieerd — de review en CI op de pull request zelf zijn de enige controles die hij krijgt. Je reden wordt vastgelegd in het auditspoor en in de pull request geschreven.",
        reasonLabel: "Waarom toch openen?",
        reasonPlaceholder: "bijv. de sandbox kan de licentieserver niet bereiken; de wijziging is alleen config en is met de hand nagelopen",
        cta: "Toch pull request openen",
        busy: "Bezig met openen…",
        cancel: "Annuleren",
        start: "Toch openen op eigen risico",
        reasonTooShort: "Geef een reden van minstens 8 tekens — die komt in de pull request te staan.",
      },
      branch: "Branch",
      commit: "Commit",
      strategyLocal: "Toegepast op de lokale codebase.",
      strategySsh: "Toegepast op de remote host.",
      strategyManual: "Handmatige levering vereist — pas toe vanuit de workspace.",
      prStatus: {
        open: "PR open",
        merged: "Gemerged",
        closed: "PR gesloten",
      },
      ciState: {
        pending: "CI draait",
        success: "CI geslaagd",
        failure: "CI gefaald",
        neutral: "CI neutraal",
        unknown: "CI onbekend",
      },
    },
  },

  projectsList: {
    eyebrow: "Werk",
    title: "Projecten",
    subtitle: "Elk project is één werkgebied — klantenservice, een app die je onderhoudt — met eigen verbindingen, goedkeuringen en mensen",
    newProject: "Nieuw project",
    searchPlaceholder: "Projecten zoeken…",
    metadata: {
      codebases: "Codebases",
      primary: "Primair",
      platforms: "Platforms",
      mode: "Modus",
      updated: "Bijgewerkt",
      dash: "—",
    },
    guardrailsCount: "{count} guardrail",
    guardrailsCountPlural: "{count} guardrails",
    empty: {
      title: "Nog geen projecten",
      body: "Maak een project aan om herbruikbare werkruimte-profielen te definiëren.",
    },
    count: "{count} project",
    countPlural: "{count} projecten",
    delete: {
      title: "Project verwijderen",
      body: "{name} verwijderen? Het project is niet meer beschikbaar voor nieuw werk en een beheerder kan het terugzetten. De audithistorie, goedkeuringsbeslissingen en deployment-bewijzen blijven bewaard, en bestaande sessies behouden hun opgeloste context.",
      cancel: "Annuleren",
      confirm: "Verwijderen",
    },
    create: {
      title: "Nieuw project",
      subtitle: "Definieer een herbruikbaar werkruimte-profiel met codebases en integraties.",
      nameLabel: "Projectnaam",
      nameRequired: "*",
      namePlaceholder: "bijv. POS Backend",
      descriptionLabel: "Beschrijving",
      descriptionPlaceholder: "Korte beschrijving...",
      primaryCodebase: "Primaire codebase",
      noneAddLater: "Geen (later toevoegen)",
      helper: "Je kunt na aanmaken meer codebases, integraties, tags en guardrails toevoegen.",
      errorDefault: "Project aanmaken mislukt",
      cancel: "Annuleren",
      submit: "Project aanmaken",
      submitting: "Aanmaken...",
    },
  },

  projectsHistory: {
    eyebrow: "Projecten",
    eyebrowWithName: "Projecten / {name}",
    title: "Patch-historie",
    subtitle: "Alle door AI gegenereerde codewijzigingen in dit project",
    backToProject: "Terug naar project",
    refresh: "Vernieuwen",
    searchPlaceholder: "Zoek op prompt, bestand of werkruimte...",
    filter: {
      status: "Status",
      allStatuses: "Alle statussen",
      agent: "Agent",
      allAgents: "Alle agents",
      workspace: "Werkruimte",
      allWorkspaces: "Alle werkruimtes",
      group: "Groep",
      noGrouping: "Geen groepering",
      byWorkspace: "Per werkruimte",
      byIssue: "Per issue",
    },
    view: {
      list: "Lijst",
      timeline: "Tijdlijn",
    },
    count: "{filtered} van {total} patch",
    countPlural: "{filtered} van {total} patches",
    loadFailedDefault: "Laden mislukt",
    retry: "Opnieuw proberen",
    empty: {
      totalZeroTitle: "Nog geen patches",
      filterTitle: "Geen patches die aan je filters voldoen",
      totalZeroBody: "Wanneer agents codewijzigingen genereren in een werkruimte, verschijnen ze hier als patches.",
      filterBody: "Wis je filters om meer patches te zien.",
    },
    unassigned: "Niet toegewezen",
    unknown: "Onbekend",
    dash: "—",
    showMore: "Toon nog {count}",
    remaining: "({count} resterend)",
    loading: "Laden...",
    selectAPatch: "Kies een patch om de details te bekijken",
    noPatchesYet: "Nog geen patches",
    noFileChanges: "Geen bestandswijzigingen in deze patch",
    fileCount: "{count} bestand",
    fileCountPlural: "{count} bestanden",
    patchTitleFallback: "Patch v{version}",
    detail: {
      patchVersion: "Patch v{version}",
      originalPrompt: "Oorspronkelijke prompt",
      meta: {
        agent: "Agent",
        workspace: "Werkruimte",
        issue: "Issue",
        created: "Aangemaakt",
        files: "Bestanden",
        applied: "Toegepast",
        decidedBy: "Besloten door",
      },
      filesValue: "{count} · +{additions} -{deletions}",
    },
    context: {
      empty: "Geen context vastgelegd voor deze patch. (Oudere patches hebben geen tool-call-historie.)",
      title: "Gebruikte context ({count} tool-call)",
      titlePlural: "Gebruikte context ({count} tool-calls)",
      filesRead: "Gelezen bestanden",
      searches: "Zoekopdrachten",
      other: "Overig",
      emptyItem: "(leeg)",
    },
    actions: {
      approve: "Goedkeuren",
      reject: "Afwijzen",
      markApplied: "Markeer als toegepast",
      alreadyStatus: "Al {status}",
      approveFirst: "Eerst goedkeuren",
      rerun: "Opnieuw",
      rerunTitle: "Voer de prompt van deze patch opnieuw uit in zijn werkruimte",
      compareWithCurrent: "Vergelijk met huidige",
      viewingVsCurrent: "Bekijkt vs huidige",
      compareOffTitle: "Vergelijk met de huidige bestanden op schijf",
      compareOnTitle: "Toon de originele patch-diff",
      openInWorkspace: "Open in werkruimte",
    },
    compareLabel: "Huidig ↔ Patch",
    compareFailedDefault: "Vergelijken mislukt",
  },

  time: {
    justNow: "zojuist",
    minutesAgoShort: "{n} m geleden",
    hoursAgoShort: "{n} u geleden",
    daysAgoShort: "{n} d geleden",
    secondsAgoShort: "{n} s geleden",
  },

  pager: {
    label: "Item-navigatie",
    previous: "Vorige",
    next: "Volgende",
    position: "{current} van {total}",
  },

  /** De vaste balk boven elke pagina: kruimelpad, itemnavigatie en het
   *  ⋯-menu met de standaard verwijderoptie. */
  pageBar: {
    more: "Meer acties voor dit item",
    remove: {
      label: "Verwijderen",
      confirmTitle: "{name} verwijderen?",
      confirmBody: "Hiermee haal je {name} uit het actieve werk.",
      confirmKept: "De al vastgelegde governance-historie — audit-events, goedkeuringsbeslissingen en verificatiebewijzen — blijft bewaard.",
      confirmReversible: "Een beheerder kan het terugzetten.",
      confirm: "Verwijderen",
      error: "Verwijderen mislukt ({status}).",
      impact: {
        workspace: "De patches, goedkeuringsbeslissingen, sandbox-verdicten en audithistorie blijven bewaard. Een beheerder kan de werkruimte terugzetten.",
      },
    },
  },


  dashboard: {
    eyebrow: "Overzicht",
    controlCenter: "Control Center",
    realtimeOverview: "Realtime overzicht van agents, issues en de dev-pijplijn.",
    last7Days: "Laatste 7 dagen",
    allSystemsOperational: "Alle systemen operationeel",
    agents: "Agents",
    agentsHint: "Je AI-team: gespecialiseerde assistenten die vragen sorteren en fixes voorstellen. Jij houdt de controle — ze verzenden of wijzigen niets zonder jouw goedkeuring.",
    tasksInProgress: "Taken in uitvoering",
    pendingApprovals: "Wacht op goedkeuring",
    loading: "Laden…",
    awaitingBoardReview: "Wacht op review",
    agentBreakdown: "{enabled} actief, {paused} gepauzeerd, {error} fout",
    recentCount: "{count} recent",
    runActivity: "Run-activiteit",
    issuesByPriority: "Issues per prioriteit",
    issuesByStatus: "Issues per status",
    successRate: "Succespercentage",
    automatedFixes: "Geautomatiseerde fixes",
    recentActivity: "Recente activiteit",
    liveAgentTimeline: "Live agent-tijdlijn",
    recentTasks: "Recente taken",
    latestIssuesInPipeline: "Nieuwste issues in de pijplijn",
    recentlyResolved: "Recent opgelost",
    last5IssuesReplied: "Laatste 5 issues met klantantwoord verstuurd",
    legendCritical: "Kritiek",
    legendHigh: "Hoog",
    legendMedium: "Gemiddeld",
    legendLow: "Laag",
    legendToDo: "Te doen",
    legendInProgress: "Bezig",
    legendReview: "Review",
    legendDone: "Klaar",
    noAgentRunsYet: "Nog geen agent-runs",
    noIssuesYet: "Nog geen issues",
    noAgentActivityYet: "Nog geen agent-activiteit",
    noTasksYet: "Nog geen taken",
    nothingRepliedYet: "Nog niets beantwoord",
    empty: {
      runsHint: "Koppel een project en start een agent om deze weergave te vullen.",
      issuesHint: "Stuur een e-mail door of koppel een integratie om issues te ingesten.",
      activityHint: "Zodra agents werken zie je hier hun voortgang in realtime.",
      tasksHint: "Getriagede issues en patches in behandeling verschijnen hier.",
      resolvedHint: "Wanneer de support-reply agent een klantantwoord stuurt, verschijnt dat hier.",
      openAgents: "Agents openen",
      openInbox: "Inbox openen",
      openIssues: "Issues openen",
    },
  },

  tenants: {
    eyebrow: "Account",
    title: "Tenants",
    subtitle: "Elke tenant is een eigen werkruimte met eigen issues, agents, inbox en kennisbank.",
    addTenant: "Tenant toevoegen",
    addFirstTenant: "Voeg je eerste tenant toe",
    createRequiresPlatformAdmin: "Je mag geen workspace aanmaken. Vraag een beheerder van deze workspace of je platformbeheerder.",
    stats: {
      tenants: "Tenants",
      tenantsSub: "In je account",
      totalIssues: "Totaal issues",
      totalIssuesSub: "Over alle tenants",
      totalAgents: "Totaal agents",
      totalAgentsSub: "Over alle tenants",
      active: "Actief",
      activeSub: "Huidige werkruimte",
    },
    metrics: {
      issues: "Issues",
      agents: "Agents",
      members: "Leden",
    },
    inboxLabel: "Inbox: {mailbox}",
    activeBadge: "Actief",
    currentWorkspace: "Huidige werkruimte",
    switchTo: "Wissel naar",
    editTitle: "Bewerken",
    deleteTitle: "Verwijdering plannen",
    deleteConfirmTitle: "Verwijdering van \"{name}\" plannen?",
    deleteConfirm: "De toegang tot \"{name}\" wordt direct bevroren en alle bijbehorende data (issues, agents, kennis, inbox) wordt na 14 dagen definitief verwijderd. Tot die tijd kan een platformbeheerder de tenant terugzetten, en het auditspoor van de geplande verwijdering blijft bewaard.",
    deleteConfirmLabel: "Verwijdering plannen",
    deletedToast: "Verwijdering gepland voor {name}",
    deleteFailedToast: "Verwijdering voor {name} kon niet worden gepland",
    createdToast: "{name} aangemaakt",
    updatedToast: "{name} bijgewerkt",
    empty: {
      title: "Geen tenants in je account",
      hint: "Voeg je eerste tenant toe om een werkruimte aan te maken met eigen issues, agents, inbox en kennisbank.",
    },
    modal: {
      addTitle: "Nieuwe tenant",
      editTitle: "{name} bewerken",
      addSubtitle: "Maak een nieuwe werkruimte aan met eigen issues, agents en inbox.",
      editSubtitle: "Werk tenantgegevens en configuratie bij.",
      createButton: "Tenant aanmaken",
      saveButton: "Opslaan",
      submitFailed: "Mislukt",
      platformAdminRequired: "Je mag geen workspace aanmaken. Vraag een beheerder van deze workspace of je platformbeheerder.",
      twoFactorSetupRequired: "Een workspace aanmaken vereist tweefactorauthenticatie. Stel die eerst in en probeer het daarna opnieuw.",
      twoFactorReauthRequired: "Een workspace aanmaken vereist een actieve tweefactorverificatie. Log opnieuw in met je code.",
      workspaceLimitReached: "Je hebt het maximale aantal workspaces bereikt dat je zelf mag aanmaken. Vraag je platformbeheerder om de limiet te verhogen.",
      nameInUse: "Er bestaat al een workspace met deze naam. Kies een andere naam.",
      nameOnlyHint: "Alleen de naam is nodig. Plan, omgeving en domein worden daarna door je platformbeheerder ingesteld.",
      switchToCreated: "Ga erheen",
    },
    fields: {
      name: "Tenantnaam",
      namePlaceholder: "FixControl",
      slug: "Slug",
      slugPlaceholder: "fixcontrol",
      domain: "Domein",
      domainPlaceholder: "fixcontrol.nl",
      contactEmail: "Contact-e-mail",
      contactEmailPlaceholder: "info@fixcontrol.nl",
      mailbox: "Inbox-mailbox",
      mailboxPlaceholder: "support@fixcontrol.nl — e-mails hier worden issues",
      mailboxHelp: "E-mails naar dit adres worden geclassificeerd en omgezet in issues voor deze tenant.",
      plan: "Plan",
      logoLetter: "Logo-letter",
      logoLetterPlaceholder: "B",
      members: "Leden",
      brandColor: "Merkkleur",
    },
    plans: {
      free: "Free",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    environments: {
      prod: "Prod",
      staging: "Staging",
      test: "Test",
    },
    environmentField: {
      label: "Omgeving",
      help: "Label dat naast de tenant verschijnt in de switcher, zodat je in één oogopslag sandbox van productie kunt onderscheiden.",
    },
    preview: {
      placeholderName: "Tenantnaam",
      placeholderSlug: "slug",
    },
  },

  inbox: {
    eyebrow: "Intake",
    title: "Inbox",
    subtitle: "Binnenkomende e-mails per organisatie — automatisch geclassificeerd.",
    pollGmail: "Gmail ophalen",
    refresh: "Vernieuwen",
    searchPlaceholder: "Inbox doorzoeken…",
    statusFilterAria: "Statusfilter",
    emptyFiltered: "Geen berichten binnen de huidige filters.",
    clearFilters: "Filters wissen",
    emptyTab: {
      active: "Geen actieve berichten. Binnenkomende e-mail van gekoppelde mailboxen verschijnt hier automatisch.",
      archived: "Niets gearchiveerd. Archiveren haalt een bericht uit de actieve lijst maar houdt het doorzoekbaar.",
      spam: "Geen spam. Berichten die je als spam markeert komen hier terecht en worden niet meer door AI verwerkt.",
      deleted: "Niets verwijderd. Verwijderen is omkeerbaar — verwijderde berichten blijven hier staan en zijn altijd te herstellen.",
    },
    emptyElsewhereHint: "{count} bericht(en) staan onder Gearchiveerd, Spam of Verwijderd — zie de tabs hierboven.",
    selectHint: "Kies een bericht om de hele conversatie te zien.",
    convertToIssue: "Omzetten naar issue",
    ignore: "Negeren",
    all: "Alle",
    status: {
      new: "Nieuw",
      analyzing: "Analyseren",
      triaged: "Getrieerd",
      answered: "Beantwoord",
      converted: "Omgezet",
      ignored: "Genegeerd",
    },
    lifecycle: {
      tabAria: "Weergave",
      tab: {
        active: "Actief",
        archived: "Gearchiveerd",
        spam: "Spam",
        deleted: "Verwijderd",
      },
      state: {
        active: "Actief",
        archived: "Gearchiveerd",
        spam: "Spam",
        deleted: "Verwijderd",
      },
      archive: "Archiveren",
      spam: "Markeer als spam",
      delete: "Verwijderen",
      restore: "Herstellen",
      moreActions: "Meer acties",
      bulkResult: "{changed} bijgewerkt · {skipped} overgeslagen",
      syncWarning: "{failed} mailbox-sync(s) mislukt — controleer de Gmail/Office365-integratie.",
      confirmArchiveTitle: "Geselecteerde berichten archiveren?",
      confirmArchiveBody: "{count} bericht(en) gaan naar het tabblad Gearchiveerd en worden gearchiveerd in de gekoppelde mailbox. Ze blijven doorzoekbaar en kunnen altijd worden hersteld.",
      confirmSpamTitle: "Geselecteerde als spam markeren?",
      confirmSpamBody: "{count} bericht(en) gaan naar het tabblad Spam, triggeren geen AI-verwerking meer en worden naar de Spam/Ongewenst-map van de gekoppelde mailbox verplaatst. Ze kunnen worden hersteld.",
      confirmDeleteTitle: "Geselecteerde berichten verwijderen?",
      confirmDeleteBody: "{count} bericht(en) verhuizen naar het tabblad Verwijderd en naar de Prullenbak van de gekoppelde mailbox. Niets wordt permanent verwijderd — alles blijft bewaard voor audit en kan altijd worden hersteld.",
      confirmRestoreTitle: "Geselecteerde berichten herstellen?",
      confirmRestoreBody: "{count} bericht(en) keren terug naar de actieve wachtrij en naar de inbox in de gekoppelde mailbox.",
      archivedBanner: "Gearchiveerd — verborgen uit de actieve inbox en gearchiveerd in de bron-mailbox. Herstel om terug te halen.",
      spamBanner: "Gemarkeerd als spam — uitgesloten van AI-verwerking en verplaatst naar de Spam/Ongewenst-map van de mailbox.",
      deletedBanner: "Verwijderd (herstelbaar) — verborgen uit de inbox maar bewaard voor audit, en verplaatst naar de Prullenbak van de gekoppelde mailbox. Niets is permanent verwijderd; gebruik Herstellen om het terug te zetten.",
      mailboxNote: "Inbox-acties worden ook gespiegeld naar de gekoppelde Gmail/Office365-mailbox (best-effort). FixControl-status is leidend — een mislukte mailbox-sync blokkeert de actie nooit.",
    },
    analysis: {
      title: "AI-analyse",
      sub: "Ticket Analyst · zekerheid {pct}%",
      kind: "Type",
      priority: "Prioriteit",
      readiness: "Gereedheid",
      confidence: "Zekerheid",
      convertedTo: "Omgezet naar",
      flagsLabel: "Vlaggen",
    },
    sort: {
      receivedNew: "Ontvangen · nieuwste",
      receivedOld: "Ontvangen · oudste",
      status: "Status",
      sender: "Afzender",
      subject: "Onderwerp",
      tenant: "Organisatie",
    },
    queue: {
      loading: "Laden…",
      range: "{from}-{to} van {total}",
      prev: "Vorige pagina",
      next: "Volgende pagina",
    },
    bulk: {
      selectedCount: "{count} geselecteerd",
      selectPage: "Alles op deze pagina selecteren",
      ignore: "Negeren",
      reanalyse: "Heranalyseren",
      assignCategory: "Categorie toewijzen",
      clear: "Selectie wissen",
      confirmIgnoreTitle: "Geselecteerde berichten negeren?",
      confirmIgnoreBody: "{count} bericht(en) worden gemarkeerd als genegeerd. Reeds omgezette berichten worden overgeslagen.",
      confirmReanalyseTitle: "Geselecteerde berichten heranalyseren?",
      confirmReanalyseBody: "{count} bericht(en) gaan opnieuw door de classifier. Reeds omgezette berichten worden overgeslagen.",
      ignoredResult: "Genegeerd {ignored} · overgeslagen {skipped}",
      reanalysedResult: "Opnieuw analyseren {reanalysed} · overgeslagen {skipped}",
      assignedResult: "Toegewezen {assigned} · overgeslagen {skipped}",
      assignToMe: "Aan mij toewijzen",
      assignToUser: "Aan gebruiker toewijzen",
      unassign: "Toewijzing wissen",
      assignedToUserResult: "Toegewezen {count} · overgeslagen {skipped}",
      unassignedResult: "Toewijzing gewist op {count} · overgeslagen {skipped}",
    },
    mailbox: {
      all: "Alles",
      mine: "Van mij",
      unassigned: "Niet toegewezen",
      drafts: "Concepten",
      sent: "Verzonden",
      switcherAria: "Filter op mailbox-identiteit",
    },
    thread: {
      loading: "Threadcontext laden…",
      noContext: "Nog geen threadcontext",
      headerLabel: "Thread",
      unnamed: "(geen onderwerp)",
      via: "via",
      lastReply: "laatste reactie",
      unreadCount: "{count} ongelezen",
      siblings: "{count} ander bericht in deze thread",
      timelineTitle: "Conversatie",
      timelineCount: "{count} items",
      timeline: {
        inbound: "Binnen",
        replySent: "Verzonden",
        replySending: "Wordt verzonden…",
        note: "Notitie",
      },
      notesTitle: "Interne notities",
      notesHint: "Alleen operator · niet zichtbaar voor klant",
      notesPlaceholder: "Voeg een interne notitie toe…",
      notesAdd: "Notitie toevoegen",
      notesEmpty: "Nog geen interne notities",
    },
    signal: {
      no_assignee: "Niet toegewezen",
      stale: "Vergeten",
      customer_waiting: "Klant wacht",
      high_risk: "Hoog risico",
      unread: "Heeft ongelezen",
      low_confidence: "AI lage zekerheid",
      filterAll: "Alle threads",
      filterAria: "Filter op operator-signaal",
    },
    attachments: {
      title: "Bijlagen",
      count: "{count}",
      loading: "Bijlagen laden…",
    },
    assign: {
      me: "{name} (ik)",
      clear: "Toewijzing wissen",
      noUsers: "Geen actieve gebruikers in deze organisatie.",
    },
    reply: {
      title: "Antwoord",
      from: "Van:",
      placeholder: "Typ je antwoord…",
      save: "Concept opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      error: "Opslaan mislukt",
      discard: "Verwijderen",
      send: "Verzenden",
      sendDisabledHint: "Antwoorden staan uit voor deze mailbox. Zet \"Antwoorden toestaan\" aan bij Instellingen → Mailboxes.",
      sending: "Verzenden…",
      sent: "Verzonden",
      sentEmpty: "Dit antwoord is verzonden.",
      alreadyAnswered: "Al {count}× beantwoord — laatst om {time}.",
      confirmAgainTitle: "Deze e-mail is al beantwoord",
      confirmAgainBody: "Er zijn al {count} antwoord(en) naar deze klant gegaan, het laatste om {time}. Nog een antwoord sturen?",
      confirmAgain: "Toch versturen",
      submitForApproval: "Ter goedkeuring sturen",
      submitForApprovalHint: "Dit project vraagt goedkeuring voordat een antwoord verstuurd wordt. Goedkeurders krijgen een melding en een e-mail.",
      awaitingApproval: "Wacht op goedkeuring",
      awaitingApprovalHint: "Goedkeurders hebben een melding en een e-mail gekregen. Er gaat niets naar de klant tot iemand het goedkeurt; wordt het afgewezen, dan komt het hier terug met hun toelichting.",
      approvalRejected: "Afgewezen door {name}",
      approvalReturned: "Teruggestuurd door {name} — dien het opnieuw in als het klaar is",
      approverFallback: "een goedkeurder",
      colleagueAwaitingApproval: "Er wacht al een ander antwoord op deze e-mail op goedkeuring (van een collega of de AI).",
      queued: "Antwoord in verzendwachtrij",
      queuedHint: "Verzending loopt op de achtergrond en wordt automatisch opnieuw geprobeerd. Het antwoord staat in de conversatiegeschiedenis en wordt Verzonden zodra het is afgeleverd; mislukt het definitief, dan komt het concept hier terug om te bewerken.",
      sendError: "Verzenden mislukt",
      sendErrorProvider: "Er is geen mailintegratie gekoppeld voor verzenden.",
      sendErrorThread: "Kon dit antwoord niet aan het oorspronkelijke bericht koppelen.",
      sendErrorToken: "Toegang tot de mailintegratie is verlopen. Koppel Gmail of Microsoft opnieuw.",
      sendErrorRetry: "Provider weigerde tijdelijk. Probeer het zo opnieuw.",
      sendErrorAlready: "Dit antwoord is al verzonden.",
      sendErrorRate: "Te veel verzendpogingen kort na elkaar — even rustig aan.",
    },
    timeline: {
      title: "Geschiedenis",
      collapse: "Inklappen",
      loading: "Geschiedenis laden…",
      empty: "Nog geen activiteit.",
      system: "systeem",
      event: {
        created: "Bericht ingelezen",
        categorized: "Categorie toegewezen",
        assigned: "Toegewezen aan gebruiker",
        unassigned: "Toewijzing gewist",
        ignored: "Gemarkeerd als genegeerd",
        reanalyzed: "Opnieuw in classifier",
        converted: "Omgezet naar issue",
        reply_drafted: "Concept aangemaakt",
        reply_sent: "Antwoord verzonden",
        reply_discarded: "Concept weggegooid",
        ai_suggestion_created: "AI-voorstel gegenereerd",
        ai_policy_matched: "AI-beleid toegepast",
        ai_auto_draft_created: "AI heeft concept opgesteld",
        ai_auto_send_blocked: "AI auto-verzenden geblokkeerd door beleid",
        ai_auto_reply_sent: "AI auto-antwoord verzonden",
        ai_suggestion_accepted: "AI-voorstel geaccepteerd",
        ai_suggestion_rejected: "AI-voorstel als onjuist gemarkeerd",
        thread_created: "Thread gestart",
        thread_reopened: "Thread heropend",
        thread_resolved: "Thread opgelost",
        thread_status_changed: "Thread-status gewijzigd",
        internal_note_added: "Interne notitie toegevoegd",
        archived: "Gearchiveerd",
        spam_marked: "Gemarkeerd als spam",
        deleted: "Verwijderd (herstelbaar)",
        restored: "Hersteld naar actief",
        intake_classified: "Intake heeft besloten",
        appended_to_issue: "Toegevoegd aan het gekoppelde issue",
        intake_failed: "Intake mislukt",
      },
    },
    categories: {
      manage: "Categorieën",
      manageTitle: "Inbox-categorieën beheren",
      manageSub: "Organisatiegebonden labels om de inbox te organiseren. Eén hoofd-categorie per bericht.",
      newPlaceholder: "Nieuwe categorie…",
      create: "Aanmaken",
      empty: "Nog geen categorieën — maak hierboven de eerste aan.",
      rename: "Hernoemen",
      archive: "Archiveren",
      unarchive: "Dearchiveren",
      delete: "Verwijderen",
      archivedTag: "gearchiveerd",
      confirmEdit: "Opslaan",
      cancelEdit: "Annuleren",
      filter: "Categorie",
      filterAll: "Alle categorieën",
      filterUncategorised: "Niet gecategoriseerd",
      filterByLabel: "Filter op {name}",
      clearAssignment: "Categorie wissen",
    },
    detail: {
      changeCategory: "Categorie wijzigen",
      uncategorised: "Niet gecategoriseerd",
      changeAssignment: "Toewijzing wijzigen",
      unassigned: "Niet toegewezen",
      filterBySender: "Inbox filteren op deze afzender",
    },
    filter: {
      clearSender: "Afzenderfilter wissen",
    },
    splitter: {
      aria: "Kolomverdeling queue / detail aanpassen",
    },
    subtitleWithView: "Weergave: {view} · operator-werkbank",
    workbench: {
      listAria: "Gesprekken",
      resizeList: "Breedte van de gesprekkenlijst aanpassen",
      resizeComposer: "Hoogte van het antwoordvak aanpassen",
      conversationAria: "Geselecteerd gesprek",
      allMailboxes: "Alle mailboxen",
      filter: "Filter",
      filterTitle: "Filters",
      showLabel: "Tonen",
      categoryLabel: "Categorie",
      signalLabel: "Signaal",
      sortLabel: "Sorteren",
      more: "Meer inbox-acties",
      exportCsv: "CSV exporteren",
      fetchGmail: "Gmail nu ophalen",
      mailboxSettings: "Mailboxinstellingen",
      shortcutsHelp: "Sneltoetsen",
      selectRow: "{subject} selecteren",
      selectedCount: "{count} geselecteerd",
      unread: "Ongelezen",
      back: "Terug naar inbox",
      noSelection: "Selecteer een gesprek",
      noSelectionMove: "om te bladeren",
      confirm: {
        archive: { title: "{count} gesprek(ken) archiveren?", body: "Gearchiveerde gesprekken verdwijnen uit de inbox. Je vindt ze terug via ⋯ → Archief. In de mailbox van de klant verandert niets.", confirm: "Archiveren" },
        spam: { title: "{count} gesprek(ken) als spam markeren?", body: "Spam verdwijnt uit de inbox en wordt nooit door AI geanalyseerd. Je kunt het herstellen via ⋯ → Spam.", confirm: "Als spam markeren" },
        delete: { title: "{count} gesprek(ken) verwijderen?", body: "Verwijderde gesprekken gaan naar ⋯ → Verwijderd en zijn te herstellen. De mailbox van de klant wordt niet aangeraakt.", confirm: "Verwijderen" },
        discard: { title: "Dit antwoord weggooien?", body: "De tekst in het antwoordvak wordt verwijderd. Dit kun je niet ongedaan maken.", confirm: "Weggooien" },
      },
      lifecycleView: {
        archived: "Archief",
        spam: "Spam",
        deleted: "Verwijderd",
        back: "Terug naar inbox",
      },
      undo: "Ongedaan maken",
      deleteTitle: "{count} gesprek(ken) verwijderen?",
      deleteBody: "Verwijderde gesprekken gaan naar Verwijderd en zijn te herstellen. De mailbox van de klant wordt niet aangeraakt.",
      views: {
        aria: "Inbox-weergaven",
        all: "Alles",
        mine: "Mijn",
        unassigned: "Niet toegewezen",
        waiting: "Wacht",
        review: "Te beoordelen",
      },
      empty: {
        all: "Inbox leeg. Nieuwe mail verschijnt hier vanzelf.",
        mine: "Er is niets aan jou toegewezen.",
        unassigned: "Elk gesprek heeft een eigenaar.",
        waiting: "Geen gesprek wacht op de klant.",
        review: "Niets te beoordelen — intake heeft alles afgehandeld wat kon.",
      },
      done: {
        archive: "{count} gearchiveerd",
        spam: "{count} als spam gemarkeerd",
        delete: "{count} verwijderd",
        restore: "{count} hersteld",
      },
      status: {
        open: "Open",
        waiting_customer: "Wacht op klant",
        pending_internal: "Intern in behandeling",
        resolved: "Opgelost",
        ignored: "Genegeerd",
        new: "Open",
        ai_drafted: "Open",
        changeFailed: "Status kon niet worden gewijzigd",
      },
      snooze: {
        laterToday: "Later vandaag",
        tomorrow: "Morgenochtend",
        nextWeek: "Volgende week",
        clear: "Sluimeren opheffen",
      },
      header: {
        via: "via",
        details: "Details",
        activity: "Activiteit ({count})",
        createIssue: "Issue aanmaken",
        openIssue: "Issue openen",
        linkedIssue: "Gekoppeld aan {key}",
        archive: "Archiveren",
        restore: "Herstellen",
        snooze: "Sluimeren",
        snoozed: "Sluimert tot",
        status: "Status",
        assignee: "Toegewezen aan",
        assignMe: "Aan mij toewijzen",
        me: "Ik",
        unassigned: "Niet toegewezen",
        more: "Meer acties",
        markSpam: "Als spam markeren",
        delete: "Verwijderen",
        reanalyse: "Intake opnieuw uitvoeren",
        filterSender: "Alles van deze afzender tonen",
        categoryItem: "Categorie: {name}",
        fullscreen: "Volledig scherm",
        exitFullscreen: "Volledig scherm verlaten",
        aiDraftReady: "AI-concept klaar",
      },
      intake: {
        label: "AI stelt voor",
        notRun: "Nog niet geanalyseerd",
        confidence: "{pct}% zeker van deze inschatting",
        viewAnalysis: "Analyse bekijken",
        retry: "Opnieuw",
        run: "Intake uitvoeren",
        retried: "Intake ingepland",
        action: {
          create_issue: "Issue aanmaken",
          append_to_existing_issue: "Toegevoegd aan het gekoppelde issue",
          keep_in_inbox: "De klant antwoorden",
          ignore: "Geen actie nodig",
          needs_review: "Handmatig beoordelen",
        },
        state: {
          queued: "Wacht op analyse",
          running: "Bezig met analyseren…",
          linked: "Gekoppeld aan een issue",
          support: "Support",
          needs_review: "Te beoordelen",
          noise: "Geen actie",
          failed: "Intake mislukt",
        },
        reason: {
          linked_thread: "Vervolg op een bestaand issue",
          issue_created: "Issue automatisch aangemaakt",
          noise_detected: "Automatisch, leeg of alleen een bedankje",
          classified_noise: "Niet uitvoerbaar",
          support_request: "Supportvraag",
          ambiguous: "Onduidelijk wat de afzender nodig heeft",
          low_confidence: "Lage zekerheid",
          project_unresolved: "Aan deze mailbox is geen project gekoppeld",
          auto_create_disabled: "Automatisch issues aanmaken staat uit voor deze mailbox",
          classifier_unavailable: "AI-analyse was niet beschikbaar",
          intake_error: "Intake kon niet afronden",
        },
        intent: {
          bug: "Bug",
          feature: "Featureverzoek",
          support: "Support",
          operational: "Account- of beheerverzoek",
          spam_noise: "Ruis",
          acknowledgement: "Bevestiging",
          auto_reply: "Automatisch antwoord",
          unknown: "Onduidelijk",
        },
        actionability: {
          actionable: "Uitvoerbaar",
          non_actionable: "Niet uitvoerbaar",
          ambiguous: "Onduidelijk",
        },
      },
      composer: {
        aria: "Opsteller",
        reply: "Antwoorden",
        note: "Interne notitie",
        notePlaceholder: "Schrijf een interne notitie — wordt nooit naar de klant gestuurd…",
        noteAdd: "Notitie toevoegen",
        noteHint: "Alleen het team",
        noteTooLong: "De notitie is te lang",
        noThread: "Interne notities hebben een gesprek nodig.",
        sendShortcut: "⌘/Ctrl + Enter",
        aiSeeded: "AI-concept ingevuld — controleer het voordat je verstuurt.",
        insertAi: "AI-concept gebruiken",
      },
      conversation: {
        aria: "Berichten",
        loading: "Gesprek laden…",
        note: "Interne notitie",
        teammate: "Teamlid",
        replySent: "Antwoord verstuurd",
        replyQueued: "Antwoord in de wachtrij",
        showQuoted: "Geciteerde tekst tonen",
        hideQuoted: "Geciteerde tekst verbergen",
        truncated: "Bericht ingekort voor weergave.",
        unavailable: "Het gesprek kon niet worden geladen.",
      },
      details: {
        title: "Details",
        tabs: { details: "Details", ai: "AI-analyse", activity: "Activiteit" },
        contact: "Contact",
        project: "Project",
        noProject: "Geen project",
        mailbox: "Mailbox",
        issue: "Gekoppeld issue",
        noIssue: "Niet gekoppeld",
        assignee: "Toegewezen aan",
        category: "Categorie",
        priority: "Prioriteit",
        received: "Ontvangen",
        intake: "Intake",
        decided: "Besloten",
        evidence: "Onderbouwing",
        headers: "Intake-headers",
        messageId: "Bericht-id",
        intent: "Bedoeling",
        actionability: "Uitvoerbaarheid",
        confidence: "Zekerheid",
        summary: "Samenvatting",
        reason: "Waarom",
        noAnalysis: "Nog geen AI-analyse voor dit bericht.",
        projectSource: {
          thread_issue: "Project van het gekoppelde issue",
          mailbox_binding: "Project van de mailboxkoppeling",
        },
      },
      createIssue: {
        title: "Issue aanmaken",
        subtitle: "Uit het gesprek met {sender}",
        project: "Project",
        noProject: "Geen project (niet toegewezen)",
        projectHint: "Voorgesteld op basis van het project van de mailbox.",
        issueTitle: "Titel",
        summary: "Samenvatting",
        kind: "Type",
        kinds: { BUG: "Bug", FEATURE: "Feature", SUPPORT: "Support" },
        source: "Het gesprek blijft aan het issue gekoppeld; latere antwoorden worden eraan toegevoegd.",
        policyHint: "Of het werk automatisch start, volgt de instellingen van het project.",
        submit: "Issue aanmaken",
        created: "{key} aangemaakt",
        alreadyLinked: "Al gekoppeld aan {key}",
        failed: "Het issue kon niet worden aangemaakt",
      },
      shortcuts: {
        next: "Volgend gesprek",
        prev: "Vorig gesprek",
        open: "Gesprek openen",
        reply: "Antwoorden",
        note: "Interne notitie",
        archive: "Archiveren",
        issue: "Issue aanmaken of openen",
        assign: "Toewijzen",
        snooze: "Sluimeren",
        select: "Gesprek selecteren",
        search: "Zoeken",
        fullscreen: "Volledig scherm aan / uit",
        escape: "Veld verlaten / selectie wissen",
      },
    },
  },

  followUps: {
    eyebrow: "Werk",
    title: "Opvolgingen",
    subtitle: "Taken die automatisch zijn aangemaakt nadat patches binnen {company} zijn goedgekeurd of toegepast. Triëer en sluit ze hier af.",
    subtitleNoTenant: "Taken die automatisch zijn aangemaakt nadat wijzigingen binnen jouw organisatie zijn goedgekeurd of toegepast. Triëer en sluit ze hier af.",
    statsOpen: "Open",
    statsOverdue: "Te laat",
    statsUnassigned: "Niet toegewezen",
    filter: {
      open: "Open",
      done: "Klaar",
      all: "Alle",
      anyone: "Iedereen",
      mine: "Van mij",
      unassigned: "Niet toegewezen",
    },
    empty: "Geen opvolgingen die aan de huidige filters voldoen.",
    // Distinguishes rows that are otherwise identical: the same task raised
    // by two patches on one issue differs only by which patch shipped.
    patchVersion: "patch v{version}",
    overdue: "te laat",
    due: "deadline {when}",
    actions: {
      assignToMe: "Aan mij toewijzen",
      start: "Starten",
      markDone: "Markeer als klaar",
      cancel: "Annuleren",
    },
    confirmCancel: "Deze opvolging annuleren?",
    toast: {
      cancelled: "Geannuleerd",
      updateFailed: "Bijwerken mislukt ({status})",
      updateFailedGeneric: "Bijwerken mislukt",
      deleteFailed: "Verwijderen mislukt ({status})",
      deleteFailedGeneric: "Verwijderen mislukt",
    },
    status: {
      new: "Nieuw",
      assigned: "Toegewezen",
      in_progress: "Bezig",
      done: "Klaar",
      blocked: "Geblokkeerd",
    },
    priority: {
      low: "Laag",
      medium: "Gemiddeld",
      high: "Hoog",
      critical: "Kritiek",
    },
  },

  runs: {
    eyebrow: "Systeem",
    title: "Agent-runs",
    subtitle: "Elke vastgelegde agent-uitvoering voor {company} — laatste 100, nieuwste eerst.",
    subtitleNoTenant: "Elke vastgelegde agent-uitvoering voor jouw organisatie — laatste 100, nieuwste eerst.",
    statsRuns: "Runs",
    statsSuccessRate: "Succespercentage",
    statsAvgLatency: "Gem. latency",
    statsSpend: "Uitgaven",
    statsSpendUnpriced: "Alleen runs met een bekende prijs. Van {n} run(s) in dit overzicht is de prijs onbekend — een CLI-run rapporteert geen tokens, en de catalogus kent alleen Anthropic-modellen.",
    statsAction: {
      clearFilters: "Toon alle runs",
      showFailed: "Toon de {n} mislukte run(s)",
      sortLatency: "Sorteer op traagste eerst",
      sortCost: "Sorteer op duurste eerst",
    },
    filter: {
      all: "Alle ({n})",
      success: "Succes ({n})",
      failed: "Mislukt ({n})",
    },
    allAgents: "Alle agents",
    empty: "Geen werksessies die aan het huidige filter voldoen.",
    emptyHint: "Een werksessie is één ding dat een agent deed — een melding lezen, een antwoord opstellen, een wijziging voorstellen. Ze verschijnen hier zodra een agent aan de slag gaat.",
    emptyAsk: "Hoe zet ik een agent aan het werk?",
    searchPlaceholder: "Zoek runs…",
    columns: {
      status: "Status",
      run: "Run",
      agent: "Agent",
      issue: "Issue",
      latency: "Latency",
      cost: "Kosten",
      when: "Wanneer",
    },
    runLabel: {
      defaultVerb: "Verwerk",
      fallback: "Run {id}",
    },
    emptyNeverRecords:
      "Deze agent roept geen model aan en legt hier dus nooit een run vast. Zijn "
      + "werk staat vastgelegd bij de missie waarin hij draaide — en, waar hij er "
      + "een opent, op de goedkeuring van die poort.",
    detail: {
      titleFallback: "Run",
      subtitle: "{issue} · {when}",
      noIssue: "Geen issue",
      agentRuns: "Runs van deze agent",
      gone: "Deze run is niet meer vastgelegd.",
      goneHint:
        "Runs worden opgeruimd volgens de bewaartermijn bij onderhoud (standaard 7 "
        + "dagen), dus een link kan de run overleven waar hij naar wijst. De missie "
        + "waarin hij draaide, en het auditspoor, blijven bewaard.",
      backToRuns: "Terug naar runs",
      failed: "Deze run kon niet worden geladen.",
    },
  },

  orchestrationConsole: {
    page: {
      title: "Orkestratie & context",
      subtitle: "Hoe FixControl agents kiest, wat ze zien en wat je veilig kunt bijstellen",
      pickTenant: "Kies eerst een organisatie.",
      intro:
        "FixControl draait een vaste set gespecialiseerde agents, aangestuurd door een "
        + "orkestrator. Hier zie je welke agents er zijn, waarom de orkestrator er een kiest, "
        + "welke context die krijgt en welke instellingen je veilig kunt wijzigen. "
        + "Kernbegrenzingen voor beveiliging en uitvoering zijn hier bewust niet aanpasbaar.",
      loadFailed: "Kon het orkestratie-overzicht niet laden.",
      retry: "Opnieuw proberen",
    },
    tabs: {
      overview: "Overzicht",
      agents: "Agents",
      routing: "Routering",
      context: "Context & zoeken",
      simulator: "Simulator",
    },
    common: {
      systemAgent: "Systeemagent",
      systemAgentHint:
        "Geleverd door FixControl. Ondersteunde instellingen kun je bijstellen; "
        + "kernbegrenzingen voor beveiliging en uitvoering kun je niet overschrijven, "
        + "en verwijderen kan niet.",
      enabled: "Actief",
      disabled: "Gepauzeerd",
      cannotDisable: "Kan niet gepauzeerd worden",
      open: "Openen",
      none: "Geen",
      loading: "Laden…",
      advancedOnly: "Alleen zichtbaar voor beheerders",
    },
    source: {
      systemDefault: "FixControl-standaard",
      tenantOverride: "Ingesteld voor deze organisatie",
      projectOverride: "Ingesteld voor dit project",
      locked: "Vastgezet op {scope}",
      lockedReason: "Vastgezet op {scope}: {reason}",
      platformDefault: "Platformstandaard",
    },
    tiers: {
      safe: "Aanpasbaar",
      guarded: "Vereist bevestiging",
      fixed: "Elders ingesteld",
      invariant: "Beschermd",
      safeHint: "Beïnvloedt kosten, snelheid of grondigheid. Nooit wat de agent mág doen.",
      guardedHint:
        "Verandert wat het systeem mag doen, of wie moet tekenen. "
        + "Elk veld moet apart bevestigd worden.",
      fixedHint:
        "Ingesteld op een plek die dit scherm niet bereikt — een vaste runtimewaarde, een "
        + "platformbrede instelling, of de FixControl-beheerconsole. Getoond zodat je het "
        + "kunt zien. Het is geen beveiligingsgrens.",
      invariantHint: "Een beveiligingsgrens. Op geen enkel niveau aanpasbaar, door niemand.",
    },
    overview: {
      rosterTitle: "De agents",
      rosterBody:
        "{total} agents, {enabled} actief. {planner} worden ingepland door de missieplanner; "
        + "de rest wordt aangestuurd door intake- en workflowgebeurtenissen.",
      modelTitle: "Welke een model draaien",
      modelBody:
        "{llm} agents roepen een taalmodel aan. De andere {deterministic} draaien "
        + "deterministische code — die tonen we zonder model, in plaats van met een model "
        + "dat ze nooit gebruiken.",
      findingsTitle: "Configuratiecontrole",
      findingsNone:
        "Niets blokkerends. De huidige configuratie kan elke missiefase uitvoeren.",
      protectedTitle: "Wat je hier niet kunt wijzigen",
      protectedBody:
        "Organisatie-isolatie, het goedkeuringschokepunt, het runstatus-contract, uitgaande "
        + "schrijfacties via de outbox en levering uitsluitend via pull requests zijn in code "
        + "afgedwongen. Ze staan bij elke agent vermeld zodat je ziet dat ze bewust zo zijn, "
        + "niet dat ze ontbreken.",
      severityBlocking: "Blokkerend",
      severityWarning: "Waarschuwing",
      severityInfo: "Ter info",
    },
    agents: {
      role: "Rol",
      model: "Model",
      deterministic: "Geen model — deterministisch",
      capabilities: "{n} bevoegdheden",
      responsibilities: "Verantwoordelijkheden",
      lastUsed: "Laatst gebruikt",
      never: "Nooit",
      configState: "Configuratie",
      stateDefault: "FixControl-standaarden",
      stateTenant: "Organisatie-afwijkingen",
      stateProject: "Projectafwijkingen",
      warningsTitle: "Tijdens het bepalen van deze configuratie",
      warnings: {
        agentRowUnreadable:
          "De agentregel kon niet gelezen worden; de fail-open standaard (beschikbaar) wordt "
          + "getoond.",
        behaviorChainFallback:
          "De governance-gedragsketen kon niet bepaald worden; het ingebouwde gebalanceerde "
          + "profiel wordt getoond, en dat is ook wat de runtime gebruikt.",
        modelRoutingUnresolved:
          "De modelroutering kon niet bepaald worden; de meegeleverde taakstandaard geldt. "
          + "Er wordt geen ingestelde afwijking getoond omdat er geen gelezen kon worden.",
        budgetCeilingsUnread:
          "Een of meer budgetplafonds konden niet gelezen worden; de bepaalde waarde wordt "
          + "getoond.",
        scopedBehaviorOff:
          "Gedragsinstellingen per agent staan voor deze organisatie niet scherp "
          + "(ORCH_SCOPED_BEHAVIOR_RESOLUTION staat uit), dus de waarden hieronder zijn de "
          + "ORGANISATIEBREDE waarden — er hier één wijzigen wijzigt hem voor elke agent.",
        projectOverridesNotEnforced:
          "De koppelingen van dit project zijn wel opgeslagen maar gelden NIET: "
          + "de organisatie heeft ORCH_SCOPED_BEHAVIOR_RESOLUTION niet scherpgesteld, dus "
          + "agents bepalen hun gedrag nog steeds op organisatieniveau. Scherpstellen laat "
          + "alleen de koppelingen gelden die zijn beoordeeld en geactiveerd.",
        projectBindingsPendingReview:
          "Een gedragskoppeling op dit project wacht op beoordeling: ze is opgeslagen maar "
          + "geldt pas als iemand haar activeert onder Beleid & bereik, Agentgedrag.",
      },
      deterministicReason: {
        codebase:
          "De architectbriefing wordt samengesteld uit de lijst met codebases en de "
          + "geïndexeerde kenniszoekopdracht. Op de headless route wordt geen provider "
          + "aangeroepen; de asynchrone route geeft het werk door aan de implementatie-agent.",
        cto:
          "De technische gate wordt afgeleid, niet gegenereerd: hij combineert het meest "
          + "recente verificatierapport met de intrinsieke risicovlaggen van de melding.",
        security:
          "Engine-inline. Leest de risicovlaggen van de CTO en het verificatierapport en "
          + "geeft een oordeel; er komt geen provider aan te pas.",
        release:
          "Engine-inline. Stuurt de twee-fasencommit van het gecontroleerde toepassen aan. "
          + "Levering is een door een mens getekende stap, geen gegenereerde.",
        ux:
          "Engine-inline. Draait een UX-beoordeling over de echte run, melding en routes, "
          + "inclusief het vastleggen van schermafbeeldingen.",
        default: "De runtime van deze agent roept geen provider aan.",
      },
    },
    governance: {
      gates: {
        humanSignOff: "Menselijke handtekening voordat het werk van deze rol doorgaat",
        technical: "Technische gate (goedkeuren / wijzigingen vragen / afwijzen)",
        deployment: "Deploymentgate — levering gaat nooit automatisch",
        riskyPatch:
          "Gate voor risicovolle patches wanneer de wijziging een gemarkeerd gebied raakt",
        communications: "Communicatiegoedkeuring voordat een antwoord een klant bereikt",
      },
      validation: {
        sandboxBeforeReview: "Sandboxverificatie voor de overdracht naar de review",
        verificationBeforeClosure:
          "Verificatierapport vereist voordat de afsluitgate vrijgeeft",
        qaReportRequired:
          "Er moet een verificatierapport bestaan; is dat er niet, dan draait de gate eerst "
          + "de verificatie",
      },
      productionRestrictions: {
        pullRequestOnlyDelivery:
          "Code wordt uitsluitend via een pull- of merge-request geleverd — nooit met een "
          + "push naar een hoofd- of productiebranch.",
        armedDeploymentCapabilities:
          "Een oordeel naar een CI/CD-host brengen vereist dat de deploymentbevoegdheden van "
          + "de organisatie scherpstaan.",
      },
      policySources: {
        behaviorProfileChain: "Keten van agentgedragsprofielen",
        governanceScopeHierarchy: "Governance-scopehiërarchie",
      },
      enforcementNotes: {
        enforced:
          "Afgedwongen: de completions van deze agent bepalen het routeringsbeleid van de "
          + "organisatie vóór het verdelen.",
        notEnforced:
          "De completions van deze agent worden gedaan zonder meetcontext, dus het "
          + "pogingenbudget, de tijdslimiet en het kostenplafond van de organisatie gelden op "
          + "deze route niet. De waarden staan er omdat ze ingesteld zijn, niet omdat ze nu "
          + "afgedwongen worden.",
        notEnforcedStreaming:
          "Deze agent streamt rechtstreeks naar de modelprovider in plaats van via de "
          + "routeringslaag, dus het pogingenbudget, de tijdslimiet en het kostenplafond "
          + "gelden op deze route niet — de uitgaven worden achteraf gemeten, nooit vooraf "
          + "begrensd. De waarden staan er omdat ze ingesteld zijn, niet omdat ze nu "
          + "afgedwongen worden.",
      },
      retryLoops: {
        qaJsonRetry:
          "De verificatie probeert een onleesbaar JSON-oordeel net zo goed opnieuw als een "
          + "providerfout. Opgeven laat hem terugvallen op een inhoudsblind 'wijzigingen "
          + "nodig'-oordeel, dus de lus is bewust. Ingesteld met QA_LLM_MAX_ATTEMPTS.",
      },
    },
    routing: {
      title: "Hoe werk bij een agent terechtkomt",
      intro:
        "Een missie wordt gepland als een geordende reeks fasen. Fasen draaien op volgorde; "
        + "taken binnen één fase draaien parallel. Elke taak noemt een rol, en elke rol wijst "
        + "naar één agent. Niets hieronder is ter illustratie getekend — dit is de echte "
        + "koppeling.",
      shapesTitle: "De drie plannen die de orkestrator kan maken",
      shapesIntro:
        "Deze sluiten elkaar uit — een missie krijgt er precies één. De controles "
        + "lopen in deze volgorde.",
      shapes: {
        ux: {
          name: "UI/UX-beoordeling",
          when:
            "Als eerste gekozen, wanneer de aanvraagtekst of het gekoppelde issue "
            + "leest als een UI/UX-wijziging.",
        },
        support: {
          name: "Supportantwoord",
          when: "Gekozen wanneer het issue als supportvraag is geclassificeerd.",
        },
        fix: {
          name: "Herstelpijplijn",
          when:
            "Al het overige — bugs, features en vrije instructies volgen deze.",
        },
      },
      stagesTitle: "Missiefasen",
      stage: "Fase {n}",
      notPlanned: "Niet ingepland voor deze organisatie",
      rolesTitle: "Rollen en de agent waar elke rol naartoe wijst",
      roleColumn: "Rol",
      agentColumn: "Agent",
      delegates: "Plant en delegeert",
      engineInline: "Draait in de engine — geen model",
      requiresApproval: "Standaard achter een menselijke goedkeuring",
      entryPointsTitle: "Andere manieren waarop deze agent wordt aangeroepen",
      entryPointLabels: {
        inbound_email: "Binnenkomende e-mail",
        intake_form: "Intakeformulier",
        webhook_ingest: "Webhook-invoer",
        agents_run_api: "Het API-eindpunt om een agent te draaien",
        issue_state_change: "Statuswijziging van een melding",
        workspace_event: "Werkruimtegebeurtenis",
        patch_orchestrator: "Patch-orkestrator",
        issue_resolved: "Melding opgelost",
        planner_root: "Missiewortel",
        planner_architect: "Missieplanner (architectfase)",
        planner_implementation: "Missieplanner (implementatiefase)",
        planner_technical_gate: "Missieplanner (fase technische gate)",
        planner_verification: "Missieplanner (verificatiefase)",
        planner_support: "Missieplanner (supportmissies)",
        planner_delivery: "Missieplanner (leveringsfase, wanneer ingeschakeld)",
        planner_ux: "Missieplanner (UX-missies)",
      },
      prerequisiteTitle: "Draait eerst als die ontbreekt",
      fallbackTitle: "Als deze agent gepauzeerd is",
      fallbackDropped:
        "Zijn taken vallen uit het plan. Er is geen vervangende agent — deze runtime heeft "
        + "geen generalist om naar te herrouteren.",
      fallbackRefused:
        "Kan niet gepauzeerd worden: de pijplijn is er structureel van afhankelijk.",
    },
    simulator: {
      title: "Een missie voorspellen",
      intro:
        "Beschrijf wat je FixControl zou vragen. Er draait niets: er wordt geen missie "
        + "aangemaakt, geen agent aangeroepen, geen goedkeuring geopend en geen extern "
        + "systeem aangeraakt.",
      instructionLabel: "Wat zou je vragen?",
      instructionPlaceholder: "Los de timeout bij het afrekenen op in de betaaldienst",
      issueLabel: "Bij een bestaand issue (optioneel)",
      issuePlaceholder: "FIX-123",
      kindLabel: "Soort issue",
      kindAuto: "Overnemen van het issue",
      run: "Voorspellen",
      running: "Bezig met voorspellen…",
      failed: "Kon de voorspelling niet uitvoeren.",
      simulationOnly: "Simulatie — er is niets uitgevoerd",
      planTitle: "Plan dat de orkestrator zou maken",
      whyTitle: "Waarom dit plan",
      stepStage: "Fase {n}",
      stepWhy: "Waarom deze agent",
      stepContext: "Context die hij zou lezen",
      stepCapabilities: "Wat hij zou mogen",
      stepModel: "Model",
      stepDeterministic: "Geen model",
      stepSkipped: "Overgeslagen",
      approvalsTitle: "Benodigde menselijke beslissingen",
      approvalsNone: "Geen — dit plan loopt door zonder goedkeuringsmoment.",
      validationTitle: "Verificatie die het werk moet doorstaan",
      riskTitle: "Risicomarkeringen op het issue",
      limitsTitle: "Wat deze voorspelling niet dekt",
      unusedTitle: "Agents die dit plan niet zou gebruiken",
      empty: "Beschrijf een taak om te zien hoe die georkestreerd zou worden.",
    },
    inspector: {
      // Read by /runs/[id] via <RunInspectionPanels>. The block keeps its
      // historical name: the orchestration console's Run-inspector tab was
      // removed once /runs grew a real drill-down.
      runTitle: "De run",
      agent: "Agent",
      outcome: "Resultaat",
      succeeded: "Geslaagd",
      failedOutcome: "Mislukt",
      duration: "Duur",
      model: "Model",
      tokens: "Tokens",
      cost: "Kosten",
      unknown: "Onbekend",
      unknownCostHint:
        "Onbekend is niet nul: het model heeft geen catalogusprijs, of de aanroep liep "
        + "via het CLI-abonnement, dat geen tokens rapporteert.",
      configTitle: "Configuratie waaronder hij draaide",
      configVersion: "Versie",
      configUnstamped: "Niet gestempeld",
      configUnstampedHint:
        "Niet gestempeld is niet \u2018standaard’: deze run dateert van vóór de stempel, "
        + "is een flow in plaats van één van de tien agents, of het bepalen mislukte destijds.",
      contextTitle: "Context die de prompt voedde",
      contextNone: "Er is geen contextmanifest aan deze run gekoppeld.",
      contextTokens: "Cachebaar {cacheable} / dynamisch {dynamic} tokens",
      contextFiles: "Meegenomen bestanden",
      contextSources: "Gebruikte bronnen",
      contextExcluded: "Buiten gehouden",
      snapshotHash: "Snapshot-hash",
      timelineTitle: "Configuratiewijzigingen rond deze run",
      gapsTitle: "Wat niet getoond kon worden",
    },
    context: {
      title: "Wat elke agent ziet",
      intro:
        "Alleen bronnen die de runtime daadwerkelijk leest, staan hier. Het ophalen is een "
        + "combinatie van vector-, full-text- en trigramzoeken in één rangschikking, beperkt "
        + "tot het project van het issue plus organisatiebrede bronnen.",
      sourcesTitle: "Bronnen",
      depth: "Top {n}",
      evidence: "Gelezen in",
      configurable: "Aanpasbaar",
      notConfigurable: "Vast",
      sources: {
        intake: {
          request: {
            label: "Het binnenkomende bericht",
            evidence: "De intakestap, op de binnenkomende payload",
          },
          memory: {
            label: "Geheugenregels van de organisatie en het project",
            evidence:
              "De classificatie-aanroep, uit het geheugen van de organisatie en het project",
          },
          caller_context: {
            label: "Contextblokken die de aanroeper meegeeft",
            evidence:
              "De classificatie-aanroep, uit de contextblokken die de aanroeper meegeeft",
          },
        },
        workflow: {
          current_task: {
            label: "De melding en haar huidige status",
            evidence: "De workflow-agent, wanneer die de volgende stap bepaalt",
          },
          history: {
            label: "Recente werkruimtegebeurtenissen",
            evidence: "De workflow-agent, terwijl die zijn prompt samenstelt",
          },
          memory: {
            label: "Geheugenregels van de organisatie en het project",
            evidence: "De workflow-agent, uit het geheugen van de organisatie en het project",
          },
        },
        codebase: {
          codebases: {
            label: "Geregistreerde codebases",
            evidence: "De codebase-agent, uit de lijst met geregistreerde codebases",
          },
          knowledge: {
            label:
              "Geïndexeerde kennis (hybride vector-, volledige-tekst- en "
              + "trigramzoekopdracht)",
            evidence: "De codebase-agent, via de projectgebonden kenniszoekopdracht",
          },
          project_scope: {
            label: "De projectgrens van de melding",
            evidence: "De ophaalcontext die voor de melding geladen is",
          },
        },
        dev: {
          repository: {
            label: "Bestanden in de werkkopie van de meldingswerkruimte",
            evidence: "De werkruimtetaak die de implementatie-agent draait",
          },
          current_task: {
            label: "De melding en de architectbriefing",
            evidence:
              "De implementatie-agent, vanuit de melding en de architectbriefing waarmee die "
              + "begint",
          },
          evidence: {
            label: "Sandboxfouten uit de vorige poging",
            evidence: "De sandboxrevisie-terugkoppeling uit de vorige poging",
          },
          history: {
            label: "Reviewcommentaar op de vorige patchversie",
            evidence: "Het reviewcommentaar dat op de vorige patchversie is vastgelegd",
          },
        },
        cto: {
          evidence: {
            label: "Het meest recente verificatierapport",
            evidence: "De technische gate, uit het meest recente verificatierapport",
          },
          governance: {
            label: "De intrinsieke risicovlaggen van de melding",
            evidence: "De technische gate, uit de risicovlaggen van de melding",
          },
        },
        qa: {
          current_task: {
            label: "De melding en de patch die beoordeeld wordt",
            evidence: "De verificatie-agent, wanneer die een verificatie draait",
          },
          evidence: {
            label: "Resultaten van de sandboxverificatie",
            evidence: "De sandboxgate die de patch verifieert",
          },
          context_assembly: {
            label: "De samengestelde contextmomentopname voor deze fase",
            evidence: "De contextsamenstellingsstap voor deze fase",
          },
        },
        support: {
          support_context: {
            label: "Eerder opgeloste tickets",
            evidence: "De supportstap, uit de meegegeven historie van opgeloste tickets",
          },
          knowledge: {
            label: "Kennisbronnen van de organisatie",
            evidence: "De support-antwoordagent, vanuit zijn kenniscontext",
          },
          memory: {
            label: "Geheugenregels van de organisatie en het project",
            evidence:
              "De support-antwoordagent, uit het geheugen van de organisatie en het project",
          },
        },
        security: {
          governance: {
            label: "Risicovlaggen die de technische gate heeft opgeworpen",
            evidence: "De securityreviewstap, uit de risicovlaggen van de technische gate",
          },
          evidence: {
            label: "Het verificatierapport",
            evidence: "Het opgeslagen verificatierapport",
          },
        },
        release: {
          current_task: {
            label: "De goedgekeurde patch",
            evidence: "De leveringsstap, op de goedgekeurde patch",
          },
          governance: {
            label: "De deploymentkoppeling en gatestatus van het project",
            evidence: "De deploymentkoppeling die voor het project bepaald is",
          },
        },
        ux: {
          current_task: {
            label: "De UI-missie en de gekoppelde melding",
            evidence: "De UX-beoordelingsstap, vanuit de missie en de gekoppelde melding",
          },
          live_connector: {
            label: "Gerenderde routes en vastgelegde schermafbeeldingen",
            evidence: "De routecontrole die routes rendert en schermafbeeldingen vastlegt",
          },
        },
      },
      capabilityLabels: {
        intake_classify: "Een binnenkomend bericht classificeren",
        intake_duplicates: "Dubbele meldingen herkennen",
        repo_write: "Code wijzigen",
        deployment_production_release: "Productierelease",
        workflow_transition: "Een melding naar de volgende status brengen",
        workflow_assign: "Werk toewijzen",
        workflow_escalate: "Escaleren naar een mens",
        repo_read: "Repositorycode lezen",
        knowledge_search: "Geïndexeerde kennis doorzoeken",
        patch_write_virtual: "Codewijzigingen voorstellen",
        sandbox_run: "Sandboxvalidatie draaien",
        sandbox_debug_exec: "Een mislukte verificatie onderzoeken in de eigen sandbox",
        git_pull_request: "Een pull- of merge-request openen",
        git_push_default: "Pushen naar een hoofd- of productiebranch",
        gate_technical: "Een patch goedkeuren of wijzigingen vragen",
        orchestration_delegate: "Werk aan andere agents delegeren",
        patch_read: "De patch lezen die beoordeeld wordt",
        verification_write: "Een verificatieoordeel vastleggen",
        reply_draft: "Een klantantwoord opstellen",
        reply_send: "Een antwoord naar een klant sturen",
        support_context_read: "Eerder opgeloste tickets lezen",
        risk_read: "Risicovlaggen en verificatiebewijs lezen",
        gate_security: "Een securitygate openen",
        patch_apply: "Een goedgekeurde patch toepassen",
        gate_deployment: "Een deploymentgate openen",
        devops_carry_verdict: "Een oordeel terugbrengen naar een CI/CD-host",
        deployment_unattended: "Uitrollen zonder menselijke beslissing",
        ux_assess: "Een operatorworkflow beoordelen",
        ux_screenshot: "Schermafbeeldingen van echte routes vastleggen",
        preview_browser_verify: "De gepatchte applicatie in een browser openen",
      },
      capabilityReasons: {
        intake: {
          intake_classify: "Zijn hele verantwoordelijkheid.",
          intake_duplicates: "Draait over de bestaande meldingen van deze organisatie.",
          repo_write: "Intake raakt nooit een repository aan.",
          deployment_production_release:
            "Levering is een aparte, door een mens getekende gate die bij de Release Manager "
            + "ligt.",
        },
        workflow: {
          workflow_transition:
            "Uitsluitend via het runstatus-contract; geen directe schrijfacties op rijen.",
          workflow_assign: "Binnen de organisatie.",
          workflow_escalate: "Tilt de missie naar een operator.",
          repo_write: "De flow controller voert zelf geen wijzigingen door.",
        },
        codebase: {
          repo_read: "Leest geregistreerde codebases.",
          knowledge_search: "Projectgebonden hybride ophaling plus organisatiebrede bronnen.",
          repo_write: "De navigator levert een briefing, nooit een patch.",
          deployment_production_release:
            "Levering is een aparte, door een mens getekende gate die bij de Release Manager "
            + "ligt.",
        },
        dev: {
          repo_read: "Binnen de meldingswerkruimte.",
          patch_write_virtual: "Schrijft een patch ter beoordeling; hij zet hem niet door.",
          sandbox_run: "Verifieert zijn eigen patch voor de overdracht.",
          sandbox_debug_exec:
            "Een begrensde shell in de container van de mislukte run — geen host, geen netwerk, verdwijnt met de run.",
          preview_browser_verify:
            "De agent die een wijziging schreef certificeert die niet in een browser; dat is aan de QA-agent.",
          git_pull_request: "Uitsluitend via pull- of merge-request, na de goedkeuringsgate.",
          git_push_default:
            "Geen bereikbare toestand: levering gaat uitsluitend via pull request.",
          deployment_production_release:
            "Levering is een aparte, door een mens getekende gate die bij de Release Manager "
            + "ligt.",
        },
        cto: {
          repo_read: "Voor het inspecteren van de patch.",
          gate_technical:
            "De technische gate; het oordeel wordt vastgelegd op de goedkeuringsregel.",
          orchestration_delegate: "De enige agent die een missie plant en delegeert.",
          repo_write: "De reviewer bewerkt nooit zelf.",
        },
        qa: {
          patch_read: "Leest de patch en het sandboxbewijs erbij.",
          verification_write: "Schrijft het verificatierapport dat de afsluitgate leest.",
          preview_browser_verify:
            "Uitsluitend via de sandbox-previewgateway, binnen de eigen origin van die preview.",
          repo_write: "Verificatie bewerkt niet.",
        },
        support: {
          reply_draft: "Alleen opstellen.",
          reply_send: "Versturen loopt via de communicatiegoedkeuringsgate.",
          support_context_read: "Supporthistorie, onafhankelijk van het ticketsysteem.",
        },
        security: {
          risk_read: "Zijn invoer.",
          gate_security: "Werk van deze rol staat standaard achter een menselijke goedkeuring.",
          repo_write: "Alleen beoordelen.",
        },
        release: {
          patch_apply: "Twee-fasencommit, na een menselijke handtekening.",
          gate_deployment:
            "Legt de beslissing vast; die naar buiten dragen wordt apart scherpgesteld.",
          devops_carry_verdict:
            "Vereist dat de deploymentbevoegdheden van de organisatie scherpstaan.",
          deployment_unattended: "Levering gaat nooit automatisch.",
        },
        ux: {
          ux_assess: "Zijn verantwoordelijkheid.",
          ux_screenshot: "Tegen de draaiende applicatie.",
          repo_write: "Levert een beoordeling op, geen patch.",
        },
      },
      boundaryTitle: "De grens",
      boundaryBody:
        "Een agent die over een issue antwoordt, ziet de bronnen van dat project plus "
        + "organisatiebrede bronnen — nooit die van een ander project. Die grens zit in de "
        + "query en in de database, en is geen instelling.",
      untrustedTitle: "Opgehaalde inhoud is data, geen instructie",
      untrustedBody:
        "Tekst uit een repository of kennisbron kan een agent nooit een bevoegdheid geven of "
        + "een goedkeuringseis opheffen. Rechten komen uit beleid, niet uit wat een document "
        + "zegt.",
      capabilitiesTitle: "Wat deze agent mag doen",
      granted: "Toegestaan",
      withheld: "Niet toegestaan",
    },
  },
  orchestration: {
    panel: {
      title: "AI-operatiecentrum",
      subtitle: "CTO-geleide agent-missieorkestratie",
      live: "live",
      reconnecting: "verbinden…",
      controlCentre: "Open missie-cockpit",
      newMission: "Nieuwe missie",
      checking: "Controleren op een actieve missie…",
      startError: "Kon de missie niet starten — {error}",
      loading: "Missie laden…",
      loadError: "Kon de missie niet laden — {error}",
      noData: "Geen missiegegevens.",
      tasksProgress: "{done}/{total} taken",
      approvalsCount: "{n} goedkeuring(en)",
      approvalsPending: "{n} goedkeuring(en) openstaand",
      blockers: "{n} blokkade(s)",
      blocks: {
        approvalCenter: "Goedkeuringscentrum",
        delegationTree: "Delegatieboom",
        tasks: "Taken",
        timeline: "Agent-activiteit",
        approvals: "Goedkeuringen",
      },
      ledger: {
        approver: "goedkeurder",
        unknownActor: "Onbekende actor",
        auditEvent: "Audit-gebeurtenis",
        via: {
          ui: "via FixControl",
          slack: "via Slack",
          email_token: "via e-maillink",
          system: "via systeem",
          command_center: "via Command Center",
          unknown: "via FixControl",
        },
        status: {
          approved: "Goedgekeurd",
          rejected: "Afgewezen",
          changes_requested: "Wijzigingen gevraagd",
          expired: "Verlopen",
        },
        result: {
          execStarted: "Uitvoering gestart.",
          deployPromoted: "Deployment gepromoot.",
          retryAuthorized: "Nieuwe poging geautoriseerd.",
          overrideApplied: "Override toegepast.",
          changeAuthorized: "Wijziging geautoriseerd.",
          approved: "Goedgekeurd.",
          missionStopped: "Missie gestopt.",
          rejected: "Afgewezen.",
          changesRequested: "Wijzigingen gevraagd.",
        },
      },
    },
  },
  skillsPage: {
    eyebrow: "Agents",
    title: "Skills",
    subtitle: "Vaardigheden die je AI-team kan gebruiken.",
    pickTenant: "Kies een organisatie om de skills te zien.",
    loading: "Skills laden…",
    loadFailed: "Skills laden is mislukt.",
    empty: "Geen skills die aan dit filter voldoen.",
    addSkill: "Skill toevoegen",
    noAgents: "Niet toegewezen",
    neverChanged: "Nooit gewijzigd",
    unsatisfied: "Een toegewezen agent kan deze skill niet uitvoeren",
    searchPlaceholder: "Zoek in skills, agents, capabilities…",
    attentionBanner:
      "{count} skill(s) zijn toegewezen aan een agent die ze niet kan uitvoeren. Ze staan aan "
      + "en worden nooit gekozen zolang de toewijzing of de eis niet verandert.",
    columns: {
      skill: "Skill",
      type: "Type",
      usedBy: "Gebruikt door",
      status: "Status",
      capabilities: "Benodigde capabilities",
      lastChanged: "Laatst gewijzigd",
      source: "Bron",
      category: "Categorie",
      trust: "Vertrouwen",
      risk: "Risico",
      version: "Versie",
      lastReviewed: "Laatst beoordeeld",
      platforms: "Platforms",
      languages: "Talen",
      frameworks: "Frameworks",
      lastUpdated: "Laatst bijgewerkt",
      checksum: "Checksum",
      license: "Licentie",
      ref: "Versie / ref",
      repository: "Bron",
    },
    filters: {
      all: "Alle",
      system: "FixControl",
      custom: "Eigen",
      attention: "Aandacht nodig",
      quick: {
        all: "Alle",
        installed: "Geïnstalleerd",
        active: "Actief",
        needs_review: "Beoordeling nodig",
        attention: "Aandacht",
      },
      source: "Bron",
      trust: "Vertrouwen",
      category: "Categorie",
      risk: "Risico",
      agent: "Gebruikt door agent",
      platform: "Platform",
      attentionKind: "Aandacht",
      anySource: "Alle bronnen",
      anyTrust: "Alle vertrouwensniveaus",
      anyCategory: "Alle categorieën",
      anyRisk: "Alle risiconiveaus",
      anyAgent: "Alle agents",
      anyPlatform: "Alle platforms",
      anyAttention: "Alle aandachtspunten",
      sourceOption: {
        fixcontrol: "FixControl",
        custom: "Eigen",
        verified: "Geverifieerd extern",
        community: "Community",
      },
    },
    status: {
      active: "Actief",
      draft: "Concept",
      disabled: "Uit",
      archived: "Gearchiveerd",
    },
    type: {
      system: "FixControl",
      custom: "Eigen",
      edited: "aangepast",
      external: "Extern",
    },
    procedure: {
      descriptive: "Beschrijft werk dat de agent al doet",
      browser_route_check: "Opent de gewijzigde routes in een echte browser",
      preview_browser_check: "Opent de gepatchte applicatie in een echte browser",
      guidance: "Adviserende werkwijze die de agent meekrijgt",
    },
    costClass: {
      low: "Laag",
      medium: "Gemiddeld",
      high: "Hoog",
    },
    events: {
      "skill.created": "Skill aangemaakt",
      "skill.updated": "Skill gewijzigd",
      "skill.restored": "FixControl-standaard hersteld",
      "skill.status_changed": "Status gewijzigd",
      "skill.assigned": "Toegewezen aan een agent",
      "skill.unassigned": "Weggehaald bij een agent",
      "skill.deleted": "Concept verwijderd",
      "skill.imported": "Geïmporteerd",
      "skill.review_started": "Beoordeling gestart",
      "skill.approved": "Goedgekeurd",
      "skill.rejected": "Afgewezen",
      "skill.re_review_requested": "Nieuwe beoordeling aangevraagd",
      "skill.update_available": "Update beschikbaar",
      "skill.version_switched": "Van versie gewisseld",
      "skill.rolled_back": "Teruggedraaid",
      "skill.quarantined": "In quarantaine geplaatst",
    },
    triggers: {
      none: "Nog geen triggers. Zonder trigger kiest niets deze skill ooit.",
      kind: "Wanneer",
      matcher: "Komt overeen met",
      priority: "Prioriteit",
      pick: "Kies…",
      add: "Trigger toevoegen",
      remove: "Trigger verwijderen",
      priorityNote:
        "Prioriteit bepaalt de volgorde als meerdere skills afgaan op één taak. Het breekt een "
        + "gelijkspel; het geeft niets.",
      kinds: {
        always: "Altijd, voor de eigen agents",
        agent_role: "De taak draait als rol",
        issue_kind: "De issue is van een soort",
        issue_flag: "De issue draagt een vlag",
        changed_file_pattern: "Een gewijzigd bestand matcht",
        ux_mission: "Het verzoek is een UI/UX-missie",
        failing_test: "Een test of sandbox-check faalde",
        explicit_request: "Het verzoek vraagt erom",
      },
      placeholder: {
        changed_file_pattern: "src/app/**",
        explicit_request: "browserverificatie",
        agent_role: "frontend",
        issue_kind: "BUG",
        issue_flag: "PAYMENT",
        always: "",
        ux_mission: "",
        failing_test: "",
      },
    },
    simulator: {
      title: "Skill testen",
      intro:
        "Beschrijf een taak. Er draait niets — dit laat zien welke agent de skill zou oppakken, "
        + "waarom, en wat hij dan mag gebruiken.",
      instruction: "Wat zou je vragen?",
      instructionPlaceholder: "Controleer of de issuelijst nog rendert na de refactor",
      issueKind: "Soort issue",
      flags: "Risicovlaggen",
      changedFiles: "Gewijzigde bestanden",
      run: "Test uitvoeren",
      failed: "De simulatie kon niet draaien.",
      noSelection: "Niets zou deze skill kiezen voor die taak.",
      wouldRun: "Zou draaien",
      wouldNotRun: "Zou niet draaien",
      matched: "Trigger",
      capabilities: "Capabilities die hij mag gebruiken",
      context: "Context binnen bereik",
      procedure: "Hoe hij draait",
      version: "Versie",
    },
    runs: {
      title: "Waar deze skill heeft gedraaid",
      loading: "Runhistorie laden…",
      empty: "Deze skill is nog niet op een run verschenen.",
      olderVersion: "· een eerdere versie van deze skill",
      matched: "Trigger: {triggers}",
      evidence: "{count} bewijsartefact(en)",
      routesTested: "Routes: {routes}",
      assertions: "{passed} checks geslaagd, {failed} gefaald",
      consoleErrors: "{count} consolefout(en)",
      status: {
        selected: "Gekozen",
        executed: "Gedraaid",
        refused: "Geweigerd",
        failed: "Mislukt",
      },
    },
    detail: {
      loading: "Skill laden…",
      loadFailed: "Deze skill laden is mislukt.",
      backToList: "Alle skills",
      saved: "Opgeslagen.",
      requestFailed: "Het verzoek is niet afgerond.",
      save: "Opslaan",
      revert: "Terugzetten",
      unsatisfiedTitle: "Een toegewezen agent kan deze skill niet uitvoeren",
      setStatus: {
        active: "Activeren",
        disabled: "Uitzetten",
        draft: "Terug naar concept",
        archived: "Archiveren",
      },
      tabs: {
        overview: "Overzicht",
        instructions: "Instructies",
        triggers: "Triggers",
        context: "Context",
        capabilities: "Capabilities",
        agents: "Agents",
        verification: "Verificatie",
        versions: "Versies",
        provenance: "Herkomst",
        review: "Beoordeling",
      },
      overview: {
        purpose: "Doel",
        type: "Type",
        status: "Status",
        agents: "Toegewezen agents",
        howItRuns: "Hoe hij draait",
        version: "Huidige versie",
        lastChanged: "Laatst gewijzigd",
      },
      instructions: {
        reopensReview: "Een wijziging aan deze goedgekeurde skill opslaan stuurt hem terug naar beoordeling; agents gebruiken hem pas weer na een nieuwe goedkeuring.",
        intro:
          "Wat deze skill de agent vertelt. Instructies sturen hóé het werk gebeurt; ze kunnen "
          + "niet veranderen wat de agent mag.",
        bounds: "{count} tekens",
      },
      triggers: {
        intro:
          "Wanneer deze skill relevant is. Meerdere kunnen afgaan; de hoogste prioriteit gaat "
          + "voorop.",
      },
      context: {
        intro:
          "Wat deze skill wil lezen. Het contextbeleid van de agent bepaalt nog steeds wat hij "
          + "werkelijk ziet — een skill kan nooit een bron openen die de agent niet mag lezen.",
        none: "Deze skill vraagt geen specifieke context.",
        required: "Vereist",
        optional: "Handig",
        boundedNote:
          "Effectieve context is de overlap van deze lijst met wat de runtime van de agent "
          + "leest, binnen de grenzen van het project en de organisatie.",
      },
      capabilities: {
        boundary:
          "Alleen lezen. Een skill geeft nooit een capability: hij benoemt wat de agent al moet "
          + "hebben, en blijft onbeschikbaar als de agent dat niet heeft.",
        required: "Vereist",
        optional: "Handig indien beschikbaar",
        none: "Geen.",
        satisfiedBy: "In bezit van {agents}",
        missingOn: "Ontbreekt bij {agents}",
        noAgentsYet: "Nog geen agent toegewezen.",
      },
      agents: {
        intro:
          "Welke agents deze skill dragen. Een agent die de vereiste capabilities niet heeft, "
          + "kan niet worden toegewezen — de skill zou actief en stilletjes afwezig zijn.",
        assigned: "Toegewezen",
        assign: "Toewijzen",
        unassign: "Weghalen",
      },
      verification: {
        expectations: "Wat waar moet zijn voordat een run meetelt",
        noExpectations: "Voor deze skill zijn geen verwachtingen vastgelegd.",
      },
      versions: {
        restore: "FixControl-standaard herstellen",
        restoreAvailable:
          "Deze organisatie heeft deze skill aangepast. Herstellen laat die aanpassingen vallen en "
          + "gaat terug naar de geleverde versie. De historie blijft intact.",
        alreadyDefault: "Deze skill is ongewijzigd ten opzichte van wat FixControl levert.",
        timeline: "Wat er wanneer veranderde",
        definitions: "Versies",
        noChanges: "Geen wijzigingen vastgelegd.",
        noVersions: "Er is nog geen versie vastgelegd.",
        versionN: "Versie {n}",
        current: "Huidig",
        system: "systeem",
        changedFields: "Gewijzigd: {fields}",
      },
    },
    registry: {
      seedOff: "{count} FixControl-engineeringskills zijn beschikbaar maar staan uit voor deze organisatie. Eenmaal ingeschakeld voegen ze een adviserende werkwijze toe aan de opdrachten van de agents waaraan ze zijn toegewezen.",
      seedOn: "FixControl-engineeringskills staan aan voor deze organisatie ({count}).",
      seedEnable: "FixControl-engineeringskills inschakelen ({count})",
      seedDisable: "Uitschakelen",
      seedKeepOwn: "{count} daarvan houden hun eigen status: die zijn één voor één aan- of uitgezet, en deze schakelaar laat ze zoals ze zijn.",
      seedFailed: "De instelling voor engineeringskills kon niet worden gewijzigd.",
      meta: "{installed} geïnstalleerd · {active} actief",
      addOrDiscover: "Skill toevoegen / ontdekken",
      menuLabel: "Manieren om een skill toe te voegen",
      menuDiscover: "Skills ontdekken",
      menuImport: "Importeren uit een bron",
      menuCustom: "Eigen skill schrijven",
      tabInstalled: "Geïnstalleerd",
      tabDiscover: "Ontdekken",
      viewLabel: "Weergave van het register",
      label: "Skillsregister",
      rowActions: "Acties",
      openFullPage: "Volledige pagina openen",
      openDetails: "Details openen",
      resetFilters: "Filters wissen",
      emptyTitle: "Nog geen skills geïnstalleerd",
      emptyHint: "Voeg een skill toe uit de ontdekcatalogus, importeer er een uit git of schrijf er zelf een.",
      filteredEmpty: "Geen skills die aan deze filters voldoen",
      filteredEmptyHint: "Verruim of wis de filters.",
      moreAgents: "+{count}",
      attentionMarker: "Aandacht nodig",
      reviewMarker: "Beoordeling nodig",
      updateMarker: "Update beschikbaar",
      anyPlatform: "Alle",
      checksumTitle: "Checksum {value}",
      shipped: "Meegeleverd",
      neverReviewed: "Nooit",
      columns: "Kolommen",
      quickLabel: "Snelfilter",
      externalSkill: "Externe skill",
      retry: "Opnieuw proberen",
    },
    attention: {
      title: "Aandacht nodig",
      kind: {
        unsatisfied: "Toegewezen agent kan hem niet uitvoeren",
        quarantined: "In quarantaine",
        checksum_mismatch: "Checksum wijkt af",
        update_available: "Update beschikbaar",
      },
      count: {
        unsatisfied: "{count} toegewezen aan een agent die ze niet kan uitvoeren",
        quarantined: "{count} in quarantaine",
        checksum_mismatch: "{count} met een afwijkende checksum",
        update_available: "{count} met een beschikbare update",
      },
    },
    trust: {
      internal: "Intern",
      verified: "Geverifieerd",
      community: "Community",
      quarantined: "In quarantaine",
    },
    review: {
      pending_review: "Wacht op beoordeling",
      approved: "Goedgekeurd",
      rejected: "Afgewezen",
      needs_re_review: "Opnieuw beoordelen",
    },
    risk: {
      low: "Laag",
      medium: "Gemiddeld",
      high: "Hoog",
      restricted: "Beperkt",
    },
    source: {
      fixcontrol: "FixControl",
      custom: "Eigen",
      verified: "Geverifieerd",
      community: "Community",
      quarantined: "In quarantaine",
    },
    sourceType: {
      internal: "FixControl",
      manual: "Handmatig",
      git: "Git-repository",
      openclaw: "SKILL.md (git)",
      catalog: "Ontdekcatalogus",
    },
    health: {
      ok: "In orde",
      update_available: "Update beschikbaar",
      checksum_mismatch: "Checksum wijkt af",
      quarantined: "In quarantaine",
    },
    category: {
      debugging: "Debuggen",
      planning: "Planning",
      implementation: "Implementatie",
      testing: "Testen",
      verification: "Verificatie",
      code_review: "Codereview",
      security: "Beveiliging",
      architecture: "Architectuur",
      frontend: "Frontend",
      backend: "Backend",
      api: "API",
      database: "Database",
      performance: "Prestaties",
      browser: "Browser",
      git: "Git",
      devops: "DevOps",
      containers: "Containers",
      kubernetes: "Kubernetes",
      cloud: "Cloud",
      incident_response: "Incidentrespons",
      support: "Support",
      release: "Release",
      windows: "Windows",
      dotnet: ".NET",
      windev: "WinDev",
      documentation: "Documentatie",
      intake: "Intake",
    },
    platform: {
      linux: "Linux",
      windows: "Windows",
      browser: "Browser",
    },
    scan: {
      severity: {
        critical: "Kritiek",
        high: "Hoog",
        medium: "Gemiddeld",
        low: "Laag",
      },
      category: {
        prompt_hierarchy_override: "Overschrijft systeeminstructies",
        secret_exfiltration: "Lekken van geheimen",
        approval_bypass: "Omzeilen van goedkeuring",
        production_push: "Push naar productie",
        arbitrary_shell: "Willekeurige shell-opdrachten",
        destructive_fs: "Destructieve bestandsbewerkingen",
        network_exfiltration: "Gegevens wegsturen via het netwerk",
        permission_escalation: "Rechtenescalatie",
        persistence: "Blijvende verankering",
        hidden_instructions: "Verborgen instructies",
        scope_override: "Scope overschrijven",
        unknown_capability: "Onbekende capability",
      },
      summary: "{critical} kritiek · {high} hoog · {medium} gemiddeld · {low} laag",
      clean: "Geen bevindingen",
    },
    reasons: {
      missing_capability: "Kan niet toewijzen: agent mist capability {missing}",
      forbidden_capability: "Vraagt een capability die geen skill mag gebruiken",
      role_not_allowed: "Niet bedoeld voor de rol van deze agent",
      agent_disabled: "De agent staat op pauze",
      skill_not_active: "De skill staat uit",
      context_unavailable: "De agent kan de context die deze skill nodig heeft niet lezen",
      runtime_unavailable: "De runtime die deze skill nodig heeft is niet beschikbaar",
      not_assigned: "Niet toegewezen",
      trust_blocked: "Het vertrouwensniveau staat gebruik niet toe",
      review_pending: "Wacht op goedkeuring na beoordeling",
      quarantined: "In quarantaine",
      platform_mismatch: "Platform past niet bij deze omgeving",
      runtime_mismatch: "Runtime past niet bij deze omgeving",
      checksum_mismatch: "Inhoud gewijzigd sinds de beoordeling (checksum wijkt af)",
      no_capabilities: "Vraagt geen enkele capability en zou dus elke agent bereiken",
    },
    refusals: {
      guidance_text_locked: "De tekst van een meegeleverde werkwijze-skill is door FixControl beoordeeld en kan hier niet worden bewerkt.",
      skill_no_capabilities: "Deze skill noemt geen enkele benodigde capability, dus toewijzen of inschakelen kan niet.",
      guidance_too_long: "De werkwijzetekst is langer dan de 4.000 tekens die een agent meekrijgt.",
      reason_reserved: "Dat begin van een reden is gereserveerd voor quarantaines die het systeem zelf instelt.",
      ssrf_blocked: "Dat bronadres is niet toegestaan.",
      invalid_source: "De bron is geen geldig bestand in een repository.",
      source_too_large: "Het skillbestand is te groot (maximaal 256 KB).",
      already_installed: "Dit catalogusitem is al geïnstalleerd.",
      import_failed: "De import is niet gelukt.",
      not_external: "Alleen een geïmporteerde skill heeft een bron.",
      not_updatable: "Deze skill heeft geen bron om te controleren.",
      external_content_pinned: "Geïmporteerde inhoud is vastgezet; wijzig die door een nieuwe versie te importeren.",
      already_pinned: "Deze versie is al vastgezet.",
      review_pending: "Deze skill is nog niet goedgekeurd. Beoordeel en keur hem goed voordat je hem inschakelt.",
      trust_blocked: "Het vertrouwensniveau van deze skill staat inschakelen niet toe.",
      quarantined: "Deze skill staat in quarantaine en kan niet worden ingeschakeld of toegewezen.",
      assignment_authority_unsatisfied: "Deze agent kan deze skill niet uitvoeren, dus toewijzen kan niet.",
      findings_not_acknowledged: "Bevestig elke hoge en kritieke bevinding voordat je goedkeurt.",
      acknowledgement_required: "Bevestig elke hoge en kritieke bevinding voordat je goedkeurt.",
      snapshot_not_approved: "Alleen een goedgekeurde versie kan worden vastgezet.",
      snapshot_not_found: "Die versie bestaat niet meer.",
      skill_version_conflict: "Iemand anders heeft deze skill eerst gewijzigd. Hij is opnieuw geladen; probeer het nog eens.",
      skill_not_found: "Deze skill bestaat niet meer.",
      checksum_mismatch: "De opgehaalde inhoud komt niet overeen met de checksum.",
      source_unavailable: "De bron was niet bereikbaar.",
      reason_required: "Een reden is verplicht.",
      skill_exists: "Er is al een skill met dit id geïnstalleerd.",
      forbidden: "Je hebt geen recht op deze actie.",
    },
    drawer: {
      acknowledgeFinding: "Bevinding bevestigen: {finding}",
      hiddenCharacters: "{count} verborgen tekens verwijderd",
      guidanceNotRead: "De runtime van deze agent leest geen werkwijze-instructies",
      externalTriggersNote: "De inhoud van een geïmporteerde skill staat vast op de bronversie. Wanneer hij in aanmerking komt, stelt deze organisatie zelf in.",
      snapshotFindingsNotice: "Deze versie heeft hoge of kritieke bevindingen, maar de server heeft ze niet meegestuurd. Goedkeuren kan zodra ze hier zichtbaar zijn; afwijzen kan nu.",
      approveVersion: "Deze versie goedkeuren",
      rejectVersion: "Deze versie afwijzen",
      loading: "Skill laden…",
      loadFailed: "Deze skill laden is mislukt.",
      sections: {
        triggers: "Triggers",
        provenance: "Herkomst",
        instructions: "Instructies",
        review: "Beveiligingsbeoordeling",
        assigned: "Toegewezen agents",
        available: "Andere agents",
        platforms: "Platforms, talen en frameworks",
        versions: "Bronversies",
        audit: "Auditgeschiedenis",
      },
      fields: {
        source: "Bron",
        sourceType: "Brontype",
        repository: "Repository",
        path: "Pad",
        version: "Versie / commit",
        imported: "Geïmporteerd",
        lastReviewed: "Laatst beoordeeld",
        checksum: "Checksum",
        license: "Licentie",
        inspiredBy: "Geïnspireerd door",
        trust: "Vertrouwen",
        review: "Beoordeling",
        risk: "Risico",
        status: "Status",
        category: "Categorie",
        quarantineReason: "Reden van quarantaine",
        platforms: "Platforms",
        languages: "Talen",
        frameworks: "Frameworks",
        runtimes: "Runtimes",
      },
      shippedWithFixControl: "Meegeleverd met FixControl",
      never: "Nooit",
      reviewedBy: "{when} door {name}",
      copy: "Kopiëren",
      any: "Alle",
      instructionsNote: "Getoond als platte tekst, precies zoals opgeslagen. Hier wordt niets weergegeven als opmaak of uitgevoerd.",
      guidanceNote: "Wordt alleen als adviserende werkwijze aan de agent meegegeven. Het kan systeem-, governance- en goedkeuringsregels niet overschrijven.",
      scanNone: "De scan vond niets in de vastgezette versie.",
      scanNotApplicable: "Meegeleverd met FixControl — er is geen bronscan van toepassing.",
      acknowledge: "Ik heb deze bevinding gelezen",
      line: "regel {line}",
      noneAssigned: "Aan geen enkele agent toegewezen.",
      noneAvailable: "Geen andere agents.",
      canRun: "Kan hier draaien",
      cannotRun: "Kan hier niet draaien",
      compatible: "Compatibel",
      incompatible: "Niet compatibel",
      reviewCapabilities: "Capabilities van deze agent bekijken",
      assign: "Toewijzen",
      remove: "Weghalen bij agent",
      snapshotsNone: "Geen bronversies — deze skill is niet uit een bron geïmporteerd.",
      pinned: "Vastgezet",
      fetched: "Opgehaald {when}",
      viewDiff: "Verschil bekijken",
      hideDiff: "Verschil verbergen",
      diffLoading: "Verschil laden…",
      diffFailed: "Het verschil laden is mislukt.",
      diffEmpty: "Geen verschillen in de instructies.",
      diffLabel: "Wijzigingen ten opzichte van de vastgezette versie",
      switchTo: "Naar deze versie overschakelen",
      rollBack: "Terugdraaien",
      checkUpdate: "Controleren op update",
      updateFound: "Er is een nieuwere versie opgehaald die op beoordeling wacht (+{added} / -{removed} regels). De vastgezette versie is ongewijzigd.",
      noUpdate: "De bron heeft geen nieuwere versie.",
      auditNone: "Nog geen geschiedenis vastgelegd.",
      system: "systeem",
      done: "Gelukt.",
      requestFailed: "Het verzoek is niet voltooid.",
      actions: {
        label: "Skillacties",
        enable: "Inschakelen",
        disable: "Uitschakelen",
        review: "Beoordelen",
        approve: "Goedkeuren",
        reject: "Afwijzen",
        requestReReview: "Nieuwe beoordeling aanvragen",
        openFullPage: "Volledige pagina openen",
        reason: "Reden",
        reasonPlaceholder: "Waarom? Dit wordt vastgelegd in de auditgeschiedenis.",
        ackRequired: "Bevestig elke hoge of kritieke bevinding om goed te keuren.",
        reasonRequired: "Een reden is verplicht.",
        cancel: "Annuleren",
        confirmApprove: "Skill goedkeuren",
        confirmReject: "Skill afwijzen",
        confirmReReview: "Nieuwe beoordeling aanvragen",
        enableBlocked: "Kan worden ingeschakeld zodra de skill is goedgekeurd.",
      },
      state: {
        pending_review: "Wacht op beoordeling. Geen enkele agent kan deze skill gebruiken totdat hij is goedgekeurd.",
        needs_re_review: "Gewijzigd sinds de goedkeuring. Er is een nieuwe beoordeling nodig voordat agents hem weer kunnen gebruiken.",
        rejected: "Afgewezen bij de beoordeling. Hij kan niet worden ingeschakeld.",
        quarantined: "In quarantaine. Hij kan niet worden ingeschakeld of toegewezen.",
      },
    },
    import: {
      title: "Skill toevoegen",
      stepOf: "Stap {current} van {total}",
      chooseSource: "Waar komt de skill vandaan?",
      sources: {
        catalog: "Uit de ontdekcatalogus",
        git: "Git-repository (GitHub/GitLab)",
        openclaw: "SKILL.md uit git",
        manual: "Handmatig plakken",
      },
      hints: {
        catalog: "Een geselecteerd item, bij het importeren vastgezet op één commit.",
        git: "Een FixControl-skillbestand in een repository die je opgeeft.",
        openclaw: "Een SKILL.md-bestand; de tekst wordt een adviserende werkwijze.",
        manual: "Plak een naam en instructies.",
      },
      customWizard: "Of schrijf een eigen skill met de wizard",
      catalogEntry: "Catalogusitem",
      pickEntry: "Kies een item…",
      repository: "Repository",
      repositoryPlaceholder: "eigenaar/repo",
      path: "Pad in de repository",
      pathPlaceholder: "skills/debugging/SKILL.md",
      ref: "Tag of commit",
      refPlaceholder: "v1.2.0 of een commit van 40 tekens",
      refHint: "Een branchnaam wordt één keer omgezet naar een vastgezette commit. FixControl volgt nooit 'latest': een nieuwere versie wordt alleen opgehaald als je op een update controleert, en wacht dan op beoordeling.",
      name: "Naam",
      instructions: "Instructies",
      instructionsHint: "Opgeslagen en getoond als platte tekst. Het wordt gescand en moet worden beoordeeld voordat een agent het kan gebruiken.",
      required: "Vul de verplichte velden in.",
      back: "Terug",
      next: "Volgende",
      submit: "Importeren",
      importing: "Bezig met importeren…",
      failed: "De import is niet gelukt.",
      close: "Sluiten",
      resultTitle: "Geïmporteerd — uitgeschakeld tot goedkeuring",
      pinnedCommit: "Vastgezette commit",
      checksum: "Checksum",
      scan: "Scan",
      trust: "Vertrouwen",
      review: "Beoordeling",
      status: "Status",
      disabledNote: "De skill is UITGESCHAKELD geïnstalleerd. Hij moet worden beoordeeld en goedgekeurd voordat een agent hem kan gebruiken. Er is niets toegewezen of ingeschakeld.",
      openSkill: "Skill openen",
    },
    discover: {
      intro: "Een geselecteerde index van externe skills. Alleen metadata wordt getoond — er wordt niets opgehaald tot je importeert, en een import komt uitgeschakeld binnen en wacht op beoordeling.",
      label: "Ontdekcatalogus",
      import: "Importeren",
      installed: "Geïnstalleerd",
      notInstalled: "Niet geïnstalleerd",
      openInstalled: "Geïnstalleerde skill openen",
      empty: "De ontdekcatalogus is leeg.",
      filteredEmpty: "Geen catalogusitems die aan deze filters voldoen.",
      loadFailed: "De ontdekcatalogus laden is mislukt.",
    },
    wizard: {
      title: "Nieuwe skill",
      close: "Sluiten",
      stepOf: "Stap {current} van {total} — {name}",
      back: "Terug",
      next: "Volgende",
      create: "Skill aanmaken",
      failed: "De skill kon niet worden aangemaakt.",
      notValid: "Deze skill is nog niet geldig",
      name: "Naam",
      namePlaceholder: "API-regressiecheck",
      id: "Id",
      idHint: "Kleine letters met koppeltekens. Verandert nooit meer na aanmaken.",
      description: "Wat hij doet",
      descriptionPlaceholder: "Eén zin die een operator in een lijst kan lezen.",
      triggersIntro: "Wanneer moet deze skill overwogen worden?",
      contextIntro:
        "Wat moet hij kunnen lezen? Het beleid van de agent bepaalt nog steeds wat hij echt "
        + "ziet.",
      capabilitiesBoundary:
        "Een skill benoemt wat de agent al mag. Een capability kiezen geeft hier niets — het "
        + "bepaalt welke agents deze skill überhaupt kunnen dragen.",
      capabilityForbidden: "Beslist over workflow of oplevering — niet beschikbaar voor een skill.",
      heldBy: "In bezit van {agents}",
      heldByNobody: "Geen enkele agent heeft dit vandaag.",
      readBy: "Gelezen door {agents}",
      eligibleAgents: "Agents die dit kunnen draaien: {agents}",
      noEligibleAgents:
        "Geen enkele agent heeft al deze capabilities, dus deze skill zou nergens aan toegewezen "
        + "kunnen worden. Haal er één weg, of kies capabilities die één agent heeft.",
      instructionsIntro:
        "Schrijf de stappen, het doel en hoe een goed resultaat eruitziet. Geen commando's, geen "
        + "URL's, geen credentials — een skill beschrijft werk, hij voert het niet uit.",
      instructionsPlaceholder:
        "Vergelijk het API-oppervlak voor en na de wijziging. Meld elk verwijderd of hernoemd "
        + "endpoint als breaking change, met de route erbij.",
      outputIntro: "Hoe wordt deze skill uitgevoerd, en wat legt hij vast?",
      procedure: "Hoe hij draait",
      procedureHint: {
        descriptive:
          "De agent doet dit al als onderdeel van zijn normale werk. De skill beschrijft het en "
          + "wordt op de run vastgelegd; hij voegt geen stap toe.",
        browser_route_check:
          "Opent de routes die deze wijziging raakt in een echte browser, controleert de "
          + "kritieke elementen en legt screenshots en consolefouten vast als bewijs.",
        preview_browser_check:
          "Opent de gepatchte applicatie via de sandboxpreview, binnen de eigen origin van die "
          + "preview, en legt paginastatus, consolefouten, mislukte requests en een schermafbeelding "
          + "vast als bewijs.",
      },
      verificationIntro:
        "Wat moet waar zijn voordat een run van deze skill meetelt? Eén per regel.",
      expectationsPlaceholder: "Elke geteste route heeft een opgeslagen screenshot.",
      costClass: "Verwachte kosten",
      budgetCeiling:
        "Het budget en retrybeleid van de agent blijven het plafond. Een skill kan minder "
        + "vragen, nooit meer.",
      draftNote:
        "De skill wordt als concept aangemaakt. Wijs hem toe aan een agent en activeer hem om "
        + "hem te laten draaien.",
      steps: {
        identity: "Naam en doel",
        triggers: "Triggers",
        context: "Context",
        capabilities: "Capabilities",
        instructions: "Instructies",
        output: "Output",
        verification: "Verificatie",
        review: "Controle",
      },
    },
  },
  // Zie en.ts — AI Team-overzicht (/agents).
  aiTeam: {
    eyebrow: "AI Operations",
    title: "AI Team",
    subtitle: "Je autonome team voor support, engineering, verificatie en release.",
    headline: {
      agents: "{count} agents",
      agentsOne: "1 agent",
      active: "{count} actief",
      waiting: "{count} wacht op goedkeuring",
      attention: "{count} heeft aandacht nodig",
      attentionPlural: "{count} hebben aandacht nodig",
    },
    actions: {
      assignTask: "Taak toewijzen",
      viewAllRuns: "Alle runs bekijken",
      orgChart: "Organigram",
      openAgent: "Open agent",
      openAgentNamed: "Open {name}",
      assignTo: "Taak toewijzen aan {name}",
    },
    ask: {
      label: "Vraag het team",
      placeholder: "Waar wil je dat het AI Team aan werkt?",
      submit: "Vragen",
      hint: "Opent het Command Center met je verzoek — dezelfde bewaakte flow, er draait niets ongevraagd.",
    },
    summary: {
      total: "Agents",
      running: "Actief",
      idle: "Inactief",
      waiting: "Wacht",
      attention: "Aandacht nodig",
      pausedNote: "{count} gepauzeerd",
      enabledNote: "{count} ingeschakeld",
      windowNote: "Cijfers over de laatste {days} dagen",
      updated: "Bijgewerkt {ago}",
    },
    status: {
      running: "Aan het werk",
      waiting: "Wacht",
      attention: "Aandacht",
      idle: "Inactief",
      paused: "Gepauzeerd",
      liveLabel: "Werkt op dit moment",
    },
    filters: {
      label: "Agents filteren",
      all: "Alle",
      active: "Actief",
      waiting: "Wacht",
      attention: "Aandacht",
      idle: "Inactief",
      searchPlaceholder: "Zoek agents of huidig werk…",
      searchLabel: "Zoek agents of huidig werk",
      clear: "Filters wissen",
      noResults: "Geen agents voldoen aan dit filter.",
      noResultsHint: "Probeer een andere status of wis de zoekopdracht.",
    },
    view: {
      label: "Weergave",
      team: "Team",
      flow: "Flow",
    },
    roles: {
      intake: "Triage & classificatie",
      workflow: "Flow-controller",
      codebase: "Repository-intelligentie",
      dev: "Implementatie",
      cto: "Technische review",
      qa: "Validatie & bewijs",
      support: "Klantcommunicatie",
      security: "Security-review",
      release: "Leveringscoördinatie",
      ux: "Workflow-UX",
    },
    card: {
      currentWork: "Huidig werk",
      nothingInProgress: "Niets in behandeling",
      pausedHint: "Gedeactiveerd — de runtime slaat deze agent over tot hij wordt hervat.",
      waitingOn: "Wacht op goedkeuring",
      queuedShort: "{count} in wachtrij",
      runs: "{count} runs",
      runsOne: "1 run",
      success: "{rate}% geslaagd",
      noRunsYet: "Nog geen runs",
      noRunsWindow: "Geen runs in de laatste {days} dagen",
      noRunLedger: "Voor deze agent worden geen runs vastgelegd",
      metricsUnavailable: "Cijfers niet beschikbaar",
      lastRun: "Laatste run {ago}",
      lastRunFailed: "Laatste run mislukt {ago}",
      sparklineLabel: "Runs per dag over de laatste {days} dagen: {values}",
      engineInline: "Engine-inline",
    },
    attention: {
      agent_error: "De agent meldde een fout",
      failed_run: "De laatste run is mislukt",
      job_error: "Een werksessie is mislukt",
      failed_task: "Een taak is mislukt",
      blocked_task: "Een taak is geblokkeerd",
    },
    flow: {
      title: "Hoe werk door het team stroomt",
      intro: "De geleverde support-naar-engineering-pijplijn, met wat er per stap onderhanden is. De CTO plant aan het begin en beoordeelt aan het eind; de UX-architect sluit aan bij UI-onderzoeken.",
      stages: {
        intake: "Intake",
        workflow: "Workflow",
        plan: "Plan",
        codebase: "Codebase",
        dev: "Developer",
        qa: "QA",
        review: "CTO-review",
        security: "Security",
        release: "Release",
        support: "Supportantwoord",
        ux: "UX-architect",
      },
      branch: "Sluit aan na {stage} bij UI-onderzoeken",
      running: "{count} bezig",
      waiting: "{count} wacht",
      blocked: "{count} geblokkeerd",
      quiet: "Niets onderhanden",
      stageLabel: "Stap {index} van {total}: {stage}",
    },
    activity: {
      title: "Live activiteit",
      hint: "De laatste dingen die het team deed, nieuwste eerst.",
      empty: "Geen recente activiteit",
      emptyHint: "Gebeurtenissen verschijnen hier zodra een agent aan het werk gaat.",
      unavailable: "Recente activiteit kon niet worden geladen.",
      system: "Systeem",
      kinds: {
        run_succeeded: "Run afgerond",
        run_failed: "Run mislukt",
        task_started: "Gestart: {title}",
        task_completed: "Afgerond: {title}",
        task_failed: "Mislukt: {title}",
        approval_requested: "Wacht op goedkeuring: {title}",
        approval_approved: "Goedgekeurd: {title}",
        approval_rejected: "Afgewezen: {title}",
        approval_changes_requested: "Wijzigingen gevraagd: {title}",
        mission_started: "Onderzoek gestart: {title}",
        mission_completed: "Onderzoek afgerond: {title}",
        mission_failed: "Onderzoek mislukt: {title}",
        mission_blocked: "Onderzoek geblokkeerd: {title}",
        mission_awaiting_approval: "Onderzoek wacht op goedkeuring: {title}",
      },
    },
    workload: {
      title: "Werkvoorraad",
      hint: "Waar werk zich ophoopt.",
      columns: {
        agent: "Agent",
        active: "Actief",
        queued: "Wachtrij",
        waiting: "Wacht",
        success: "Geslaagd",
        lastRun: "Laatste run",
      },
      empty: "Nog geen werk vastgelegd.",
      notRecorded: "n.v.t.",
    },
    running: {
      title: "Nu bezig",
      hint: "Werk dat op dit moment wordt uitgevoerd.",
      empty: "Er draait op dit moment geen enkele agent",
      emptyHint: "Wijs een taak toe of start een onderzoek om het team aan het werk te zetten.",
      startedAgo: "Gestart {ago}",
      more: "+{count} meer",
    },
    empty: {
      noAgents: "Er zijn nog geen agents geconfigureerd",
      noAgentsHint: "Het AI-team wordt voor elke werkruimte automatisch aangemaakt. Blijft deze lijst leeg, vraag dan je beheerder om de agents voor deze organisatie opnieuw aan te maken.",
    },
    error: {
      title: "Het AI Team-overzicht kon niet worden geladen",
      degraded: "Sommige onderdelen zijn nu niet beschikbaar: {sections}. De rest van de pagina is live.",
      sections: {
        metrics: "run-cijfers",
        work: "huidig werk",
        approvals: "openstaande goedkeuringen",
        activity: "recente activiteit",
        queue: "issues in wachtrij",
      },
    },
  },
  agentsPage: {
    orchestration: {
      editor: {
        title: "Orkestratie-instellingen",
        intro:
          "De vastgestelde configuratie waaronder deze agent draait, en de delen die je "
          + "kunt wijzigen. Bij elke waarde staat waar die vandaan komt en of jij die "
          + "mag verzetten.",
        groupLifecycle: "Beschikbaarheid",
        groupModel: "Model",
        groupBudget: "Budget",
        groupRetry: "Pogingen en time-outs",
        groupContext: "Context",
        groupRouting: "Routering",
        groupCapabilities: "Bevoegdheden",
        groupGovernance: "Governance",
        historyTitle: "Configuratiegeschiedenis",
        historyNone: "Geen configuratiewijzigingen vastgelegd.",
        versionsTitle: "Configuraties die deze agent heeft gehad",
        currentVersion: "Huidige versie",
        edit: "Wijzigen",
        cancel: "Annuleren",
        review: "Wijziging beoordelen",
        apply: "Toepassen",
        applying: "Bezig met toepassen…",
        restore: "FixControl-standaarden herstellen",
        restoreConfirm:
          "De afwijkingen van deze organisatie voor {name} verwijderen en terug naar de "
          + "FixControl-standaarden? De geschiedenis blijft — dit legt een nieuwe versie "
          + "vast in plaats van de vorige te wissen.",
        previewTitle: "Wat er zou veranderen",
        previewFrom: "Nu",
        previewTo: "Daarna",
        previewRisk: "Risico",
        riskLow: "Laag",
        riskMedium: "Middel",
        riskHigh: "Hoog",
        guardedTitle: "Deze vragen een expliciete bevestiging",
        guardedAck: "Ik begrijp wat {field} verandert",
        refusedTitle: "Geweigerd",
        graphTitle: "Effect op de orkestratiegraaf",
        noChanges: "Niets toe te passen — de waarden zijn al gelijk.",
        conflict:
          "De configuratie van deze agent is gewijzigd terwijl je aan het bewerken was. "
          + "De huidige waarden zijn opnieuw geladen; beoordeel ze en probeer opnieuw.",
        applied: "Toegepast. Nieuwe versie {version}.",
        restored: "FixControl-standaarden hersteld.",
        failed: "Kon de wijziging niet toepassen.",
        readOnly: "Je kunt deze configuratie bekijken, maar niet wijzigen.",
        alsoAffects: "Verandert ook: {agents}. Die delen deze instelling; er is geen variant per agent.",
        restoreSkipped: "Niet aangeraakt (wijzig deze bewust): {fields}.",
        fields: {
          enabled: "Beschikbaar voor de orkestrator",
          preferredModel: "Voorkeursmodel",
          platformTaskModel: "Platformstandaardmodel",
          anthropicOnlyClamp: "Providerbeperking",
          maxCostUsd: "Kostenplafond per aanvraag (USD)",
          maxOutputTokens: "Maximale antwoordlengte (tokens)",
          retryCap: "Nieuwe pogingen op dezelfde taak",
          retryBudget: "Providerpogingen vóór de terugval",
          deadlineMs: "Tijdslimiet per poging (ms)",
          sandboxAutoRevision: "Zelfherstel na een mislukte sandboxrun",
          sandboxAutoReviseMaxDepth: "Zelfherstelrondes",
          qaLlmMaxAttempts: "Herhaallus van de verificatie",
          contextExpansion: "Hoeveel context verzameld wordt",
          planningDepth: "Hoe ver vooruit gepland wordt",
          confidenceThreshold: "Benodigd vertrouwen om zelfstandig te handelen",
          retrievalLimit: "Kennistreffers in de briefing",
          hybridWeights: "Weging van de zoekrangschikking",
          rerankEnabled: "Herrangschikkingsstap",
          supportHistoryLimit: "Eerdere tickets als context",
          retrievalProjectBoundary: "Projectgrens bij ophalen",
          includeReleaseStep: "Leveringsfase inplannen",
          planApprovalPolicy: "Wanneer een plan getekend moet worden",
          haltDevOnGate: "Werk stilleggen zolang een gate openstaat",
          ctoPrerequisiteQa: "Verificatie vereist vóór de gate",
          protectedAgent: "Dragende agent",
          agentSetFixed: "Vaste agentset",
          scopedBehaviorResolution: "Project- en per-agent-afwijkingen toepassen",
          deploymentCapabilities: "Deploymentbevoegdheden",
          externalWritesViaOutbox: "Externe schrijfacties via de outbox",
          gitDeliveryPrOnly: "Levering uitsluitend via pull request",
          gateApproveSemantics: "Wat goedkeuren van een wijzigingsverzoek doet",
          rejectGateAborts: "Een afwijzing beëindigt de missie",
          resumeReviseOnComplete: "Opnieuw draaien na een revisieverzoek",
          verificationStrictness: "Hoe streng verificatie is",
          governanceBlockedEnforcement: "Wanneer beleid een plan blokkeert",
          approvalChokepoint: "Eén goedkeuringschokepunt",
          tenantIsolation: "Organisatie-isolatie",
          runStateContract: "Runstatus-contract",
        },
        impacts: {
          enabled:
            "Een gedeactiveerde agent wordt bij elk verdeelpunt geweigerd, en de planner "
            + "laat zijn taken vallen. Waar die agent een goedkeuringsstap bezit (security "
            + "review, gecontroleerde levering), verdwijnt die stap uit het plan in plaats van "
            + "het te blokkeren.",
          preferredModel:
            "Bepaalt welk model voor DEZE organisatie antwoordt, en daarmee de kosten en de "
            + "antwoordkwaliteit. Het verandert nooit wat de agent mag doen.",
          platformTaskModel:
            "Het meegeleverde standaardmodel voor de taak van deze agent. Eén waarde voor "
            + "het hele platform, alleen te wijzigen door een FixControl-platformbeheerder in "
            + "Instellingen → AI. Een organisatie stelt haar eigen model in met het "
            + "voorkeursmodel hierboven, dat deze waarde alleen voor die organisatie "
            + "overschrijft.",
          anthropicOnlyClamp:
            "De code-agents draaien een tool-use-lus van Anthropic. Een afwijking naar een "
            + "andere provider wordt genegeerd in plaats van aan hen doorgegeven.",
          maxCostUsd:
            "Geschat kostenplafond per aanvraag in USD, gecontroleerd vóór de aanroep. 0 = "
            + "geen plafond. Boven het plafond wordt de aanvraag geweigerd, niet afgekapt.",
          maxOutputTokens:
            "Beperkt de antwoordlengte alleen naar BENEDEN. Te laag kapt gestructureerde "
            + "uitvoer af en laat de agent terugvallen op zijn deterministische route.",
          retryCap:
            "Maximaal aantal opeenvolgende orkestratiepogingen op dezelfde taak voordat de "
            + "missie faalt in plaats van te blijven rondlopen.",
          retryBudget:
            "Providerpogingen op het primaire model vóór de terugvalarm. Vermenigvuldigt met "
            + "de eigen herhaallus van een agent, waar die er een heeft.",
          deadlineMs:
            "Kloktijd per poging. 0 = de gedeelde standaard. Een verstreken tijdslimiet telt "
            + "als een herhaalbare fout en verbruikt het pogingenbudget.",
          sandboxAutoRevision:
            "Uit betekent dat elke mislukte sandboxverificatie naar een mens gaat in plaats "
            + "van dat de agent zichzelf herstelt.",
          sandboxAutoReviseMaxDepth:
            "Hoeveel zelfherstelrondes een sandboxfout achter elkaar mag ketenen voordat het "
            + "stopt.",
          qaLlmMaxAttempts:
            "Verificatie probeert zijn eigen leesfouten opnieuw, wat de routeringslaag niet "
            + "ziet. Vandaag alleen via een omgevingsvariabele — niet op het organisatievlak.",
          contextExpansion:
            "Hoeveel omringende context de agent verzamelt voordat hij handelt. Opgeslagen "
            + "op het agentgedragsprofiel en meegenomen in het effectieve profiel, maar GEEN "
            + "enkele plek in de runtime leest het vandaag. Dit als knop aanbieden zou een "
            + "instelling zijn die stilletjes niets doet — precies het gebrek dat dit scherm "
            + "zichtbaar moet maken. Getoond zodat de opgeslagen waarde zichtbaar is; hij "
            + "wordt instelbaar zodra de runtime hem leest.",
          planningDepth:
            "Hoe ver vooruit de agent plant voordat hij begint. Opgeslagen op het "
            + "agentgedragsprofiel en meegenomen in het effectieve profiel, maar GEEN enkele "
            + "plek in de runtime leest het vandaag. Dit als knop aanbieden zou een instelling "
            + "zijn die stilletjes niets doet — precies het gebrek dat dit scherm zichtbaar "
            + "moet maken. Getoond zodat de opgeslagen waarde zichtbaar is; hij wordt "
            + "instelbaar zodra de runtime hem leest.",
          confidenceThreshold:
            "Het minimale vertrouwen voordat de agent handelt zonder te vragen. Opgeslagen "
            + "op het agentgedragsprofiel en meegenomen in het effectieve profiel, maar GEEN "
            + "enkele plek in de runtime leest het vandaag. Dit als knop aanbieden zou een "
            + "instelling zijn die stilletjes niets doet — precies het gebrek dat dit scherm "
            + "zichtbaar moet maken. Getoond zodat de opgeslagen waarde zichtbaar is; hij "
            + "wordt instelbaar zodra de runtime hem leest.",
          retrievalLimit:
            "Het aantal kennistreffers dat de architectbriefing meedraagt. Vastgelegd in de "
            + "handler.",
          hybridWeights:
            "De weging van vector, volledige tekst en trigram in de RRF-combinatie. Een "
            + "centrale instelling: één waarde voor elke organisatie, dus geen knop per "
            + "organisatie.",
          rerankEnabled:
            "Een herrangschikkingsronde met een cross-encoder over de kandidatenlijst. "
            + "Centrale instelling.",
          supportHistoryLimit:
            "Eerder opgeloste tickets die als context bij het antwoord worden aangeboden.",
          retrievalProjectBoundary:
            "Een agent die over een melding antwoordt, ziet de bronnen van het project van "
            + "die melding plus de organisatiebrede bronnen, nooit die van een ander project. "
            + "Geen schakelaar.",
          includeReleaseStep:
            "Voegt een leveringsfase toe aan elke geplande missie. Levering blijft achter "
            + "een menselijke goedkeuring, maar de fase bestaat waar die er eerder niet was.",
          scopedBehaviorResolution:
            "Uit (standaard) bepaalt elke agent zijn gedrag op organisatieniveau, waardoor "
            + "een koppeling op project- of agentniveau wel wordt opgeslagen maar niets "
            + "verandert. Aanzetten laat elke koppeling per agent op organisatieniveau in één "
            + "keer gelden, en elke projectkoppeling die is beoordeeld en geactiveerd — neem ze "
            + "door voordat je dit scherpstelt. Een projectkoppeling die nooit is geactiveerd, "
            + "blijft wachten.",
          planApprovalPolicy:
            "Of een missieplan een menselijke handtekening nodig heeft voordat er werk "
            + "begint. Het wordt afgeleid uit de governance-header van deze organisatie en dus "
            + "op de governanceconsole gewijzigd — het hier wijzigen zou twee schrijvers op "
            + "één gate zetten.",
          haltDevOnGate:
            "Uit laat de implementatie-agent doorwerken terwijl een goedkeuringsgate "
            + "openstaat, waardoor er werk vooruitloopt op een beslissing.",
          ctoPrerequisiteQa:
            "Een CTO-akkoord zonder verificatierapport draait eerst de verificatie. De gate "
            + "is niet bereikbaar zonder verificatiebewijs.",
          protectedAgent:
            "De technische gate en de afsluitgate zijn dragend. Ze deactiveren wordt door de "
            + "API en de runtime geweigerd.",
          agentSetFixed:
            "Agents kunnen niet worden aangemaakt of verwijderd: achter elk id moet een "
            + "echte uitvoerder zitten. De tien zijn de set.",
          deploymentCapabilities:
            "Drie van de vier stellen een externe schrijfactie scherp — een oordeel dat "
            + "FixControl verlaat. Dat gebeurt op het governancescherm, niet hier.",
          externalWritesViaOutbox:
            "Elk effect op een extern systeem draagt een idempotentiesleutel, een "
            + "herhaalbeleid en een dead-letter-route. Geen directe aanroepen naar providers.",
          gitDeliveryPrOnly:
            "Code wordt uitsluitend via een pull- of merge-request geleverd. Pushen naar een "
            + "hoofd- of productiebranch is geen bereikbare toestand.",
          gateApproveSemantics:
            "`accept` betekent dat het goedkeuren van een gate met 'wijzigingen gevraagd' "
            + "het CTO-oordeel overruled en de versie ongewijzigd overneemt, in plaats van om "
            + "een revisie te vragen.",
          rejectGateAborts:
            "Of een afwijzing de missie beëindigt of hem langs een andere route laat "
            + "doorlopen.",
          resumeReviseOnComplete:
            "Of de agent automatisch opnieuw draait zodra een revisieverzoek is afgerond.",
          verificationStrictness:
            "Hoeveel bewijs verificatie eist voordat een wijziging erdoor mag. Opgeslagen op "
            + "het agentgedragsprofiel en meegenomen in het effectieve profiel, maar GEEN "
            + "enkele plek in de runtime leest het vandaag. Dit als knop aanbieden zou een "
            + "instelling zijn die stilletjes niets doet — precies het gebrek dat dit scherm "
            + "zichtbaar moet maken. Getoond zodat de opgeslagen waarde zichtbaar is; hij "
            + "wordt instelbaar zodra de runtime hem leest.",
          governanceBlockedEnforcement:
            "`observe` legt een beleidsblokkade vast zonder de missie te stoppen; `enforce` "
            + "weigert hem überhaupt te plannen.",
          approvalChokepoint:
            "Elk gate-oordeel legt de besliste rol, route, bewijsvoering, beleidsversie, "
            + "consequentie en het kanaal vast op de goedkeuringsregel. Geen achterdeur.",
          tenantIsolation:
            "Organisatie-isolatie wordt in de database afgedwongen en faalt dicht. Het is op "
            + "geen enkel niveau en voor geen enkele rol een instelling.",
          runStateContract:
            "Wijzigingen in de runstatus lopen via één overgangsfunctie. Geen directe "
            + "schrijfacties.",
        },
        refusals: {
          unknownField: "Onbekende instelling \"{key}\".",
          notApplicable: "\"{key}\" geldt niet voor deze agent.",
          fixed:
            "\"{key}\" ligt vast in de runtime ({handle}). De waarde is hier zichtbaar ter "
            + "inzage en is geen beveiligingsgrens, maar je kunt hem hier niet wijzigen.",
          platformInvariant:
            "\"{key}\" is een beveiligingsgrens van het platform en is nooit instelbaar.",
          unknownModel:
            "\"{value}\" staat niet in de modelcatalogus van deze installatie. Een id dat de "
            + "catalogus niet kent wordt bij het verdelen genegeerd, dus dit accepteren zou "
            + "een wijziging vastleggen die nooit effect heeft.",
          outOfBoundsNumber: "\"{key}\" moet tussen {min} en {max} liggen.",
          outOfBoundsEnum: "\"{key}\" moet een van deze zijn: {values}.",
          outOfBoundsBoolean: "\"{key}\" verwacht aan of uit.",
          outOfBoundsModel: "\"{key}\" verwacht een model-id uit de catalogus.",
          outOfBoundsReadonly: "\"{key}\" is alleen-lezen.",
          outOfBounds: "\"{key}\" valt buiten het toegestane bereik.",
        },
        enums: {
          contextExpansion: {
            narrow: "Smal",
            moderate: "Gemiddeld",
            wide: "Breed",
          },
          planningDepth: {
            shallow: "Oppervlakkig",
            normal: "Normaal",
            deep: "Diep",
          },
          gateApproveSemantics: {
            revise: "Om een revisie vragen",
            accept: "De versie ongewijzigd overnemen",
          },
          verificationStrictness: {
            light: "Licht",
            normal: "Normaal",
            strict: "Streng",
          },
          governanceBlockedEnforcement: {
            observe: "Signaleren (alleen vastleggen)",
            enforce: "Afdwingen (niet plannen)",
          },
        },
        values: {
          on: "aan",
          off: "uit",
          unset: "—",
        },
        modelNotInCatalog: "{model} — niet in de catalogus van deze installatie",
      },
    },
    list: {
      protectedAgent: "Onmisbaar — kan niet gedeactiveerd worden",
      engineInline: "Engine-inline",
    },
    detail: {
      loadingAgent: "Agent laden...",
      breadcrumbAgents: "Agents",
      assignTask: "Taak toewijzen",
      runHeartbeat: "Werkvoorraad controleren",
      pause: "Pauzeren",
      resume: "Hervatten",
      heartbeatFailed: "De werkvoorraad kon niet worden gecontroleerd. Er is niets gewijzigd — probeer het opnieuw.",
      heartbeatComplete: "Werkvoorraad gecontroleerd.",
      heartbeatIdle: "Er staat op dit moment niets klaar voor deze agent.",
      heartbeatOpen: "Er staan {n} ta(a)k(en) klaar voor deze agent.",
      assignedFlash: "{key} aangemaakt en toegewezen aan {name}.",
      tabs: {
        dashboard: "Dashboard",
        instructions: "Instructies",
        skills: "Skills",
        configuration: "Configuratie",
        orchestration: "Orkestratie",
        stats: "Statistieken",
      },
      state: {
        running: "actief",
        idle: "inactief",
        paused: "gepauzeerd",
        error: "fout",
      },
      dashboard: {
        loading: "Dashboard laden…",
        latestRun: "Laatste run",
        viewDetails: "Bekijk details →",
        succeeded: "geslaagd",
        failed: "mislukt",
        noRunsYet: "Deze agent heeft nog geen werk gedaan. Wijs een taak toe of controleer de werkvoorraad om te beginnen.",
        runActivity: "Run-activiteit",
        last14Days: "Laatste 14 dagen",
        legendSucceeded: "Geslaagd",
        legendFailed: "Mislukt",
        issuesByPriority: "Issues per prioriteit",
        legendCritical: "Kritiek",
        legendHigh: "Hoog",
        legendMedium: "Gemiddeld",
        legendLow: "Laag",
        issuesByStatus: "Issues per status",
        legendDone: "Klaar",
        legendActive: "Actief",
        legendBlocked: "Geblokkeerd",
        successRate: "Succespercentage",
        successRateNoRuns: "Nog geen runs",
        successRateSummary: "{rate}% over {count} run",
        successRateSummaryPlural: "{rate}% over {count} runs",
        recentIssues: "Recente issues",
        seeAll: "Bekijk alles →",
        noAssignedIssues: "Aan deze agent zijn momenteel geen issues toegewezen.",
      },
      skills: {
        emptyPrefix: "Aan deze agent zijn nog geen skills toegewezen. Wijs ze toe via ",
        emptySuffix: ".",
        emptyConfigurationLink: "Skills",
        granted: "Toegewezen",
        manageLink: "Skills beheren →",
        activeTitle: "Actieve skills",
        activeEmpty: "Geen actieve skills op deze agent.",
        inactiveTitle: "Toegewezen, uitgeschakeld",
        gapsTitle: "Ontbrekende capabilities",
        gapsHint: "Toegewezen, maar deze agent kan ze niet uitvoeren. Een skill geeft nooit een capability.",
        availableTitle: "Beschikbare compatibele skills",
        availableEmpty: "Geen andere compatibele skills.",
        availableUnknown: "Compatibiliteit voor niet-toegewezen skills is nog niet beschikbaar op deze server.",
        missing: "Ontbreekt: {caps}",
        loadFailed: "Skills laden is mislukt.",
      },
      configuration: {
        title: "Configuratie",
        edit: "Bewerken",
        rows: {
          name: "Naam",
          role: "Rol",
          model: "Model",
          state: "Status",
          tenant: "Organisatie",
          reportsTo: "Rapporteert aan",
          description: "Beschrijving",
        },
        topOfTree: "— top van de boom",
        emptyDash: "—",
      },
      stats: {
        rows: {
          runs14d: "Runs (laatste 14 dagen)",
          successRate14d: "Succespercentage (14d)",
          avgLatencyRecent: "Gem. latency (recente runs)",
          openIssuesAssigned: "Openstaande issues toegewezen",
          lastRun: "Laatste run",
        },
      },
    },
    org: {
      eyebrow: "Platform / Agents",
      title: "Organogram",
      subtitle: "Hiërarchie van je AI-workforce — wie rapporteert aan wie.",
      backToList: "Terug naar lijst",
      refresh: "Vernieuwen",
      loading: "Laden…",
      retry: "Opnieuw proberen",
      loadFailed: "Agents laden mislukt",
      emptyTitle: "Nog geen agents",
      emptyBody: "De canonieke 7 agents worden automatisch per organisatie aangemaakt. Zie je dit, dan is de database-bootstrap waarschijnlijk nog niet klaar — probeer Vernieuwen.",
    },
  },

  shared: {
    newIssue: {
      title: "Nieuwe issue",
      sub: "Maak handmatig een issue aan — Ticket Analyst verrijkt deze.",
      createIssue: "Issue aanmaken",
      titleLabel: "Titel",
      titlePlaceholder: "Korte beschrijving van het probleem…",
      kind: "Soort",
      priority: "Prioriteit",
      customer: "Klant",
      customerPlaceholder: "Klantnaam (optioneel)",
      project: "Project",
      projectNone: "Geen project",
      projectHint: "Optioneel — laat leeg om FixControl het project te laten bepalen.",
      reporter: "Melder",
      flagsLabel: "Flags (komma-gescheiden)",
      flagsPlaceholder: "CRASH, PAYMENT, HARDWARE",
      description: "Beschrijving",
      descriptionPlaceholder: "Wat is er gebeurd? Stappen om te reproduceren, verwacht vs werkelijk…",
      kindBug: "Bug",
      kindFeature: "Feature",
      kindSupport: "Support",
      priorityCritical: "Kritiek",
      priorityHigh: "Hoog",
      priorityMedium: "Gemiddeld",
      priorityLow: "Laag",
      submitFailed: "Mislukt",
    },
    attachments: {
      label: "Bijlagen",
      hint: "Plak een screenshot (Ctrl+V), sleep bestanden hierheen of blader. Ze gaan mee als context naar de AI-agents.",
      dropHint: "Sleep bestanden hierheen, plak een screenshot, of",
      browse: "kies bestanden",
      limits: "Maximaal 10 bestanden, 10 MB per bestand.",
      uploading: "Uploaden…",
      uploadFailed: "Uploaden mislukt",
      tooMany: "Maximaal {max} bijlagen per issue.",
      remove: "{name} verwijderen",
    },
    notifications: {
      title: "Meldingen",
      unread: "{count} ongelezen",
      markAllRead: "Alles markeren als gelezen",
      empty: "Geen meldingen",
      bellAria: "Meldingen",
      elsewhere: "{count} ongelezen in {workspace}",
      elsewhereSwitch: "Wissel",
      elsewhereSwitchAria: "Wissel naar {workspace} om die meldingen te zien",
      viewAll: "Alle meldingen bekijken",
      eyebrow: "Overzicht",
      pageSubtitle: "Alles wat je aandacht nodig heeft, nieuwste eerst.",
      refresh: "Vernieuwen",
      filterAll: "Alles",
      filterUnread: "Ongelezen",
      filterType: "Filter op type",
      allTypes: "Alle types",
      loading: "Meldingen laden…",
      emptyUnread: "Je bent helemaal bij",
      emptyHint: "Nieuwe meldingen over goedkeuringen, runs en reviews verschijnen hier.",
      markReadOne: "Markeren als gelezen",
      markUnread: "Markeren als ongelezen",
      // Actie-vereist-balk + bulk/selectie-knoppen + zijbalkindicator.
      actionNeeded: "Actie vereist",
      actionNeededHint: "Deze staan gepauzeerd en wachten op jou.",
      review: "Beoordelen",
      reviewAll: "Alles beoordelen",
      balloonDismiss: "Wegklikken",
      deleteOne: "Melding verwijderen",
      deleteAction: "Verwijderen",
      selectedCount: "{count} geselecteerd",
      bulkMarkRead: "Markeer als gelezen",
      clearSelection: "Wissen",
      selectAll: "Alles selecteren",
      loadMore: "Meer laden",
      deleteConfirmTitle: "Meldingen verwijderen?",
      deleteConfirmBody: "Hiermee verwijder je de geselecteerde meldingen definitief. Dit kan niet ongedaan worden gemaakt.",
      waitingOnYou: "{count} wacht op jou",
      waitingOnYouHint: "Missies gepauzeerd voor jouw goedkeuring",
      bucket: {
        today: "Vandaag",
        yesterday: "Gisteren",
        thisWeek: "Deze week",
        earlier: "Ouder",
      },
      types: {
        action_assigned: "Toegewezen",
        action_overdue: "Te laat",
        action_escalated: "Geëscaleerd",
        issue_needs_review: "Review nodig",
        patch_ready: "Patch klaar",
        workspace_update: "Werkruimte-update",
        system_reminder: "Herinnering",
        inbox_mention: "Vermelding",
        approval_required: "Goedkeuring vereist",
        run_completed: "Run voltooid",
        run_failed: "Run mislukt",
        integration_health: "Integratie",
        integration_recovered: "Integratie hersteld",
        preview_ready: "Preview klaar",
        preview_failed: "Preview mislukt",
      },
    },
    commandCenter: {
      title: "Command Center",
      collapse: "Inklappen",
      expand: "Uitklappen",
      thinking: "Aan het nadenken...",
      thinkingNl: "Aan het nadenken...",
      listening: "Luisteren...",
      pleaseWait: "Even geduld...",
      askPlaceholder: "Stel een vraag of vertel FixControl wat te doen…",
      stopListening: "Stop luisteren",
      voiceInput: "Spraak invoer",
      hintToggle: "Ctrl+Shift+K om te wisselen · Enter om te versturen",
      hintBrowse: " · ↑↓ om door commando's te bladeren",
      messages: "{count} berichten",
      tipExploring: "Tip: de kernagents van {company} staan al voor je klaar — van intake tot QA. Vraag bijvoorbeeld \"wat doen de agents?\" of \"wijzig het model van de Developer-agent\".",
      welcomeIntro: "Ik help je {company} in te richten. Stel een vraag of kies een suggestie hieronder.",
      onPageHint: "Je bent op {path}. Ik kan je uitleggen wat je hier ziet en helpen met acties op deze pagina.",
      paletteHeading: "Suggesties voor deze pagina. Klik om uit te voeren, of typ een commando.",
      reasoningModes: "Denkmodi",
      sectionExplain: "Uitleggen",
      sectionNavigate: "Navigeren",
      sectionAct: "Uitvoeren",
      confirmBadge: "bevestigen",
      nextSteps: "Volgende stappen",
      statusSuccess: "Gelukt",
      statusPartial: "Gedeeltelijk",
      statusError: "Fout",
      statusRefused: "Geweigerd",
      statusUnsupported: "Niet ondersteund",
      actionsDetected: "{count} actie gedetecteerd",
      actionsDetectedPlural: "{count} acties gedetecteerd",
      actionsUrgent: "{count} urgent",
      actionsTrigger: "Trigger:",
      strategyLabel: "Strategie",
      strategyHint: "Productdenken, scope, simplificatie",
      decideLabel: "Beslis",
      decideHint: "Wat nu te doen, waar focussen",
    },
    firstRun: {
      skip: "Dit later instellen",
      back: "Terug",
      next: "Volgende",
      finish: "Aan de slag",
      stepOf: "Stap {n} van {total}",
      goal: {
        title: "Waar wil je FixControl als eerste voor gebruiken?",
        why: "Je antwoord bepaalt welke onderdelen we tonen — de rest blijft verborgen tot je het nodig hebt.",
        support: "Supportvragen beantwoorden",
        tickets: "Tickets beheren",
        code: "Codeproblemen oplossen",
        approvals: "AI-acties laten goedkeuren",
        website: "Websitevragen afhandelen",
        explore: "Eerst alleen rondkijken",
      },
      channel: {
        title: "Waar werkt je team nu?",
        why: "Hier komen je vragen binnen én gaan reviews terug. Kies er één om te beginnen — later koppel je er meer.",
        slack: "Slack",
        teams: "Microsoft Teams",
        email: "E-mail (Gmail)",
        o365: "Office 365 / Outlook",
        widget: "Chat op mijn website",
        later: "Dit later",
      },
      autonomy: {
        title: "Wat mag FixControl zelf doen, en wat keur jij goed?",
        why: "Dit is de belangrijkste veiligheidskeuze. We stellen de veilige standaard voor; je kunt hem later fijn afstemmen onder AI & goedkeuring.",
        safe: "Veilig — alles voorbereiden, ik keur elk extern bericht en elke fix goed",
        balanced: "Gebalanceerd — routine automatisch, risicovol werk naar mij",
        observe: "Alleen observeren — FixControl stelt voor, verstuurt nooit iets",
        recommended: "Aanbevolen",
        approver: "Goedkeuringen komen voorlopig bij jou. Je kunt later collega's uitnodigen.",
      },
      summary: {
        title: "Je voorgestelde configuratie",
        proposalNotice: "Dit is je voorgestelde configuratie. De volgende stappen moeten nog worden voltooid — er is nog niets gekoppeld of geactiveerd.",
        may: "MAG — zodra je de veilige standaard hebt ingesteld",
        mayNot: "MAG NIET zonder jou",
        mayReceive: "Vragen ontvangen en sorteren",
        mayDraft: "Concept-antwoorden opstellen",
        mayRoutine: "Routine-antwoorden automatisch versturen",
        mayNotSend: "Een antwoord naar een klant sturen",
        mayNotDeploy: "Code live zetten",
        mayNotRefund: "Een terugbetaling of risicovolle actie uitvoeren",
        nextTitle: "Je eerste aanbevolen stap",
        nextConnect: "Koppel {channel} — daar komt je supportkanaal binnen",
        next_support: "Stel je support-mailbox of kanaal in",
        next_tickets: "Koppel je ticketsysteem (Jira, Freshdesk of Linear)",
        next_code: "Koppel je eerste codebase",
        next_approvals: "Bekijk wat AI mag voorbereiden en wat jij goedkeurt",
        next_website: "Zet de websitechat aan voor je site",
        next_explore: "Laad de veilige voorbeeldomgeving en klik rond",
        nextExplore: "Bekijk de voorbeelddata en klik rond",
        goToSetup: "Verder met instellen",
        adjustLater: "Je kunt dit allemaal later wijzigen onder Instellingen; installatie hervatten kan altijd via het dashboard.",
      },
    },
    onboarding: {
      dismiss: "Sluiten",
      title: "Aan de slag",
      progress: "{completed} van {total} stappen voltooid",
      allDone: "Alle installatiestappen zijn afgerond",
      resume: "Installatie hervatten",
      revisitWizard: "Installatiewizard opnieuw openen",
      steps: {
        createProjectLabel: "Maak een project",
        createProjectDesc: "Leg een herbruikbaar werkruimteprofiel vast met repo, branch en richtlijnen",
        inviteTeamLabel: "Nodig een collega uit",
        inviteTeamDesc: "Klaar zodra je team minstens 2 accounts heeft — goedkeuringen hoeven dan niet allemaal via jou",
        connectMailboxLabel: "Koppel je support-mailbox",
        connectMailboxDesc: "Verbind Gmail of Office 365, of stel een mailbox in — daar komen klantvragen binnen",
        chooseInboxAiModeLabel: "Kies wat AI in de inbox mag",
        chooseInboxAiModeDesc: "Alleen suggesties, concepten ter goedkeuring, of beperkte automatische antwoorden",
        connectTrackerLabel: "Koppel je ticketsysteem",
        connectTrackerDesc: "Verbind Jira, Freshdesk of Linear zodat tickets in FixControl zichtbaar zijn",
        firstIssueLabel: "Bekijk je eerste issue",
        firstIssueDesc: "Zodra er een vraag of ticket binnenkomt, vind je die hier terug",
        connectCodebaseLabel: "Koppel een codebase",
        connectCodebaseDesc: "Kies je provider en repository — AI leest mee, schrijven gaat altijd via een review",
        reviewGovernanceLabel: "Stel je goedkeuringsregels in",
        reviewGovernanceDesc: "Bepaal wat AI mag voorbereiden en welke acties jouw goedkeuring vereisen",
        createWidgetLabel: "Zet de websitechat aan",
        createWidgetDesc: "Maak een chatsleutel voor je website en kies welke kennis openbaar beantwoord mag worden",
        loadDemoLabel: "Laad de oefenomgeving",
        loadDemoDesc: "Veilige voorbeelddata om mee te klikken — later met één klik te verwijderen",
        verifyCodebaseLabel: "Test de repository-verbinding",
        verifyCodebaseDesc: "Controleer dat FixControl je repository echt kan lezen — via Verbinding testen bij de codebase",
        runDemoMissionLabel: "Draai een oefenmissie",
        runDemoMissionDesc: "Start het voorbeeldscenario en zie het hele proces — van issue tot goedgekeurde fix",
      },
    },
    agentModal: {
      editTitle: "{name} bewerken",
      sub: "Werk de agent-configuratie bij.",
      name: "Naam",
      namePlaceholder: "bijv. QA Tester",
      role: "Rol",
      rolePlaceholder: "bijv. Test Automation",
      model: "Model",
      state: "Status",
      stateRunning: "Actief",
      statePaused: "Gepauzeerd",
      stateIdle: "Inactief",
      stateError: "Fout",
      skillsLabel: "Skills (komma-gescheiden)",
      skillsPlaceholder: "Search repo, Generate code, Run tests",
      description: "Beschrijving",
      descriptionPlaceholder: "Wat doet deze agent?",
      submitFailed: "Mislukt",
    },
    assignTask: {
      title: "Taak toewijzen aan {name}",
      sub: "Maakt een nieuwe issue aan in {tenant} toegewezen aan deze agent.",
      assignTask: "Taak toewijzen",
      agent: "Agent",
      titleLabel: "Titel",
      titlePlaceholder: "Korte beschrijving van de taak…",
      kind: "Soort",
      priority: "Prioriteit",
      description: "Beschrijving",
      descriptionPlaceholder: "Context, verwachte uitkomst, eventuele beperkingen…",
      kindBug: "Bug",
      kindFeature: "Feature",
      kindSupport: "Support",
      priorityCritical: "Kritiek",
      priorityHigh: "Hoog",
      priorityMedium: "Gemiddeld",
      priorityLow: "Laag",
      submitFailed: "Aanmaken van taak mislukt",
    },
    instructions: {
      files: "Bestanden",
      addFile: "Bestand toevoegen",
      entry: "entry",
      filenamePlaceholder: "NOTES.md",
      filenameInvalid: "Bestandsnaam moet een simpele *.md-naam zijn (bijv. NOTES.md)",
      loading: "Instructies laden…",
      loadFailed: "Instructies laden mislukt",
      saveFailed: "Opslaan mislukt",
      createFailed: "Bestand aanmaken mislukt",
      deleteFailed: "Verwijderen mislukt",
      deleteConfirm: "{filename} verwijderen? Dit kan niet ongedaan worden gemaakt.",
      deleteTitle: "{filename} verwijderen",
      empty: "Nog geen instructiebestanden. Klik op + om er een toe te voegen.",
      emptyContent: "(leeg)",
      markdownMeta: "markdown-bestand · {size} · bijgewerkt {when}",
    },
    knowledgeSource: {
      sub: "{tenant} · toegevoegd door {addedBy} · {when}",
      deleteSource: "Bron verwijderen",
      deleteConfirmOne: "{count} chunk verwijderen?",
      deleteConfirmMany: "{count} chunks verwijderen?",
      confirmDelete: "Bevestig verwijderen",
      kind: "Soort",
      chunks: "Chunks",
      size: "Grootte",
      status: "Status",
      tags: "Tags",
      contentHeader: "Inhoud ({count} chunks)",
      contentHelper: "Gebruikt door agents voor context-ophaling",
      loadingChunks: "Chunks laden…",
      noChunks: "Geen chunks gevonden. De bron wordt mogelijk nog verwerkt.",
      chunkLabel: "chunk {index} / {total}",
      chunkChars: "{count} tekens",
    },
    folderPicker: {
      selectFolder: "Kies een map",
      select: "Selecteren",
      noSubfolders: "Geen submappen",
    },
  },

  issuesDetail: {
    breadcrumb: {
      issues: "Issues",
    },
    attachments: {
      title: "Bijlagen",
      add: "Bijlage toevoegen",
      empty: "Geen bijlagen bij dit issue.",
      download: "{name} downloaden",
    },
    // Top-level option labels used by the issues TABLE (issues-table-columns.tsx)
    // — distinct from editModal.* which the edit form uses. Without these the
    // Priority/Status column rendered raw keys (issuesDetail.priorityOptions.*).
    priorityOptions: {
      critical: "Kritiek",
      high: "Hoog",
      medium: "Gemiddeld",
      low: "Laag",
    },
    statusOptions: {
      intake: "Intake",
      review: "Review",
      ready: "Klaar",
      in_progress: "Bezig",
      blocked: "Geblokkeerd",
      done: "Klaar",
    },
    readinessOptions: {
      ready: "Dev-klaar",
      investigate: "Onderzoeken",
      blocked: "Geblokkeerd",
    },
    header: {
      reportedBy: "{tenant} · gemeld door {reporter} · bijgewerkt {when}",
    },
    panels: {
      lastVerification: "Laatste verificatie",
      lastVerificationSub: "Van de meest recente run",
      possibleDuplicates: "Mogelijke duplicaten",
      possibleDuplicatesSub: "Automatisch gedetecteerd",
      details: "Details",
      detailsSub: "Volledige issue-tekst",
      metadata: "Metadata",
      verificationSkipped: "(overgeslagen)",
    },
    metadata: {
      tenant: "Organisatie",
      project: "Project",
      noProject: "Niet aan een project gekoppeld",
      noProjectHint: "Het project draagt de geverifieerde testomgeving — koppel er een op de werksessie van deze melding.",
      assignee: "Toegewezen aan",
      reporter: "Melder",
      created: "Aangemaakt",
      updated: "Bijgewerkt",
      unassigned: "Niet toegewezen",
    },
    actions: {
      approve: "Goedkeuren",
      approved: "Goedgekeurd",
      reject: "Afwijzen",
      markDuplicate: "Markeer als duplicaat",
      split: "Splitsen",
      dryRun: "Dry run",
      runFix: "Fix draaien",
      done: "Klaar",
      pending: "…",
      pr: "PR →",
      criticalWarning: "Kritiek / payment flow — dry run aanbevolen. CTO-gate kan vereist zijn voor een volledige run.",
      supportNote: "Klantgericht antwoord — bekijk en verstuur via het Auto-reply paneel hierboven.",
    },
    actionFeedback: {
      approved: "{key} goedgekeurd → dev-pijplijn",
      updated: "{key} bijgewerkt",
      rejected: "{key} afgewezen",
      markedDuplicate: "Gemarkeerd als duplicaat van {of}",
      splitInto: "Gesplitst in {n} issues",
      dryRunPatches: "Dry run: {n} patches voorgesteld",
      prOpened: "PR #{n} geopend",
      runStarted: "Run gestart",
      failed: "Mislukt",
      approveFailed: "Goedkeuren mislukt",
      rejectFailed: "Afwijzen mislukt",
      duplicateFailed: "Duplicaat mislukt",
      splitFailed: "Splitsen mislukt",
      runFailed: "Run mislukt",
      deleted: "{key} verwijderd",
      deleteFailed: "Verwijderen mislukt",
    },
    prompts: {
      duplicate: "Markeer {key} als duplicaat van welke issue? (bijv. BIZ-22)",
      split: "Splits in onderliggende titels (één per regel):",
    },
    aiAnalysis: {
      title: "AI-analyse",
      author: "Ticket Analyst",
      flaggedAs: "Gemarkeerd als {flag}",
      summary: "Samenvatting",
      impact: "Impact",
      likelyCause: "Waarschijnlijke oorzaak",
      confidence: "Zekerheid",
      causeFallback: "Te weinig signaal — onderzoek vereist.",
      impactFallback: "Nog te bepalen.",
      confidenceHigh: "Hoog — veilig om actie te ondernemen.",
      confidenceMedium: "Gemiddeld — beoordeel voor goedkeuring.",
      confidenceLow: "Laag — verder onderzoeken.",
    },
    risks: {
      title: "Risico's & impact",
      sub: "Operationele samenvatting",
      affects: "Raakt",
      blocks: "Blokkeert",
      approval: "Goedkeuring",
      deploy: "Deploy",
      affectsPayment: "Klanten in de payment flow",
      affectsTenant: "Gebruikers in organisatie {tenant}",
      blocksCrash: "Klantgerichte flow ligt eruit",
      blocksPayment: "Betalingsverwerking aangetast",
      blocksHardware: "Werking van in-store hardware",
      blocksDefault: "Functie niet beschikbaar voor getroffen gebruikers",
      approvalGate: "CTO-gate vóór merge",
      approvalStandard: "Standaard review",
      ctoApprovalRequired: "CTO-goedkeuring vereist vóór merge",
      paymentDryRun: "Payment-flow wijziging — eerst dry run gebruiken",
      securityReview: "Security-review-gate",
      datalossBackup: "Risico op dataverlies — back-up vereist vóór deploy",
      noConstraints: "Geen bijzondere beperkingen",
    },
    reply: {
      title: "Auto-reply",
      to: "Aan {reporter}",
      tones: {
        friendly: "vriendelijk",
        formal: "formeel",
        concise: "beknopt",
        professional: "professioneel",
        technical: "technisch",
      },
      drafting: "Concept maken…",
      draftWithAI: "Concept met AI",
      regenerate: "Opnieuw genereren",
      placeholder: "Schrijf een antwoord of laat de Support Agent er één opstellen…",
      draftSaved: "Concept opgeslagen.",
      sentTo: "✓ Verstuurd naar {reporter}",
      failed: "Mislukt",
      draftFailed: "Concept mislukt",
      saveFailed: "Opslaan mislukt",
      sendFailed: "Versturen mislukt",
      charCount: "{n} tekens",
      saving: "Opslaan…",
      saveDraft: "Concept opslaan",
      sending: "Versturen…",
      send: "Versturen",
      signaturePreview: {
        label: "Wordt automatisch toegevoegd bij verzenden",
        showFull: "Toon de volledige e-mail",
        hideFull: "Verberg de volledige e-mail",
        fullLabel: "Wat de klant ontvangt",
      },
      templates: {
        pickerLabel: "Template invoegen…",
      },
    },
    governedReply: {
      title: "Gegoverneerd klantantwoord",
      pending: "Wacht op goedkeuring",
      approved: "Goedgekeurd",
      sent: "Verstuurd naar klant",
      rejected: "Afgewezen",
      escalated: "Geëscaleerd",
      revise_requested: "Wijzigingen gevraagd",
      toCustomer: "Aan {email}",
      decidedBy: "door {who}",
      edited: "aangepast vóór versturen",
      pendingHint: "Dit antwoord wacht op goedkeuring. Goedkeuren verstuurt het naar de klant via het kanaal waarvoor het is opgesteld.",
      sentHint: "Geplaatst als publiek antwoord op het gekoppelde Freshdesk-ticket.",
      showMessage: "Toon bericht",
      hideMessage: "Verberg bericht",
      approve: "Goedkeuren en versturen",
      reject: "Afwijzen",
      confirmApproveTitle: "Dit antwoord naar de klant sturen?",
      confirmApproveBody: "Goedkeuren verstuurt het bericht hierboven naar de klant. Dit kan niet ongedaan worden gemaakt.",
      confirmRejectTitle: "Dit antwoord afwijzen?",
      confirmRejectBody: "De klant ontvangt niets. Het concept blijft bewaard met jouw beslissing.",
      cancel: "Annuleren",
      failed: "Kon de beslissing niet vastleggen ({code}). Er is niets verstuurd.",
      heldUnsafe: "Tegengehouden: het antwoord bevat iets dat niet verstuurd mag worden ({reasons}). Pas eerst het concept aan.",
    },
    actionItems: {
      title: "Actiepunten",
      subLoading: "Operations Coordinator · laden…",
      sub: "Operations Coordinator · {open} open · {total} totaal",
      subHandoff: "QA-handoff · {open} open · {total} totaal",
      generating: "Genereren…",
      regenerate: "Opnieuw genereren",
      generate: "Acties genereren",
      empty: "Nog geen acties. Klik op Genereren om de Operations Coordinator vervolgacties te laten extraheren.",
      risky: "risicovol",
      starting: "Bezig met starten…",
      start: "Starten",
      inProgress: "bezig",
      remove: "Verwijderen",
      removeConfirmTitle: "Deze actie verwijderen?",
      removeConfirm: "Ze verdwijnt uit de vervolgactielijst. De missietijdlijn en audithistorie houden vast dat ze heeft bestaan.",
      removeConfirmLabel: "Verwijderen",
      markOpen: "Markeer als open",
      markDone: "Markeer als klaar",
      dispatchTitle: "Stuur deze sub-taak naar de {role}-agent (indien aanwezig)",
      priority: {
        urgent: "urgent",
        high: "hoog",
        normal: "normaal",
        low: "laag",
      },
      type: {
        support: "support",
        commercial: "commercieel",
        operational: "operationeel",
        configuration: "configuratie",
      },
      result: {
        started: "gestart",
        startedFallback: "gestart (fallback)",
        manualTask: "handmatige taak",
        limited: "beperkt",
        error: "fout",
        noMessage: "geen bericht",
        openWorkspace: "Open werkruimte →",
      },
      requestFailed: "verzoek mislukt",
    },
    editModal: {
      title: "{key} bewerken",
      sub: "Werk issue-velden bij.",
      labels: {
        title: "Titel",
        kind: "Type",
        priority: "Prioriteit",
        status: "Status",
        readiness: "Gereedheid",
        customer: "Klant",
        assignee: "Toegewezen aan",
        flags: "Flags (komma-gescheiden)",
        summary: "Samenvatting",
        impact: "Impact",
        description: "Beschrijving",
        retryNote: "Wat ging er mis, of wat moet de AI nu weten?",
      },
      kindOptions: {
        bug: "Bug",
        feature: "Feature",
        support: "Support",
      },
      priorityOptions: {
        critical: "Kritiek",
        high: "Hoog",
        medium: "Gemiddeld",
        low: "Laag",
      },
      statusOptions: {
        intake: "Intake",
        review: "Review",
        ready: "Klaar",
        in_progress: "Bezig",
        blocked: "Geblokkeerd",
        done: "Klaar",
      },
      readinessOptions: {
        ready: "Dev klaar",
        investigate: "Onderzoeken",
        blocked: "Geblokkeerd",
      },
      customerPlaceholder: "Klantnaam (optioneel)",
      failed: "Mislukt",
      saveAndRetry: "Opslaan en opnieuw proberen",
      retryNoteHint: "Gaat mee als instructie naar de nieuwe poging. Wordt niet op het issue opgeslagen.",
      retryNotePlaceholder: "Bijv.: de vorige patch paste de kassamodule aan, maar het bundelen gebeurt in de factuurservice.",
      standing: {
        running: {
          title: "Er loopt nu een poging",
          body: "Deze bewerking verandert de tekst waar het AI-team nu aan werkt. De lopende poging leest de oude omschrijving uit en wordt daarna gemarkeerd als verouderd.",
        },
        "pending-approval": {
          title: "Er staat een goedkeuring open",
          body: "Iemand moet nog beslissen over werk dat op de huidige omschrijving is gebaseerd. Die poort blijft open en beslisbaar — na het opslaan staat erbij dat hij over een eerdere versie gaat.",
        },
        "approved-patch": {
          title: "Er ligt een goedgekeurde patch",
          body: "Die goedkeuring is echt gegeven en blijft staan; FixControl haalt hem niet weg. Wel vervalt de directe Toepassen-knop: de patch is goedgekeurd op de tekst zoals die nu nog is, en wordt daarna gemarkeerd als verouderd.",
        },
      },
    },
    focus: {
      title: "Vraagt aandacht",
      clearFilter: "Filter wissen",
      allClearTitle: "Niets vraagt je aandacht",
      allClearBody: "Alle issues zijn getrieerd, beoordeeld of uitgerold.",
      criticalSingular: "Kritieke issue blokkeert klanten",
      criticalPlural: "{n} kritieke issues blokkeren klanten",
      reviewSingular: "Issue heeft review nodig",
      reviewPlural: "{n} issues hebben review nodig",
      blockedSingular: "Geblokkeerde issue",
      blockedPlural: "{n} geblokkeerde issues",
      emptyCritical: "Geen kritieke issues",
      emptyReview: "Review-wachtrij is leeg",
      emptyBlocked: "Niets geblokkeerd",
    },
    summary: {
      critical: "Kritiek",
      review: "Review",
      blocked: "Geblokkeerd",
      openTotal: "Open",
    },
    row: {
      replied: "beantwoord · {when}",
      repliedTooltip: "Klantantwoord verstuurd {when}",
      readinessStatusTooltip: "Gereedheid: {readiness} · Status: {status} · Zekerheid {pct}%",
    },
    bulk: {
      approve: "Goedkeuren",
      reject: "Afwijzen",
      delete: "Verwijderen",
      deleteConfirmTitle: "Geselecteerde issues verwijderen?",
      deleteConfirmLabel: "Issues verwijderen",
      deleteConfirmBody: "Dit haalt {count} issue(s) uit het actieve werk. Een beheerder kan ze terugzetten; hun audithistorie, goedkeuringsbeslissingen en verificatieresultaten blijven bewaard.",
    },
    pane: {
      title: "Issue-detail",
      open: "Openen",
      openFullPage: "Volledige pagina openen",
      close: "Detail sluiten",
      enableSplit: "Detailpaneel openen",
      disableSplit: "Detailpaneel sluiten",
      resizeHandle: "Detailpaneel verkleinen/vergroten",
      customer: "Klant",
      reporter: "Melder",
      assignee: "Toegewezen aan",
      replied: "Laatste antwoord",
      impact: "Impact",
      summary: "Samenvatting",
      emptyTitle: "Geen issue geselecteerd",
      emptyBody: "Klik op een rij in de tabel om de details hier te zien.",
    },
    rowActions: {
      approve: "Goedkeuren",
      runFix: "Fix draaien",
      open: "Openen",
      edit: "Bewerken",
      duplicate: "Markeer duplicaat",
      reject: "Afwijzen",
      delete: "Verwijderen",
      more: "Meer acties",
      pr: "PR",
      titles: {
        approve: "Goedkeuren → markeer als dev-klaar",
        runFix: "Implementatie-agent draaien (dry run)",
        duplicate: "Markeer als duplicaat",
        edit: "Issue bewerken",
        reject: "Afwijzen → sluiten als irrelevant",
        delete: "Issue verwijderen",
      },
      deleteConfirm: "{key} verwijderen? Het verdwijnt uit het actieve werk en kan door een beheerder worden teruggezet. De audithistorie, goedkeuringsbeslissingen en verificatieresultaten blijven bewaard.",
      duplicatePrompt: "Markeer {key} als duplicaat van welke issue? (bijv. BIZ-22)",
    },
    why: {
      possibleDuplicate: "Mogelijk duplicaat van {key}",
      breaksPayment: "Breekt payment flow",
      blocksHardware: "Blokkeert in-store werking",
      crash: "Klantgerichte crash",
      dataloss: "Risico op dataverlies",
      security: "Security-issue",
      touchesPayment: "Raakt payment flow",
      hardwareIssue: "Hardware-issue",
    },
  },

  issueDetailV2: {
    primaryAction: {
      label: {
        none: "Geen actie nodig",
        reviewApproval: "Beoordelen",
        dryRun: "Test in sandbox",
        applyPatch: "Toepassen",
        viewPatch: "Bekijk patch",
        viewSandboxRun: "Bekijk sandbox-run",
        runFix: "Fix draaien",
        reply: "Beantwoord klant",
      },
      reason: {
        issueDone: "Deze issue is afgerond.",
        approvalPending: "Het AI-team wacht op jouw goedkeuring om verder te gaan.",
        patchNeedsDryRun: "Er ligt een patch — test 'm eerst veilig in de sandbox.",
        readyToApply: "Sandbox geslaagd en patch goedgekeurd — klaar om toe te passen.",
        sandboxFailed: "De sandbox-run is mislukt — open de run om te zien welke stap faalde.",
        patchChangesRequested: "De AI stuurde deze patch terug voor wijzigingen en is gestopt — bekijk 'm en beslis: doorzetten, verwerpen of zelf bijsturen.",
        patchReady: "Er ligt een patch klaar om te bekijken.",
        noRun: "Nog geen AI-missie gestart voor deze issue.",
        replyReady: "Er staat een conceptantwoord klaar voor de klant.",
        aiWorking: "Het AI-team is bezig — er is nu niets van jou nodig.",
        nothingNeeded: "Er is op dit moment geen actie van jou nodig.",
        issueChangedSinceRun: "Dit issue is herschreven nadat deze patch werd goedgekeurd — de goedkeuring gaat over de oude tekst. Bekijk de patch voor je 'm toepast, of start een nieuwe poging.",
      },
    },
    missionStart: {
      alreadyActive: "Er loopt al een poging op dit issue. Wacht tot die klaar is of annuleer 'm in de missie, en probeer het dan opnieuw.",
      capExceeded: "Er draaien al {active} van de maximaal {cap} gelijktijdige AI-missies in deze organisatie. Rond er een af of annuleer er een, dan kan deze poging starten.",
      quotaExceeded: "De AI-acties van dit abonnement zijn op voor deze periode ({used} van {limit} gebruikt). Verhoog de limiet of wacht op de nieuwe periode.",
      httpFailed: "Kon de poging niet starten (foutcode {status}). Probeer het opnieuw of neem contact op met een beheerder.",
      unknown: "Kon de poging niet starten.",
    },
    status: {
      resolved: "Afgerond",
      awaitingApproval: "Wacht op jouw goedkeuring",
      investigating: "AI-team onderzoekt",
      working: "AI-team werkt aan een fix",
      blocked: "Missie geblokkeerd",
      missionFailed: "Missie mislukt",
      verificationFailed: "Verificatie mislukt",
      verifying: "Patch wordt getest",
      readyToApply: "Klaar om toe te passen",
      patchChangesRequested: "Patch teruggestuurd — jouw review nodig",
      patchReadyDryRun: "Patch klaar voor sandbox-test",
      patchReady: "Patch klaar voor review",
      missionDone: "AI-missie afgerond",
      notStarted: "Nog niet opgepakt",
      issueChangedSinceRun: "Issue herschreven na deze poging",
    },
    commandBar: {
      updated: "Bijgewerkt",
      statusLabel: "Status",
      overflow: "Meer acties",
      edit: "Bewerken",
      reject: "Afwijzen",
      markDuplicate: "Markeer als duplicaat",
      split: "Splitsen",
      rerun: "Bewerken en opnieuw proberen",
      openDetails: "Details openen",
      delete: "Verwijderen",
      runFixExplain: {
        title: "Wat gebeurt er als je een fix start?",
        body: "FixControl laat een AI-agent dit issue onderzoeken en een codevoorstel maken. Elk voorstel wordt eerst in een geïsoleerde omgeving getest.",
        guarantee: "Er gaat niets naar een klant of naar productie zonder jouw goedkeuring.",
        eta: "Duurt meestal een paar minuten. Je kunt gewoon verder werken.",
        confirm: "Start de fix",
        cancel: "Annuleren",
      },
    },
    summary: {
      title: "Wat speelt er?",
      empty: "Geen omschrijving beschikbaar.",
      expected: "Verwacht gedrag",
      actual: "Werkelijk gedrag",
      environment: "Omgeving",
      impact: "Impact",
      impactUnknown: "Nog niet bepaald.",
      supportTicket: "Supportticket",
      createTicket: "Supportticket aanmaken",
      ticketCreateError: "Kon het supportticket niet aanmaken.",
      ticketKindUnsupported: "Alleen bug- en feature-issues worden naar de tracker gespiegeld.",
    },
    run: {
      title: "AI-uitvoering",
      openFull: "Open volledige operatie",
      loading: "Bezig met laden…",
      loadError: "Kon de missie niet laden.",
      noRun: "Nog geen AI-missie gestart.",
      noRunHint: "Start er één met de actieknop bovenaan.",
      tasksDone: "{done}/{total} taken",
      currentStep: "Huidige stap",
      idleStep: "Wacht op de volgende stap",
      blocker: "Blokkade",
      blockerCount: "{n} geblokkeerde stap(pen)",
      result: "Opgeleverd",
      patchVersion: "Patch v{v}",
      patchVersions: "{n} versies",
      lastEvent: "Laatste update",
    },
    execution: {
      eyebrow: "Beheerde uitvoering",
      trackerLabel: "Uitvoeringsvoortgang",
      live: "Live",
      currentActivity: "Huidige uitvoering",
      finished: "Geen stap actief — de missie is afgerond.",
      assignedTo: "Uitgevoerd door {role}",
      activeAgent: "Hier wordt nu aan gewerkt",
      noActiveAgent: "Er wordt op dit moment niet aan gewerkt.",
      missionOwner: "Missie-eigenaar: {role}",
      pipelineTitle: "Uitvoeringspijplijn",
      sideActivityTitle: "Live nevenactiviteit",
      sideActivityHint: "Workspace-agents die naast de missie draaien (chat / codebase research).",
      sideActivityEmpty: "Geen nevenactiviteit op dit moment.",
      sideActivityAgent: { dev: "Dev agent", codebase: "Codebase agent" },
      sideActivityHeartbeat: "Laatste signaal {age}",
      failureReason: "Faalreden",
      auditTitle: "Uitvoeringssamenvatting",
      auditNote: "Hash-geketend uitvoeringslogboek",
      viewLog: "Toon gedetailleerd operationeel logboek ({n})",
      hideLog: "Verberg operationeel logboek",
      outputTitle: "Resultaat",
      patchLabel: "Patch v{version}",
      metricsTitle: "Operationele telemetrie",
      metricDuration: "Duur",
      metricElapsed: "Verstreken",
      metricAffected: "Geraakte bestanden",
      metricBlocked: "Geblokkeerde deps",
      metricChecks: "QA-checks",
      checksValue: "{passed}/{total} · {pct}%",
      notAssessed: "Niet beoordeeld",
      metricRollback: "Rollback",
      metricAudit: "Uitvoeringsevents",
      metricCost: "Uitvoeringskosten",
      costRuns: "{n} gemeten uitvoeringsevent(s)",
      notAvailable: "Niet beschikbaar",
      notMetered: "Voor deze issue is nog geen uitvoering gemeten.",
      rollback: {
        available: "Beschikbaar",
        unavailable: "Niet beschikbaar",
        rolled_back: "Teruggedraaid",
        na: "N.v.t.",
      },
      header: {
        title: "Operationele status",
        label: {
          overall: "Status",
          stage: "Fase",
          risk: "Risico",
          validation: "Validatie",
          approval: "Goedkeuring",
          deployment: "Uitrol",
        },
        overall: {
          executing: "In uitvoering",
          awaiting_approval: "Wacht op goedkeuring",
          blocked: "Geblokkeerd",
          resolved: "Opgelost",
          failed: "Mislukt",
          halted: "Gestopt",
          idle: "Niet gestart",
        },
        risk: {
          low: "Laag",
          medium: "Gemiddeld",
          high: "Hoog",
          critical: "Kritiek",
          unassessed: "Niet beoordeeld",
        },
        validation: {
          passed: "Geslaagd",
          failed: "Mislukt",
          running: "Bezig",
          pending: "In afwachting",
        },
        approval: {
          required: "{n} vereist",
          clear: "Vrij",
        },
        deployment: {
          deployed: "Uitgerold",
          rolled_back: "Teruggedraaid",
          ready: "Veilig om uit te rollen",
          awaiting: "Wacht op goedkeuring",
          blocked: "Geblokkeerd",
          not_ready: "Niet gereed",
        },
      },
      summary: {
        tasksDispatched: "{n} taak/taken verzonden",
        tasksDispatchedNone: "Nog geen taken verzonden",
        tasksCompleted: "{done}/{total} taak/taken afgerond",
        validation: {
          passed: "Validatie geslaagd",
          failed: "Validatie mislukt",
          running: "Validatie bezig",
          pending: "Validatie niet uitgevoerd",
        },
        review: {
          accepted: "Review akkoord",
          rejected: "Review afgewezen",
          changes: "Wijzigingen gevraagd",
          in_progress: "Review bezig",
          pending: "Review in afwachting",
        },
        security: {
          pending: "Securityreview in afwachting",
          in_progress: "Securityreview bezig",
          cleared: "Securityreview akkoord",
        },
        deployment: {
          deployed: "Uitgerold",
          rolled_back: "Uitrol teruggedraaid",
          ready: "Uitrol gereed",
          awaiting: "Uitrol wacht op goedkeuring",
          blocked: "Uitrol geblokkeerd",
          not_ready: "Uitrol niet gereed",
        },
        rollback: {
          available: "Rollback beschikbaar",
          unavailable: "Rollback niet beschikbaar",
          rolled_back: "Teruggedraaid",
          na: "Rollback niet van toepassing",
        },
      },
      phase: {
        intake: "Intake",
        analysis: "Analyse",
        plan_review: "Plangoedkeuring",
        implementation: "Implementatie",
        validation: "Validatie",
        final_review: "Eindreview",
      },
      state: {
        completed: "Afgerond",
        active: "Bezig",
        attention: "Beoordeling nodig",
        pending: "In wachtrij",
        failed: "Mislukt",
        skipped: "Overgeslagen",
      },
    },
    planReview: {
      title: "Uitvoeringsplan",
      awaitingTitle: "Wacht op plangoedkeuring",
      version: "Plan v{n}",
      riskLevel: "Risico",
      requiredApprover: "Voorgestelde route",
      approvalStages: "Goedkeuringsfases",
      requestedBy: "Aangevraagd door",
      generatedAt: "Gegenereerd",
      approvalStatus: "Status",
      notAvailable: "Niet beschikbaar",
      blockedByGovernance: "Implementatie is geblokkeerd door governancebeleid totdat dit plan is goedgekeurd.",
      readOnlyNote: "Alleen een beheerder of manager kan over dit uitvoeringsplan beslissen.",
      readOnlyNoteStaged: "Dit plan wordt in fases goedgekeurd. Over de huidige fase beslist {role}.",
      anyApproverDecides: "De route is een suggestie: elke beheerder of manager kan over dit plan beslissen, tenzij goedkeuring in fasen het in fases zet.",
      stagedNote: "Dit plan wordt in fases goedgekeurd, op volgorde, één persoon per fase. Iedereen die mag beslissen kan het afwijzen.",
      anApprover: "een goedkeurder",
      reviewerFeedback: "Gevraagde wijzigingen",
      approve: "Plan goedkeuren",
      requestChanges: "Wijzigingen vragen",
      reject: "Plan afwijzen",
      commentPlaceholder: "Wat moet er veranderen? (verplicht, vastgelegd in de audit trail)",
      commentRequired: "Een opmerking is verplicht om wijzigingen te vragen.",
      status: {
        draft: "Concept",
        awaiting_approval: "Wacht op goedkeuring",
        approved: "Goedgekeurd",
        changes_requested: "Wijzigingen gevraagd",
        rejected: "Afgewezen",
        superseded: "Vervangen",
      },
      risk: { low: "Laag", medium: "Middel", high: "Hoog", critical: "Kritiek" },
      fields: {
        problemInterpretation: "Probleeminterpretatie",
        proposedApproach: "Voorgestelde aanpak",
        affectedSystems: "Betrokken systemen",
        affectedFiles: "Betrokken bestanden",
        riskLevel: "Risiconiveau",
        riskReasons: "Risicoredenen",
        validationStrategy: "Validatiestrategie",
        rollbackPlan: "Terugrolplan",
        customerImpact: "Klantimpact",
        estimatedExecutionTime: "Geschatte uitvoeringstijd",
        estimatedCost: "Geschatte kosten",
        requiredPermissions: "Vereiste rechten",
        requiredApprovals: "Voorgestelde goedkeurders",
        openQuestions: "Openstaande vragen",
        confidence: "Vertrouwen",
      },
    },
    nextAction: {
      title: "Jouw actie",
      why: "Waarom dit nodig is:",
      nonePending: "Er staat niets open voor jou.",
      noneHint: "Zodra het AI-team je nodig heeft, verschijnt het hier.",
      openCta: "Open de beslissing",
      moreWaiting: "Nog {count} beslissing wacht ook",
      moreWaitingPlural: "Nog {count} beslissingen wachten ook",
    },
    reply: {
      title: "Klantantwoord",
      draftReady: "Conceptantwoord staat klaar — openen om te versturen",
      none: "Nog geen conceptantwoord — klik om op te stellen",
    },
    verification: {
      title: "Verificatie",
      sandbox: "Sandbox",
      patch: "Patch",
      qa: "QA-review",
      notRun: "Nog niet uitgevoerd",
      // Zie en.ts.
      onOlderVersion: "op v{version}",
      currentVersionNotRun: "Patch v{version} is zelf nog niet geverifieerd \u2014 alle pogingen staan onder Sandbox.",
      qaBlocking: "{n} blokkerend punt open",
      qaAccept: "Akkoord",
      qaReject: "Afgewezen",
      qaNeedsChanges: "Wijzigingen nodig",
      qaPending: "In behandeling",
      sandboxStatus: {
        pending: "In wachtrij",
        provisioning: "Voorbereiden",
        running: "Bezig",
        passed: "Geslaagd",
        failed: "Mislukt",
        timeout: "Time-out",
        error: "Fout",
        cancelled: "Geannuleerd",
      },
      patchStatus: {
        draft: "Concept",
        approved: "Goedgekeurd",
        rejected: "Afgewezen",
        applied: "Toegepast",
        changes_requested: "Wijzigingen gevraagd",
      },
    },
    tabs: {
      operation: "Operatie",
      patch: "Patch",
      sandbox: "Sandbox",
      analysis: "Analyse",
      details: "Details",
      history: "Geschiedenis",
      operationEmpty:
        "Nog geen AI-missie voor deze issue. Start er één met de actieknop bovenaan.",
      patchEmpty: "Nog geen patch gegenereerd.",
      sandboxIntro:
        "Elke keer dat FixControl een voorgestelde oplossing voor deze melding bouwde en testte \u2014 inclusief pogingen op eerdere patchversies.",
      sandboxEmpty: "Er is voor deze melding nog geen verificatie gedraaid.",
      historyEmpty: "Nog geen activiteit vastgelegd.",
      delegation: "AI-team",
      tasks: "Taken",
      approvals: "Goedkeuringen",
      patchFiles: "{n} bestanden",
      openPatch: "Open patch",
    },
  },

  workspaceDetail: {
    loading: "Werkruimte laden...",
    notFound: "Werkruimte niet gevonden",
    backToList: "Terug naar lijst",
    allWorkspaces: "← Alle werkruimtes",
    mobileTabs: {
      details: "Details",
      chat: "Chat",
      patch: "Patch",
    },
    meta: {
      issue: "Issue",
      project: "Project",
      branch: "Branch",
      created: "Aangemaakt",
      updated: "Bijgewerkt",
      runs: "runs",
      patch: "patch",
      patches: "patches",
      decision: "beslissing",
      decisions: "beslissingen",
    },
    project: {
      eyebrow: "Project",
      editProject: "Project bewerken",
      name: "Naam",
      codebases: "Codebases",
      mode: "Modus",
      guardrailActive: "{count} guardrail actief",
      guardrailsActive: "{count} guardrails actief",
      missingEyebrow: "Geen project",
      missingTitle: "Deze werksessie hangt aan geen enkel project",
      missingBody: "Het project draagt de geverifieerde testomgeving. Zonder project kan de sandbox de wijziging nergens starten en komt een patch ongeverifieerd bij de goedkeuring aan. Koppel een project en start de verificatie opnieuw.",
      missingNoIssue: "Koppelen kan pas zodra deze werksessie aan een melding hangt.",
      link: "Koppelen",
      linking: "Bezig met koppelen…",
      linkChoose: "Kies een project",
      linkFailed: "Koppelen is niet gelukt. Probeer het opnieuw.",
      change: "Wijzigen",
      changing: "Bezig met wijzigen…",
      changeFailed: "Wijzigen is niet gelukt. Probeer het opnieuw.",
      changeNoOthers: "Deze organisatie heeft geen ander project om de melding naartoe te verplaatsen.",
    },
    codeContext: {
      eyebrow: "Code-context",
      changeContext: "Context wijzigen",
      repository: "Repository",
      sshUrl: "SSH-URL",
      path: "Pad",
      branch: "Branch",
      apply: "Toepassen",
      cancel: "Annuleren",
      platform: "Platform",
      source: "Bron",
      sourceGithub: "GitHub",
      sourceSsh: "SSH",
      sourceLocal: "Lokaal",
      repo: "Repo",
      snapshot: "Snapshot",
      projectFile: "Projectbestand",
      projectRoot: "Projectroot",
      scm: "SCM",
      scmPcsoft: "PCSoft SCM",
      scmGit: "Git",
      scmLocal: "Lokaal",
      analyses: "Analyses",
      analysisFile: "{count} .wda-bestand",
      analysisFiles: "{count} .wda-bestanden",
      techDocs: "Tech docs",
      techDocsAvailable: "{count} beschikbaar",
      techDocsNone: "Geen",
      mode2: "Modus",
      loaded: "Geladen",
    },
    contextPreview: {
      eyebrow: "Agentcontext",
      collapse: "inklappen",
      show: "tonen",
      generic: "Generiek",
      memoryActive: "Geheugen actief",
      guardrail: "{count} guardrail",
      guardrails: "{count} guardrails",
      source: "{count} bron",
      sources: "{count} bronnen",
      project: "Project",
      analyses: "Analyses",
      techDocs: "Tech docs",
      procedures: "Procedures",
      guardrailsList: "Guardrails",
      contextSections: "Contextsecties ({count})",
      cached: "(cached)",
    },
    windev: {
      eyebrow: "WINDEV-context",
      analyses: "Analyses",
      technicalDocs: "Technische docs",
      procedures: "Procedures",
      chunks: "{count} chunks",
    },
    history: {
      eyebrow: "Historie",
      empty: "Nog geen activiteit",
    },
    timeline: {
      you: "Jij",
      agent: "Agent",
      codebaseAnalyst: "Codebase Analyst",
      developer: "Developer",
      system: "Systeem",
      patchVersion: "Patch v{version}",
      file: "bestand",
      files: "bestanden",
      noteBy: "door {name}",
      noteWith: "{name}: {note}",
    },
    chat: {
      title: "Chat",
      messages: "{count} berichten",
      compactingNow: "Bezig met comprimeren…",
      compact: "Comprimeren",
      compactedTooltip: "Handmatig gecomprimeerde thread — klik om de samenvatting te zien",
      summaryTooltip: "Toon de samenvatting van eerdere berichten",
      compactedLabel: "{count} berichten gecomprimeerd",
      summarizedLabel: "{count} eerdere berichten samengevat",
      compactDisabledShort: "Thread is te kort om te comprimeren",
      compactTooltip: "Comprimeer deze thread tot een samenvatting zodat nieuwe berichten met behoud van context vers beginnen",
      newChat: "+ Nieuw",
      newChatTooltip: "Nieuwe chat",
      deleteChat: "Verwijderen",
      deleteChatTooltip: "Deze chat verwijderen",
      switchChat: "Wissel chat",
      agentRunning: "Agent draait",
      stop: "Stoppen",
      stopping: "Stoppen…",
      stopTooltip: "Stop de actieve agent",
      compactedThread: "Gecomprimeerde thread",
      earlierInConversation: "Eerder in dit gesprek",
      summaryCount: "({count} berichten)",
      compactConfirm: "Deze thread comprimeren? De agent ziet alleen een samenvatting van de berichten tot nu toe, plus nieuwe berichten vanaf dit punt. Eerdere berichten blijven zichtbaar in de chat — alleen de view van de agent verandert.",
      compactFailed: "Comprimeren mislukt: {error}",
      deleteChatConfirm: "\"{title}\" verwijderen? Alle berichten in deze chat worden verwijderd.",
      deleteChatDefaultName: "deze chat",
      sendPlaceholder: "Beschrijf de wijziging…",
      sendBtn: "Verstuur",
      thinkingBtn: "Denkt na…",
      steerBtn: "Aanwijzing sturen",
      steerTooltip: "Stuur dit naar de lopende agent — hij pakt het op na zijn huidige stap",
      steerPlaceholder: "Geef de lopende agent een aanwijzing…",
      steerDelivered: "onderweg toegevoegd",
      steerQueued: "verstuurd als volgende beurt",
      emptyHint: "Stuur een bericht om codewijzigingen aan te vragen.",
      dropFilesHint: "Sleep bestanden hier om toe te voegen",
      uploadTooltip: "Bestanden uploaden (afbeeldingen, PDF, tekst)",
      micStart: "Spraakinvoer",
      micStop: "Stop opname",
      removeAttachment: "Verwijder",
      seeAttachment: "(zie bijlage)",
      moreActions: "Meer chatacties",
      sendHint: "Enter verstuurt · Shift+Enter voor een nieuwe regel",
      activityStep: "1 agentstap",
      activitySteps: "{count} agentstappen",
      activityCollapse: "Stappen inklappen",
      activityExpand: "Stappen tonen",
      fileTypeUnsupported: "Bestandstype niet ondersteund: {type}",
      fileTooLarge: "Bestand te groot (max 10 MB): {name}",
      codeBlock: "Codeblok · {count} regels",
      filterLabel: "Filter",
      filterAgent: "Agent",
      filterUser: "Jij",
      filterReview: "CTO/Review",
      filterActivity: "Activiteit",
      filterSystem: "Systeem",
      filterShow: "Toon {type}",
      filterHide: "Verberg {type}",
      filterEmpty: "Geen berichten in dit filter.",
      expand: "Uitvergroten",
      expandTooltip: "Chat schermvullend openen",
      collapse: "Sluiten",
      collapseTooltip: "Schermvullende weergave sluiten (Esc)",
    },
    patch: {
      title: "Patch",
      titleVersion: "Patch v{version}",
      cycleTooltip: "Cyclus-iteratie {n}",
      cycleLabel: "cyclus {n}",
      ctoApproved: "CTO: Goedgekeurd",
      ctoRejected: "CTO: Afgewezen",
      ctoChangesRequested: "CTO: Wijzigingen aangevraagd",
      file: "bestand",
      files: "bestanden",
      versions: {
        heading: "Versies in deze werksessie",
        tooltip: "Bekijk de versies van de patch in deze werksessie",
        projectHistory: "Alle patches van dit project →",
        viewPr: "Bekijk pull request #{number}",
        status: {
          draft: "Concept",
          approved: "Goedgekeurd",
          rejected: "Afgewezen",
          applied: "Toegepast",
          changes_requested: "Wijzigingen gevraagd",
        },
      },
      historyLink: "Historie",
      openPatch: "Patch openen",
      openPatchTooltip: "Open deze patch op een eigen pagina — de volledige diff, de verificatie en de beslissing",
      historyTooltip: "Bekijk alle patches in dit project",
      selectFile: "Kies een bestand om de diff te bekijken",
      expandDiffTooltip: "Diff schermvullend openen",
      collapseDiff: "Sluiten",
      collapseDiffTooltip: "Schermvullende weergave sluiten (Esc)",
      revisionInProgress: "Revisie bezig",
      revisionInProgressShort: "Revisie bezig…",
      revisionDescription: "Een nieuwe patch wordt gegenereerd op basis van de CTO-feedback. De vorige patch is vergrendeld tot de nieuwe versie klaar is.",
      revisionRunRef: "run {id}",
      ctoChangesRequestedLabel: "CTO: wijzigingen aangevraagd",
      autoRetryLabel: "auto-retry {current}/{max}",
      devAutoRevising: "Dev voert automatisch een revisie uit. Volg de chat voor de volgende poging.",
      autoReviseCapReached: "Auto-revise-limiet bereikt. Handmatige revisie vereist.",
      reRunDevWithFeedback: "Developer opnieuw met feedback",
      startingRetry: "Retry starten…",
      finalApplied: "✓ Toegepast",
      finalRejected: "✕ Afgewezen",
      finalAppliedPr: "Pull request geopend op GitHub.",
      finalAppliedNoPr: "Bestanden geschreven naar de codebase.",
      finalRejectedNote: "Deze patch wordt niet uitgerold.",
      viewPr: "Bekijk PR",
      prStatus: {
        open: "PR open",
        merged: "Gemerged",
        closed: "PR gesloten",
      },
      ciState: {
        pending: "CI draait",
        success: "CI geslaagd",
        failure: "CI gefaald",
        neutral: "CI neutraal",
      },
      safetyWarning: "Deze patch raakt een gevoelig gebied. Valideer met een dry run voordat je toepast.",
      followUpsCreated: "Opvolg-acties aangemaakt",
      followUpOverdue: "te laat",
      followUpDue: "deadline {when}",
      followUpUnassigned: "NIET TOEGEWEZEN",
      decisions: "Beslissingen",
      noPatchYet: "Nog geen patch — stuur een instructie om te starten.",
      previousRejected: "Vorige patch afgewezen",
      previousRejectedHint: "Gebruik de CTO-feedback om de volgende patch te schrijven.",
      retryInProgressShort: "Revisie al bezig",
      fixRejectedPatch: "Afgewezen patch herstellen",
      startingShort: "Starten…",
      viewPatchInHistory: "Bekijk patch v{version}",
      noActivePatchApplied: "Geen actieve patch — de vorige patch is toegepast.",
      sendNewInstruction: "Stuur een nieuwe instructie om de volgende patch te schrijven.",
    },
    review: {
      recommended: "Aanbevolen",
      overrideApprove: "Toch goedkeuren",
      overrideApproveHint: "Goedkeuren ondanks de openstaande waarschuwingen",
      missionGate: {
        title: "Wacht op missie-goedkeuring",
        body: "Voor deze issue staat een goedkeuring open in de missie. Beslis daar — de patch wordt automatisch mee bijgewerkt.",
        cta: "Ga naar goedkeuring",
      },
      fallback: {
        title: "Rule-based review (geen LLM)",
        body: "Er was geen LLM-provider actief, dus dit is een geautomatiseerde rule-based controle — geen inhoudelijke code-review. Stel een model in via Instellingen → AI voor een echte review.",
      },
      confidence: {
        high: "hoog vertrouwen",
        medium: "gemiddeld vertrouwen",
        low: "laag vertrouwen",
      },
      status: {
        inProgress: "Agent bezig…",
        revising: "Revisie bezig",
        awaitingReview: "Revisie voltooid — opnieuw te beoordelen",
        changesRequested: "Wijzigingen aangevraagd",
        verificationFailed: "Verificatie mislukt",
        readyToApprove: "Klaar voor goedkeuring",
        approvalDiscouraged: "Goedkeuring afgeraden",
        approved: "Goedgekeurd — wacht op toepassen",
        approvedBlocked: "Goedgekeurd — toepassen geblokkeerd",
        approvedBlockedRunning: "Goedgekeurd — verificatie loopt",
        verifyingAfterReview: "Review positief — verificatie loopt",
        reviewOkGateBlocked: "Review positief — sandbox nog niet groen",
        approvedBlockedStale: "Goedgekeurd — verificatie verouderd",
        approvedBlockedMissing: "Goedgekeurd — nog niet geverifieerd",
        approvedBlockedEnvironment: "Goedgekeurd — omgeving gewijzigd",
        approvedBlockedRuntime: "Goedgekeurd — start van applicatie onbewezen",
        applied: "Toegepast",
        rejected: "Afgewezen",
      },
      conclusion: {
        inProgress: "De agent genereert en beoordeelt deze wijziging nog — nog geen oordeel.",
        revising: "Er wordt een nieuwe versie van deze patch gegenereerd.",
        awaitingReview: "Deze revisie is nog niet geverifieerd. Voer een verificatie uit voordat je goedkeurt.",
        changesRequested: "Niet alle gevraagde wijzigingen zijn verwerkt — bekijk de openstaande punten.",
        changesCapped: "Auto-revisie-limiet bereikt. Geef nieuwe feedback of voer de revisie handmatig opnieuw uit.",
        verificationFailed: "De sandbox-validatie is mislukt. Goedkeuring wordt afgeraden tot dit is opgelost.",
        safeToApprove: "Geen openstaande review-opmerkingen of structurele wijzigingen buiten de gevraagde fixes.",
        discouraged: "Deze wijziging vereist eerst verificatie of CTO-akkoord. Goedkeuring wordt afgeraden.",
        approved: "De patch is goedgekeurd en klaar om te worden toegepast.",
        approvedBlocked: "De goedkeuring staat, maar toepassen kan nog niet: de sandbox-validatie is niet geslaagd voor deze versie van de patch. Draai de verificatie opnieuw.",
        approvedBlockedRunning: "De goedkeuring staat. Er draait op dit moment een verificatie voor deze versie van de patch — wacht tot die klaar is; toepassen kan zodra hij slaagt.",
        verifyingAfterReview: "De AI-review vond geen problemen. De sandbox test deze versie nu; goedkeuren heeft pas zin als die slaagt. Tot die tijd hoef je niets te doen.",
        approvedBlockedStale: "De goedkeuring staat, maar de patch is gewijzigd na de laatste verificatie. Draai de verificatie opnieuw voor deze versie.",
        approvedBlockedMissing: "De goedkeuring staat, maar deze versie van de patch is nog niet geverifieerd. Draai een verificatie voordat je toepast.",
        approvedBlockedEnvironment: "De goedkeuring staat en de verificatie is geslaagd — maar in een omgeving die dit project niet meer gebruikt. Draai de verificatie opnieuw in de huidige omgeving.",
        approvedBlockedRuntime: "De goedkeuring staat en de tests slaagden — maar niets bewees dat de applicatie zelf nog start. Draai de verificatie opnieuw, zodat het starttarget wordt uitgevoerd.",
        applied: "De patch is toegepast.",
        rejected: "De patch is afgewezen en wordt niet uitgerold.",
      },
      viewSandboxRun: "Bekijk sandbox-run →",
      viewStartLog: "Bekijk het startlog →",
      // Waarom toepassen geweigerd wordt, per gate-code — de eigen `message`
      // van de gate is een Engelse logzin en komt nooit in het paneel.
      gateBlock: {
        sandbox_required: "Deze versie van de patch is nog niet geverifieerd en kan dus nog niet worden toegepast.",
        sandbox_stale: "De patch is gewijzigd na de laatste verificatie; het groene resultaat gaat over andere code.",
        sandbox_in_progress: "Er loopt nog een verificatie voor deze versie. Toepassen kan zodra die slaagt.",
        sandbox_not_passed: "De verificatie van deze versie is niet geslaagd. Bekijk de run voordat je toepast.",
        sandbox_environment_required: "Deze workspace vereist bewijs uit een volledige omgeving met meerdere services, en deze run leverde dat niet.",
        sandbox_remote_required: "Dit project vereist validatie in je eigen omgeving, en voor deze versie is zo'n run nog niet geslaagd.",
        environment_contract_stale: "De geverifieerde omgeving van het project is gewijzigd na de verificatie; het resultaat gaat over een andere omgeving.",
        runtime_verification_required: "De tests slaagden, maar niets bewees dat de applicatie zelf nog start.",
        unknown: "De sandbox-poort weigert deze versie van de patch toe te passen. Open de run voor de details.",
      },
      // Waarom de start van de applicatie onbewezen is — bepaald door
      // lib/patch-review/start-proof.ts. De poort blokkeert in elk geval
      // hetzelfde; alleen de uitleg en de aangeboden acties verschillen.
      startProof: {
        lane: {
          aws_fargate: "AWS Fargate",
          customer_runner: "de runner in je eigen omgeving",
          unknown: "de omgeving die hem draaide",
        },
        laneCannotStart: {
          status: "Goedgekeurd — deze testomgeving kan de applicatie niet starten",
          block: "De tests slaagden op {lane}, maar die testomgeving start de applicatie niet, dus het starttarget is nooit uitgevoerd.",
          conclusion: "Opnieuw verifiëren komt weer op {lane} uit en bewijst niets nieuws. Bewijs komt van een run die de applicatie start en antwoord krijgt van het starttarget — dat kan deze testomgeving nog niet. Tot dat wel kan, kan een projectbeheerder de startcontrole voor dit project laten vervallen op de pagina Omgevingen; dat wordt vastgelegd met naam en datum.",
        },
        startFailed: {
          status: "Goedgekeurd — de applicatie startte niet",
          block: "De tests slaagden, maar de applicatie startte niet: {reason}.",
          conclusion: "Een nieuwe verificatie faalt op dezelfde manier. Lees het startlog en wijs de patch af, zodat een revisie het kan oplossen.",
          conclusionEnvironment: "Dit gaat over hoe de omgeving de applicatie start, niet over de patch. Corrigeer het starttarget op de pagina Omgevingen van het project en verifieer daarna opnieuw.",
        },
        failedReason: {
          launch_failed: "het startcommando zelf faalde",
          process_exited: "hij startte en stopte daarna weer",
          readiness_error: "hij draait, maar antwoordt met een fout (HTTP {status})",
          readiness_timeout: "hij antwoordde niet binnen {seconds}s",
          start_refused: "het starttarget van de omgeving kan hier niet worden uitgevoerd",
        },
        noStartTarget: {
          status: "Goedgekeurd — geen starttarget ingericht",
          block: "De omgeving van het project beschrijft niet hoe de applicatie start, dus niets kon bewijzen dat hij start.",
          conclusion: "Richt op de pagina Omgevingen van het project in hoe de applicatie start; de volgende verificatie start hem dan.",
        },
        proofMissing: {
          status: "Goedgekeurd — start van applicatie onbewezen",
          block: {
            legacy: "De tests slaagden, maar deze run is ouder dan de startverificatie en bevat dus geen bewijs dat de applicatie start.",
            not_attempted: "De tests slaagden, maar de start van de applicatie is in deze run nooit bereikt.",
            infrastructure: "De tests slaagden, maar de sandbox kon de startcontrole niet uitvoeren; dat zegt niets over de patch.",
            stale_proof: "De run bewees een start die de omgeving van het project niet meer beschrijft.",
            lane_now_starts: "Deze run startte de applicatie niet, maar de testomgeving kan dat inmiddels wel. Een nieuwe verificatie kan het bewijzen.",
            unknown: "De tests slaagden, maar niets bewees dat de applicatie zelf nog start.",
          },
          conclusion: "Draai de verificatie opnieuw, zodat de applicatie gestart wordt en het starttarget antwoordt.",
        },
      },
      conclusionLabel: "AI-conclusie",
      conclusionDetail: {
        allResolved: "Alle gevraagde wijzigingen zijn verwerkt.",
        openItems: "{n} reviewpunt(en) nog openstaand.",
        openVerification: "{n} verificatiestap(pen) is niet uitgevoerd.",
        partialResolved: "{resolved}/{total} punten opgelost.",
        verifyOk: "Geen regressies gedetecteerd — verificatie geslaagd.",
        verifyFailed: "Verificatie mislukt ({n} controle(s)).",
        sandboxVerified: "De sandbox heeft deze versie geverifieerd.",
        sandboxBlocked: "De sandbox heeft deze versie niet vrijgegeven — bekijk de run.",
        notVerified: "Nog niet geverifieerd.",
      },
      recommendedAction: {
        approve: "Goedkeuren",
        apply: "Toepassen",
        newFeedback: "Nieuwe feedback geven",
        runVerification: "Verificatie uitvoeren",
        openEnvironments: "Omgevingen van het project openen",
        rerunRevision: "Revisie opnieuw uitvoeren",
        wait: "Wachten op de revisie",
      },
      resolution: {
        heading: "Gevraagde wijzigingen",
        // Dezelfde lijst, maar QA accepteerde de code: dit zijn stappen die nog
        // uitgevoerd moeten worden, geen verzoeken die de patch heeft verwerkt.
        verificationHeading: "Nog te verifiëren",
        resolved: "opgelost",
        partial: "gedeeltelijk",
        unresolved: "niet opgelost",
        unverified: "niet uitgevoerd",
        count: "{resolved}/{total} opgelost",
        verifiedCount: "{resolved}/{total} geverifieerd",
        unverifiedNote: "{n} van deze stappen is niet uitgevoerd — goedkeuren voert ze niet alsnog uit.",
        unverifiedAfterRunNote: "{n} van deze stappen is door geen enkele stap van de sandbox-run uitgevoerd: het zijn controles voor een persoon of een UI-test. Goedkeuren of opnieuw verifiëren voert ze niet alsnog uit.",
        verifiedByStep: "geverifieerd door sandbox",
        proofInstall: "Sandbox-stap \"{step}\" deed dit.",
        proofTests: "Sandbox-stap \"{step}\" draaide {n} tests, alle geslaagd.",
        proofLink: "Bekijk run →",
        proofUi: "Browsertest \"{test}\" in sandbox-stap \"{step}\" is geslaagd.",
        uiTestsUnsupported: "UI-tests konden in deze testomgeving niet draaien: {reason}",
        uiTestsNotRun: "De UI-tests die deze patch meelevert zijn niet uitgevoerd: {reason}",
        coarseNote: "Per-punt-status nog niet beschikbaar — handmatig verifiëren tegen de diff.",
      },
      verification: {
        heading: "Verificatie",
        notRun: "Nog niet geverifieerd — voer een verificatie uit voor de zekerheid.",
        summary: "{ok}/{total} ok",
        failedCount: "{n} mislukt",
        passed: "geslaagd",
        failed: "mislukt",
        skipped: "overgeslagen",
      },
      testEnvironment: {
        heading: "Testomgeving",
        manage: "Alle testomgevingen",
      },
      diffSection: "Diff & bestanden",
      decisionsSection: "Beslissingen",
    },
    gate: {
      heading: "Goedkeuringsbeoordeling",
      status: "Wacht op goedkeuring",
      purpose: {
        plan: "Keur het missieplan van de CTO goed voordat het werk start.",
        risky_patch: "De CTO-gate markeerde deze patch als risicovol — een menselijke sign-off is vereist.",
        deployment: "Keur het toepassen / uitrollen van de gereviewde patch goed.",
        retry: "Een taak is mislukt — keur een nieuwe poging goed voordat de missie verdergaat.",
        security_override: "Beveiligingsrisico — een handmatige sign-off is vereist om door te gaan.",
        ui_change_approval: "Een UI-wijziging die de interface herstructureert — keur goed vóór het toepassen.",
        ux_regression_override: "Een UX-regressie die de architect niet zelf kon oplossen — keur goed om door te gaan.",
      },
      riskLabel: "Risico",
      risk: { low: "Laag risico", medium: "Gemiddeld risico", high: "Hoog risico", critical: "Kritiek risico" },
      riskReason: {
        planOnly: "Alleen een plan — er is nog niets gewijzigd.",
        ctoFlagged: "De CTO-gate markeerde deze patch als risicovol.",
        deploy: "Wijzigt de live-omgeving bij het toepassen.",
        taskFailed: "Een eerdere taak is mislukt.",
        securityFlags: "Raakt beveiligingsgevoelige, risico-gemarkeerde gebieden.",
        uiChange: "Wijzigt de interface (sectie / primaire actie / navigatie).",
        uxRegression: "Een UX-regressie kon niet automatisch worden opgelost.",
      },
      checks: {
        heading: "Verificatie & voortgang",
        qa: "QA-verificatie",
        security: "Beveiligingsreview",
        progress: "Taken voltooid",
        blockers: "Geblokkeerde taken",
        ci_status: "CI op deze deploy",
        validation: "Sandbox-validatie",
        app_start: "Start van de applicatie",
        ok: "geslaagd",
        warn: "aandacht nodig",
        /** Een verleende ontheffing op de startcontrole. Een eigen woord,
         *  zodat wie de statuskolom scant het nooit voor geslaagd aanziet. */
        waived: "ontheven",
        fail: "mislukt",
        pending: "nog bezig",
      },
      pipelineRun: {
        heading: "Pipeline-run",
        phase: "Fase",
        target: "Doel",
        artifact: "Artefact",
        commit: "Commit",
        actor: "Actor",
        run: "Run-id",
        resumeCapability: {
          direct: "Hervatten: direct",
          reverse_webhook: "Hervatten: in-cluster",
          git_promote: "Hervatten: git-commit",
          agent: "Clusteragent (uitgaand)",
          // Generieke CI/CD-hosts: er wordt niets "hervat" — een deployment die
          // de host vasthoudt voor zijn verplichte reviewers wordt vrijgegeven.
          environment_review: "Vrijgeven: GitHub-omgeving",
          // GitLab: één label voor elke lane (een geblokkeerde deployment —
          // vastgehouden door een goedkeuringsregel of door zijn eigen
          // handmatige job — én een job-lane-pauze). Er staat bewust geen
          // "goedkeuring" in: deployment-goedkeuringsregels zijn Premium/
          // Ultimate, en op elk ander plan wordt dezelfde gate vrijgegeven door
          // de vastgehouden job te draaien. Zie ResumeCapabilityProfile.
          gitlab_release: "Vrijgeven: GitLab",
          // Jenkins: de pauze is een `input`-stap, en die wordt vrijgegeven —
          // goedkeuren zet hem door, afwijzen breekt hem af.
          jenkins_input: "Vrijgeven: Jenkins-inputstap",
          // Bewust hostneutraal: alleen de Argo-lanes hervatten een "rollout",
          // en de chip wordt ook voor GitHub, GitLab en Jenkins getoond.
          tooltip: "FixControl kan het oordeel van deze gate doorgeven aan de host die de pauze vasthoudt.",
        },
      },
      conclusionLabel: "AI-conclusie",
      conclusion: {
        planReady: "Het plan is opgesteld en klaar voor goedkeuring.",
        readyApprove: "Geen openstaande blokkades gedetecteerd. Lijkt veilig om goed te keuren.",
        readyDeploy: "De patch is gereviewd en geverifieerd. Klaar om uit te rollen.",
        manualReview: "Dit raakt risicogebieden. Handmatige controle aanbevolen vóór goedkeuring.",
        ctoChangesRequested: "De CTO vroeg de hierboven genoemde wijzigingen. Goedkeuren laat de agent ze verwerken in een nieuwe versie; afwijzen verwerpt de patch.",
        ctoChangesAccept: "De CTO vroeg de hierboven genoemde wijzigingen. Goedkeuren accepteert deze versie tóch (klaar om toe te passen); afwijzen verwerpt de patch.",
        taskFailed: "Een taak is mislukt. Beoordeel de fout voordat je opnieuw probeert.",
        verifyFirst: "De verificatie is nog niet afgerond. Beoordeel zorgvuldig.",
      },
      recommendedLabel: "Aanbevolen actie",
      recommended: {
        approve: "Klaar voor goedkeuring",
        reviewCarefully: "Beoordeel zorgvuldig vóór goedkeuring",
        approveRevises: "Goedkeuren → de agent verwerkt de gevraagde wijzigingen in een nieuwe versie",
        approveAccepts: "Goedkeuren → accepteer deze versie tóch (klaar om toe te passen)",
        reviewError: "Beoordeel de fout, keur daarna een nieuwe poging goed",
        wait: "Wacht tot de lopende taken klaar zijn",
      },
      cta: {
        approve: "Goedkeuren",
        approvePlan: "Plan goedkeuren",
        approveDeploy: "Uitrol goedkeuren",
        approveRetry: "Opnieuw proberen",
        approveOverride: "Toch goedkeuren",
        approveRevise: "Goedkeuren & reviseren",
        approveAccept: "Goedkeuren & toepasbaar maken",
        // Tier-3: goedkeuren van een deployment-gate met een gepauzeerde
        // rollout en een gekoppelde Argo-capability roept terug om de
        // rollout daadwerkelijk te hervatten. Het werkwoord volgt het
        // neveneffect.
        resumeRollout: "Rollout hervatten",
        // Niet-Argo-hosts: goedkeuren GEEFT een deployment vrij die de host
        // vasthoudt voor zijn verplichte reviewers. "Rollout hervatten" is
        // Argo-jargon en zou de klik verkeerd omschrijven.
        releaseDeployment: "Goedkeuren & uitrol vrijgeven",
        requestChanges: "Wijzigingen vragen",
        reject: "Afwijzen",
        rejectAbort: "Afwijzen — missie stoppen",
        rejectDrop: "Afwijzen — patch verwerpen",
      },
      resume: {
        promoted: "Rollout hervat",
        failed: "Hervatten van rollout mislukt: {reason}",
        skipped: "Goedkeuring vastgelegd; rollout niet hervat ({reason}).",
      },
      changesNoteRequired: "Voeg een opmerking toe die de gevraagde wijzigingen beschrijft.",
      brief: {
        decisionRequired: "Beslissing vereist",
        recommendationLabel: "FixControl adviseert",
        ifApproveLabel: "Bij goedkeuren",
        ifRejectLabel: "Bij afwijzen",
        insufficientHeading: "Onvoldoende bewijs voor een advies",
        insufficientLeadIn: "Inspecteer het bewijs voordat je beslist.",
        priorIncidentsLabel: "Vergelijkbare eerdere incidenten",
        headline: {
          plan: "Keur het missieplan goed voordat het werk start.",
          risky_patch: "Teken af voor een door de CTO gemarkeerde wijziging.",
          deployment: "Autoriseer een deployment.",
          retry: "Autoriseer een retry na een mislukte taak.",
          security_override: "Heffel de security-gate op.",
          ui_change_approval: "Keur een UI-wijziging goed.",
          ux_regression_override: "Heffel een UX-regressie op.",
          commit: "Autoriseer het landen van een commit.",
          pr_merge: "Autoriseer het mergen van de pull request.",
          pipeline: {
            deployment: "Autoriseer een {changeKind} naar {environment}.",
            pr_merge: "Autoriseer het mergen van een {changeKind} naar {environment}.",
            commit: "Autoriseer een commit met {changeKind} naar {environment}.",
            retry: "Autoriseer een retry van een {changeKind} op {environment}.",
            plan: "Keur het missieplan goed (gericht op {environment}).",
            risky_patch: "Teken af voor een door de CTO gemarkeerde wijziging gericht op {environment}.",
            security_override: "Heffel de security-gate op {environment} op.",
            ui_change_approval: "Keur een UI-wijziging op {environment} goed.",
            ux_regression_override: "Heffel een UX-regressie op {environment} op.",
          },
        },
        whatChanges: {
          blast: "Raakt {fileCount} bestand(en) verdeeld over {blastTagKeys}.",
          files: "Raakt {fileCount} bestand(en) in de codebase.",
          pipeline: "Gericht op {target} op {environment} ({changeKind}).",
          plan: "Planfase — nog geen wijzigingen; de voorgestelde stappen staan hierboven.",
          unknown: "Er is nog geen structureel bewijs beschikbaar voor deze gate.",
        },
        governance: {
          posture: "{statement}",
          planOnly: "Een plan-gate — governance beoordeelt de stappen voordat er iets verandert.",
          ctoFlagged: "De CTO-gate markeerde deze wijziging als risicovol — een medewerker moet deze eerst goedkeuren.",
          deploy: "Governance routeert wijzigingen aan de live omgeving via handmatige goedkeuring.",
          taskFailed: "Governance pauzeert de missie na een mislukte taak zodat een mens de retry autoriseert.",
          securityFlags: "Raakt security-gevoelige gebieden; governance vereist een handmatige override.",
          uiChange: "UI-wijzigingen die primaire acties of navigatie raken gaan naar UX-review.",
          uxRegression: "Een UX-regressie kon niet automatisch opgelost worden — governance vraagt een override.",
        },
        autonomy: {
          posture: "Autonomie ingetrokken ({severity}): {statement}",
        },
        escalation: {
          posture: "Approver-keten uitgebreid — voegt {requiredApprovers} toe (blast radius: {appliesAt}).",
        },
        validation: {
          passed: "Validatie geslaagd ({ok}/{total} checks); {taskDone}/{taskTotal} missietaken klaar.",
          pending: "{pending} check(s) lopen nog; {taskDone}/{taskTotal} missietaken klaar.",
          warned: "{warned} check(s) gemarkeerd ter attentie naast {ok} geslaagd; {taskDone}/{taskTotal} missietaken klaar.",
          failed: "{failed} van {total} check(s) faalden; {taskDone}/{taskTotal} missietaken klaar.",
        },
        ifApprove: {
          plan: "De uitvoering start onder het goedgekeurde plan; agents beginnen direct.",
          risky_patch: "De agent reviseert de patch om de genoemde feedback te verwerken.",
          risky_patch_accept: "De patch wordt as-is geaccepteerd en gaat door naar toepassen.",
          deployment: "De deployment gaat door en de live omgeving wijzigt.",
          retry: "De mislukte taak wordt opnieuw uitgevoerd en de missie gaat verder.",
          security_override: "De uitvoering gaat door ondanks het gemarkeerde security-risico.",
          ui_change_approval: "De UI-wijziging wordt toegepast en uitgerold naar operators.",
          ux_regression_override: "De uitvoering gaat door ondanks de regressie die de UX-agent niet kon oplossen.",
          commit: "De commit landt op de repository-branch.",
          pr_merge: "De pull request wordt gemerged en downstream CI/deploy gaan verder.",
          pipeline: {
            deployment: "De {environment}-deployment naar {target} gaat door.",
            pr_merge: "De pull request merget naar {target} op {environment}.",
            commit: "De commit landt op {target} ({environment}).",
            retry: "De mislukte pipelinestap wordt opnieuw uitgevoerd op {target} ({environment}).",
            plan: "De uitvoering start op {environment} onder het goedgekeurde plan.",
            risky_patch: "De agent reviseert de patch (gericht op {environment}).",
            security_override: "De uitvoering gaat door op {environment} ondanks het gemarkeerde risico.",
            ui_change_approval: "De UI-wijziging wordt toegepast op {environment}.",
            ux_regression_override: "De uitvoering gaat door op {environment} ondanks de regressie.",
          },
        },
        ifReject: {
          plan: "De missie wordt afgebroken; er start geen werk.",
          risky_patch: "De patch wordt verworpen en de missie stopt.",
          deployment: "De deployment wordt geblokkeerd en de missie stopt.",
          retry: "De missie wordt afgebroken zonder de mislukte taak opnieuw uit te voeren.",
          security_override: "De uitvoering blijft geblokkeerd op het gemarkeerde security-risico.",
          ui_change_approval: "De UI-wijziging wordt weggegooid.",
          ux_regression_override: "De uitvoering blijft geblokkeerd op de regressie.",
          commit: "De commit wordt verworpen en de missie stopt.",
          pr_merge: "De pull request wordt niet gemerged; de onderliggende patch kan worden gereviseerd.",
        },
        recommendation: {
          approve: {
            approve: "Validatie is geslaagd en het risico is afgebakend.",
            approveRevises: "Goedkeuren stuurt de gevraagde reviewwijzigingen terug zodat de agent de patch reviseert.",
            approveAccepts: "Accepteer de patch as-is; de reviewopmerkingen zijn verwerkt.",
          },
          hold: {
            insufficient: "Operationele posture is onvoldoende — beslis handmatig.",
            autonomyBlocked: "Een kritieke autonomie-beperking is actief ({constraintKind} op {surface}) — beoordeel voordat je goedkeurt.",
            reviewCarefully: "Risico-gevoelig gebied — beoordeel het bewijs voordat je beslist.",
            reviewError: "Een check faalde — inspecteer de fout voordat je goedkeurt.",
            wait: "Lopende taken zijn nog niet klaar.",
            default: "Onvoldoende vertrouwen om goedkeuring te adviseren.",
          },
        },
        insufficient: {
          posture: "FixControl kon niet genoeg operationele signalen verzamelen voor een advies.",
          noEvidence: "Er is nog geen patch, plan of pipeline-run aan deze gate gekoppeld.",
          noChain: "De approver-keten is niet opgelost — verantwoordelijkheid is onduidelijk.",
        },
        insufficientHint: {
          posture: "Voer een nieuwe meting uit via Kennisbank → Codebase-gezondheid, of beslis handmatig als je het gat accepteert.",
          noEvidence: "Wacht tot de agent een patch of plan produceert, of bekijk de tijdlijn van de run.",
          noChain: "Configureer een approver-keten voor dit gate-type via Instellingen → Governance → Goedkeuringen.",
        },
        modeExecutive: "Executive",
        modeTechnical: "Technisch",
        verdict: {
          approve: "Goedkeuren",
          hold: "Beoordelen",
          reject: "Afwijzen",
        },
        chipValidationPassed: "Validatie geslaagd",
        chipValidationWarn: "Aandacht nodig",
        chipValidationFailed: "Validatie gefaald",
        chipValidationPending: "Validatie loopt",
        chipScopeFiles: "{fileCount} bestand(en) aangepast",
        evidenceFiles: "Geraakte systemen & bestanden",
        evidencePipeline: "Pipeline & CI",
        evidenceVerification: "Verificatie & voortgang",
        evidenceCi: "Je eigen CI, op de geleverde commit",
        /**
         * Wat de pipeline van de KLANT zei over de commit waarmee deze patch
         * geleverd is. Een apart antwoord naast de verificatie van FixControl
         * zelf, dat daarmee mag verschillen, dus het verdwijnt nooit in
         * "Validatie". Het ruwe uitkomsttoken wordt nooit getoond.
         */
        ci: {
          outcome: {
            succeeded: "Je CI slaagde op deze commit.",
            failed: "Je CI faalde op deze commit.",
            running: "Je CI draaide nog toen dit werd uitgelezen.",
            cancelled: "De CI-run is geannuleerd voordat hij klaar was.",
            unknown: "Je CI heeft nog geen uitslag gemeld voor deze commit.",
          },
          commit: "Commit",
          commitUnknown: "de run noemde geen commit",
          captured: "Uitgelezen op",
          tests: "Tests",
          testsValue: "{failed} van {total} gefaald",
          failedJobs: "Toon de {count} job(s) die faalden",
          moreFailedJobs: "en nog {count}",
          truncated: "De uitlezing liep tegen haar limiet aan, dus dit is een deel van wat je CI meldde — niet alles.",
          open: "Open de run in je CI",
          note: "Eén keer uitgelezen en bevroren bij deze beslissing, zodat het dossier zegt wat je CI zéi op het moment van beslissen — niet wat het nu zegt.",
        },
        inspect: "Inspecteren",
        productionBannerDeploy: "Productie-wijziging — voordat deze deployment doorgaat is menselijke goedkeuring vereist.",
        productionBannerSurface: "Deze wijziging raakt een gereguleerd domein (betalingen / auth / database / externe integratie). Voor deze wijziging is menselijke goedkeuring vereist.",
        stages: {
          progress: "Fase {step} van {total}",
          waitingOn: "Wacht op: {labels}",
          done: "goedgekeurd",
          active: "nu",
          waiting: "hierna",
          skipped: "niet nodig",
          rejected: "afgewezen",
          overdue: "te laat",
          recorded: "Je goedkeuring van fase {step} van {total} ({label}) is vastgelegd. De goedkeuring blijft open tot elke fase heeft goedgekeurd.",
          recordedNext: "Hierna: {labels}.",
          note: "Deze goedkeuring heeft fases. Elke fase wordt door eigen mensen goedgekeurd, één persoon per fase; iedereen die mag beslissen kan nee zeggen.",
        },
        postDecision: {
          approve: "Beslissing vastgelegd — goedkeuring verzonden. De missie gaat door naar de volgende stap.",
          reject: "Beslissing vastgelegd — gate afgewezen. De missie is gestopt.",
          request_changes: "Beslissing vastgelegd — wijzigingen gevraagd. De agent reviseert en biedt opnieuw aan.",
        },
        queueHeader: "Beslissingswachtrij · {count} openstaand",
        queueOfType: "#{current} van {total}",
        queueNextCta: "Spring naar de volgende gate van dit type",
        routesTo: "Voorgestelde route:",
        routesToStaged: "Goedkeuringsfases:",
        routesToAnyDecider: "elke beheerder of manager beslist",
        chainQuorum: "{count} goedkeurders vereist",
        postureExpansionTag: "posture",
        postureExpansionNote: "{count} goedkeurder toegevoegd door posture",
        postureExpansionTooltip: "Toegevoegd door posture-interpretatie — vanwege: {derivedFrom}",
        postureExpansionTooltipUnknownSource: "operationeel bewijs",
        refusalAction: {
          openPosture: "Open Codebase-postuur",
          openOperations: "Open Operations",
        },
        // Weigering van de patch-goedkeuringspoort — zie en.ts.
        validationRefusal: {
          sandbox_required: "Deze patch is nog niet geverifieerd. Start een verificatierun en keur daarna goed.",
          sandbox_stale: "De patch is gewijzigd na de laatste verificatie, dus het groene resultaat gaat over andere code. Draai de verificatie opnieuw.",
          sandbox_in_progress: "De verificatie van deze patch loopt nog. Die moet klaar zijn voordat de patch goedgekeurd kan worden.",
          sandbox_not_passed: "De verificatie van deze patch is niet geslaagd. Bekijk de run voordat je goedkeurt.",
          sandbox_environment_required: "Deze werkruimte vereist bewijs uit een volledige omgeving met meerdere services, en deze run leverde dat niet op. Draai de verificatie opnieuw met de omgeving aan.",
          sandbox_remote_required: "Dit project vereist validatie in je eigen omgeving, en zo'n run is niet geslaagd voor deze versie van de patch. Een run op de infrastructuur van FixControl telt daar niet voor.",
          environment_contract_stale: "De geverifieerde omgeving van dit project is gewijzigd nadat de verificatie slaagde, dus het resultaat gaat over een andere omgeving. Draai hem opnieuw.",
          runtime_verification_required: "De tests slaagden, maar niets bewees dat de applicatie zelf nog start in deze omgeving. De patchpagina legt uit waarom, en wat het wel zou bewijzen.",
        },
        /**
         * Elke reden waarop het goedkeuringspunt een beslissing kan weigeren,
         * als zin. Elke zegt WAT het tegenhield en of er iets te doen valt —
         * eerder kwam hier de Engelse tekst van de server, geschreven voor een
         * logregel. Bewaakt door de gesloten lijst in
         * `lib/approval-refusal-copy.ts`.
         */
        refusal: {
          not_found: "Deze goedkeuring bestaat niet meer. Waarschijnlijk is ze ingetrokken of is de missie gearchiveerd terwijl deze pagina openstond.",
          already_decided: "Iemand heeft deze al beslist. Herlaad om te zien wie, en wat die koos.",
          mission_terminal: "Deze missie is al afgerond, dus haar poorten kunnen niet meer worden beantwoord.",
          proposal_changed: "Wat er werd voorgesteld is veranderd nadat deze pagina laadde. Herlaad en lees het huidige voorstel voordat je beslist.",
          unsupported_decision: "Wijzigingen vragen kan bij dit soort poort niet — die kan alleen worden goedgekeurd of afgewezen.",
          note_required: "Zeg wat er moet veranderen. Wijzigingen vragen zonder toelichting laat de agent niets om mee te werken.",
          ci_unknown: "Je CI heeft nog niets gemeld over de geleverde commit, dus er valt nog niets tegen af te wegen. Geef het even, of bekijk de run.",
          ci_not_green: "Je eigen CI faalde op de geleverde commit. Goedkeuren zou een wijziging uitbrengen die je pipeline afwees.",
          posture_insufficient: "Deze codebase is niet diep genoeg beoordeeld voor een wijziging met dit risico. Voer eerst de beoordeling uit.",
          human_decision_required: "Deze moet door een mens worden beslist, in dit scherm — automatisch beantwoorden kan niet.",
          execution_missing: "Het werk waar deze poort over gaat bestaat niet meer, dus er valt niets goed te keuren.",
          reviewer_is_initiator: "Jij bent dit werk gestart, dus je kunt niet ook degene zijn die het goedkeurt. Iemand anders moet ernaar kijken.",
          not_authorized_for_review: "Jouw rol dekt dit soort goedkeuring niet. Wie het wel kan, staat hierboven.",
          validation_not_green: "De wijziging is nog niet bewezen, dus er is niets groens om goed te keuren.",
          validation_state_unavailable: "We konden het bewijs voor deze wijziging niet lezen, dus er is nog niets om tegen af te wegen. Er is niets besloten en de poort staat nog open — probeer het zo opnieuw.",
        },
        validationRefusalDetails: "Wat het systeem meldde",
        rolloutPhase: {
          queued: "Uitrol staat in de wachtrij op {environment}",
          running: "Uitrol loopt op {environment}",
          succeeded: "Uitrol voltooid op {environment}",
          failed: "Uitrol gefaald op {environment} — inspecteer en rol indien nodig terug",
          cancelled: "Uitrol geannuleerd op {environment}",
          awaiting_approval: "Uitrol gepauzeerd op {environment} — een andere gate wacht",
        },
        rolloutBlocked: "Uitrol naar {environment} is geblokkeerd.",
        rolloutResumed: "Rollout-promotie gestart via Argo Rollouts ({profile}).",
        rolloutResumeFailed: "Automatische rollout-promotie niet voltooid ({profile}) — een operator volgt op.",
        // Host-neutrale tekst voor CI/CD-hosts zonder rollout om te promoveren
        // (GitHub Actions, GitLab, Jenkins): de gate GEEFT een deployment vrij
        // die de host vasthield. De Argo-tekst hierboven mag daar nooit staan.
        deploymentReleased: "De gepauzeerde deployment is vrijgegeven op de CI/CD-host ({profile}).",
        deploymentReleaseFailed: "De gepauzeerde deployment kon niet worden vrijgegeven ({profile}) — een operator volgt op.",
        // Alleen het LABEL. De zin erachter is de server-geauteerde
        // operatortekst van de resume-handler (welke credential je moet
        // koppelen, en waarom) en wordt letterlijk getoond — hosttaal, geen
        // i18n-sleutel.
        hostNotTold: "Je beslissing is vastgelegd, maar de CI/CD-host is niet op de hoogte gebracht:",
        viewDiffCta: "Bekijk patch-diff",
        rolloutDetailsCta: "Details bekijken",
        rolloutDrawerTitle: "Uitroldetails",
        rolloutDrawerNote: "Live deployment-events lopen door naar de operations-surface. Open die voor de volledige historie, inclusief rollback-controls wanneer beschikbaar.",
        openOperationsCta: "Open operations",
        openTimelineCta: "Open issue-tijdlijn",
        notePlaceholder: "Beslissingsnotitie (optioneel, vastgelegd in het audit-spoor)…",
        confirmHighRisk: "Risicovolle wijziging — klik nogmaals op Goedkeuren ter bevestiging.",
        confirmApproveCta: "Bevestig goedkeuren",
      },
    },
    actions: {
      approve: "Goedkeuren",
      reject: "Afwijzen",
      requestChanges: "Wijzigingen aanvragen",
      dryRun: "Dry run",
      apply: "Toepassen",
      export: "Exporteren",
      exportTooltip: "Schrijf de patch als unified-diff-bestand dat je met git apply kunt toepassen",
      alreadyApproved: "Al goedgekeurd",
      approveBeforeApplying: "Keur de patch goed voordat je hem toepast",
      labels: {
        approve: "Goedkeuren",
        reject: "Afwijzen",
        request_changes: "Wijzigingen aanvragen",
        dry_run: "Dry run",
        apply: "Toepassen",
      },
    },
    patchApply: {
      label: {
        applyLocally: "Lokaal toepassen",
        openPullRequest: "Pull request openen",
        openMergeRequest: "Merge request openen",
        openReviewRequest: "Review request openen",
        applyOnRemote: "Op externe server toepassen",
        manualFallback: "Patch downloaden / in desktop openen",
      },
      manualFallback: {
        title: "Pas deze patch zelf toe",
        body: "Hosted FixControl kan deze codebase niet bereiken om de wijziging voor je toe te passen. Kies hoe je verder wilt:",
        downloadPatch: "Patch downloaden",
        copyPatch: "Patch naar klembord kopiëren",
        openInDesktop: "In desktop-app openen",
        connectGitProvider: "Git-provider koppelen",
        copied: "Patch gekopieerd naar klembord",
        copyFailed: "Patch kopiëren is mislukt — probeer downloaden",
      },
      remoteSsh: {
        confirmTitle: "Op externe server toepassen",
        confirmBody: "FixControl maakt verbinding met {host} via SSH en schrijft de patch onder {path}. Bestaande bestanden worden geback-upt; eventueel geconfigureerde tests draaien automatisch en triggeren rollback bij falen.",
        host: "Host",
        repoPath: "Repo-pad",
        files: "Te schrijven bestanden",
        rollbackNote: "Als er iets misgaat herstelt FixControl vanuit de zojuist aangemaakte back-up.",
      },
    },
    requestChangesModal: {
      title: "Wijzigingen aanvragen",
      subtitle: "Beschrijf wat er bij het testen nog niet goed ging. Er wordt direct een nieuwe versie gemaakt die op deze patch voortbouwt.",
      placeholder: "Bijv.: ik hoor nog het foutgeluid en kom direct op het tafelplan; de Order-knop licht niet op als er printbare producten zijn toegevoegd…",
      cancel: "Annuleren",
      submit: "Nieuwe versie aanvragen",
    },
    toasts: {
      revisionAlreadyInProgress: "Revisie al bezig — volg de chat voor voortgang",
      retryStartedDeduped: "Revisie al bezig — volg de chat voor voortgang",
      retryStarted: "Vervolg-patch wordt geschreven — volg de chat voor voortgang",
      changesRequestedRevising: "Wijzigingen aangevraagd — er wordt een nieuwe versie gemaakt, volg de chat voor voortgang",
      retryFailedMissing: "Retry kon niet starten: {error} (ontbreekt: {missing})",
      retryFailed: "Retry kon niet starten: {error}",
      retryFailedNetwork: "Retry kon niet starten: {error}",
      networkError: "Netwerkfout",
      actionFailed: "{action} mislukt: {error}",
      patchApproved: "Patch goedgekeurd — opvolg-acties aangemaakt",
      patchRejected: "Patch afgewezen",
      changesRequested: "Wijzigingen aangevraagd",
      dryRunPassed: "Dry run geslaagd — sandbox-checks groen",
      dryRunFailed: "Dry run mislukt — sandbox meldde problemen",
      dryRunCompleted: "Dry run voltooid",
      patchApplied: "Patch toegepast — opvolg-acties aangemaakt",
      patchAppliedPr: "Patch toegepast — PR geopend: {url}",
      exportFailedStatus: "Export mislukt ({status})",
      verifyStarted: "Verificatie gestart — de sandbox-run staat in de wachtrij.",
      verifyAlreadyRunning: "Er loopt al een verificatie voor deze versie van de patch.",
      verifyFailed: "De verificatie kon niet worden gestart.",
      verifyFailedStatus: "De verificatie kon niet worden gestart ({status})",
      exportFailed: "Export mislukt",
      exported: "Geëxporteerd: {path} ({fileCount} bestand)",
      exportedPlural: "Geëxporteerd: {path} ({fileCount} bestanden)",
      connectionError: "Verbindingsfout: {message}",
      cancelledFallback: "Agent gestopt door gebruiker.",
      errorPrefix: "Fout: {error}",
    },
    impact: {
      customerAffected: "{count} klant geraakt",
      customersAffected: "{count} klanten geraakt",
      ifNotFixed: "Als dit niet wordt opgelost",
    },
    risk: {
      eyebrow: "Risico",
      confidence: "{pct}% zekerheid",
      dryRunRecommended: "Dry run aanbevolen",
      ctoApprovalRequired: "CTO-goedkeuring vereist",
      blast: {
        label: "Wijzigt",
        payments: "betalingen",
        authFlow: "auth-flow",
        dbSchema: "database-schema",
        webhook: "webhook-verwerking",
        externalApi: "externe integratie",
        frontendOnly: "alleen frontend",
      },
    },
    followUp: {
      eyebrow: "Opvolging",
      doneCount: "{done}/{total} klaar",
      overdue: "{count} te laat",
      unassigned: "{count} niet toegewezen",
      ownerUnassigned: "niet toegewezen",
    },
    busy: {
      pondering: "Aan het overpeinzen",
      reasoning: "Aan het redeneren",
      reflecting: "Aan het reflecteren",
      cogitating: "Aan het nadenken",
      synthesising: "Aan het synthetiseren",
      investigating: "Aan het onderzoeken",
    },
    chatBubble: {
      agentLabel: "Agent",
      patchMoved: "Patch-inhoud verplaatst naar het Patch-paneel.",
      softErrorTitle: "De verkennende poging van de agent leverde geen match — de run gaat door met de volgende tool-aanroep.",
    },
    runFix: {
      runFix: "Fix starten",
      dryRun: "Dry run",
      failed: "Mislukt",
      patchesReady: "{count} patches gereed",
      pr: "PR #{number}",
      skipped: "(overgeslagen)",
    },
    replyDrafter: {
      to: "Aan:",
      toneFriendly: "vriendelijk",
      toneFormal: "formeel",
      toneConcise: "beknopt",
      draftWithAi: "Antwoord opstellen met AI",
      drafting: "Bezig met opstellen…",
      confidence: "Zekerheid",
      confidencePct: "{pct}%",
      editBeforeSending: " · bewerk voor verzending",
      regenerate: "Opnieuw genereren",
      copy: "Kopiëren",
      copied: "Gekopieerd",
    },
  },

  intake: {
    headerTitle: "Meld een probleem",
    inputPlaceholder: "Beschrijf het probleem…",
    greeting:
      "Hoi 👋 — stel een vraag, meld een bug of vraag een feature aan. Ik probeer je direct te helpen en maak alleen een ticket aan als we het hier niet kunnen oplossen.",
    pageSubtitle: "Meld een probleem of stel een vraag — we maken er een ticket van.",
    pageBody:
      "Klik op de bubble rechtsonder en beschrijf wat er aan de hand is. De intake-assistent stelt een paar korte vragen en stuurt de melding door naar het dev-team van {company}.",
    pageProductLabel: "Product:",
    bubbleAria: "Intake-widget openen",
    closeAria: "Sluiten",
    sendAria: "Verstuur",
    thinking: "bezig…",
    contactPlaceholder: "E-mail voor opvolging (optioneel)",
    askAnother: "Stel nog een vraag",
    reportAnother: "Nog een melding maken",
    yesHelped: "Ja, dat hielp",
    noStuck: "Nee, het werkt nog steeds niet",
    yesCreateTicket: "Ja, maak een ticket",
    noThanks: "Nee, dank je",
    addDetail: "…of voeg detail toe en druk op Enter",
    createIssue: "Ticket aanmaken",
    tellMeChange: "of vertel wat ik moet aanpassen…",
    issueCreated: "Issue {key} aangemaakt",
    typeStatus: "Type: {kind} · Status: In behandeling",
    sortedOut: "Mooi dat het is opgelost",
    sortedOutDetail: "Geen ticket aangemaakt — kom terug als er nog iets is.",
    issueCreatedThanks:
      "Bedankt — issue **{key}** is aangemaakt. Het team pakt het van hieruit op.",
    didThisSolve: "Heeft dit je probleem opgelost?",
    inputPlaceholderShort: "Stel een vraag, meld een bug of vraag een feature aan…",
    openInFixControl: "Openen in FixControl",
    preview: {
      ticketPreview: "Ticket-preview",
      steps: "Stappen",
      expected: "Verwacht",
      actual: "Werkelijk",
      environment: "Omgeving",
      impact: "Impact",
    },
  },

  twoFactor: {
    header: "Tweestapsverificatie",
    badgeEnabled: "Ingeschakeld",
    badgeRequired: "Verplicht",
    description: "Bescherm je account met een eenmalige code uit een authenticator-app (Google Authenticator, 1Password, Microsoft Authenticator, …) naast je wachtwoord. Sms wordt niet gebruikt.",
    ssoNote: "Je logt in via de identityprovider van je organisatie. Multifactor-authenticatie wordt daar beheerd; native 2FA hier inschakelen is optioneel.",
    enableButton: "Tweestapsverificatie inschakelen",
    errors: {
      loadFailed: "Kon de 2FA-status niet laden",
      network: "Netwerkfout",
      badCode: "Die code is niet geldig — controleer je authenticator-app",
      badPassword: "Huidig wachtwoord is onjuist",
      passwordRequired: "Voer je huidige wachtwoord in om de bestaande authenticator te vervangen",
      requiredByPolicy: "Tweestapsverificatie is verplicht voor dit account en kan niet worden uitgeschakeld",
      noPending: "Geen registratie bezig — begin opnieuw",
      rateLimited: "Te veel pogingen — wacht even en probeer opnieuw",
      generic: "Er ging iets mis. Probeer het opnieuw.",
    },
    enroll: {
      scanHint: "Scan de QR-code met je authenticator-app, of voer de instelsleutel handmatig in. Bevestig daarna met de 6-cijferige code die de app toont.",
      qrAlt: "QR-code voor het instellen van de authenticator-app",
      manualKey: "Instelsleutel",
      manualHint: "Tijdgebaseerd (TOTP), 6 cijfers, periode van 30 seconden, SHA-1.",
      codeLabel: "Code uit de app",
      verify: "Controleren & inschakelen",
    },
    recovery: {
      showOnceWarning: "Bewaar deze herstelcodes nu — ze worden maar één keer getoond. Elke code geeft je precies één keer toegang als je je authenticator kwijtraakt.",
      copy: "Kopiëren",
      copied: "Gekopieerd",
      download: "Downloaden",
      savedThem: "Ik heb mijn herstelcodes opgeslagen",
      fileNote: "Elke code is één keer bruikbaar. Bewaar dit bestand op een veilige plek.",
    },
    stepUp: {
      passwordLabel: "Huidig wachtwoord",
      codeLabel: "Authenticator- of herstelcode",
      codePlaceholder: "123456 of XXXX-XXXX-XXXX-XXXX",
    },
    disable: {
      button: "Uitschakelen",
      hint: "Als je tweestapsverificatie uitschakelt, is je account alleen nog met een wachtwoord beveiligd. Bevestig met je huidige wachtwoord en een geldige code.",
      confirm: "Tweestapsverificatie uitschakelen",
    },
    regenerate: {
      button: "Nieuwe herstelcodes",
      hint: "Genereer een nieuwe set herstelcodes. Alle eerder uitgegeven codes werken per direct niet meer.",
      confirm: "Herstelcodes vernieuwen",
    },
    reenroll: {
      button: "Nieuwe authenticator",
      start: "Instellen starten",
    },
    status: {
      enabledSince: "Ingeschakeld sinds {date}.",
      recoveryRemaining: "{count} ongebruikte herstelcodes over.",
      requiredNote: "Tweestapsverificatie is voor dit account verplicht volgens het beleid en kan niet worden uitgeschakeld.",
    },
    setupPage: {
      title: "Stel tweestapsverificatie in om verder te gaan",
      explanation: "Je organisatie vereist tweestapsverificatie voor dit account. Stel het hieronder in met een authenticator-app — het duurt ongeveer een minuut.",
      continue: "Door naar de app",
      signOut: "Uitloggen",
    },
    policy: {
      header: "2FA-beleid voor deze workspace",
      description: "Verplicht elk lid van deze workspace om tweestapsverificatie in te schakelen. Leden zonder 2FA komen bij hun volgende login in de setup terecht en kunnen de app pas gebruiken zodra het aanstaat.",
      requireAllLabel: "2FA verplicht voor alle leden",
      adminsAlwaysNote: "Deze instelling geldt ook voor de beheerders van deze workspace. FixControl-platformmedewerkers hebben altijd 2FA nodig, ongeacht deze instelling.",
      ownSetupFirst: "Schakel eerst tweestapsverificatie op je eigen account in; beheer daarna het workspace-beleid.",
      saved: "Opgeslagen",
    },
  },

  login: {
    metaTitle: "Inloggen — FixControl",
    backToHomeAria: "Terug naar de homepage",
    subtitle: "Log in op je account",
    signInRequired: "Log in om die pagina te openen.",
    sessionExpired: "Je sessie is verlopen. Log opnieuw in.",
    emailLabel: "E-mail",
    emailPlaceholder: "jij@bedrijf.com",
    passwordLabel: "Wachtwoord",
    passwordPlaceholder: "Voer je wachtwoord in",
    signingIn: "Bezig met inloggen…",
    signIn: "Inloggen",
    loginFailed: "Inloggen mislukt",
    forgotPassword: "Wachtwoord vergeten?",
    twoFactor: {
      title: "Tweestapsverificatie",
      codeHint: "Voer de 6-cijferige code uit je authenticator-app in.",
      recoveryHint: "Voer een van je eenmalige herstelcodes in.",
      codeLabel: "Verificatiecode",
      recoveryLabel: "Herstelcode",
      verifying: "Controleren…",
      verify: "Controleren",
      useRecovery: "Gebruik een herstelcode",
      useAuthenticator: "Gebruik je authenticator-app",
      back: "Terug",
      expired: "Deze inlogpoging is verlopen. Voer je wachtwoord opnieuw in.",
      badCode: "Die code is niet geldig",
    },
    demoAccounts: "Demo-accounts",
    demoPasswordLabel: "Wachtwoord:",
    noAccountPrompt: "Nog geen account?",
    signUpFree: "Meld je gratis aan",
    roles: {
      admin: "Beheerder",
      adminAccess: "Volledige toegang",
      manager: "Manager",
      managerAccess: "Toewijzen & goedkeuren",
      operator: "Operator",
      operatorAccess: "Bekijken & werken",
    },
  },

  forgotPassword: {
    metaTitle: "Wachtwoord resetten — FixControl",
    backToHomeAria: "Terug naar de homepage",
    title: "Wachtwoord resetten",
    subtitle: "Vul je e-mailadres in, dan sturen we een link om een nieuw wachtwoord te kiezen.",
    emailLabel: "E-mail",
    emailPlaceholder: "jij@bedrijf.com",
    submit: "Stuur resetlink",
    sending: "Versturen…",
    sentTitle: "Check je e-mail",
    sentBody:
      "Als dat adres een account heeft, is er een resetlink onderweg. Deze verloopt na een uur.",
    backToLogin: "Terug naar inloggen",
  },

  resetPassword: {
    metaTitle: "Nieuw wachtwoord kiezen — FixControl",
    backToHomeAria: "Terug naar de homepage",
    title: "Kies een nieuw wachtwoord",
    subtitle: "Vul een nieuw wachtwoord in voor je account.",
    passwordLabel: "Nieuw wachtwoord",
    passwordPlaceholder: "Minimaal 8 tekens",
    confirmLabel: "Bevestig nieuw wachtwoord",
    confirmPlaceholder: "Herhaal je wachtwoord",
    submit: "Wachtwoord resetten",
    saving: "Opslaan…",
    mismatch: "Wachtwoorden komen niet overeen.",
    tooShort: "Wachtwoord moet minimaal 8 tekens zijn.",
    missingToken: "Deze resetlink is ongeldig. Vraag een nieuwe aan.",
    invalidToken: "Deze resetlink is ongeldig of verlopen. Vraag een nieuwe aan.",
    genericError: "Er ging iets mis. Probeer het opnieuw.",
    successTitle: "Wachtwoord gereset",
    successBody:
      "Je wachtwoord is gewijzigd en je bent overal uitgelogd. Log in met je nieuwe wachtwoord.",
    goToLogin: "Naar inloggen",
    requestNew: "Nieuwe link aanvragen",
  },

  signup: {
    alreadyHaveAccount: "Heb je al een account?",
    signIn: "Inloggen",
    eyebrowPlan: "{plan}-plan",
    eyebrowCodebase: "Codebase-setup",
    eyebrowIssues: "Issue-pilot",
    eyebrowDefault: "Registreren",
    headlinePlan: "Start je {plan}-werkruimte",
    headlineCodebase: "Verbind je codebase",
    headlineIssues: "Probeer het met je eigen issue",
    headlineDefault: "Maak je werkruimte aan",
    sublinePlan: "Gratis te beginnen — je werkruimte is binnen seconden klaar. Je betaalt pas bij een upgrade.",
    sublineCodebase: "Zet een geïsoleerde werkruimte op voor je team. Verbind je codebase en lever je eerste beoordeelde fix op.",
    sublineIssues: "Zet een geïsoleerde werkruimte op voor je team. Wijs ons één ticket aan en zie de loop triage → patch → goedkeuren.",
    sublineDefault: "Zet je eigen geïsoleerde werkruimte op. Jij wordt de beheerder — nodig teamgenoten uit wanneer jij er klaar voor bent.",
    selectedPlan: "Gekozen plan",
    changePlan: "Plan wijzigen",
    perkIsolatedTitle: "Je eigen geïsoleerde werkruimte",
    perkIsolatedBody: "Jouw data, jouw codebase, jouw guardrails. Niets wordt gedeeld met andere tenants.",
    perkApprovalTitle: "Standaard eerst goedkeuring",
    perkApprovalBody: "Jij hebt het laatste woord — standaard wacht elke patch op jouw groen licht, en elke autonomie is expliciet en opt-in.",
    perkAuditTitle: "Audit trail standaard",
    perkAuditBody: "Elke beslissing onder governance — wie goedkeurde, op basis van welk bewijs — wordt vastgelegd bij het issue en blijft bewaard als het issue wordt verwijderd.",
    freeBlurb: "Gratis te beginnen. Geen creditcard nodig. Op elk moment opzegbaar. Je kunt teamgenoten uitnodigen vanuit je werkruimte zodra je binnen bent.",
    fullName: "Volledige naam",
    fullNamePlaceholder: "Ada Lovelace",
    workEmail: "Zakelijke e-mail",
    workEmailPlaceholder: "jij@bedrijf.com",
    company: "Bedrijf",
    companyPlaceholder: "Acme Logistics",
    password: "Wachtwoord",
    passwordPlaceholder: "Minstens 8 tekens",
    fairUseNote: "Door een account aan te maken ga je akkoord met fair use van FixControl. We sturen je nooit spam.",
    creatingWorkspace: "Werkruimte wordt aangemaakt…",
    createAccount: "Account aanmaken",
    genericError: "Er is iets misgegaan. Probeer het opnieuw.",
    networkError: "Netwerkfout. Probeer het opnieuw.",
    createWorkspace: "Maak je workspace aan",
    checkEmail: "Controleer je e-mail voor een bevestigingscode.",
    verifyTitle: "Bevestig je e-mailadres",
    verifySubtitle: "We hebben een 6-cijferige code naar {email} gestuurd. Voer deze in om je workspace af te ronden.",
    codeLabel: "Bevestigingscode",
    verifyCta: "E-mail bevestigen",
    verifying: "Bezig met bevestigen…",
    resend: "Code opnieuw versturen",
    resendIn: "Opnieuw versturen over {sec}s",
    resendSent: "Een nieuwe code is onderweg.",
    codeInvalid: "Code verlopen of ongeldig.",
    verifiedTitle: "E-mailadres bevestigd",
    verifiedBody: "Je workspace wordt voorbereid…",
    reviewTitle: "Aanvraag ontvangen",
    reviewBody: "Je aanvraag is ontvangen. We bekijken deze handmatig en nemen contact op.",
    useDifferentEmail: "Ander e-mailadres gebruiken",
    loadDemoTitle: "Voeg voorbeeld bedrijf 'Northwind Bank' toe",
    loadDemoBody: "Bekijk FixControl meteen met issues, codebases, workspaces en kennis. Met één klik weer te verwijderen.",
    plans: {
      starter: "Starter",
      starterPrice: "€29 / maand",
      growth: "Growth",
      growthPrice: "€99 / maand",
      pro: "Pro",
      proPrice: "€299+ / maand",
    },
  },

  contact: {
    metaTitle: "Contact — FixControl",
    metaDescription: "Neem contact op met FixControl met een vraag, use case of demo-aanvraag.",
    eyebrow: "Contact",
    titlePart1: "Stel ons een vraag over",
    titleHighlight: "fit, security of uitrol.",
    body: "Gebruik het formulier voor eerste vragen, evaluatie-fit en integratievragen. Gebruik e-mail voor langere technische of commerciële gesprekken. Boek een live demo om door de Slack-, Jira-, Freshdesk- of engineering-remediatieflows te lopen.",
    walkthroughLabel: "Liever een live walkthrough?",
    walkthroughCta: "Boek een demo →",
    emailLabel: "E-mail — langere technische of inkoopvragen",
    trust:
      "FixControl bestuurt AI-ondersteund support- en engineeringwerk — en, waar je ze configureert, deployment-gates — met standaard menselijke goedkeuring, scope per project en een vastgelegd beslisspoor.",
    whoReplies:
      "Je bericht wordt rechtstreeks gelezen en beantwoord door het team dat het product bouwt — doorgaans binnen één werkdag. Security- en procurementmateriaal (architectuuroverzicht, subverwerkerslijst, documentatie over gegevensverwerking) wordt tijdens onboarding aangeleverd.",
    submitLabel: "Verstuur bericht",
    successTitle: "Bericht ontvangen.",
    successBody: "Bedankt. We reageren binnen één werkdag.",
    fields: {
      name: "Je naam",
      namePlaceholder: "Alex Jansen",
      email: "E-mail",
      emailPlaceholder: "alex@jouwbedrijf.com",
      company: "Bedrijf (optioneel)",
      companyPlaceholder: "Acme B.V.",
      message: "Wat wil je vragen?",
      messagePlaceholder: "Eerste vraag, demo-fit of een integratievraag (Slack, Jira, Freshdesk, GitHub…). Hoe concreter, hoe beter.",
    },
  },

  demo: {
    metaTitle: "Demo boeken — FixControl",
    metaDescription: "Een begeleide demo van 15 minuten: zie hoe FixControl een echt productie-issue van intake naar root cause, opgesteld patch en goedkeuring brengt.",
    eyebrow: "Demo boeken",
    titlePart1: "Zie FixControl een echt issue afhandelen —",
    titleHighlight: "end-to-end.",
    body: "Een begeleide walkthrough van 15 minuten. We nemen één echt productie-issue end-to-end door — intake, root cause, opgesteld patch, jouw goedkeuring en de toegepaste fix — zodat je precies ziet hoe de workflow en guardrails zich gedragen. Geen slides, geen salesteam.",
    bullets: {
      codebase: "Getoond op een echte codebase",
      noDeploys: "Geen automatische deploys",
      approval: "Elke wijziging in deze walkthrough vereist goedkeuring",
      sandbox: "Evalueer met demodata voordat je een productiesysteem koppelt",
    },
    afterSubmit:
      "Wat er daarna gebeurt: we reageren binnen één werkdag om in te plannen. Pilots starten met demodata en één integratie en bepalen de approval gates en succescriteria voor uitbreiding; security- en procurementmateriaal wordt tijdens onboarding aangeleverd.",
    preferEmail: "Liever mailen?",
    contactLink: "Gebruik het contactformulier →",
    submitLabel: "Demo aanvragen",
    successTitle: "Demo-aanvraag ontvangen.",
    successBody: "We nemen binnen één werkdag contact op om je walkthrough van 15 minuten in te plannen.",
    fields: {
      name: "Je naam",
      namePlaceholder: "Alex Jansen",
      email: "Zakelijke e-mail",
      emailPlaceholder: "alex@jouwbedrijf.com",
      company: "Bedrijf",
      companyPlaceholder: "Acme B.V.",
      codebase: "Stack of repo (optioneel)",
      codebasePlaceholder: "Next.js + Postgres · github.com/acme/api",
      teamSize: "Grootte engineeringteam",
      teamSizePlaceholder: "Hoe groot is het engineeringteam?",
      message: "Wat zou je willen zien?",
      messagePlaceholder: "bijv. het soort productie-issue dat je FixControl wilt zien afhandelen.",
    },
  },

  adminWebhookOutbox: {
    eyebrow: "FixControl admin",
    title: "Webhook DLQ",
    subtitle: "Cross-tenant dead-letter queue voor uitgaande webhook-deliveries. Repliceer rijen individueel of selecteer er meerdere om in bulk opnieuw te proberen.",
    accessRequired: "Platform admin-toegang vereist",
    accessRequiredDetail: "Deze pagina is alleen voor FixControl-medewerkers.",
    empty: "Geen DLQ-rijen voor het huidige filter.",
    searchPlaceholder: "Zoek integratie / idempotency-key / error…",
    filter: {
      allProviders: "Alle providers",
      includeReplayed: "Toon herspeeld",
    },
    /**
     * Waarom een levering vastzit, uitgedrukt als wat de beheerder eraan doet.
     * `transient` / `permanent_auth` / `permanent_other` zijn de opgeslagen
     * soorten (src/server/webhook-outbox/types.ts).
     */
    errorKind: {
      transient: "Wordt opnieuw geprobeerd",
      permanent_auth: "Inloggegevens geweigerd",
      permanent_other: "Definitief geweigerd",
    },
    columns: {
      diedAt: "Gefaald",
      provider: "Provider",
      integration: "Integratie",
      attempts: "Pogingen",
      errorKind: "Fouttype",
      lastError: "Laatste fout",
      replayed: "Herspeeld",
      notReplayed: "—",
    },
    actions: {
      replay: "Herspeel",
    },
    bulk: {
      replaySelected: "Selectie herspelen",
      exportSelected: "Selectie exporteren",
      confirmTitle: "{n} DLQ-rijen herspelen?",
      confirmBody: "Elke rij wordt opnieuw in de wachtrij gezet met attempts=0. Reeds herspeelde rijen worden overgeslagen.",
      confirmLabel: "Herspeel",
    },
  },

  adminScimTokens: {
    eyebrow: "FixControl admin",
    title: "SCIM tokens",
    subtitle: "Cross-tenant overzicht van SCIM-bearer-tokens. Plaintext wordt nooit getoond; nieuwe tokens en rotatie verlopen via de per-tenant instellingenpagina.",
    accessRequired: "Platform admin-toegang vereist",
    accessRequiredDetail: "Deze pagina is alleen voor FixControl-medewerkers.",
    empty: "Geen SCIM-tokens voor dit filter.",
    searchPlaceholder: "Zoek label / tenant / aangemaakt-door…",
    statusEnabled: "Actief",
    statusDisabled: "Uitgeschakeld",
    confirmDisable: "SCIM-token \"{label}\" voor tenant \"{tenant}\" uitschakelen? Volgende SCIM-aanroepen met dit token worden geweigerd.",
    filter: { all: "Alle statussen" },
    columns: {
      tenant: "Tenant",
      label: "Label",
      status: "Status",
      lastUsed: "Laatst gebruikt",
      expires: "Verloopt",
      created: "Aangemaakt",
    },
    actions: { disable: "Uitschakelen" },
    bulk: {
      disableSelected: "Selectie uitschakelen",
      confirmTitle: "{n} SCIM-token(s) uitschakelen?",
      confirmBody: "Elk geselecteerd token wordt direct uitgeschakeld. Reeds uitgeschakelde tokens worden overgeslagen.",
      confirmLabel: "Uitschakelen",
    },
  },

  adminOidcProviders: {
    eyebrow: "FixControl admin",
    title: "OIDC providers",
    subtitle: "Cross-tenant overzicht van OIDC-providers. Bulk metadata-refresh; secret-rotatie blijft per provider op de per-tenant pagina.",
    accessRequired: "Platform admin-toegang vereist",
    accessRequiredDetail: "Deze pagina is alleen voor FixControl-medewerkers.",
    empty: "Geen OIDC-providers voor dit filter.",
    searchPlaceholder: "Zoek naam / slug / tenant / issuer…",
    statusEnabled: "Actief",
    statusDisabled: "Uitgeschakeld",
    allDomains: "alle domeinen",
    confirmDelete: "OIDC-provider \"{name}\" voor tenant \"{tenant}\" verwijderen? Gebruikers die via deze IdP inloggen verliezen direct toegang.",
    filter: { all: "Alle statussen" },
    columns: {
      tenant: "Tenant",
      provider: "Provider",
      status: "Status",
      domains: "Domeinen",
      updated: "Bijgewerkt",
    },
    actions: {
      refresh: "Metadata verversen",
      delete: "Verwijderen",
    },
    bulk: {
      refreshSelected: "Metadata verversen",
    },
  },

  adminCustomers: {
    loading: "Laden…",
    deniedTitle: "Alleen voor FixControl-support",
    deniedBody: "Deze pagina is voorbehouden aan FixControl-medewerkers. Als je hier per ongeluk bent beland, ga terug naar je",
    deniedWorkspace: "werkruimte",
    eyebrow: "FixControl-support",
    title: "Klanten",
    subtitleOps: "Operationeel overzicht van elke klantwerkruimte — gezondheid, onboarding, integraties en activiteit. Klik op een rij voor details; support-login houdt je ingelogd als FixControl.",
    platformAdmin: "Platformbeheerder",
    updated: "Bijgewerkt {ago}",
    refresh: "Vernieuwen",
    healthError: "Kon gezondheidsdata niet laden. Probeer te vernieuwen.",
    never: "nooit",
    searchPlaceholderOps: "Zoek op naam, domein, contact of integratie",
    countOf: "{shown} van {total}",
    cardActive: "Actief (7d)",
    cardOnboarding: "Onboarding onvolledig",
    cardAtRisk: "Risico",
    cardReconnect: "Opnieuw verbinden",
    cardInactive: "Inactief",
    cardAiToday: "AI-runs vandaag",
    headerCustomer: "Klant",
    headerHealth: "Gezondheid",
    headerOnboarding: "Onboarding",
    headerIntegrations: "Integraties",
    headerLastActivity: "Laatste activiteit",
    headerUsage: "Gebruik 7d",
    headerActions: "Acties",
    activeBadge: "Actief",
    openAction: "Openen",
    supportLogin: "Support-login",
    supportLoginTitle: "{name} openen als support",
    statusBlocked: "Geblokkeerd",
    blockTitle: "{name} blokkeren",
    unblockTitle: "{name} deblokkeren",
    confirmBlock: "Weet je zeker dat je deze klant wilt blokkeren? Hun gebruikers kunnen niet meer inloggen tot je ze deblokkeert.",
    blockError: "Status wijzigen mislukt. Probeer het opnieuw.",
    wipeTitle: "Alle data van {name} wissen",
    confirmWipePrompt: "Dit wist alle issues, workspaces, integraties en kennis voor {name}. Account + leden blijven bestaan. Typ de bedrijfsnaam '{name}' om te bevestigen:",
    confirmWipeMismatch: "Naam komt niet overeen — actie geannuleerd.",
    wipeError: "Data wissen mislukt. Probeer het opnieuw.",
    wipeOk: "Klantdata gewist ({total} rijen verwijderd).",
    deleteTitle: "Omgeving {name} verwijderen",
    confirmDeletePrompt: "Dit plant {name} in voor verwijdering. De toegang wordt nu bevroren en na een herstelperiode van 2 weken wordt alles definitief verwijderd — tot die tijd kun je het ongedaan maken. Typ de exacte naam '{name}' om te bevestigen:",
    confirmDeleteMismatch: "Naam komt niet overeen — verwijdering geannuleerd.",
    deleteError: "Inplannen van verwijdering mislukt. Probeer het opnieuw.",
    deleteScheduled: "Ingepland voor verwijdering. Nu bevroren; definitief verwijderd op {date} tenzij hersteld.",
    restoreTitle: "{name} herstellen",
    restoreError: "Herstellen mislukt. Probeer het opnieuw.",
    pendingDeletionBadge: "Verwijdering {date}",
    pendingDeletionTitle: "Ingepland voor definitieve verwijdering op {date}. Herstel om te behouden.",
    downloadTitle: "Alle data van {name} downloaden",
    emptyNone: "Nog geen klanten",
    emptyFiltered: "Geen klanten komen overeen met het filter",
    footerNote: "Plan, mailbox of domein van een klant aanpassen? Gebruik de",
    footerLink: "Tenants",
    footerLinkSuffix: "pagina.",
    lastActivity: "Laatste activiteit",
    lastLogin: "Laatste login",
    lastInbox: "Laatste inbox-intake",
    lastIssue: "Laatste issue aangemaakt",
    lastPatch: "Laatste patch beoordeeld",
    drawerClose: "Sluiten",
    supportContext: "Supportcontext",
    drawerBlockedNote: "Deze tenant is geblokkeerd — gebruikers kunnen niet inloggen.",
    drawerNoSignals: "Nog geen activiteitssignalen.",
    onboardingTitle: "Onboarding",
    usageTitle: "Gebruik",
    usageInbox7: "Inbox 7d",
    usageIssues7: "Issues 7d",
    usagePatches7: "Patches 7d",
    usageAi7: "AI-runs 7d",
    usageInbox30: "Inbox 30d",
    usageIssues30: "Issues 30d",
    usageActiveUsers: "Actieve gebruikers 7d",
    usageAiToday: "AI-runs vandaag",
    integrationsTitle: "Integraties",
    drawerNoIntegrations: "Geen integraties verbonden.",
    mailboxes: "Mailboxen",
    mailboxIngestOff: "intake uit",
    recentIssues: "Recente issues",
    drawerNoIssues: "Nog geen issues.",
    recentInbox: "Recente inbox",
    drawerNoInbox: "Nog geen inbox-activiteit.",
    recentPatches: "Recente patches",
    drawerNoPatches: "Nog geen patches.",
    activityTimeline: "Activiteitstijdlijn",
    drawerNoActivity: "Geen geregistreerde activiteit.",
    supportHistory: "Support-sessiehistorie",
    drawerNoSupport: "Geen support-sessies geregistreerd.",
    supportOngoing: "actief",
    supportActions: "{n} acties",
    drawerLoadError: "Kon tenantdetails niet laden.",
    health: {
      healthy: "Gezond",
      onboarding: "Onboarding",
      at_risk: "Risico",
      integration_problem: "Integratieprobleem",
      inactive: "Inactief",
      abandoned: "Verlaten",
    },
    filter: {
      all: "Alle",
      healthy: "Gezond",
      onboarding: "Onboarding",
      at_risk: "Risico",
      integration_problem: "Integratieproblemen",
      inactive: "Inactief",
    },
    integrationState: {
      active: "Verbonden",
      reconnect: "Opnieuw verbinden",
      error: "Fout",
      pending: "In behandeling",
      disabled: "Uitgeschakeld",
    },
    onboarding: {
      workspace: "Werkruimte aangemaakt",
      team: "Teamlid uitgenodigd",
      channel: "Intakekanaal verbonden",
      intake: "Eerste intake ontvangen",
      issue: "Eerste issue aangemaakt",
      patch: "Eerste patch beoordeeld",
      optional: "optioneel",
    },
    usage: {
      inbox: "Inbox",
      issues: "Issues",
      ai: "AI-runs",
    },
    reason: {
      never_logged_in: "Nooit ingelogd",
      no_login_long: "Geen login in {days} dagen",
      no_login: "Geen login in {days} dagen",
      recent_login: "Recent ingelogd",
      no_inbox_ever: "Nog geen inbox-intake",
      no_inbox_activity: "Geen inbox-activiteit in {days} dagen",
      inbox_active: "Inbox actief ({count} in 7d)",
      integration_reconnect: "{name} moet opnieuw verbonden worden",
      integration_error: "{name}-integratiefout",
      no_conversions: "{count} intake-items, geen conversies",
      onboarding_incomplete: "Onboarding {done}/{total} voltooid",
      ai_active: "AI-flows in gebruik ({count} in 7d)",
    },
  },

  leadForm: {
    backToHome: "← Terug naar home",
    sending: "Versturen…",
    networkError: "Netwerkfout. Probeer het opnieuw.",
    genericError: "Er is iets misgegaan. Probeer het opnieuw.",
    selectPlaceholder: "Selecteer...",
  },

  landingNav: {
    example: "Voorbeeld",
    useCases: "Use-cases",
    howItWorks: "Hoe het werkt",
    control: "Controle",
    compare: "Vergelijk",
    pricing: "Prijzen",
    docs: "Docs",
    openApp: "App openen",
    talkToFounder: "Neem contact op",
    tryYourFirstIssue: "Probeer je eerste issue",
    integrations: "Integraties",
    safety: "Security",
    review: "Review",
    contact: "Contact",
    tryFree: "Probeer gratis",
    // Grouped-nav (dropdown) labels
    product: "Product",
    exampleWorkflow: "Voorbeeld workflow",
    approvalLedger: "Approval ledger",
    integrationHealth: "Integratiestatus",
    ucTicketQuestions: "Ticketvragen in Slack/Jira",
    ucApprovedReplies: "AI-replies voor Freshdesk/Jira",
    ucRemediation: "Support naar engineering",
    ucDeployment: "Deployment governance",
    openMenu: "Menu openen",
    closeMenu: "Menu sluiten",
    // Mega-menu (mm) — de onderwijzende nav. Elke desc is één zin ≤80
    // tekens, claims-safe (docs/ai/fixcontrol-website-claims.md).
    mm: {
      product: {
        eyebrow: "Platform",
        tagline: "De governance-laag voor AI-ondersteund engineeringwerk.",
        supportIntelligence: {
          title: "Support Intelligence",
          desc: "Begrijp incidenten via docs, eerdere tickets en operationele context.",
        },
        operationalGovernance: {
          title: "Operationele governance",
          desc: "AI werkt binnen policies, goedkeuringen en audit-eisen.",
        },
        evidenceEngine: {
          title: "Evidence Engine",
          desc: "Elke aanbeveling draagt het bewijs waarop ze rust.",
        },
        changeValidation: {
          title: "Wijzigingen valideren",
          desc: "Een wijziging is pas groen als de tests slagen én de applicatie nog start.",
        },
        commandCenter: {
          title: "Command Center",
          desc: "Vraag het in je eigen woorden; elke wijziging stopt nog steeds bij een bevestiging.",
        },
        missionTimeline: {
          title: "Missietijdlijn",
          desc: "Speel elke beslissing terug, van intake tot deployment.",
        },
        whyTitle: "Wat FixControl afdwingt",
        why: {
          humanApproval: "Menselijke goedkeuring standaard",
          explainable: "Voorstellen met bewijs",
          auditTrail: "Audit trail",
          multiTenant: "Multi-tenant",
          kubernetes: "Kubernetes-ready",
        },
        cta: "Bekijk het productoverzicht",
      },
      useCases: {
        supportEmail: {
          title: "Support & e-mail",
          desc: "Van de vraag van je klant naar gecontroleerd antwoord — met demovideo.",
        },
        engineeringBugs: {
          title: "Engineering & bugs",
          desc: "Van bugmelding naar getest wijzigingsvoorstel — met demovideo.",
        },
        customerSupport: {
          title: "Customer support",
          desc: "Slack en Freshdesk worden de ingang voor engineeringwerk.",
        },
        supportToEngineering: {
          title: "Support → engineering",
          desc: "Zet supportincidenten om in gestructureerde engineering-missies.",
        },
        deploymentGovernance: {
          title: "Deployment governance",
          desc: "Goedkeuringspoorten over deployments in Kubernetes en Argo CD, waar geconfigureerd.",
        },
        operationalAi: {
          title: "Operationele AI",
          desc: "AI die engineers helpt zonder governance te omzeilen.",
        },
        hrQuestions: {
          title: "HR-vragen",
          desc: "Vragen van je medewerkers, beantwoord onder een rechtenmodel dat HR uitlegt.",
        },
      },
      integrations: {
        colSupport: "Support",
        colHr: "HR",
        colDevelopment: "Development",
        colInfrastructure: "Infrastructuur",
        slack: { title: "Slack", desc: "Gebruik Slack als intake- en goedkeuringskanaal." },
        teams: { title: "Microsoft Teams", desc: "Keur gates goed en reageer direct vanuit Teams." },
        freshdesk: { title: "Freshdesk", desc: "Houd Freshdesk als je support-bronsysteem." },
        odoo: { title: "Odoo Helpdesk", desc: "Tickets gekoppeld, statussen teruggelezen, antwoorden na goedkeuring." },
        topdesk: { title: "TOPdesk", desc: "Incidenten aangemaakt en beantwoord na goedkeuring, melder gekoppeld." },
        afas: { title: "AFAS Profit", desc: "Alleen-lezen HR-velden voor een gegoverneerd antwoord." },
        youforce: { title: "Visma Raet Youforce", desc: "Hetzelfde alleen-lezen HR-contract op jouw Youforce-endpoints." },
        jira: { title: "Jira", desc: "Synchroniseer missies met Jira-issues en -transities." },
        github: { title: "GitHub", desc: "Genereer reviewbare patches en pull requests." },
        gitlab: { title: "GitLab", desc: "Lever governed merge requests in GitLab." },
        kubernetes: { title: "Kubernetes", desc: "Bestuur een gepauzeerde rollout via een goedkeuring, waar geconfigureerd." },
        argo: { title: "Argo Rollouts", desc: "Koppel rollout-promoties aan approval gates, waar geconfigureerd." },
        fcagent: { title: "FC Agent", desc: "Koppel privéclusters en CI — alleen uitgaand." },
      },
      security: {
        tenantIsolation: {
          title: "Tenant-isolatie",
          desc: "Elke organisatie draait in een geïsoleerde omgeving.",
        },
        governance: { title: "Governance", desc: "Menselijke goedkeuring vóór risicovolle acties, standaard." },
        evidence: { title: "Bewijs", desc: "Elke aanbeveling draagt haar bewijs mee." },
        auditability: { title: "Auditeerbaarheid", desc: "Speel elke operationele beslissing terug." },
        cta: "Lees de security-documentatie",
      },
      about: {
        label: "Over ons",
        mission: { title: "Missie", desc: "Waarom FixControl bestaat." },
        principles: { title: "Designprincipes", desc: "Menselijke controle over AI-operaties." },
        architecture: { title: "Architectuur", desc: "Gebouwd rond bestaande enterprise-tooling." },
        company: { title: "Bedrijf", desc: "Over het bedrijf en de visie." },
      },
    },
  },

  landing: {
    meta: {
      title: "FixControl — Van klantmelding of bug naar geteste oplossing",
      description:
        "FixControl begint bij een supportticket van een klant of bij een bug die je team al kent, onderzoekt die met je documentatie en code, stelt een codewijziging voor en test die in een afgeschermde sandbox. De wijziging komt binnen als pull request — standaard pas nadat je team goedkeurt.",
    },
    hero: {
      eyebrow: "Een klant meldt een bug — en development krijgt een geteste fix voorgesteld.",
      title: "Van melding naar geteste oplossing.",
      sub:
        "FixControl verbindt je supportdesk, documentatie en codebase. Begin bij een klantmelding of bij een engineering-issue: FixControl onderzoekt de oorzaak, stelt een codewijziging voor en test die in een afgeschermde sandbox — en legt hem daarna voor aan je developers.",
      second:
        "Standaard gaat er niets naar je klanten, repositories of productie zonder een vastgelegde goedkeuring van je team. Codewijzigingen komen binnen als pull request, nooit rechtstreeks op je hoofdbranch.",
      ctaPrimary: "Bekijk hoe het werkt",
      ctaSecondary: "Begin met een project",
      underCtas: "FixControl werkt bovenop Freshdesk, Jira, GitHub, GitLab, Slack, Teams en e-mail — en vervangt geen van alle.",
      screenshotAlt:
        "Een voorstel in FixControl: links de gebruikte informatie van het project, rechts de voorbereide codewijziging die wacht op goedkeuring.",
    },
    flow: {
      title: "Twee startpunten. Eén weg naar een geteste oplossing.",
      intro:
        "Support kan het starten. Engineering kan het starten. Wat daarna gebeurt, loopt door dezelfde stappen: FixControl onderzoekt en test, je team beslist.",
      entriesLabel: "Twee startpunten",
      entries: {
        support: {
          title: "Begin bij support",
          body: "Een klant meldt een probleem. FixControl combineert het ticket met je documentatie en code, onderzoekt de waarschijnlijke oorzaak en stelt een oplossing voor.",
          path: { p1: "Klantmelding", p2: "Onderzoek", p3: "Voorgestelde oplossing" },
          note: "Voor bugs die klanten melden en escalaties van support naar development.",
        },
        engineering: {
          title: "Begin bij engineering",
          body: "Je team kent de bug al. Leg hem vast in FixControl of open een werksessie op de codebase, en het werk gaat direct naar de code: analyse, een patch, tests.",
          path: { p1: "Bekende bug", p2: "Codeanalyse", p3: "Geteste wijziging" },
          note: "Geen supportticket nodig — voor defecten, backlog-items en interne issues.",
        },
      },
      converge: "Verschillende startpunten. Dezelfde acht stappen naar een geteste wijziging:",
      listLabel: "De acht stappen van melding tot pull request",
      phases: {
        investigate: { title: "Onderzoeken", note: "Alleen lezen, binnen het project" },
        propose: { title: "Voorstellen en testen", note: "Afgeschermd getest — nog niets in je repository" },
        decide: { title: "Je team beslist", note: "Standaard gaat niets verder zonder vastgelegde goedkeuring" },
      },
      steps: {
        s1: {
          title: "Er komt een melding binnen",
          body: "Van een klant via je supportdesk, e-mail, Slack of Teams — of direct vastgelegd door je team.",
        },
        s2: {
          title: "FixControl verzamelt tickets, documentatie en code",
          body: "De melding, eerdere tickets, de documentatie die je koppelde en de repository van het project.",
        },
        s3: {
          title: "Het onderzoekt de code",
          body: "Het volgt de melding naar het betrokken stuk code en de recente wijzigingen.",
        },
        s4: {
          title: "Het wijst de waarschijnlijke oorzaak aan",
          body: "Welk deel van de code waarschijnlijk verantwoordelijk is, en waarom.",
        },
        s5: {
          title: "Het stelt een codewijziging voor",
          body: "Een concrete patch. Er is nog niets aangepast of verstuurd.",
        },
        s6: {
          title: "Het test de wijziging in een sandbox",
          body: "Build en tests draaien in een afgeschermde omgeving. Waar FixControl weet hoe je applicatie start, moet die ook opkomen.",
        },
        s7: {
          title: "Een developer beoordeelt",
          body: "Die ziet de wijziging en de testresultaten, en keurt goed, wijst af of vraagt een aanpassing.",
        },
        s8: {
          title: "Pull request na goedkeuring",
          body: "De wijziging komt binnen als pull request, nooit rechtstreeks op je hoofdbranch. Een deployment wordt alleen gepromoot waar je dat hebt ingesteld, na een eigen goedkeuring.",
        },
      },
      principle: "Eerst de juiste informatie. Daarna een voorstel. Pas na controle een actie.",
      cta: "Volg één melding door alle stappen",
    },
    different: {
      title: "Waar FixControl past",
      items: {
        support: {
          title: "Meer dan support-AI",
          body: "Supporttools beantwoorden vragen. FixControl kan een klantmelding volgen tot in de code en terugkomen met een geteste wijziging.",
        },
        coding: {
          title: "Meer dan een codeerassistent",
          body: "Een codeerassistent begint bij de prompt van een developer. FixControl begint bij de melding zelf — van een klant of een bekende bug — met de eerdere tickets, documentatie en code van het project, en test de wijziging voordat iemand haar beoordeelt.",
        },
        unchecked: {
          title: "Geen AI die zelf zijn gang gaat",
          body: "Standaard wordt elke wijziging getest, door je team beoordeeld en vastgelegd voordat ze verder gaat — en ze komt binnen als pull request, nooit rechtstreeks op je hoofdbranch.",
        },
      },
    },
    sources: {
      title: "De kennis is er al. Ze staat alleen overal.",
      intro:
        "Het antwoord op een klantvraag staat zelden op één plek. Het zit in eerdere tickets, in de code, in een Slack-gesprek van vorige maand en in documentatie die niemand meer terugvindt.",
      items: {
        tickets: { title: "Tickets", body: "Eerdere meldingen en hoe ze zijn opgelost — in Freshdesk, Jira, Odoo of TOPdesk." },
        code: { title: "Code", body: "De repositories waarin het werk gebeurt, inclusief recente wijzigingen." },
        conversations: { title: "Gesprekken", body: "Slack- en Teams-kanalen waar afspraken en beslissingen langskomen." },
        mail: { title: "E-mail", body: "Klantvragen en afspraken in Gmail of Office 365." },
        docs: { title: "Documentatie", body: "Handleidingen, procedures en projectdocumenten die je koppelt of uploadt." },
        deployments: { title: "Deployments", body: "Wat er is uitgerold en wanneer — waar een gekoppeld systeem dat ondersteunt." },
        decisions: { title: "Vastgelegde beslissingen", body: "En achteraf: wie wat goedkeurde en waarom, vastgelegd naast het werk zodat je het terug kunt zoeken." },
      },
      outro:
        "AI zonder deze informatie gokt. FixControl geeft AI de juiste context per project — uit tickets, code en documentatie — voordat het iets voorstelt, en legt de beslissing die volgt vast.",
    },
    project: {
      title: "Jij bepaalt welke informatie bij een project hoort",
      intro:
        "Een project in FixControl is een afgebakende werkomgeving. Je koppelt er precies de bronnen aan die voor die taak relevant zijn: een mailbox, een Slack- of Teams-kanaal, een of meer repositories, documentatie.",
      exampleATitle: "Project: facturatie",
      exampleABody:
        "De supportmailbox, de facturatie-repository en de documentatie over facturen. Vragen over facturen worden beantwoord met déze informatie — niet met alles wat je organisatie ooit heeft opgeschreven.",
      exampleBTitle: "Project: mobiele app",
      exampleBBody:
        "Het app-kanaal in Slack, de app-repository en de release-documentatie. Een storing in de app wordt onderzocht met de code en meldingen van de app — niet met die van facturatie.",
      outro:
        "Zo werkt AI per project met de juiste informatie: niet alle informatie van je organisatie, maar alleen de informatie die voor die taak relevant en toegestaan is.",
    },
    layers: {
      title: "Zo zit FixControl tussen je systemen en je mensen",
      note: "Van onder naar boven: je systemen blijven de bron, je mensen houden het laatste woord.",
      l1: "Je bestaande systemen — Freshdesk, Jira, Odoo, TOPdesk, GitHub, Slack, Teams, e-mail",
      l2: "De informatie die bij een project hoort",
      l3: "Regels, controles en goedkeuring (governance)",
      l4: "Wat FixControl mag doen: meekijken · onderzoeken · voorstellen voorbereiden · goedgekeurde acties uitvoeren",
      l5: "Taken: support, engineering, deployment",
      l6: "Je mensen — zij beslissen",
      stepsTitle: "Wat FixControl doet, in zeven stappen",
      s1: "Je koppelt de systemen die je al gebruikt.",
      s2: "Per project kies je welke informatie erbij hoort.",
      s3: "FixControl onderzoekt met die informatie: het leest het ticket, de code en eerdere meldingen.",
      s4: "Vóór een actie controleert FixControl welke regels gelden.",
      s5: "Iemand van je team beslist — standaard over alles wat naar je klanten, repositories of productie gaat.",
      s6: "Pas daarna voert FixControl de goedgekeurde actie uit: een antwoord, een pull request, een deployment-stap.",
      s7: "Alles is terug te vinden: wie besloot, waarom, en op basis van welke informatie.",
    },
    example: {
      label: "Fictief maar representatief voorbeeld — geen bestaande klant",
      title: "Een klant van je organisatie meldt dat de factuurexport vastloopt. Wat gebeurt er dan?",
      moments: {
        m1: {
          tag: "Melding",
          title: "De melding komt binnen",
          body:
            "De klant mailt je servicedesk: “De factuurexport crasht bij grote bestanden.” Het ticket staat in Freshdesk en blijft daar gewoon staan.",
        },
        m2: {
          tag: "Project",
          title: "FixControl herkent het project",
          body:
            "De mailbox is gekoppeld aan het project Facturatie. FixControl ziet bij welk project en welke klant de melding hoort.",
        },
        m3: {
          tag: "Onderzoek",
          title: "FixControl onderzoekt met projectinformatie",
          body:
            "Het leest eerdere meldingen over de export, de documentatie over facturen, de code van de exportfunctie en de wijziging van vorige week — en wijst de waarschijnlijke oorzaak aan.",
        },
        m4: {
          tag: "Voorstel",
          title: "AI bereidt twee dingen voor",
          body:
            "Een conceptantwoord aan de klant en een codewijziging die de waarschijnlijke oorzaak aanpakt. Er is nog niets verstuurd of aangepast.",
        },
        m5: {
          tag: "Test",
          title: "De wijziging wordt eerst getest",
          body:
            "De codewijziging draait in een afgeschermde testomgeving — en waar die omgeving weet hoe de applicatie start, moet de applicatie opkomen en antwoord geven voordat de run als groen telt. Het conceptantwoord wordt gecontroleerd op gevoelige informatie.",
        },
        m6: {
          tag: "Beslissing",
          title: "Iemand van je team beslist",
          body:
            "De reviewer — iemand van je eigen team — ziet het voorstel en de testresultaten en keurt goed, wijst af of vraagt een aanpassing. Standaard vertrekt er niets naar je klant of naar productie zonder deze vastgelegde beslissing.",
        },
        m7: {
          tag: "Uitvoering",
          title: "Pas nu gebeurt er iets",
          body:
            "Het antwoord gaat naar de klant. De codewijziging vertrekt als pull request — niet rechtstreeks naar je hoofdbranch. En alles is terug te vinden: wie besloot, waarom, op basis waarvan.",
        },
      },
      close: "Freshdesk bleef Freshdesk, en de wijziging kwam in GitHub binnen als een gewone pull request. Er kwam alleen controle en overzicht bij.",
      engineering: {
        label: "Een tweede voorbeeld — dit keer begint het bij engineering",
        title: "Een developer merkt dat de retry-logica sommige webhooks twee keer verstuurt.",
        steps: {
          s1: "De developer legt de bug vast in FixControl. Er komt geen supportticket aan te pas.",
          s2: "FixControl vindt het codepad van de retry en de recente wijzigingen daarin.",
          s3: "Het stelt een patch voor de waarschijnlijke oorzaak voor.",
          s4: "De build en tests van het project draaien in de afgeschermde sandbox.",
          s5: "De developer ziet de patch en de testresultaten, keurt goed — en de wijziging komt binnen als pull request.",
        },
      },
    },
    integrations: {
      label: "Geen migratie",
      title: "FixControl werkt bovenop de systemen die je al gebruikt — en vervangt geen van alle.",
      names: "Freshdesk · Jira · Odoo · TOPdesk · GitHub · GitLab · Slack · Teams · Gmail · Office 365",
      linkLabel: "Bekijk alle integraties",
    },
    video: {
      eyebrow: "In één minuut",
      title: "Bekijk hoe FixControl werkt",
      intro:
        "Van verspreide informatie naar een voorbereid voorstel, controles op regels en tests, en iemand van je team die beslist.",
      posterCaption: "Bekijk FixControl in 60 seconden",
      points: {
        p1: "Alleen de tickets, code en documenten die bij het project horen",
        p2: "Gespecialiseerde AI-hulpen (agents) voor tickets, antwoorden en codewijzigingen",
        p3: "Regels en controles voordat er iets naar je klanten of repositories gaat",
        p4: "Iemand van je team beslist waar dat nodig is",
      },
    },
    appShot: {
      title: "Dit is het echte product",
      body: "De flow hierboven, in de echte interface: het FixControl-controlecentrum op demodata — een echte schermafbeelding, geen mockup.",
      caption: "Home — het werk van vandaag",
      alt: "Het FixControl-startscherm met gekoppelde bronnen, openstaand werk en projectstatus",
      cta: "Bekijk meer schermen van het product",
    },
    valueBand: {
      title: "Wat is een kortere weg van melding naar oplossing waard voor je team?",
      body: "Minder zoekwerk in support, sneller herstel in engineering, en achter elke actie een vastgelegde beslissing. Reken uit wat dat voor je team betekent.",
      ctaCalc: "Bereken je potentiële waarde",
      ctaDemo: "Bekijk de demo van 60 seconden",
    },
    demos: {
      title: "Bekijk FixControl voor je team",
      intro: "Twee demo's: één voor klantvragen en e-mail, één voor bugs en code.",
      support: {
        title: "Support- en e-mailautomatisering",
        body: "Van klantvraag naar een gecontroleerd antwoord, met de juiste tickets, gesprekken en documentatie.",
        cta: "Bekijk de supportdemo",
      },
      engineering: {
        title: "Bug- en codeautomatisering",
        body: "Van bugmelding naar een getest wijzigingsvoorstel en een pull request na goedkeuring.",
        cta: "Bekijk de engineeringdemo",
      },
    },
    useCases: {
      title: "Bugfixes zijn één voorbeeld. Dezelfde regels gelden voor meer.",
      intro:
        "Elk project krijgt een doel. Klantantwoorden, codewijzigingen, releases en vragen over je documentatie lopen door dezelfde controles, goedkeuringen en vastleggingen. Dit zijn de taken die FixControl vandaag aankan.",
      items: {
        support: {
          title: "Support",
          body:
            "Vragen over tickets beantwoorden vanuit Slack of Teams en conceptantwoorden aan je klanten voorbereiden, in je huisstijl. Iemand van je team controleert standaard wat naar de klant gaat.",
        },
        engineering: {
          title: "Engineering",
          body:
            "Van een klantmelding of een bekende bug naar een geteste codewijziging, geleverd als pull request — nooit rechtstreeks naar je hoofdbranch.",
        },
        deployment: {
          title: "Deployment",
          body:
            "Een goedkeuring zet een gepauzeerde uitrol door, met vastlegging van wie besloot — alleen beschikbaar voor gekoppelde systemen die dit ondersteunen.",
        },
        security: {
          title: "Security",
          body:
            "Onderzoek is alleen-lezen en staat achter goedkeuring; elke voorgestelde wijziging krijgt een risicoklasse voordat iemand beslist.",
        },
        knowledge: {
          title: "Kennisbank",
          body:
            "Antwoorden op vragen van medewerkers, voorbereid met de documentatie en projectbronnen die je koppelt.",
        },
      },
      linkLabel: "Verken alle use-cases",
    },
    control: {
      title: "AI heeft nu de juiste informatie. Maar mag het ook iets doen?",
      intro:
        "Jij bepaalt wat AI zelfstandig mag doen en wanneer een mens moet beslissen — met regels, controles en goedkeuring (governance).",
      rules: {
        r1: "Een antwoord aan je klant vertrekt standaard pas na goedkeuring door iemand van je team.",
        r2: "Een risicovolle codewijziging vereist een geslaagde test in een afgeschermde omgeving, een applicatie die nog start, én een senior reviewer.",
        r3: "Een deployment doorzetten kan alleen na een vastgelegde goedkeuring — waar dit is geconfigureerd.",
        r4: "Alleen-lezen onderzoek binnen het project mag AI zonder goedkeuring doen; acties naar buiten niet.",
      },
      outro:
        "FixControl controleert deze regels voordat een actie wordt uitgevoerd en legt iedere beslissing vast.",
      ladder: {
        title: "Begin veilig en breid later uit",
        steps: {
          observe: {
            title: "Alleen meekijken",
            body: "FixControl leest mee met tickets en code en vat samen wat er speelt. Er verandert niets.",
          },
          analyze: {
            title: "Onderzoeken",
            body: "FixControl zoekt oorzaken: het verbindt een melding met eerdere tickets en recente wijzigingen in de code.",
          },
          prepare: {
            title: "Voorstellen voorbereiden",
            body: "FixControl schrijft conceptantwoorden en bereidt codewijzigingen voor — klaar om te beoordelen, nog nergens verstuurd.",
          },
          external: {
            title: "Goedgekeurde acties uitvoeren",
            body: "Pas na goedkeuring vertrekt een antwoord, pull request of deployment-stap. Deze stap staat standaard achter een menselijke beslissing; uitzonderingen stel je zelf expliciet in.",
          },
        },
        outro:
          "Je hoeft niet direct alles aan te zetten. Begin met meekijken en onderzoeken, en voeg de volgende stap pas toe wanneer je team het vertrouwt.",
      },
    },
    boundaries: {
      title: "FixControl vervangt je bestaande systemen niet",
      items: {
        workflow: {
          title: "Geen workflowbouwer",
          body:
            "Een workflowtool voert stappen uit die iemand vooraf heeft gebouwd. FixControl verzamelt eerst de informatie van het project en controleert daarna de regels en de goedkeuring voor het voorgestelde werk — van ticket tot pull request.",
          link: "Vergelijk met workflow-automatisering",
        },
        search: {
          title: "Geen algemene zoekmachine",
          body:
            "Een zoeklaag of AI-assistent zoekt in alles waar hij bij kan en formuleert een antwoord. FixControl gebruikt alleen de bronnen die bij het project horen — en verbindt een antwoord of wijziging aan controle en goedkeuring.",
          link: "Vergelijk met enterprise search",
        },
        platform: {
          title: "Geen nieuw ticketsysteem of codeplatform",
          body:
            "Je tickets blijven in Freshdesk, Jira, Odoo of TOPdesk, en wijzigingen komen in GitHub of GitLab binnen als gewone pull requests. Er is geen nieuw systeem om naartoe te migreren; je team blijft werken waar het al werkt.",
          link: "Vergelijk met ServiceNow",
        },
      },
      outro:
        "FixControl verbindt de informatie die nodig is, bereidt het werk voor en controleert wat ermee mag gebeuren.",
    },
    trust: {
      title: "Controleer ons voordat je ons vertrouwt",
      checkTitle: "Wat je vandaag zelf kunt controleren",
      checks: {
        c1: "Hoe FixControl is gebouwd en beveiligd staat op de security-pagina: isolatie per organisatie, versleuteling, tweestapsverificatie, SSO (OIDC) en SCIM — gebouwd als schaalbare multi-tenant SaaS.",
        c2: "Wat af is, wat in preview is en wat gepland is, staat open op de productpagina — zonder “binnenkort”-beloftes.",
        c3: "Elke goedkeuring wordt vastgelegd met wie besliste, waarom en op welk bewijs, in een hash-geketend logboek dat de database zelf niet laat bewerken. Op Pro en Enterprise exporteer je het als ondertekend bestand dat aantoont dat het na export niet is gewijzigd; op de gehoste dienst worden auditgebeurtenissen een jaar bewaard.",
        c4: "Prijzen staan openbaar op de prijzenpagina.",
      },
      notYetTitle: "Wat er nog niet is — en hoe we daarmee omgaan",
      notYet: {
        n1: "Deployment-gates zijn pilotfunctionaliteit: waar je er een configureert, legt FixControl een oordeel vast over een pauze die je pipeline al vasthoudt — het start, maakt of draait zelf nooit een deployment terug. Onboarding is gecontroleerd en stapsgewijs, omdat we met gevoelige systemen koppelen: elke uitrol wordt eerst gevalideerd.",
        n2: "SOC 2- of ISO-certificeringen zijn er nog niet. Klanten beoordelen de architectuur, de controles en de vastlegging van beslissingen rechtstreeks.",
      },
      faqTitle: "Drie vragen die lezers ons stellen",
      faq: {
        q1: "Hoe komt al die informatie bij elkaar?",
        a1: "Per project koppel je de bronnen die erbij horen: een mailbox, een Slack- of Teams-kanaal, een ticketsysteem, een repository, documentatie. Geen enkele bron is verplicht. FixControl leest daaruit mee; er gaat pas iets terug — een antwoord, een statusupdate — na de goedkeuring die je regels vereisen.",
        q2: "Wie mag wat zien en doen?",
        a2: "Toegang volgt je eigen indeling: rechten per project en per rol, en gevoelige categorieën — zoals HR-vragen — zijn extra afgeschermd tot toegewezen beoordelaars. Ook de AI valt onder die grenzen: wat FixControl in een project mag (meekijken, onderzoeken, voorstellen voorbereiden, goedgekeurde acties uitvoeren) stel je per project in, en elke actie wordt vastgelegd.",
        q3: "Wat als AI het fout heeft?",
        a3: "Daar rekenen we op — daarom vertrekt er standaard niets zonder beoordeling door iemand van je team: wijs je af of vraag je een aanpassing, dan is er niets gebeurd. Blijkt een wijziging achteraf toch mis — bijvoorbeeld omdat je team de productie-deploy terugdraaide — dan kan FixControl het gekoppelde Freshdesk-ticket heropenen, waar je dat aanzet, zodat support weet dat het probleem terug is. En valt een koppeling of controle uit, dan doet FixControl liever niets dan gokken: storingen zijn per onderdeel zichtbaar, niet verstopt achter één groen vinkje.",
      },
      partner: {
        title: "Een begeleide start, per koppeling gevalideerd",
        body: "Elk nieuw team start met begeleide onboarding: we valideren elke koppeling op je eigen data voordat je team erop bouwt. Start je nu, dan werk je tijdens de uitrol rechtstreeks met ons samen — en wat je team daarna nodig heeft, landt op de roadmap.",
        cta: "Bespreek een begeleide start",
      },
    },
    finalCta: {
      title: "Geef AI de context van één echt project.",
      body:
        "Koppel je tickets, code of documentatie, begin met meekijken en onderzoeken, en bekijk zelf wat FixControl ziet. Voorstellen en goedgekeurde acties zet je pas aan wanneer je team daar klaar voor is. De begeleide onboarding bewijst elke koppeling voordat je erop bouwt.",
      ctaPrimary: "Maak je eerste project",
      ctaSecondary: "Plan een pilot op je eigen code",
      pilotNote:
        "Een begeleide pilot begint klein: één koppeling, repository-toegang die we samen afbakenen, en vooraf afgesproken goedkeuringsregels en succescriteria om uit te breiden. De pilot wordt verrekend met een jaarcontract als je doorgaat.",
    },
    footer: {
      tagline: "AI bereidt voor. Jouw mensen beslissen — dat is de standaard. Elke beslissing wordt vastgelegd.",
      product: "Product",
      resources: "Informatie",
      app: "App",
      contact: "Contact",
      links: {
        product: "Productoverzicht",
        howItWorks: "Hoe het werkt",
        example: "Voorbeeld",
        integrations: "Integraties",
        safety: "Regels en goedkeuring",
        review: "Use-cases",
        pricing: "Prijzen",
        docs: "Documentatie",
        security: "Security",
        api: "API",
        login: "Inloggen",
        tryFree: "Gratis starten",
        intake: "Probeer met je eigen melding",
        demo: "Plan een demo",
        contactUs: "Neem contact op",
        privacy: "Privacy",
        terms: "Voorwaarden",
      },
      copyright: "© {year} FixControl. Alle rechten voorbehouden.",
      built: "Voor organisaties die AI willen inzetten — met de juiste informatie, en standaard een goedkeuring vóór een actie.",
      legal: "FixControl is een handelsnaam van {legal} · KvK {kvk} · BTW {vat}",
      legalPreRegistration: "FixControl is een handelsnaam van {legal}",
    },
  },



  // Vertaalde tegenhanger van de effective-decision summary-zinnen die
  // `resolveEffectivePolicy` in het Engels bouwt voor server-side surfaces.
  // Geconsumeerd via src/lib/governance/summary-format.ts (novice-audit D-12).
  governanceSummary: {
    autoExecute: "Voert automatisch uit ({risk} risico, geen goedkeuringspoort).",
    draftOnly: "Voorbereid voor menselijke verzending ({risk} risico).",
    approvalRequired: "Goedkeuring vereist: {roles} ({risk} risico).",
    humanSignOff: "menselijke ondertekening",
    blocked: "Geblokkeerd door beleid ({risk} risico).",
    risk: { low: "laag", medium: "gemiddeld", high: "hoog", critical: "kritiek" },
    outcome: {
      auto_execute: "Automatisch uitvoeren",
      draft_only: "Alleen concept",
      approval_required: "Goedkeuring vereist",
      blocked: "Geblokkeerd door beleid",
    },
    signals: {
      escalates: "Escaleert na {minutes}m",
      requires: "Vereist {list}",
      recordsOne: "Legt 1 audit-event vast",
      recordsMany: "Legt {count} audit-events vast",
      conflict: "Conflict op {field}: {winner} wint",
      verificationTip: "Verificatiepoorten nog niet voldaan",
    },
    gates: {
      dryRunRecommended: "Dry-run aanbevolen",
      approvalRequired: "Goedkeuring vereist",
    },
    inboxOutcome: {
      suggestOnly: "Alleen suggestie",
      draft: "Automatisch concept",
      send: "Automatisch verzonden",
      blocked: "Geblokkeerd door beleid",
    },
    requestApproval: "Vraag goedkeuring aan: {label}",
    checkFailed: "Governance-check mislukt — actie geblokkeerd",
    checkFailedShort: "Governance-check mislukt",
    whyCount: "Waarom? ({count})",
    resolving: "Beleid bepalen…",
    approvers: "Goedkeurders:",
  },

  settingsLayout: {
    sectionLabel: "Instellingen",
    groups: {
      getStarted: "Aan de slag",
      peopleAccess: "Mensen & toegang",
      supportComms: "Support & communicatie",
      aiApproval: "AI & goedkeuring",
      engineering: "Engineering",
      security: "Beveiliging",
      advanced: "Geavanceerd",
      platform: "Platformbeheer",
    },
    tabs: {
      overview: "Overzicht & voortgang",
      profile: "Profiel",
      account: "Account",
      infrastructure: "Infrastructuur",
      integrations: "Kanalen",
      codebases: "Codebases",
      ai: "AI-providers",
      agentBehavior: "Agentgedrag",
      governance: "AI & goedkeuring",
      billing: "Abonnement en facturatie",
      navigationPolicy: "Navigatiebeleid",
      inboxMailboxes: "Mailboxen",
      outboundEmail: "Uitgaande e-mail",
      websiteChat: "Websitechat",
      replyStyle: "Klantcommunicatie",
      replyTemplates: "Antwoordtemplates",
      devops: "DevOps",
      users: "Gebruikers",
      system: "Systeem",
      security: "Mijn beveiliging",
      securityOrganization: "Organisatiebeveiliging",
      securityPlatform: "Platformbeveiliging",
      audit: "Audit",
      demo: "Oefenomgeving",
    },
    advancedToggle: {
      show: "Geavanceerde instellingen tonen",
      hide: "Geavanceerde instellingen verbergen",
      hint: "Verborgen onderdelen blijven gewoon bereikbaar via een directe link.",
    },
    status: {
      completed: "Voltooid",
      action_needed: "Actie nodig",
      optional: "Optioneel",
      not_used: "Niet gebruikt",
      blocked_prerequisite: "Beschikbaar nadat {prerequisite} is ingesteld",
      connection_error: "Verbinding werkt niet",
      advanced: "Geavanceerd",
    },
  },

  settingsOverview: {
    eyebrow: "Instellingen",
    title: "Overzicht & voortgang",
    subtitleWithGoal: "Je doel: {goal}. Hieronder zie je wat al werkt, wat nog ontbreekt en wat je bewust hebt overgeslagen.",
    subtitleNoGoal: "Hieronder zie je wat al werkt, wat nog ontbreekt en wat je bewust hebt overgeslagen. Kies een doel via Installatie hervatten voor gerichte stappen.",
    progressTitle: "Installatie",
    nextStep: "Eerstvolgende aanbevolen stap",
    workingTitle: "Dit werkt al",
    workingEmpty: "Nog niets ingesteld — begin met de aanbevolen stap hierboven.",
    missingTitle: "Dit ontbreekt nog of heeft aandacht nodig",
    missingEmpty: "Niets — alles wat voor jouw doel nodig is, is ingesteld.",
    skippedTitle: "Bewust overgeslagen of niet gebruikt",
    skippedHint: "Optionele onderdelen. Je kunt ze altijd later alsnog instellen.",
    onboarding: {
      started: "installatie nog niet afgerond",
      postponed: "installatie uitgesteld",
      completed: "installatie afgerond",
    },
  },

  /**
   * Google Cloud-koppeling (settings/integrations/gcp).
   *
   * De beheerder koppelt zijn EIGEN Google Cloud-project zodat FixControl
   * logs en fouten kan lezen tijdens het onderzoeken van een bug. Alleen de
   * IAM-rolnamen blijven staan: die moet de beheerder letterlijk overtypen
   * in Google Cloud.
   */
  /**
   * Het AI-capaciteitscockpit voor platformbeheerders (/admin/ai-capacity).
   *
   * Een expertscherm voor FixControl-medewerkers, dus de cijfers houden hun
   * precieze namen (p50, contextvenster, outputbudget) — maar het blijft een
   * scherm met een taal, en een pagina die alleen in het Engels bestaat is een
   * pagina die de helft van de mensen die dit product draaien niet kan lezen.
   * De weigeringsteksten van de leverancier blijven letterlijk: dat is bewijs,
   * geen tekst.
   */
  adminAiCapacity: {
    eyebrow: "Beheer",
    title: "AI-capaciteit",
    subtitle: "De LLM-aanroepen van elke klant tegenover wat de modellen en het account toestaan: weigeringen, contextdruk, outputbudgetten.",
    windowLabel: "Tijdvenster",
    windowOption: "Laatste {label}",
    platformAdmin: "Platformbeheerder",
    deniedTitle: "Platformbeheerder vereist",
    deniedBody: "Dit cockpit is alleen voor medewerkers van FixControl.",
    loading: "Laden…",
    loadingAnnounce: "AI-capaciteitscijfers worden geladen",
    loadedAnnounce: "AI-capaciteitscijfers bijgewerkt",
    error: {
      titleStale: "Deze cijfers konden niet worden ververst",
      titleEmpty: "De capaciteitscijfers konden niet worden geladen",
      bodyStale: "Wat je hieronder ziet is de laatste meting die binnenkwam. Er is hierdoor niets veranderd aan de AI-laag — alleen deze pagina kon het niet uitlezen.",
      bodyEmpty: "De meting zelf mislukte, dus er valt nog niets te zeggen over weigeringen of contextdruk. Dit is een probleem van deze pagina, niet van de AI-laag.",
      retry: "Opnieuw proberen",
      retrying: "Opnieuw proberen…",
    },
    noConditions: "Er staat niets open in de laatste {hours} uur.",
    seenIn: "Gezien bij:",
    severity: { critical: "Kritiek", warn: "Waarschuwing" },
    stats: {
      calls: "LLM-aanroepen",
      failed: "Mislukt",
      inputTokens: "Inputtokens",
      outputTokens: "Outputtokens",
      cost: "Kosten (USD)",
      conditions: "Openstaande situaties",
    },
    models: {
      title: "Per model",
      sub: "Input tegenover het contextvenster van het model; completions die op het outputbudget stopten.",
      model: "Model",
      calls: "Aanroepen",
      failed: "Mislukt",
      inputP50: "Input p50",
      inputP95: "Input p95",
      inputMax: "Input max",
      context: "Context",
      pressure: "Druk",
      truncated: "Afgekapt",
      outputMax: "Output max",
      cost: "Kosten",
      empty: "Geen aanroepen in dit venster.",
    },
    tenants: {
      title: "Per klant",
      sub: "Wie er aanroept, en wie er wordt geweigerd.",
      tenant: "Klant",
      calls: "Aanroepen",
      failed: "Mislukt",
      input: "Input",
      inputMax: "Input max",
      cost: "Kosten",
    },
    kinds: {
      title: "Weigeringen per soort",
      sub: "Status, fouttype van de leverancier en de diagnostische slug — nooit proza van de leverancier.",
      empty: "Geen fouten in dit venster.",
    },
    largest: {
      title: "Grootste prompts",
      sub: "De aanroepen die het dichtst bij het contextvenster van een model zaten.",
      when: "Wanneer",
      tenant: "Klant",
      flow: "Flow",
      model: "Model",
      input: "Input",
      context: "Context",
      pressure: "Druk",
    },
    failures: {
      title: "Recente fouten",
      sub: "Nieuwste eerst, maximaal 50.",
      when: "Wanneer",
      tenant: "Klant",
      flow: "Flow",
      model: "Model",
      error: "Weigering, zoals de leverancier hem schreef",
      empty: "Geen fouten in dit venster.",
    },
  },

  /**
   * "Wat zou FixControl lezen om dit te beantwoorden?" — de zoekproef op de
   * kennispagina. Geschreven voor wie wil weten of de agents het juiste
   * materiaal vinden, niet voor wie een ranking-functie leest.
   */
  /**
   * De opgeslagen AI-suggestie bij een inboxregel, zoals een supportmedewerker
   * die tussen tickets door leest. De waarden van de classificatie (bug /
   * feature / support, kritiek…laag) krijgen een woord; de eigen beleids- en
   * guardrailnamen van de organisatie kan niemand behalve de organisatie zelf
   * vertalen, dus die staan er als regelnamen en niet als proza.
   */
  inboxAiSuggestion: {
    title: "Wat FixControl voorstelt",
    subtitle: "Opgesteld volgens je antwoordbeleid — er gaat niets weg zonder jou",
    grounded: "Beantwoord vanuit {count} bron(nen) in je kennis",
    notGrounded: "Niet onderbouwd door iets in je kennis",
    groundingKnowledge: "Zekerheid gebaseerd op {count} passende kennisbron(nen), beste match {best}%.",
    groundingNoMatch: "Lage zekerheid: niets in je kennis past duidelijk bij deze vraag (beste match {best}%).",
    groundingEarlierResolution: "Zekerheid alleen gebaseerd op een eerdere oplossing, dus hooguit gemiddeld.",
    groundingUnscored: "Zekerheid gebaseerd op context die niet voor deze vraag is gerangschikt, dus hooguit gemiddeld.",
    groundingNone: "Lage zekerheid: niets in je kennis past bij deze vraag.",
    groundingLowered: "Lager omdat {reasons}.",
    groundingReason: {
      single_supporting_source: "maar één bron het onderbouwt",
      single_retriever: "maar één zoekmethode de beste match vond",
      weak_semantic_match: "de beste match inhoudelijk maar losjes aansluit",
      top_hit_stale: "de beste match met een ouder model is geïndexeerd",
      retriever_agreement_unknown: "de zoekmethoden niet onderling te controleren waren",
    },
    intent: "Lijkt op: {value}",
    intentValue: {
      bug: "er is iets stuk",
      feature: "een verzoek om iets nieuws",
      support: "een vraag",
    },
    priority: "Urgentie: {value}",
    priorityValue: {
      critical: "kritiek",
      high: "hoog",
      medium: "normaal",
      low: "laag",
    },
    reasonsTitle: "Waarom het beleid dit besloot",
    reasonsLead: "Wat de AI hier wel en niet mocht, en waarom.",
    reason: {
      policy_off: "De AI staat uit voor deze mail.",
      mode_suggest_only: "De AI mag hier alleen een antwoord voorstellen.",
      mode_auto_draft: "De AI mag hier alleen een concept klaarzetten.",
      mode_auto_send_low_risk: "De AI mag hier zelf antwoorden met laag risico versturen.",
      low_confidence: "De zekerheid is lager dan je minimum voor zelf versturen.",
      risk_not_low: "Het risico is niet laag genoeg om zelf te versturen.",
      no_kb_grounding: "Het antwoord steunt niet op een bron uit de kennisbank.",
      intent_blocked_global: "Dit soort vraag gaat nooit automatisch (klacht, bug, terugbetaling, juridisch, beveiliging).",
      intent_blocked_policy: "Je beleid sluit dit soort vraag uit van automatisch versturen.",
      intent_not_allowed: "Dit soort vraag staat niet in de lijst die zelf verstuurd mag worden.",
      intent_not_autosendable: "Alleen vragen en hoe-doe-ik-vragen gaan automatisch.",
      category_blocked: "Deze categorie gaat nooit automatisch.",
      angry_sentiment: "De klant klinkt boos; een mens antwoordt.",
      human_requested: "De klant vraagt om een mens.",
      max_autoreplies_reached: "Het maximum aantal automatische antwoorden per dag in dit gesprek is bereikt.",
      empty_body: "Het voorgestelde antwoord is leeg.",
      missing_recipient: "Er is geen afzender om naar te antwoorden.",
      provider_unavailable: "De mailbox kan nu niet versturen.",
      human_sends: "Een mens verstuurt dit antwoord.",
      workspace_ai_disabled: "AI staat uit voor deze organisatie.",
      out_of_office_hours: "Buiten de kantooruren uit je beleid.",
      disclosure_missing: "De verplichte AI-melding ontbreekt in het antwoord.",
      defers_to_team: "Het antwoord laat een deel aan het team over. Dat wordt nooit automatisch verstuurd: maak het af of stuur het zelf.",
      internal_knowledge: "Het antwoord steunt mede op interne documentatie uit de code. Lees het na voordat het naar de klant gaat.",
      awaiting_approval: "Het antwoord wacht op goedkeuring.",
      already_sending: "Er wordt al een antwoord verstuurd.",
      draft_unavailable: "Het concept was niet meer beschikbaar.",
      approval_stage_failed: "Het antwoord kon niet ter goedkeuring worden aangeboden.",
    },
    replyTitle: "Voorgesteld antwoord",
    insert: "Zet dit in mijn concept",
    accept: "Goede suggestie",
    reject: "Verkeerde suggestie",
  },

  knowledgeRetrievalPreview: {
    title: "Kijk wat FixControl zou lezen",
    sub: "Stel een vraag zoals een klant die zou stellen. Dit draait de zoekfunctie van de agents zelf en toont wat zij zouden vinden — er wordt niets gewijzigd of verstuurd.",
    inputLabel: "De vraag om te proberen",
    placeholder: "bijv. Waarom wordt mijn factuur niet verstuurd?",
    ask: "Proberen",
    asking: "Zoeken…",
    resultCount: "{n} resulta(a)t(en) gevonden",
    failedTitle: "De zoekopdracht kon niet worden uitgevoerd",
    failedBody: "Er is niets gewijzigd — dit is een probleem met het uitlezen van je kennis, niet met de kennis zelf. Probeer het zo opnieuw.",
    failed: "Kon de zoekopdracht niet uitvoeren ({status}).",
    excerptTruncated: "…",
    staleEmbedding: "Deze passage is geïndexeerd door een ouder model en scoort daardoor lager dan anders. Bij de volgende herindexering schuift hij omhoog.",
    /** Waarom een resultaat op die plek staat. De opgeslagen reden is
     *  primaryCodebase / project / tenantGlobal / …, en dat verklaart niets
     *  voor wie wil weten of zijn documentatie bereikbaar is. */
    reason: {
      primaryCodebase: "Staat bovenaan omdat het bij de hoofdcodebase van dit project hoort.",
      projectCodebase: "Staat hoog omdat het bij een codebase hoort die aan dit project is gekoppeld.",
      project: "Staat hoog omdat het bij dit project hoort.",
      tenantGlobal: "Meegenomen omdat het voor je hele workspace geldt.",
      outOfScope: "Meegenomen, maar laag gerangschikt: het hoort niet bij dit project en niet bij je gedeelde kennis.",
      unscoped: "Puur gerangschikt op hoe goed het past — de vraag was niet aan een project gekoppeld.",
    },
    idle: {
      title: "Probeer een echte vraag",
      hint: "Typ iets dat een klant daadwerkelijk zou vragen. Je ziet precies welke documenten de agents zouden lezen, in welke volgorde en waarom — vóórdat het op een echte melding aankomt.",
      ask: "Welke kennis gebruiken de agents om een vraag te beantwoorden?",
    },
    noHits: {
      title: "Niets in je kennis beantwoordt dit",
      hint: "De agents zouden hier niets voor hebben om mee te werken. Voeg het document toe dat dit beantwoordt, of verbreed wat dit project mag lezen.",
      ask: "Help me kennis toe te voegen die dit beantwoordt: {query}",
    },
    excluded: {
      title: "Niet bereikbaar voor deze vraag",
      /** Geteld, nooit beweerd — de proef meldt wat hij kon tellen. */
      rule: {
        tombstoned_owner: "{count} bron(nen) waarvan de eigenaar is verwijderd.",
        another_project: "{count} bron(nen) die bij een ander project horen.",
        tenant_global_not_requested: "{count} workspace-brede bron(nen), omdat deze vraag aan één project was gekoppeld.",
        not_in_selected_sources: "{count} bron(nen) buiten de selectie waar je naar vroeg.",
      },
      /** Het aantal zelf kon niet worden gelezen. "Er is niets uitgesloten" en
       *  "we hebben niet gekeken" zijn verschillende uitspraken. */
      unknown: {
        tombstoned_owner: "Bronnen waarvan de eigenaar is verwijderd (het aantal kon niet worden gelezen).",
        another_project: "Bronnen die bij een ander project horen (het aantal kon niet worden gelezen).",
        tenant_global_not_requested: "Workspace-brede bronnen, omdat deze vraag aan één project was gekoppeld (het aantal kon niet worden gelezen).",
        not_in_selected_sources: "Bronnen buiten de selectie waar je naar vroeg (het aantal kon niet worden gelezen).",
      },
    },
  },

  /**
   * De instellingen die een WORKSPACE zelf mag wijzigen — de tenant-helft van
   * het register, afgesloten met een beoordeelde lijst
   * (`TENANT_WRITABLE_SETTING_KEYS`, server/config/registry.ts).
   *
   * Twee toestanden die deze tekst uit elkaar moet houden, want het zijn
   * verschillende feiten: een workspace die de standaard VOLGT, en een
   * workspace met een EIGEN waarde die toevallig gelijk is. Alleen de tweede
   * stopt met de standaard mee te bewegen als een beheerder die wijzigt.
   */
  tenantSettings: {
    title: "Instellingen van deze workspace",
    subtitle: "Een handvol keuzes die je workspace zelf maakt. Al het andere wordt voor de hele FixControl-omgeving ingesteld door wie die beheert.",
    loading: "Instellingen van deze workspace worden gelezen\u2026",
    loadFailed: "Deze instellingen konden niet worden gelezen. Er is niets gewijzigd — probeer het zo opnieuw.",
    on: "Aan",
    off: "Uit",
    inherited: "Volgt de standaard",
    overridden: "Door deze workspace ingesteld",
    inheritedIs: "De standaard is {value}.",
    useDefault: "Weer de standaard gebruiken",
    resetting: "Terugzetten\u2026",
    askReproduction: "Wat doet FixControl als het een mislukte controle onderzoekt?",
    saving: "Opslaan\u2026",
    refusal: {
      setting_not_tenant_writable: "Dit is geen instelling die een workspace zelf kan wijzigen. Vraag wie deze FixControl-omgeving beheert om het te wijzigen.",
      invalid_value: "Die waarde accepteert deze instelling niet. Er is niets gewijzigd.",
      insufficient_role: "Alleen beheerders en managers kunnen een workspace-instelling wijzigen.",
      unknown: "De instelling kon niet worden opgeslagen. Er is niets gewijzigd.",
    },
    key: {
      SANDBOX_REPRODUCTION_SESSIONS: {
        label: "Fouten onderzoeken in de testomgeving",
        help: "Laat FixControl bij een mislukte controle rondkijken in de omgeving waarin het misging, voordat het de volgende poging schrijft. Kost ongeveer een minuut extra op een run die toch al mislukte.",
      },
      SANDBOX_PREVIEW_ENABLED: {
        label: "Mensen een draaiend voorbeeld laten openen",
        help: "Een priv\u00e9-link die verloopt naar een draaiende kopie van de applicatie met een voorgestelde wijziging erin, zodat een beoordelaar erdoorheen kan klikken v\u00f3\u00f3r goedkeuring.",
      },
      SANDBOX_AI_DISCOVERY: {
        label: "Uitzoeken hoe een applicatie draait",
        help: "Laat FixControl een repository lezen en uitzoeken hoe die gebouwd en gestart wordt, in plaats van te wachten tot iemand dat opschrijft.",
      },
      TENANT_DEFAULT_LOCALE: {
        label: "Taal voor berichten buiten de app",
        help: "Waarin FixControl schrijft als het een bericht buiten de app opstelt: goedkeuringskaarten in Slack of Teams, goedkeurings-e-mails, missie-statusberichten. Een ticket dat in een andere taal binnenkomt houdt die taal, en in de app volgt iedereen de eigen taalkeuze.",
      },
    },
  },

  settingsGcp: {
    eyebrow: "Gekoppelde systemen",
    title: "Google Cloud (alleen lezen)",
    subtitle: "Laat FixControl je Google Cloud-logs en -fouten lezen tijdens het onderzoeken van een bug. Alleen lezen, uit tot je het aanzet, en elke uitlezing is begrensd, gemaskeerd en vastgelegd in het auditspoor.",
    add: "Google Cloud-project koppelen",
    loading: "Je gekoppelde Google Cloud-projecten worden opgehaald…",
    loadFailed: {
      title: "De gekoppelde Google Cloud-projecten konden niet worden opgehaald",
      body: "Er is niets gewijzigd — alleen deze pagina kon de lijst niet lezen. Je koppelingen en hun instellingen zijn ongemoeid.",
    },
    empty: {
      title: "Er is geen Google Cloud-project gekoppeld",
      hint: "Koppel er één en FixControl kijkt naar de echte logs en fouten achter een bugmelding in plaats van te gissen op basis van de omschrijving. Jij bepaalt welke datasets en logs het mag lezen.",
      ask: "Wat kan FixControl doen als ik Google Cloud koppel?",
    },
    /**
     * Waarvoor de koppeling ECHT wordt gebruikt, uitgesproken in plaats van
     * afgeleid uit de velden. Cloud Logging en Error Reporting hebben een
     * afnemer; BigQuery nog niet, en wie een datasetlijst invult mag aannemen
     * dat iets het leest.
     */
    whatIsRead: {
      title: "Wat FixControl leest",
      and: "en",
      logs: "— worden automatisch gelezen bij een melding die als bug is aangemerkt, zodra deze koppeling tijdens onderzoek gelezen mag worden, en toegevoegd aan de notitie die de beoordelaar ziet. Bij elke uitlezing begrensd, gemaskeerd en vastgelegd.",
      notUsedYet: "nog niet in gebruik",
      bigQuery: "Niets in FixControl voert vandaag een query uit. De datasetlijst en de scangrens hieronder worden opgeslagen en afgedwongen, en de verbindingscontrole gaat na of de sleutel BigQuery kan bereiken — maar geen functie stelt er een vraag aan, dus ze instellen verandert nu niets.",
    },
    status: {
      pending: "Nog niet geverifieerd",
      connected: "Werkt",
      reconnect_required: "Nieuwe sleutel nodig",
      error: "Werkt niet",
      disabled: "Uitgezet",
    },
    form: {
      name: "Naam",
      nameHint: "Waaraan je dit project herkent in FixControl.",
      projectId: "Google Cloud-project-id",
      projectIdHint: "Optioneel — wordt uit de sleutel hieronder gehaald.",
      datasets: "BigQuery-datasets die FixControl mag lezen",
      /** De lijst en het plafond worden opgeslagen en afgedwongen, maar
       *  geen functie bevraagt BigQuery nog — wie er een invult mag
       *  aannemen dat iets het leest, dus het label zegt het. */
      datasetsNotUsedYet: "BigQuery-datasets die FixControl mag lezen — nog niet in gebruik",
      datasetsHint: "Gescheiden door komma's. Laat leeg en BigQuery blijft dicht — er is niets te bevragen.",
      logs: "Logs die FixControl mag lezen",
      logsHint: "Gescheiden door komma's. Laat leeg om elk log in dit project toe te staan.",
      scanCeiling: "Grootste BigQuery-scan (MiB)",
      scanCeilingNotUsedYet: "Grootste BigQuery-scan (MiB) — nog niet in gebruik",
      scanCeilingHint: "Een query die meer zou scannen wordt geweigerd vóór hij draait, zodat een diagnose nooit een grote rekening wordt.",
      key: "Servicedaccount-sleutel (JSON)",
      keyHint: "Eén keer geplakt en versleuteld opgeslagen. Hij wordt nooit meer getoond, hier niet en nergens anders.",
      iamIntro: "Geef dit serviceaccount in Google Cloud alleen leesrechten:",
      iamAnd: ", en voor BigQuery",
      iamBigQueryOptional: "De BigQuery-rollen zijn vandaag optioneel — niets in FixControl voert nog een query uit; geef ze alleen als je wilt dat de verbindingscontrole de BigQuery-toegang nagaat.",
      iamOutro: "FixControl vraagt nooit meer dan leestoegang.",
      saving: "Koppelen…",
      saveFailed: "Kon dit Google Cloud-project niet koppelen ({status}). Er is niets opgeslagen — controleer de sleutel en probeer opnieuw.",
    },
    row: {
      datasets: "BigQuery-datasets:",
      datasetsNotUsedYet: "BigQuery-datasets (nog niet in gebruik):",
      datasetsNone: "geen — BigQuery blijft dicht",
      logs: "Logs:",
      logsAny: "elk log in dit project",
      scanCeiling: "Grootste BigQuery-scan: {mib} MiB — een query die meer zou scannen wordt geweigerd vóór hij draait.",
      scanCeilingNotUsedYet: "Grootste BigQuery-scan: {mib} MiB — de grens waarboven een query zou worden geweigerd, zodra iets bevraagt.",
      noKey: "Er is geen sleutel opgeslagen voor dit project, dus er valt niets te lezen. Koppel het opnieuw met een nieuwe serviceaccount-sleutel.",
      lastErrorLead: "Google Cloud weigerde deze koppeling het laatst met:",
      allowAi: "FixControl dit laten lezen tijdens onderzoek",
      allowAiHint: "Uit betekent dat de agent niets uit dit project ziet, wat er ook in de lijsten staat.",
      disconnect: "Verwijderen",
      disconnecting: "Verwijderen…",
      confirmTitle: "{name} verwijderen?",
      confirmBody: "De opgeslagen serviceaccount-sleutel wordt vernietigd en FixControl stopt met het lezen van dit Google Cloud-project. In Google Cloud verandert niets. Wil je alleen het lezen stoppen maar de instellingen bewaren, zet het dan uit.",
      /**
       * Wat elke opgeslagen status BETEKENT voor de beheerder, gezegd waar de
       * badge dat niet kan: een koppeling wordt ongecontroleerd opgeslagen en
       * elke uitlezing weigert een rij die de test niet heeft doorstaan.
       */
      notVerified: "Nog niet gecontroleerd. Klik op \"Verbinding controleren\" — tot dat lukt leest FixControl niets uit dit project.",
      reconnectRequired: "Google weigerde de opgeslagen sleutel. Plak een nieuwe serviceaccount-sleutel onder Instellingen wijzigen en controleer opnieuw.",
      disabledNote: "Uitgezet — er wordt niets gelezen en niets gecontroleerd. Je instellingen blijven bewaard.",
      noKeyEdit: "Er is geen sleutel opgeslagen voor dit project, dus er valt niets te lezen. Plak een nieuwe onder Instellingen wijzigen.",
      test: "Verbinding controleren",
      testing: "Controleren…",
      edit: "Instellingen wijzigen",
      editClose: "Sluiten",
      disable: "Uitzetten",
      enable: "Aanzetten",
      saving: "Opslaan…",
    },
    /** De uitslag per onderdeel van een verbindingscontrole. Elk Google-product
     *  wordt apart getest, zodat een beheerder die Logging wel maar Error
     *  Reporting niet heeft toegekend precies dat te horen krijgt. */
    test: {
      capability: {
        connection: "Google Cloud bereiken",
        read_logs: "Cloud Logging lezen",
        read_errors: "Error Reporting lezen",
        query_bigquery: "BigQuery bereikbaar (nog niet in gebruik)",
      },
    },
    edit: {
      name: "Naam",
      projectId: "Google Cloud-project-id",
      scanCeiling: "Grootste BigQuery-scan (MiB, maximaal {max})",
      scanCeilingNotUsedYet: "Grootste BigQuery-scan (MiB, maximaal {max}) — nog niet in gebruik",
      replaceKey: "Serviceaccount-sleutel vervangen",
      replaceKeyHint: "Laat dit leeg om de al opgeslagen sleutel te behouden. Een nieuwe sleutel — of een ander project — is nog niet bij Google bewezen, dus de koppeling gaat terug naar ongecontroleerd tot je opnieuw controleert.",
      save: "Wijzigingen opslaan",
      saving: "Opslaan…",
      saveFailed: "Kon deze instellingen niet opslaan ({status}). Er is niets gewijzigd.",
    },
  },

  settingsDevops: {
    eyebrow: "AI-operaties-governance",
    title: "DevOps & CI/CD",
    subtitle: "Koppel je CI/CD-providers zodat FixControl deploy- en pipeline-events ontvangt, op risico beoordeelt en hash-geketend en append-only vastlegt. Phase 1 is observeren-en-governen: events worden beoordeeld, nooit uitgevoerd.",
    loading: "Laden…",
    loadError: "Kon DevOps-koppelingen niet laden",
    observeOnlyNote: "Observeren-en-governen: FixControl beoordeelt en logt deployments, maar triggert of rolt zelf nooit iets uit.",
    providersTitle: "Koppelingen",
    providersSub: "Koppel een provider en plak daarna de webhook-URL in de host. GitHub & GitLab gebruiken een echte OAuth/App-flow; Jenkins is een handmatige legacy-koppeling.",
    status: {
      notConnected: "Niet gekoppeld",
      connecting: "Koppelen",
      connected: "Gekoppeld",
      degraded: "Verminderd",
      invalidCredentials: "Ongeldige inloggegevens",
      revoked: "Ingetrokken",
      comingSoon: "Binnenkort",
      // Gezondheidsdegradaties van een "gekoppelde" kaart — zie en.ts.
      failing: "Werkt niet",
      requiresReconnect: "Opnieuw koppelen nodig",
      notVerified: "Gekoppeld — niet geverifieerd",
    },
    kind: {
      githubApp: "GitHub App",
      oauthApp: "OAuth-app",
      manual: "Handmatig",
    },
    legacyNote: "Handmatige legacy-koppeling — Jenkins heeft geen standaard voor ondertekende webhooks, dus FixControl verifieert een HMAC die je op een Notification-webhook instelt. Gebruik bij voorkeur GitHub of GitLab.",
    comingSoonNote: "Deze provider wordt nog niet ondersteund voor gegoverneerde deployments. Koppelen kan zodra de ingest beschikbaar is.",
    connectedAccount: "Gekoppeld account",
    scopesLabel: "Scopes",
    lastEvent: "Laatste event",
    never: "Nooit",
    eventsLabel: "Events",
    webhookHealth: "Webhook-gezondheid",
    lastErrorLabel: "Laatste fout",
    webhookUrl: "Webhook-URL",
    webhookUrlHelp: "Plak deze URL in de webhook-instellingen van de host.",
    webhookUrlPending: "Beschikbaar zodra een publieke origin is geconfigureerd.",
    projectIdLabel: "GitLab-project-ID",
    projectIdPlaceholder: "bijv. 12345678",
    projectIdHelp: "De numerieke project-ID waarvan je pipeline-/deploy-webhooks wilt governen.",
    accountIdLabel: "Instance- / job-identifier",
    accountIdPlaceholder: "bijv. deploy-prod",
    accountIdHelp: "Een identifier die de webhook meedraagt (de job-naam, of een X-FixControl-Account-header) zodat we hem aan deze koppeling kunnen binden.",
    apiTokenLabel: "API-token (optioneel)",
    apiTokenPlaceholder: "Versleuteld opgeslagen — nooit opnieuw getoond",
    apiTokenHelp: "Optioneel API-token, versleuteld opgeslagen. Het wordt bewaard voor toekomstige verrijking; er belt nog niets mee terug.",
    apiTokenPlaceholderJenkins: "gebruikersnaam:apiToken — versleuteld opgeslagen, nooit opnieuw getoond",
    apiTokenHelpJenkins: "Alleen nodig om FixControl een goedgekeurde input-stap te laten doorzetten of afbreken. Jenkins authenticeert met een gebruiker én een token, dus vul beide in als \"gebruikersnaam:apiToken\" — maak het token in Jenkins aan bij je naam (rechtsboven) → Security. Versleuteld opgeslagen; het account heeft Job/Build-rechten op de job nodig.",
    baseUrlLabel: "Instance-URL (optioneel)",
    baseUrlPlaceholderGitlab: "https://gitlab.example.com",
    baseUrlPlaceholderJenkins: "https://ci.example.com",
    baseUrlHelpGitlab: "Alleen voor een self-managed GitLab. Laat leeg voor gitlab.com. Moet via HTTPS vanaf internet bereikbaar zijn.",
    baseUrlHelpJenkins: "Het HTTPS-adres van je Jenkins, bereikbaar vanaf internet. Privé- en localhost-adressen worden geweigerd.",
    carriageLabel: "Schrijfroute",
    carriageDirect: "Direct — FixControl belt de host",
    carriageHelpDirect: "FixControl belt deze host zelf zodra een deploy-gate is besloten, dus de host moet vanaf internet bereikbaar zijn.",
    carriageHelpAgent: "De fc-agent voert de schrijfactie uit vanuit je eigen netwerk, dus FixControl belt deze host nooit — en de instance-URL wordt niet gebruikt. De agent belt alleen adressen uit zijn eigen configuratie.",
    carriageNoAgents: "Er zijn nog geen fc-agents geregistreerd. Meld er eerst een aan op de Kubernetes-koppeling; die kan daarna ook voor deze host schrijven.",
    carriageMissingCapability: "Deze agent adverteert ci.approve_deployment nog niet en kan een gepauzeerde deploy dus niet vrijgeven. Ken die capability toe op zijn registratie.",
    carriageAgentGone: "Agent niet meer beschikbaar",
    carriageEditHelp: "Wijzig waar een goedgekeurd deployment-oordeel wordt afgeleverd. Dit raakt het webhook-geheim niet — dat roteren blijft een aparte, bewuste actie.",
    carriageEditSecretNote: "Het webhook-geheim waarmee deze host ondertekent blijft ongewijzigd.",
    recentRuns: "Recente gegoverneerde runs",
    noRuns: "Nog geen gegoverneerde runs.",
    showEvents: "Recente events",
    hideEvents: "Events verbergen",
    secretReveal: {
      title: "Kopieer nu je webhook-geheim",
      note: "Dit wordt maar één keer getoond. Stel het in als het webhook-geheim van de host (GitLab: “Secret token”; Jenkins: de HMAC-sleutel voor de X-FixControl-Signature-header). Wij bewaren alleen een versleutelde kopie.",
      copy: "Geheim kopiëren",
      copied: "Gekopieerd",
      done: "Ik heb het opgeslagen",
    },
    cta: {
      connect: "Koppelen",
      connecting: "Koppelen…",
      disconnect: "Ontkoppelen",
      disconnecting: "Ontkoppelen…",
      rotateSecret: "Geheim roteren",
      rotating: "Roteren…",
      installGithub: "GitHub App installeren",
      connectGitlab: "GitLab koppelen",
      retry: "Opnieuw koppelen",
      addAnother: "Nog een koppeling toevoegen",
      editCarriage: "Aflevering wijzigen",
      saveCarriage: "Aflevering opslaan",
      savingCarriage: "Opslaan…",
      copy: "Kopiëren",
      copied: "Gekopieerd",
    },
    disconnectConfirm: "{provider} ontkoppelen? Opgeslagen inloggegevens worden gewist en inkomende webhooks worden geweigerd.",
    needsGithubInstall: "Installeer eerst de FixControl GitHub App en kom dan terug om Actions-governance af te ronden.",
    needsGitlabOAuth: "Koppel eerst GitLab (OAuth) en voer dan een project-ID in om de pipelines te governen.",
    agents: {
      title: "fc-agents",
      sub: "Een fc-agent draait in je eigen netwerk en belt naar buiten naar FixControl — er belt niets naar binnen. Meld er een aan op een Kubernetes-koppeling en wijs hem daarna hierboven aan als schrijfdrager op een GitLab CI- of Jenkins-koppeling.",
      loading: "Agents laden…",
      loadError: "Kon fc-agents niet laden",
      empty: "Nog geen fc-agents aangemeld.",
      // Twee voorwaarden die het formulier vroeger alleen toonde door te
      // falen — zie en.ts.
      emptyPrereqIntro: "Voordat je er een kunt aanmelden:",
      emptyPrereqCapability: "Zet de fc-agent-capability aan bij Instellingen → Governance → deployment-capabilities.",
      emptyPrereqConnection: "Koppel hierboven een Kubernetes-provider — een agent wordt op die koppeling aangemeld.",
      governanceCta: "Governance-instellingen openen",
      enrolRefusal: {
        agent_lane_disabled: "De fc-agent-capability staat nog niet aan voor deze werkruimte. Zet hem aan bij Instellingen → Governance → deployment-capabilities en meld de agent daarna aan.",
      },
      state: {
        live: "Actief",
        paused: "Gepauzeerd",
        revoked: "Ingetrokken",
      },
      // Aanmeldstatus en verbinding zijn verschillende feiten — zie en.ts.
      connectivityLabel: "Clusterverbinding",
      connectivityNeverConnected: "Nooit verbonden",
      clusterLabel: "Cluster",
      lastSeenLabel: "Laatste hartslag",
      neverSeen: "Nooit — de agent heeft zich nog niet gemeld",
      fingerprintLabel: "Vingerafdruk secret",
      enrolledLabel: "Aangemeld",
      capabilitiesLabel: "Geadverteerde capabilities",
      noCapabilities: "Geen — deze agent mag geen werk krijgen.",
      revokedNote: "Ingetrokken. Het gedeelde secret authenticeert niet meer en er wordt niets meer voor ondertekend. Meld een nieuwe agent aan om de drager van dit cluster te herstellen.",
      signingKeysLabel: "Te pinnen ondertekensleutels",
      noSigningKeys: "Deze omgeving heeft nog geen platform-ondertekensleutel, dus operaties worden met het eigen gedeelde secret van de agent ondertekend en er valt niets te pinnen.",
      // Geen repo-commando meer — zie en.ts.
      signingKeysHelp: "Publieke sleutels — ze kunnen niets ondertekenen. Zet ze in de ConfigMap van de agent zodat hij elke operatie weigert die FixControl niet heeft geautoriseerd. FixControl genereert en roteert deze sleutels; neem contact op met support als een rotatie nodig is.",
      keyPrimary: "Primair",
      keyStaged: "Klaargezet",
      package: {
        label: "Installatiepakket",
        unpublished: "Het installatiepakket krijg je van je onboardingcontact. Het bevat de manifests, een idempotente install.sh met één values-bestand, en verify.sh — plus rotate, revoke en uninstall.",
        download: "Download fc-agent {version} (.tar.gz)",
        releaseNotes: "Releasenotes",
        checksum: "Checksum",
        imagesHint: "Images zijn digest-gepind; install.sh weigert een tag. Plak de twee regels uit",
      },
      values: {
        label: "values.env — plak dit in het pakket",
        hint: "Dit is de identiteitshelft van het bestand. De allowlists, egress-bestemmingen en promotiemodus bepaal je zelf; ze staan toegelicht in values.example.env. FixControl kiest ze niet en kan ze niet verruimen.",
      },
      rotateConfirm: "Secret van {name} roteren? Het oude secret werkt direct niet meer, dus de draaiende agent is doof totdat je zijn Kubernetes-Secret bijwerkt en hem herstart.",
      revokeConfirm: "{name} intrekken? Dit is definitief: het gedeelde secret sterft, elke koppeling die deze agent draagt geeft geen deployments meer vrij, en een ingetrokken agent kan niet worden hervat — alleen vervangen.",
      reveal: {
        title: "Kopieer de installatiewaarden nu",
        titleRotated: "Kopieer het nieuwe secret nu",
        note: "Het secret wordt maar één keer getoond en is daarna nooit meer op te vragen. De rest hieronder blijft zichtbaar in de lijst — maar de agent heeft alles samen nodig om te starten.",
        secretLabel: "Gedeeld secret",
        agentIdLabel: "Agent-ID",
        clusterLabel: "Cluster-ID",
        fcUrlLabel: "FixControl-URL",
        noFcUrl: "Voor deze omgeving is geen publieke origin ingesteld, dus er is nog geen URL om aan de agent te geven. Stel er eerst een in.",
        done: "Ik heb het opgeslagen",
      },
      form: {
        nameLabel: "Naam van de agent",
        namePlaceholder: "bijv. prod-eu-agent",
        nameHelp: "Letters, cijfers en koppeltekens. Dit is ook de naam van de Deployment in het cluster. Een bestaande naam hergebruiken roteert het secret van die agent in plaats van een tweede aan te maken.",
        nameRequired: "Geef de agent een naam.",
        clusterLabel: "Cluster-ID",
        clusterPlaceholder: "bijv. prod-eu-1",
        clusterHelp: "Je eigen aanduiding voor het cluster. Elke autorisatie wordt voor precies deze waarde ondertekend, en een agent weigert werk dat voor een ander cluster is ondertekend.",
        clusterRequired: "Geef het cluster een aanduiding.",
        connectionLabel: "Kubernetes-koppeling",
        connectionPlaceholder: "Kies een koppeling",
        connectionHelp: "De agentregistratie hoort bij een Kubernetes-koppeling — daar horen de clusterbinding en het gedeelde secret thuis.",
        capabilitiesLabel: "Capabilities",
        capabilitiesHelp: "Standaard wordt niets toegekend. Elke capability is een bevoegdheid die deze agent krijgt; een deployment vrijgeven en er een weigeren zijn bewust aparte toekenningen.",
        capabilityRequired: "Ken minstens één capability toe.",
        noK8s: "Koppel eerst de Kubernetes-integratie — een fc-agent wordt op een Kubernetes-koppeling aangemeld.",
      },
      cta: {
        enrol: "Agent aanmelden",
        enrolling: "Aanmelden…",
        rotate: "Secret roteren",
        revoke: "Intrekken",
        pause: "Pauzeren",
        resume: "Hervatten",
      },
    },
    agentOps: {
      title: "Getekende operaties",
      sub: "De getekende, eenmalige autorisaties die de fc-agent inwisselt — de schakel tussen een beslissing hier en een write binnen jullie netwerk. Afgeronde regels worden na ~24 uur opgeschoond.",
      empty: "Geen operaties in de afgelopen 24 uur.",
      loadError: "Kon operaties niet laden",
      loading: "Recente operaties worden geladen…",
      refusal: "Geweigerd: {reason}",
      pinnedAny: "elke geschikte agent",
      cols: { operation: "Operatie", state: "Status", agent: "Agent", cluster: "Cluster", created: "Aangemaakt" },
      copyId: "Id kopiëren",
      copied: "Gekopieerd",
      // Zie en.ts.
      states: {
        registered: "Wacht op de agent",
        claimed: "Bezig",
        succeeded: "Klaar",
        failed: "Mislukt",
        refused: "Geweigerd door de agent",
        expired: "Verlopen voordat de agent hem oppakte",
      },
      capabilities: {
        rollout_promote: "Een rollout promoveren",
        rollout_abort: "Een rollout afbreken",
        rollout_status: "Rollout-status lezen",
        test_run: "Validatie draaien",
        test_cancel: "Validatie annuleren",
        ci_approve_deployment: "Een deployment goedkeuren",
        ci_reject_deployment: "Een deployment afwijzen",
      },
      refusalReason: {
        tenant_mismatch: "de autorisatie was niet voor deze werkruimte",
        cluster_mismatch: "de autorisatie was niet voor dit cluster",
        bad_signature: "de agent kon de handtekening van FixControl niet verifiëren",
        expired: "de autorisatie was al verlopen",
        replay: "de autorisatie was al gebruikt",
        not_allowlisted: "de eigen allowlist van de agent staat dit adres niet toe",
        revision_drift: "wat er draait is gewijzigd nadat het besluit was genomen",
        capability_denied: "deze agent heeft die rechten niet gekregen",
        no_longer_pending: "de deployment stond niet meer te wachten",
        parameterized_input: "de gepauzeerde build verwacht parameters die de agent niet kan leveren",
        tier_unavailable: "het abonnement van de host biedt deze actie niet",
        host_unreachable: "de agent kon de host niet bereiken",
        host_refused: "de host wees het verzoek af",
        no_refusal_primitive: "de host biedt geen manier om dit te weigeren",
        run_cancelled: "de run is geannuleerd",
        unspecified: "er is geen reden opgegeven",
      },
    },
  },

  operations: {
    eyebrow: "Operaties",
    title: "AI-operaties-governance",
    subtitle: "Gegoverneerde CI/CD-activiteit, deploy-risico en de goedkeuringswachtrij — observeren-en-governen.",
    loadError: "Kon het dashboard niet laden",
    stats: {
      pipelineRuns: "Pipeline-runs",
      awaitingApproval: "Wacht op goedkeuring",
      blocked: "Geblokkeerd door beleid",
      pendingApprovals: "Openstaande goedkeuringen",
    },
    riskDistribution: "Risicoverdeling",
    noGovernedRuns: "Nog geen gegoverneerde runs.",
    heatmap: "Infrastructuur-risicokaart",
    noData: "Geen data.",
    recentDeployments: "Recente gegoverneerde deployments",
    emptyDeployments: "Nog geen gegoverneerde deployments",
    emptyDeploymentsHint: "Koppel een CI/CD-provider onder Instellingen → DevOps om events te ontvangen.",
    cols: {
      provider: "Provider",
      change: "Wijziging",
      environment: "Omgeving",
      phase: "Fase",
      risk: "Risico",
      outcome: "Uitkomst",
      when: "Wanneer",
    },
    risk: {
      low: "Laag", medium: "Middel", high: "Hoog", critical: "Kritiek", unknown: "Onbekend",
    },
    phase: {
      queued: "In wachtrij", running: "Bezig", succeeded: "Geslaagd",
      failed: "Mislukt", cancelled: "Geannuleerd", awaiting_approval: "Wacht op goedkeuring",
    },
    outcome: {
      auto_execute: "Automatisch", draft_only: "Alleen concept",
      approval_required: "Goedkeuring vereist", blocked: "Geblokkeerd",
    },
    environment: {
      production: "Productie", staging: "Staging", development: "Ontwikkeling",
    },
    changeKind: {
      production_deployment: "Productie-deployment",
      staging_deployment: "Staging-deployment",
      rollback: "Rollback",
      infra_modification: "Infrastructuurwijziging",
      terraform_apply: "Terraform-apply",
      kubernetes_change: "Kubernetes-wijziging",
      secret_rotation: "Geheim-rotatie",
      dns_change: "DNS-wijziging",
      database_migration: "Databasemigratie",
      feature_flag: "Feature-flag",
      unknown: "Onbekend",
    },
    provider: {
      github_actions: "GitHub Actions",
      gitlab_ci: "GitLab CI",
      jenkins: "Jenkins",
      azure_devops: "Azure DevOps",
      argo_cd: "Argo CD",
      vercel: "Vercel",
      terraform_cloud: "Terraform Cloud",
      kubernetes: "Kubernetes",
      circleci: "CircleCI",
      buildkite: "Buildkite",
    },
  },

  operationsMetrics: {
    eyebrow: "AI-operaties",
    title: "Operationele metrics",
    subtitle: "Leidende indicatoren voor de support → remediatie-lus. Een metric toont n.v.t. als er geen data in het venster is, en geeft geen oordeel onder {min} metingen.",
    loadError: "Kon metrics niet laden",
    na: "n.v.t.",
    window: { "7": "7d", "30": "30d", "90": "90d" },
    band: {
      healthy: "gezond",
      watch: "let op",
      attention: "aandacht",
      noData: "geen data",
      notConnected: "niet gekoppeld",
      lowSample: "te weinig metingen",
    },
    retrieval: {
      label: "Support-retrieval-hitrate",
      sub: "{n}/{total} queries gaf een hit",
      none: "Geen retrieval-queries vastgelegd",
      notConnected: "Vereist een helpdesk met retrieval (Freshdesk, Jira, Odoo, TOPdesk)",
    },
    kb: {
      label: "KB-suggestie-goedkeuringsratio",
      sub: "{n}/{total} beoordeelde concepten goedgekeurd",
      none: "Nog geen beoordeelde concepten",
    },
    latency: {
      label: "Goedkeuringslatency (p50 / p90)",
      sub: "{count} beslissingen · gem. {avg} · max {max}",
      none: "Geen besliste goedkeuringen",
    },
    rollback: {
      label: "Rollback-frequentie",
      sub: "{n}/{total} deployments teruggerold",
      none: "Geen deployment-uitkomsten vastgelegd",
      notConnected: "Vereist een CI/CD-koppeling die deployments meldt",
    },
    repeated: {
      label: "Herhaalde incidenten",
      sub: "{repeated} herhalingen over {total} incidenten · {distinct} uniek",
      none: "Geen incidenten in venster",
    },
    rejectedRemediation: {
      label: "Afgekeurde remediaties",
      sub: "{n}/{total} beoordeelde runs keurde de patch af",
      none: "Nog geen beoordeelde remediatie-runs",
    },
    unusableRuns: {
      label: "Runs zonder uitslag",
      sub: "{n}/{total} runs kwam niet tot een uitslag",
      none: "Geen remediatie-runs vastgelegd",
      hint: "Sandbox-storingen (timeout / error) — infrastructuur, niet patchkwaliteit",
    },
  },

  missionTimeline: {
    title: "Operationele tijdlijn",
    subtitle: "Herspeelbare operationele mijlpalen voor deze missie.",
    empty: "Nog geen operationele mijlpalen voor deze missie.",
    openLink: "link",
    milestone: {
      intake_received: "Intake ontvangen",
      clarification_requested: "Verduidelijking gevraagd",
      issue_created: "Issue aangemaakt",
      freshdesk_ticket_created: "Freshdesk-ticket aangemaakt",
      support_ticket_resolved: "Support-ticket opgelost",
      support_ticket_reopened: "Support-ticket heropend",
      support_context_loaded: "Support-context geladen",
      governance_check_started: "Governance-check gestart",
      plan_generated: "Plan gegenereerd",
      approval_requested: "Goedkeuring gevraagd",
      approval_granted: "Goedkeuring verleend",
      execution_started: "Uitvoering gestart",
      task_completed: "Taak voltooid",
      patch_generated: "Patch gegenereerd",
      qa_completed: "QA-verificatie voltooid",
      security_review_completed: "Security-review voltooid",
      cto_gate_approved: "CTO-poort goedgekeurd",
      security_override: "Security-override toegepast",
      deployment_approval_requested: "Deployment-goedkeuring gevraagd",
      deployment_approved: "Deployment goedgekeurd",
      argo_sync_started: "Argo-sync gestart",
      deployment_completed: "Deployment voltooid",
      kb_suggestion_created: "KB-suggestie aangemaakt",
      mission_completed: "Missie voltooid",
      mission_failed: "Missie mislukt",
      mission_cancelled: "Missie geannuleerd",
      approval_rejected: "Goedkeuring afgewezen",
      changes_requested: "Wijzigingen gevraagd",
      approval_expired: "Goedkeuring verlopen",
      verification_passed: "Verificatie geslaagd",
      verification_failed: "Verificatie mislukt",
      verification_blocked: "Verificatie geblokkeerd door de omgeving",
    },
  },

  settingsProfile: {
    eyebrow: "Instellingen",
    title: "Profiel",
    subtitle: "Je naam, taal en weergavegrootte. Email en toegang vind je op het tabblad Account.",
    networkError: "Netwerkfout — probeer het opnieuw.",
    identity: {
      header: "Identiteit",
      nameLabel: "Weergavenaam",
      emailLabel: "E-mail",
      emailReadOnly: "Je e-mail is je login. Vraag een admin om hem te wijzigen.",
      save: "Opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
    },
    language: {
      header: "Taal",
      description: "Schakelt de interface direct om. De keuze wordt onthouden op dit apparaat.",
    },
    display: {
      header: "Weergavegrootte",
      description: "Pas de tekstgrootte en afstand in de app aan. Wordt onthouden op dit apparaat.",
      compact: "Compact",
      default: "Standaard",
      comfortable: "Comfortabel",
      large: "Groot",
    },
    managedByAdmin: "Beheerd door je workspace-admin.",
    layoutGroup: {
      header: "Weergave & layout",
      toggleHint: "klik om te openen",
      description: "Persoonlijke keuzes voor hoe FixControl voor jou is ingericht — ze veranderen nooit waar je toegang toe hebt. Beheerders bepalen de grenzen via Instellingen → Navigatiebeleid.",
    },
    workProfile: {
      header: "Werkprofiel",
      description: "Kies hoe FixControl standaard wordt ingericht voor jouw werk. Een preset, geen recht — het verandert nooit waar je toegang toe hebt.",
    },
    startPage: {
      header: "Startpagina",
      description: "Kies welke pagina opent na het inloggen. Alleen pagina's die je kunt zien worden getoond.",
    },
    navigation: {
      header: "Navigatie",
      description: "Kies welke onderdelen in je zijmenu staan. Je kunt alleen onderdelen tonen waar je toegang toe hebt; sommige kunnen door je admin zijn ingesteld.",
    },
    profiles: {
      starter: "Starter",
      support: "Support",
      engineering: "Engineering",
      release_devops: "Release / DevOps",
      manager_reviewer: "Manager / Reviewer",
      admin: "Admin",
      custom: "Aangepast",
    },
    profileHints: {
      starter: "Alleen waar je begint: projecten, issues en wat binnenkomt. De rest verschijnt zodra je het gebruikt.",
      support: "Inbox, meldingen, issues en kennisbank vooraan.",
      engineering: "Issues, AI-missies, werkruimtes en patches.",
      release_devops: "AI-missies, governance, patches en operationele metrics.",
      manager_reviewer: "Dashboard, approvals, governance en voortgang.",
      admin: "De volledige operator-navigatie, nog steeds beperkt door je permissies.",
      custom: "Kies precies welke onderdelen je wilt zien.",
    },
  },

  settingsBilling: {
    eyebrow: "Werkruimte-instellingen",
    title: "Abonnement en facturatie",
    subtitle: "Waarop deze werkruimte een abonnement heeft, en hoe je dat wijzigt.",
    noAccess: "Alleen een workspace-admin kan facturatie inzien.",
    loadError: "Facturatiegegevens laden mislukt — probeer het opnieuw.",
    notConfigured:
      "Zelf afrekenen is niet ingericht voor deze omgeving. Je abonnement loopt rechtstreeks via ons — neem contact op om het te wijzigen.",
    currentPlan: "Huidig abonnement",
    status: {
      label: "Status",
      trialing: "Proef",
      active: "Actief",
      past_due: "Betaling mislukt",
      grace: "Betaling openstaand",
      suspended: "Opgeschort",
      canceled: "Beëindigd",
    },
    renewsOn: "Verlengt op",
    endsOn: "Toegang eindigt op",
    pilot: {
      header: "Geen abonnement",
      body: "Deze werkruimte draait zonder facturatierelatie — een pilot of een afspraak die je rechtstreeks met ons hebt gemaakt. Niets hier wordt door betaling beperkt.",
    },
    dunning: {
      pastDue: "Een betaling is mislukt. Alles blijft werken tot {date}, zodat er niets stilvalt terwijl je het regelt.",
      grace: "De betaling staat open. Toegang loopt door tot {date}.",
      suspended: "De toegang is opgeschort omdat de betaling niet is voltooid. Je gegevens zijn intact — een bijgewerkte betaalmethode herstelt de toegang.",
    },
    usage: {
      header: "Verbruik deze maand",
      body: "Wat deze werkruimte in {month} heeft verbruikt, afgezet tegen de plafonds van het abonnement.",
      aiActions: "AI-acties",
      tokens: "Modeltokens",
      spend: "Modelkosten (USD)",
      of: "van",
      unlimited: "Geen abonnementsplafond",
      footnote:
        "Er wordt geteld of er nu een plafond is ingesteld of niet, zodat het cijfer klopt. De kosten gaan over modellen waarvoor we een prijs kennen; wat geen prijs heeft telt wel tokens maar geen geld — het bedrag is dus een ondergrens.",
    },
    portal: {
      header: "Betaling en facturen",
      body: "Betaalmethode bijwerken, facturen downloaden of opzeggen. Opent de beveiligde pagina van onze betaalprovider.",
      action: "Facturatie beheren",
      opening: "Openen…",
      unavailable: "Er is nog niets te beheren — deze werkruimte heeft geen zelfbedieningsrelatie voor facturatie.",
    },
    choosePlan: {
      header: "Abonnement wijzigen",
      body: "Prijzen zijn exclusief btw. Je gaat naar de beveiligde afrekenpagina van onze betaalprovider; er verandert niets tot de betaling is voltooid.",
      action: "Kies {plan}",
      current: "Huidig abonnement",
      perMonth: "per maand",
      contactOnly: "Neem contact op",
    },
    checkout: {
      success: "Betaling ontvangen. Je abonnement wordt bijgewerkt zodra de bevestiging binnen is — meestal binnen enkele seconden.",
      cancelled: "Afrekenen geannuleerd. Er is niets gewijzigd.",
      failed: "De betaalprovider was niet bereikbaar. Probeer het opnieuw.",
    },
  },
  settingsNavigationPolicy: {
    eyebrow: "Werkruimte-instellingen",
    title: "Navigatiebeleid",
    subtitle: "Stel het standaard werkprofiel in en bepaal wat gebruikers mogen tonen, verbergen of aanpassen. Dit bepaalt alleen de navigatie — nooit een permissie.",
    noAccess: "Alleen een workspace-admin kan het navigatiebeleid wijzigen.",
    save: "Beleid opslaan",
    saving: "Opslaan…",
    saved: "Opgeslagen",
    saveError: "Opslaan mislukt — probeer het opnieuw.",
    profiles: {
      header: "Werkprofielen",
      description: "Welke werkprofielen gebruikers mogen kiezen, en de standaard voor nieuwe gebruikers.",
      allowedLabel: "Te kiezen profielen",
      defaultLabel: "Standaardprofiel",
    },
    customization: {
      header: "Aanpassing door gebruiker",
      description: "Zet een optie uit om de profiel-/beleidsstandaard voor iedereen af te dwingen.",
      nav: "Gebruikers mogen navigatie tonen/verbergen",
      startPage: "Gebruikers mogen hun startpagina kiezen",
      displaySize: "Gebruikers mogen de weergavegrootte wijzigen",
    },
    items: {
      header: "Verplichte & verborgen navigatie",
      description: "Maak een onderdeel altijd zichtbaar (voor gebruikers met permissie) of verberg het voor iedereen. Verbergen wint altijd van verplichten.",
      itemCol: "Navigatie-onderdeel",
      lockedCol: "Altijd tonen",
      hiddenCol: "Verbergen",
      note: "Een onderdeel verbergen haalt het alleen uit het zijmenu — het verandert nooit wie de route direct mag bereiken. Routes blijven server-side beschermd.",
    },
  },

  settingsAccount: {
    eyebrow: "Instellingen",
    title: "Account",
    subtitle: "Waar je bij hoort, wat je mag en waar goedkeuringsmails heen gaan. Rol en organisatie beheren admins op het tabblad Gebruikers.",
    workspace: {
      header: "Organisatie",
      nameLabel: "Actieve organisatie",
      idLabel: "Technische organisatie-id",
      noWorkspace: "Geen organisatie toegewezen",
      technicalDetails: "Technische gegevens",
    },
    access: {
      header: "Toegang",
      roleLabel: "Rol",
      roleDesc: {
        admin: "Volledige toegang: gebruikers, instellingen en alle goedkeuringen",
        manager: "Bekijken, toewijzen en goedkeuren",
        operator: "Bekijken en werken aan toegewezen zaken",
      },
      platformLabel: "Platformbeheer",
      platformYes: "Ja — FixControl-medewerker",
      platformNo: "Nee",
      changeNote: "Rol en toegang worden door beheerders beheerd. Neem contact op met een beheerder om dit te wijzigen.",
    },
    approvalEmail: {
      header: "Goedkeuringsmails",
      description: "Als een klantantwoord jouw goedkeuring nodig heeft, mailt FixControl je het antwoord met knoppen om goed te keuren of af te wijzen. Standaard gaat dat naar je account-e-mail ({email}). Gebruik hier een ander adres, bijvoorbeeld een gedeelde goedkeuringsinbox.",
      label: "Adres voor goedkeuringsmails",
      placeholder: "goedkeuring@bedrijf.nl",
      save: "Bevestiging sturen",
      saving: "Versturen…",
      remove: "Mijn account-e-mail gebruiken",
      verified: "Bevestigd — goedkeuringsmails gaan naar dit adres.",
      pending: "Wacht op bevestiging — open de link die we naar {email} stuurden. Tot die tijd gaan goedkeuringsmails naar je account-e-mail.",
      expired: "Niet bevestigd — de link is verlopen. Stuur een nieuwe bevestiging.",
      none: "Goedkeuringsmails gaan naar je account-e-mail.",
      sent: "Bevestigingsmail verstuurd naar {email}.",
      notSent: "De bevestigingsmail kon niet worden verstuurd. Controleer de uitgaande e-mail van de organisatie.",
      confirmedNow: "Adres bevestigd. Goedkeuringsmails gaan nu naar {email}.",
      confirmFailed: "Deze bevestigingslink is ongeldig of verlopen. Stuur een nieuwe bevestiging.",
      invalid: "Dit is geen geldig e-mailadres.",
      error: "Opslaan mislukt. Probeer het opnieuw.",
      onlyApprovers: "Alleen beheerders en managers krijgen goedkeuringsmails.",
    },
    session: {
      header: "Sessie",
      description: "Wijzig je wachtwoord of meld je af op deze browser.",
      openSecurity: "Wachtwoord wijzigen",
      signOut: "Uitloggen",
    },
  },

  demoSettings: {
    eyebrow: "Instellingen",
    title: "Demo data",
    subtitle: "Laad een fictief bedrijf 'Northwind Bank' in jouw werkruimte om FixControl te ontdekken — issues, codebases, workspaces, patches en Slack-threads. Met één klik weer schoon te maken.",
    loadingStatus: "Status laden…",
    statusActive: "Demo data actief",
    statusActiveSub: "Northwind Bank is geladen in deze werkruimte.",
    statusInactive: "Geen demo data",
    statusInactiveSub: "Klik op laden om Northwind Bank toe te voegen.",
    loadCta: "Demo data laden",
    loading: "Bezig met laden…",
    loadOk: "Demo data succesvol geladen.",
    loadError: "Laden is mislukt. Probeer het opnieuw.",
    resetCta: "Reset demo",
    resetting: "Bezig met resetten…",
    confirmReset: "Hiermee wordt de huidige demo data verwijderd en opnieuw aangemaakt. Lopende notities of wijzigingen op demo-issues gaan verloren.",
    resetOk: "Demo data succesvol gereset.",
    resetError: "Reset is mislukt. Probeer het opnieuw.",
    removeCta: "Verwijder demo data",
    removing: "Bezig met verwijderen…",
    confirmRemove: "Weet je zeker dat je alle demo data wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.",
    removeOk: "Demo data verwijderd ({total} rijen).",
    removeError: "Verwijderen is mislukt. Probeer het opnieuw.",
    helpLoad: "Laden voegt 15 issues, 3 workspaces met echte patches, een Slack-thread en bijbehorende kennis/audit toe — allemaal getagd als demo.",
    helpReset: "Reset verwijdert eerst de bestaande demo data en zaait dan een schone versie. Handig na een upgrade of als de data corrupt is.",
    helpRemove: "Verwijderen wist alleen de demo data — jouw eigen issues, projecten en koppelingen blijven onaangeraakt.",
  },

  integrationHealth: {
    panelTitle: "Integratiegezondheid",
    panelSubtitle: "Status per functie, retries en mislukte leveringen voor deze koppeling.",
    rollupLabel: "Algemeen",
    status: {
      healthy: "Gezond",
      degraded: "Beperkt",
      failing: "Fout",
      requires_reconnect: "Opnieuw verbinden nodig",
      disabled: "Uitgeschakeld",
      unknown: "Nog niet gecontroleerd",
    },
    capability: {
      connection: "Verbinding",
      create_ticket: "Ticket aanmaken",
      post_public_reply: "Openbare reactie",
      post_private_note: "Interne notitie",
      push_status: "Status pushen",
      read_ticket: "Ticket lezen",
      sync_webhook: "Webhook-sync",
      create_issue: "Issue aanmaken",
      post_comment: "Reactie",
      transition_status: "Statusovergang",
      read_issue: "Issue lezen",
      post_message: "Bericht plaatsen",
      update_message: "Bericht bijwerken",
      approval_actions: "Goedkeuringsacties",
      create_branch: "Branch aanmaken",
      create_pr: "PR aanmaken",
      read_checks: "Checks lezen",
      send_reply: "Antwoord versturen",
      ingest: "Mail-inname",
      token_refresh: "Mailbox-autorisatie",
      watch_renewal: "Watch-verlenging (live sync)",
      subscription_renewal: "Abonnementsverlenging (live sync)",
      webhook_receive: "Webhook-bezorging",
      // DevOps-capabilities — zie en.ts.
      read_rollout: "Rollout lezen",
      promote_rollout: "Rollout promoveren",
      abort_rollout: "Rollout afbreken",
      read_status: "Status lezen",
      approve_deployment: "Deployment goedkeuren",
      play_job: "Vastgehouden job starten",
      proceed_input: "Gepauzeerde build hervatten",
      read_repo: "Repository lezen",
      read_file: "Bestand lezen",
      write_patch: "Patch schrijven",
      customer_directory: "Klantenregister",
      jsm_customer_reply: "Servicedesk-antwoord",
      receive_actions: "Acties ontvangen",
      receive_commands: "Commando's ontvangen",
      open_modal_or_task_module: "Dialoog openen",
      connectivity: "Agent-verbinding",
      // Read-only Google Cloud-connector. De labels noemen het Google-product,
      // nooit de interne capability-sleutel.
      read_logs: "Cloud Logging lezen",
      read_errors: "Error Reporting lezen",
      query_bigquery: "BigQuery bereikbaar (nog niet gebruikt)",
      read_only_verified: "Alleen-lezen rol geverifieerd",
    },
    jobs: {
      heading: "Leveringsjobs",
      open: "Openstaand",
      failed: "Mislukt",
      deadLetter: "Dead-letter",
      succeeded: "Geslaagd",
      none: "Geen uitgaande jobs vereisen aandacht.",
      attempt: "Poging {n}/{max}",
      nextAttempt: "Volgende poging {when}",
      retrying: "Opnieuw proberen",
    },
    lastSuccess: "Laatste succes",
    lastFailure: "Laatste fout",
    lastErrorLabel: "Laatste fout",
    healthy: "Alle functies werken.",
    noChecksYet: "Er is nog niets gecontroleerd. Gebruik ‘Verbinding testen’ zodra dit systeem gekoppeld is.",
    actions: {
      retry: "Opnieuw proberen",
      test: "Koppeling testen",
      reconnect: "Opnieuw verbinden",
      requeue: "Opnieuw in wachtrij",
      retrying: "Bezig…",
      retried: "Opnieuw proberen gestart",
      retryFailed: "Opnieuw proberen mislukt",
    },
    banner: {
      pendingTitle: "Extern ticket nog niet aangemaakt",
      pendingBody: "FixControl probeert dit issue opnieuw naar {provider} te pushen. Laatste poging: {last}. Volgende poging: {next}.",
      failedTitle: "Actie vereist",
      failedBody: "{provider} accepteert momenteel geen tickets. Herstel de koppeling en probeer opnieuw.",
      lastError: "Laatste fout: {error}",
      openIntegration: "Open integratie-instellingen",
      retry: "Nu opnieuw proberen",
    },
  },
  settingsIntegrations: {
    eyebrow: "Instellingen",
    title: "Kanalen & connecties",
    subtitle: "Verbind de kanalen waar je team al werkt. Alle kanalen gebruiken dezelfde intake-, review- en patch-flow.",
    engineCallout: {
      title: "Eén keer koppelen. Overal gebruiken.",
      body: "FixControl normaliseert berichten uit Slack, Teams, e-mail en de widget tot één workflow. Reviews en updates lopen terug via hetzelfde kanaal.",
    },
    capabilities: {
      intake: "Intake",
      review: "Reviews",
      notification: "Notificaties",
      code: "Code",
      tracker: "Mirroring",
      webhook: "Webhook",
      api: "API",
      diagnostics: "Diagnostiek",
      deploy: "Bouwen & uitrollen",
    },
    capabilityTooltips: {
      intake: "Ontvang issues en requests via dit kanaal.",
      review: "Keur patches goed of vraag wijzigingen aan vanuit dit kanaal.",
      notification: "Ontvang workflow-updates via dit kanaal.",
      code: "Open pull- of merge requests met patches die agents hebben voorbereid.",
      tracker: "Spiegel BUG- en FEATURE-issues naar deze tracker.",
      webhook: "Verstuur of ontvang gesigneerde webhook-payloads.",
      api: "Programmatische toegang via API-sleutels.",
      diagnostics: "Voer alleen-lezen, goedkeuring-gated diagnostiek uit op deze databron.",
      deploy: "Volg wat dit systeem bouwde of uitbracht, en houd een promotie achter een mens.",
    },
    compact: {
      sameConnection: "Gebruikt dezelfde {name}-koppeling — instellen onder Intakekanalen.",
    },
    sections: {
      intake: {
        title: "Intakekanalen",
        description: "Ontvang supportvragen, bugs en feature requests via elk kanaal waar je team al werkt.",
        hint: "Elk kanaal kan worden gebruikt voor intake, reviews en notificaties — afhankelijk van zijn capabilities.",
      },
      reviewNotify: {
        title: "Review & notificaties",
        description: "Keur patches goed, vraag wijzigingen aan en ontvang updates zonder de context te verliezen.",
      },
      codeDelivery: {
        title: "Code- & patch-levering",
        description: "Laat agents patches maken en lever ze af als pull requests of merge requests.",
      },
      issueTracking: {
        title: "Issue-tracker spiegelen",
        description: "Spiegel BUG- en FEATURE-issues naar Jira of Linear en koppel geleverde pull requests terug aan het ticket.",
      },
      webhooksApi: {
        title: "Webhooks & API",
        description: "Gebruik eigen systemen of interne tools bovenop dezelfde workflow-engine.",
      },
      data: {
        title: "Databronnen",
        description: "Read-only connectors die de AI extra context geven bij het oplossen van BUG-tickets. Standaard uit; organisaties moeten per integratie aanzetten.",
      },
      buildRun: {
        title: "Bouwen, uitrollen & draaien",
        description: "Waar je code wordt gebouwd, uitgebracht en draait. FixControl kijkt mee, legt vast wat er gebeurde, en vraagt een mens voordat er iets wordt gepromoveerd.",
      },
    },
    status: {
      connected: "Gekoppeld",
      not_connected: "Niet gekoppeld",
      setup_required: "Setup vereist",
      coming_soon: "Binnenkort",
      per_project: "Per project instellen",
      // Health-demoties — credentials aanwezig maar de integratie werkt niet.
      failing: "Storing",
      requires_reconnect: "Herverbinden vereist",
    },
    statusCard: {
      derivedStatus: {
        connected: "Gekoppeld",
        // Inloggegevens aanwezig, niets geverifieerd — zie en.ts.
        configured: "Ingesteld — niet geverifieerd",
        sync_error: "Synchronisatiefout",
        reconnect_required: "Herverbinden vereist",
        disconnected: "Niet gekoppeld",
      },
      configuredHint: "De inloggegevens zijn opgeslagen, maar geen enkele geslaagde aanroep heeft bevestigd dat deze koppeling werkt. Gebruik hieronder “Verbinding testen” om dat te controleren.",
      lastDelivery: "Laatste levering",
      failures: "Fouten",
      lastError: "Laatste fout",
      never: "nooit",
      reconnectCta: "Ik ben opnieuw verbonden — wis de waarschuwing",
      slackReconnectHelp: "Klik hierboven op \"Opnieuw installeren met Slack\" om de OAuth-handshake opnieuw te doen. Zodra Slack een nieuw token afgeeft, verdwijnt de waarschuwing automatisch. Gebruik deze knop alleen als je de credential op een andere manier hebt hersteld.",
      slackEnvReconnectHelp: "Roteer SLACK_BOT_TOKEN of plak hieronder een nieuw bot-token, sla op en klik dan op deze knop.",
      teamsReconnectHelp: "Teams gebruikt geen OAuth. Om te herstellen: roteer het Bot Framework App-wachtwoord in de Azure-portal (App-registraties → je bot → Certificaten & secrets), plak het nieuwe wachtwoord in het formulier hieronder, sla op en klik dan op deze knop. De pin wordt alleen gewist als je het bevestigt — alleen opslaan is niet genoeg.",
      relativeJustNow: "zojuist",
      relativeSeconds: "{n}s geleden",
      relativeMinutes: "{n}m geleden",
      relativeHours: "{n}u geleden",
      relativeDays: "{n}d geleden",
    },
    health: {
      healthy: "Gezond",
      warning: "Waarschuwing",
      error: "Fout",
    },
    meta: {
      usage: "{count} runs",
      deadLetter: "{count} mislukte leveringen",
    },
    actions: {
      connect: "Koppelen",
      configure: "Configureren",
      completeSetup: "Setup voltooien",
      notifyMe: "Houd me op de hoogte",
      chooseProject: "Kies een project",
      notifyMeRegistered: "We laten het weten",
    },
    cards: {
      widget: {
        name: "Web widget",
        description: "Plaats de FixControl-widget in je product of help center. Bezoekers melden issues zonder de pagina te verlaten.",
      },
      slack: {
        name: "Slack",
        description: "Stuur issue- en patch-updates naar Slack en keur ze goed of vraag wijzigingen aan vanuit chat.",
      },
      teams: {
        name: "Microsoft Teams",
        description: "Gebruik Microsoft Teams voor issue-intake, patch-reviews en workflow-meldingen.",
      },
      gmail: {
        name: "Gmail",
        description: "Importeer support-e-mails en zet ze om in issues.",
      },
      office365: {
        name: "Office 365 / Outlook",
        description: "Importeer Outlook-supportmail en Microsoft 365-context.",
      },
      emailNotifications: {
        name: "E-mail",
        description: "Ontvang reviewverzoeken en patch-updates per e-mail via de mailboxen die je al hebt gekoppeld.",
      },
      github: {
        name: "GitHub",
        description: "Eén connectie, meerdere capabilities: repository-toegang, pull request-delivery en webhook-events. Activeer repos als codebases zodat agents erop kunnen werken.",
      },
      gitlab: {
        name: "GitLab",
        description: "Koppel GitLab-projecten, push patches en open merge requests.",
      },
      bitbucket: {
        name: "Bitbucket",
        description: "Koppel Bitbucket-repositories, push patches en open pull requests.",
      },
      jira: {
        name: "Jira",
        description: "Spiegel BUG- en FEATURE-issues naar een Jira Cloud-project en koppel geleverde pull requests terug aan het ticket.",
      },
      linear: {
        name: "Linear",
        description: "Spiegel BUG- en FEATURE-issues naar een Linear-team en koppel geleverde pull requests aan de issue.",
      },
      freshdesk: {
        name: "Freshdesk",
        description: "Maak en koppel Freshdesk-supporttickets terwijl FixControl governance en remediatie afhandelt. Freshdesk blijft het systeem van registratie voor support.",
      },
      odoo: {
        name: "Odoo",
        description: "Maak en koppel Odoo Helpdesk-tickets terwijl FixControl governance en remediatie afhandelt. Odoo blijft het systeem van registratie voor support.",
      },
      topdesk: {
        name: "TOPdesk",
        description: "Maak en koppel TOPdesk-meldingen terwijl FixControl governance en remediatie afhandelt. TOPdesk blijft de servicedesk waarin alles geregistreerd staat.",
      },
      outboundWebhooks: {
        name: "Uitgaande webhooks",
        description: "Stuur FixControl-events door naar elk HTTPS-endpoint dat je team beheert.",
      },
      publicApi: {
        name: "Publieke API",
        description: "Maak API-sleutels voor externe tools om issues te lezen, patches te beheren en webhooks te configureren.",
      },
      database: {
        name: "Database (read-only)",
        description: "Geef de patch-agent read-only toegang tot een Postgres database voor diagnostische context bij BUG-tickets. Standaard uit; elke query wordt per shape goedgekeurd en geaudit.",
      },
      gcp: {
        name: "Google Cloud (read-only)",
        description: "Geef de patch-agent read-only toegang tot Cloud Logging, Error Reporting en BigQuery voor diagnostische context bij BUG-tickets. Standaard uit; reads zijn begrensd, secrets worden gemaskeerd en BigQuery-scans worden geweigerd boven een kostenplafond.",
      },
      "github-actions": {
        name: "GitHub Actions",
        description: "Zie wat je GitHub-workflows met een commit deden, en bevries die uitslag bij de goedkeuring.",
      },
      "gitlab-ci": {
        name: "GitLab CI",
        description: "Zie wat je GitLab-pipelines met een commit deden, en bevries die uitslag bij de goedkeuring.",
      },
      jenkins: {
        name: "Jenkins",
        description: "Neem build- en uitrolgebeurtenissen van Jenkins op, weeg het risico en leg ze vast in het auditspoor.",
      },
      "azure-devops": {
        name: "Azure DevOps",
        description: "Neem build- en release-gebeurtenissen van Azure Pipelines op, weeg het risico en leg ze vast in het auditspoor.",
      },
      argocd: {
        name: "Argo CD",
        description: "Volg je Argo-rollouts, en laat een gepauzeerde pas promoveren nadat een mens akkoord geeft.",
      },
      vercel: {
        name: "Vercel",
        description: "Neem uitrolgebeurtenissen van Vercel op, zodat een release net als al het andere op de tijdlijn staat.",
      },
      "terraform-cloud": {
        name: "Terraform Cloud",
        description: "Neem run-gebeurtenissen van Terraform Cloud op, zodat een infrastructuurwijziging net zo wordt vastgelegd als elke andere release.",
      },
      kubernetes: {
        name: "Kubernetes",
        description: "Koppel een cluster zodat FixControl kan lezen wat daar draait, en een wijziging daartegen kan bewijzen.",
      },
      "fc-agent": {
        name: "FC Agent",
        description: "Een kleine dienst die je in je eigen omgeving draait. Die belt naar FixControl, nooit andersom, zodat FixControl systemen kan bereiken die niet open staan naar internet.",
      },
    },
    gmail: {
      pageTitle: "Gmail",
      pageSubtitle: "Koppel een Google-account zodat agents van {company} inkomende support-e-mail kunnen importeren.",
      addHeading: "Nog een Gmail-account toevoegen",
      emptyHeading: "Koppel Gmail om support-e-mail te importeren",
    },
    github: {
      pageTitle: "GitHub",
      pageSubtitle: "Installeer de FixControl GitHub-app op de repositories waar agents van {company} aan mogen werken.",
      addHeading: "Nog een GitHub-koppeling toevoegen",
      emptyHeading: "Koppel GitHub om patches en pull requests mogelijk te maken",
      capabilitiesTitle: "Capabilities",
      capabilityRepoAccess: "Repository-toegang",
      capabilityPrDelivery: "Pull request-delivery",
      capabilityWebhooks: "Webhook-events",
      capabilitiesHint: "GitHub is één connectie met meerdere capabilities. De connectie regelt toegang; een repository wordt een codebase waarop agents werken; PR-delivery en webhooks verzorgen status en intake.",
      manageConnection: "Connectie beheren",
      addRepoAsCodebase: "Repository als codebase toevoegen",
      codebasesHint: "Je kunt een codebase ook direct vanaf een project toevoegen via Project → Codebase toevoegen.",
    },
    gitlab: {
      pageTitle: "GitLab",
      pageSubtitle: "Koppel GitLab Cloud of een self-hosted instance zodat agents van {company} patches kunnen pushen en merge requests openen.",
      addHeading: "Nog een GitLab-koppeling toevoegen",
      emptyHeading: "Koppel GitLab om patches en merge requests mogelijk te maken",
    },
    bitbucket: {
      pageTitle: "Bitbucket",
      pageSubtitle: "Koppel een Bitbucket-workspace zodat agents van {company} patches kunnen pushen en pull requests openen.",
      addHeading: "Nog een Bitbucket-koppeling toevoegen",
      emptyHeading: "Koppel Bitbucket om patches en pull requests mogelijk te maken",
    },
    jira: {
      pageTitle: "Jira",
      pageSubtitle: "Koppel een Jira Cloud-site zodat agents van {company} BUG- en FEATURE-issues kunnen spiegelen en geleverde pull requests aan het ticket koppelen.",
      addHeading: "Nog een Jira-koppeling toevoegen",
      emptyHeading: "Koppel Jira om issues te spiegelen en pull requests te koppelen",
      webhook: {
        heading: "Inkomende webhook",
        body: "Voeg deze URL toe als Jira-webhook om statuswijzigingen van issues terug te syncen naar FixControl. Het token in de URL authenticeert jouw workspace — behandel het als een geheim en roteer het als het lekt.",
        urlLabel: "Webhook-URL",
        notMinted: "Nog geen webhook-token. Genereer er een om de URL te krijgen.",
        generate: "Genereer webhook-URL",
        rotate: "Token roteren",
        rotating: "Roteren…",
        copy: "Kopiëren",
        copied: "Gekopieerd",
        rotateWarning: "Roteren maakt de huidige URL direct ongeldig — werk je Jira-webhook bij met de nieuwe.",
      },
    },
    linear: {
      pageTitle: "Linear",
      pageSubtitle: "Koppel een Linear-workspace zodat agents van {company} BUG- en FEATURE-issues kunnen spiegelen en geleverde pull requests kunnen koppelen.",
      addHeading: "Nog een Linear-koppeling toevoegen",
      emptyHeading: "Koppel Linear om issues te spiegelen en pull requests te koppelen",
    },
    freshdesk: {
      pageTitle: "Freshdesk",
      pageSubtitle: "Koppel een Freshdesk-domein zodat {company} supporttickets kan aanmaken en koppelen terwijl FixControl governance en remediatie afhandelt.",
      addHeading: "Nog een Freshdesk-koppeling toevoegen",
      emptyHeading: "Koppel Freshdesk om supporttickets aan te maken en te koppelen",
      behaviorHeading: "Gedrag",
      autoCreateLabel: "Maak automatisch een Freshdesk-ticket aan bij Slack-intake",
      autoCreateHint: "Aan: een melding via Slack maakt direct een gekoppeld Freshdesk-ticket aan. Uit (standaard): het ticket wordt pas aangemaakt wanneer iemand er bewust voor kiest — via de knop op de intake-kaart of approval-gated.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inbound sync — Freshdesk informeert FixControl",
        inboundIntro: "Alles hieronder is leesrichting of alleen-data: een support-event kan issue-data bijwerken of een mens notificeren, maar stuurt nooit de governed workflow aan en draait nooit agents. Alles standaard uit.",
        outboundHeading: "Outbound sync — FixControl werkt het ticket bij",
        outboundIntro: "Pushes zijn best-effort en eenrichtingsverkeer: een Freshdesk-storing blokkeert nooit een FixControl-beslissing, status-pushes sluiten nooit het ticket namens support, en de klant-reply gaat alleen de deur uit na menselijke goedkeuring.",
        autoCreateLabel: "Maak automatisch een Freshdesk-ticket aan bij Slack-intake",
        autoCreateHint: "Aan: een melding via Slack maakt direct een gekoppeld Freshdesk-ticket aan. Uit (standaard): het ticket wordt pas aangemaakt wanneer iemand er bewust voor kiest — via de knop op de intake-kaart of approval-gated.",
        ingestConversationLabel: "Voer de ticketconversatie als AI-context op",
        ingestConversationHint: "De recente klant-replies en agent-notities van het gekoppelde ticket (e-mailadressen en telefoonnummers geredigeerd) verschijnen als context op het issue, zodat de agent symptomen begrijpt en weet wat support al probeerde.",
        priorityInboundLabel: "Sync ticketprioriteit naar het issue",
        priorityInboundHint: "Een prioriteitswijziging in Freshdesk werkt de prioriteit van het FixControl-issue bij. Alleen data — beïnvloedt routering, nooit uitvoering.",
        tagsInboundLabel: "Map ticket-tags naar issue-vlaggen (alleen allowlist)",
        tagsInboundHint: "Alleen tag=VLAG-paren die je hieronder opsomt mappen ooit. Een support-tag kan nooit een governance-vlag zetten die je niet expliciet toestond.",
        tagAllowlistLabel: "Tag → vlag allowlist (kommagescheiden tag=VLAG-paren)",
        tagAllowlistPlaceholder: "vip=VIP, urgent-customer=ESCALATED",
        csatResurfaceLabel: "Opnieuw aanbieden bij negatieve klanttevredenheid",
        csatResurfaceHint: "Een negatieve CSAT-score op het gekoppelde ticket notificeert je managers om te beoordelen of de fix echt is geland bij de klant.",
        mergeInboundLabel: "Markeer samengevoegde tickets als duplicaten",
        mergeInboundHint: "Voegt support het gekoppelde ticket samen met een ander, dan wordt het FixControl-issue gemarkeerd als duplicaat van het issue van het primaire ticket (alleen binnen dezelfde organisatie).",
        assignmentInboundLabel: "Sync tickettoewijzing naar het issue",
        assignmentInboundHint: "Een expliciete responder-e-mail wordt de issue-assignee; anders beslist jouw groep → assignee-map hieronder. Niet-gemapte groepen doen niets.",
        groupMapLabel: "Groep → assignee-map (kommagescheiden groupId=assignee-paren)",
        groupMapPlaceholder: "42=ops@acme.com, 7=platform@acme.com",
        slaResurfaceLabel: "Opnieuw aanbieden bij (naderende) SLA-overschrijding",
        slaResurfaceHint: "Een SLA-signaal vanuit Freshdesk notificeert je managers — net als heropenen: het vraagt een mens om te kijken, het voert nooit iets opnieuw uit.",
        captureLearningLabel: "Leg opgeloste tickets vast als projectkennis",
        captureLearningHint: "Wanneer support het gekoppelde ticket oplost, wordt de case opgeslagen als projectgebonden kennis (PII geredigeerd), zodat toekomstig werk op hetzelfde project hem kan terugvinden. Tickets zonder project worden nooit vastgelegd.",
        progressNotesLabel: "Plaats missievoortgang als privénotities",
        progressNotesHint: "Plan goedgekeurd, patch gereed, fix toegepast en deployment goedgekeurd verschijnen als agent-only notities op het gekoppelde ticket (met FixControl-deeplink en deploy-changelog). Nooit zichtbaar voor de klant.",
        statusPushLabel: "Zet het ticket op Pending wanneer het issue is opgelost",
        statusPushHint: "Het oplossen van het FixControl-issue zet het ticket op Pending (wacht op klant). Nooit Resolved/Closed — support sluit de loop; tickets die support zelf al afhandelde blijven ongemoeid.",
        regressionReopenLabel: "Heropen het ticket wanneer een geleverde fix regresseert",
        regressionReopenHint: "Een rollback of mislukte productie-deploy van de fix heropent het gekoppelde ticket met een privénotitie, zodat support weet dat het klantprobleem toch niet is opgelost.",
        priorityEscalationLabel: "Verhoog ticketprioriteit bij kritieke escalatie",
        priorityEscalationHint: "Escaleert het issue (prioriteit kritiek, of een DATALOSS/SECURITY-governance-vlag), dan gaat het ticket naar Urgent met een privénotitie. Alleen verhogen — verlaagt nooit een prioriteit.",
        customerReplyLabel: "Zet een klant-reply klaar wanneer het issue is opgelost",
        customerReplyHint: "Oplossen zet een reply-concept in de communicatie-reviewwachtrij. Er wordt nooit automatisch gepost: een mens keurt het concept goed (en bewerkt het meestal), en pas dan wordt het als publieke reply geplaatst.",
      },
      autoCreateLoadError: "Kon de instelling niet laden.",
      autoCreateSaveError: "Kon de instelling niet opslaan.",
    },
    odoo: {
      pageTitle: "Odoo",
      pageSubtitle: "Koppel een Odoo-omgeving zodat {company} Helpdesk-tickets kan maken en koppelen terwijl FixControl governance en remediatie afhandelt.",
      addHeading: "Nog een Odoo-verbinding toevoegen",
      emptyHeading: "Koppel Odoo om helpdesktickets te maken en te koppelen",
      settingsLoadError: "Kon de instellingen niet laden.",
      settingsSaveError: "Kon de instelling niet opslaan.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inkomende sync — Odoo informeert FixControl",
        inboundIntro: "Alleen leesrichting: een supportevent kan context toevoegen voor de AI, maar stuurt nooit de gegoverneerde workflow aan en draait geen agents. Standaard uit.",
        outboundHeading: "Uitgaande sync — FixControl werkt het ticket bij",
        outboundIntro: "Pushes zijn best-effort en eenrichting: een Odoo-storing blokkeert nooit een FixControl-beslissing, de fasepush verplaatst tickets alleen naar een fase die je zelf benoemt, en de klant-reply gaat pas de deur uit na menselijke goedkeuring.",
        ingestConversationLabel: "Voer het ticketgesprek toe aan de AI-context",
        ingestConversationHint: "Het recente chattergesprek van het gekoppelde ticket (e-mailadressen en telefoonnummers geredigeerd; interne notities gemarkeerd als notitie) verschijnt als context op het issue, zodat de agent symptomen begrijpt en weet wat support al heeft geprobeerd.",
        captureLearningLabel: "Leg afgehandelde tickets vast als projectkennis",
        captureLearningHint: "Wanneer het gekoppelde ticket een afgehandelde fase bereikt, wordt de case opgeslagen als projectgebonden kennis (PII geredigeerd), zodat toekomstig werk op hetzelfde project hem kan terugvinden. Tickets zonder project worden nooit vastgelegd.",
        autoCreateLabel: "Maak automatisch een Odoo-ticket bij Slack-intake",
        autoCreateHint: "Aan: een melding via Slack maakt direct een gekoppeld Odoo-ticket. Uit (standaard): het ticket wordt alleen gemaakt wanneer iemand daar bewust voor kiest — via de knop op de intakekaart of goedkeuringsgestuurd.",
        progressNotesLabel: "Plaats missievoortgang als interne notities",
        progressNotesHint: "Patch gereed, PR geopend en fix toegepast verschijnen als interne chatter-notities op het gekoppelde ticket. Nooit zichtbaar voor de klant.",
        statusPushLabel: "Verplaats het ticket naar een door jou benoemde fase wanneer het issue is opgelost",
        statusPushHint: "Het oplossen van het FixControl-issue verplaatst het ticket naar de fase hieronder (gematcht tegen de fasen van het team van het ticket). Zonder benoemde fase wordt er niets gepusht; tickets die support al heeft afgehandeld blijven ongemoeid.",
        resolveStageLabel: "Naam van de doelfase",
        resolveStagePlaceholder: "Wacht op bevestiging",
        customerReplyLabel: "Zet een klant-reply klaar wanneer het issue is opgelost",
        customerReplyHint: "Oplossen zet een reply-concept in de communicatie-reviewwachtrij. Er wordt nooit automatisch gepost: een mens keurt het concept goed (en bewerkt het meestal), en pas dan wordt het als klantzichtbaar bericht geplaatst.",
      },
    },
    topdesk: {
      pageTitle: "TOPdesk",
      pageSubtitle: "Koppel een TOPdesk-omgeving zodat {company} meldingen kan aanmaken en koppelen terwijl FixControl governance en remediatie afhandelt.",
      addHeading: "Nog een TOPdesk-verbinding toevoegen",
      emptyHeading: "Koppel TOPdesk om meldingen te maken en te koppelen",
      settingsLoadError: "Kon de instellingen niet laden.",
      settingsSaveError: "Kon de instelling niet opslaan.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inkomende sync — TOPdesk informeert FixControl",
        inboundIntro: "Alleen leesrichting: een servicedeskgebeurtenis kan context toevoegen voor de AI, maar stuurt nooit de gegoverneerde workflow aan en draait geen agents. Standaard uit.",
        outboundHeading: "Uitgaande sync — FixControl werkt de melding bij",
        outboundIntro: "Pushes zijn best-effort en eenrichting: een storing bij TOPdesk blokkeert nooit een FixControl-beslissing, de statuspush gebruikt alleen een status die je zelf benoemt, en het antwoord aan de melder gaat pas de deur uit na menselijke goedkeuring.",
        ingestConversationLabel: "Voeg de acties van de melding toe aan de AI-context",
        ingestConversationHint: "De recente acties op de gekoppelde melding (e-mailadressen en telefoonnummers geredigeerd; acties die verborgen zijn voor de melder gemarkeerd als intern) verschijnen als context op het issue, zodat de agent de symptomen begrijpt en weet wat de servicedesk al heeft geprobeerd.",
        captureLearningLabel: "Leg afgehandelde meldingen vast als projectkennis",
        captureLearningHint: "Zodra de gekoppelde melding gereed of gesloten is, wordt de casus opgeslagen als projectgebonden kennis (persoonsgegevens geredigeerd), zodat toekomstig werk op hetzelfde project hem kan terugvinden. Meldingen zonder project worden nooit vastgelegd.",
        autoCreateLabel: "Maak automatisch een TOPdesk-melding bij Slack-intake",
        autoCreateHint: "Aan: een melding via Slack maakt direct een gekoppelde TOPdesk-melding. Uit (standaard): de melding wordt alleen aangemaakt wanneer iemand daar bewust voor kiest — via de knop op de intakekaart of goedkeuringsgestuurd.",
        progressNotesLabel: "Plaats missievoortgang als interne acties",
        progressNotesHint: "Patch gereed, PR geopend en fix toegepast komen als acties op de gekoppelde melding te staan, gemarkeerd als niet zichtbaar voor de melder. Nooit zichtbaar voor de klant.",
        statusPushLabel: "Zet de melding op gereed wanneer het issue is opgelost",
        statusPushHint: "Het oplossen van het FixControl-issue zet de gekoppelde melding op gereed. Benoem je hieronder een verwerkingsstatus, dan wordt die status ook gezet — statusnamen bepaal je zelf in TOPdesk, dus er wordt niets gegokt. Laat je het veld leeg, dan wordt de melding alleen op gereed gezet; meldingen die de servicedesk al heeft afgehandeld blijven ongemoeid.",
        resolveStatusLabel: "Naam van de verwerkingsstatus",
        resolveStatusPlaceholder: "Wacht op bevestiging",
        customerReplyLabel: "Zet een antwoord aan de melder klaar wanneer het issue is opgelost",
        customerReplyHint: "Oplossen zet een conceptantwoord in de communicatie-reviewwachtrij. Er wordt nooit automatisch gepost: een mens keurt het concept goed (en bewerkt het meestal), en pas dan komt het als actie op de melding te staan die de melder kan zien.",
      },
    },
    slack: {
      pageTitle: "Slack",
      pageSubtitle: "Stuur issues, patches en beslissingen naar Slack. Koppel een workspace, plak een bot-token + signing secret en probeer /fix vanuit een kanaal waar de app is geïnstalleerd.",
      statusTitle: "Status",
      healthTitle: "Leveringsstatus",
      statusConnected: "Gekoppeld",
      statusTeamSuffix: "— team {team}",
      statusConfiguredMissingCreds: "Geconfigureerd maar credentials ontbreken",
      statusNotConnected: "Niet gekoppeld",
      reinstallCta: "Opnieuw installeren met Slack",
      connectCta: "Koppelen met Slack",
      oauthHint: "Aanbevolen — Slack levert het bot-token rechtstreeks aan FixControl.",
      envFallbackPrefix: "Environment-fallback actief:",
      envFallbackTeamSuffix: "team {team}",
      connectionTitle: "Verbinding",
      connectionSub: "Slack-workspace-identifiers en credentials. Tokens worden versleuteld opgeslagen; een leeg veld betekent dat de bestaande waarde behouden blijft.",
      fields: {
        teamId: "Slack-team-ID",
        teamIdHint: "Het team_id dat Slack bij elk verzoek meestuurt. Te zien in je Slack-URL of door /fix uit te voeren en het verzoek te inspecteren.",
        defaultChannel: "Standaard kanaal-ID",
        defaultChannelHint: "Kanaal-ID (geen naam) waar meldingen landen als er geen thread-mapping bestaat.",
        botToken: "Bot-token",
        botTokenHint: "Uit je Slack-app → OAuth & Permissions → Bot User OAuth Token.",
        signingSecret: "Signing secret",
        signingSecretHint: "Uit je Slack-app → Basic Information → Signing Secret. Wordt gebruikt om elk inkomend verzoek te verifiëren.",
        storedPlaceholder: "•••••• (opgeslagen)",
      },
      saving: "Opslaan…",
      saved: "Opgeslagen",
      sending: "Versturen…",
      sendTest: "Test versturen",
      sendTestTitleReady: "Plaats een testmelding in het standaardkanaal",
      sendTestTitleUnready: "Sla eerst credentials op",
      disable: "Uitschakelen",
      disableConfirm: "Slack-integratie uitschakelen? Meldingen stoppen totdat je hem weer inschakelt.",
      testSent: "Verstuurd — controleer het Slack-kanaal.",
      testFailed: "Mislukt: {reason}",
      testNetworkError: "Netwerkfout.",
      appSetupTitle: "Slack-app instellen",
      appSetupSub: "Configureer deze endpoints in je Slack-app. Alle drie moeten bereikbaar zijn vanaf Slack's servers — gebruik in dev een Cloudflare Tunnel of vergelijkbaar.",
      endpoints: {
        commands: "Slash-command request URL",
        actions: "Interactivity request URL",
        events: "Events API request URL",
      },
      scopesPrefix: "Vereiste bot-scopes:",
      slashCommandPrefix: "Het slash-command moet zijn",
    },
    teams: {
      pageTitle: "Microsoft Teams",
      pageSubtitle: "Stuur issues, patches en beslissingen naar Teams via het Bot Framework. Configureer je Entra-tenant en bot-credentials en kies een standaardkanaal.",
      statusTitle: "Status",
      statusConnected: "Gekoppeld",
      statusTenantSuffix: "— tenant {tenant}",
      statusConfiguredMissingCreds: "Geconfigureerd maar credentials ontbreken",
      statusNotConnected: "Niet gekoppeld",
      envFallbackPrefix: "Environment-fallback actief:",
      devInsecureWarning: "Dev-insecure-modus staat aan — verificatie van request-handtekeningen wordt overgeslagen. Zet dit nooit aan in productie.",
      connectionTitle: "Verbinding",
      connectionSub: "Bot Framework-credentials en de Teams-conversatie waarin gepost wordt. Secrets worden versleuteld opgeslagen; laat een veld leeg om de bestaande waarde te behouden.",
      fields: {
        tenantId: "Microsoft Entra-tenant-ID",
        tenantIdHint: "De Azure AD-tenant-id waarvan de bot-registratie eigenaar is.",
        conversation: "Standaard conversatie-/kanaal-ID",
        conversationHint: "Waar meldingen landen als er geen thread-mapping bestaat. Ziet eruit als 19:…@thread.tacv2.",
        serviceUrl: "Service-URL",
        serviceUrlHint: "Bot Framework regionaal endpoint, bv. https://smba.trafficmanager.net/emea/.",
        botAppId: "Bot App-ID",
        botAppIdHint: "Bot Framework MicrosoftAppId uit de Azure Bot-resource.",
        botAppPassword: "Bot App-wachtwoord",
        botAppPasswordHint: "Bot Framework client secret. Versleuteld opgeslagen.",
        storedPlaceholder: "•••••• (opgeslagen)",
      },
      saving: "Opslaan…",
      saved: "Opgeslagen",
      sending: "Versturen…",
      sendTest: "Test versturen",
      sendTestTitleReady: "Plaats een testmelding in de standaardconversatie",
      sendTestTitleUnready: "Sla eerst credentials op",
      disable: "Uitschakelen",
      disableConfirm: "Teams-integratie uitschakelen? Meldingen stoppen totdat je hem weer inschakelt.",
      testSent: "Verstuurd — controleer het Teams-kanaal.",
      testFailed: "Mislukt: {reason}",
      testNetworkError: "Netwerkfout.",
      appSetupTitle: "Bot Framework instellen",
      appSetupSub: "Wat Microsoft van je nodig heeft om berichten af te leveren.",
      appSetupBody: "Registreer een Azure Bot-resource, installeer het manifest in je Teams-tenant en wijs het Messaging-endpoint naar /api/integrations/teams. Kopieer de App-ID en een client secret naar de velden hierboven.",
      wizard: {
        title: "Verbindingswizard",
        subtitle: "Volg de stappen om Teams te koppelen. Elke stap vinkt zichzelf af zodra hij klaar is.",
        healthTitle: "Leveringsstatus",
        connectedBanner: "Teams is gekoppeld — meldingen en goedkeuringen kunnen nu naar je kanaal stromen.",
        stepDone: "Klaar",
        stepNow: "Nu",
        stepTodo: "Te doen",
        nextStep: "Klaar — volgende stap",
        open: "Openen",
        copy: "Endpoint kopiëren",
        copied: "Gekopieerd",
        serviceUrlInvalid: "Dit lijkt geen URL — hij moet beginnen met https://",
        modeQuestion: "Hoe wil je koppelen?",
        modeShared: "FixControl's Teams-app",
        modeSharedHint: "Aanbevolen — geen Azure nodig",
        modeOwn: "Eigen Azure-bot",
        modeOwnHint: "Enterprise — volledige controle",
        sharedUnavailable: "FixControl heeft op deze server nog geen gedeelde Teams-app geconfigureerd. Gebruik hieronder je eigen bot, of vraag je FixControl-beheerder om TEAMS_BOT_APP_ID en TEAMS_BOT_APP_PASSWORD in te stellen.",
        code: {
          label: "Koppelcode van de bot",
          hint: "Typ \"link\" tegen de FixControl-bot in je Teams-kanaal en plak de code die hij terugstuurt — geen tenant-ID nodig.",
          button: "Koppelen met code",
          linking: "Koppelen…",
          ok: "Gekoppeld — je Teams-werkruimte en kanaal zijn nu verbonden.",
          failed: "Koppelen mislukt: {reason}",
          orManual: "Geen code? Vul de gegevens hieronder handmatig in.",
          reasonExpired: "de code is verlopen — typ opnieuw \"link\" voor een nieuwe",
          reasonInvalid: "de code werd niet herkend — typ opnieuw \"link\" in Teams",
          reasonAlreadyLinked: "deze Teams-werkruimte is al aan een andere FixControl-werkruimte gekoppeld",
          connectedTitle: "Werkruimte verbonden",
          connectedBody: "Je Teams-werkruimte is verbonden — hier is geen code nodig. \"link\" typen in Teams koppelt vanaf nu het persoonlijke account van elk teamlid (iets anders): zij typen \"link\" tegen de FixControl-bot en bevestigen in de browser, waarna ze poorten vanuit Teams kunnen goedkeuren.",
          editManual: "Tenant / standaardkanaal handmatig wijzigen",
        },
        pkg: {
          title: "Download de Teams-app",
          hint: "Geef dit pakket aan een Teams-beheerder om de FixControl-app in je werkruimte te installeren.",
          download: "Teams-app-pakket downloaden",
          uploadTitle: "Zo installeer je het in Teams",
          step1: "In Teams: Apps → Je apps beheren → Een app uploaden.",
          step2: "Kies \"Een aangepaste app uploaden\" en selecteer de gedownloade .zip.",
          step3: "Voeg 'm toe aan het meldingenkanaal en typ \"link\" om te koppelen.",
          unavailable: "Het app-pakket is op deze server nog niet geconfigureerd (TEAMS_APP_ID ontbreekt).",
        },
        steps: {
          sharedIntroTitle: "Je gebruikt FixControl's Teams-app",
          sharedIntroBody: "Geen Azure-setup nodig. FixControl beheert een gedeelde, geverifieerde Teams-bot — je installeert hem alleen in je werkruimte en kiest een kanaal.",
          sharedInstallTitle: "Installeer de app en kies een kanaal",
          sharedInstallBody: "Installeer de FixControl-app in je Teams-tenant en @-mention de bot één keer in het kanaal waar meldingen moeten landen — FixControl vangt dan automatisch je tenant-ID, conversatie-ID en service-URL op. Ontbreekt er iets? Vul het hieronder aan.",
          prepTitle: "Voordat je begint",
          prepBody: "Je koppelt FixControl aan Teams via een Azure Bot. Je hebt twee dingen nodig: toegang tot het Azure-/Entra-portaal van je organisatie, en een Teams-kanaal waar de meldingen mogen landen. Reken op zo'n 10 minuten.",
          createBotTitle: "Maak een Azure Bot aan",
          createBotBody: "Maak in het Azure-portaal een 'Azure Bot'-resource aan (Multi Tenant is prima). Azure maakt automatisch een app-registratie voor je. Noteer twee waarden: de App-ID (MicrosoftAppId) en je Entra-tenant-ID — die plak je in stap 5.",
          createBotLinkBot: "Azure Bot aanmaken",
          createBotLinkEntra: "App-registraties (hier vind je je tenant-ID)",
          secretTitle: "Maak een client secret",
          secretBody: "Open je app-registratie → Certificates & secrets → New client secret (kies 12+ maanden). Kopieer meteen de Value — die is daarna niet meer zichtbaar. Dit is het Bot App-wachtwoord.",
          secretLink: "App-registraties openen",
          endpointTitle: "Zet het messaging endpoint",
          endpointBody: "Vul in je Azure Bot → Configuration → Messaging endpoint de onderstaande URL in. Microsoft moet deze vanaf het internet kunnen bereiken.",
          endpointLocalNote: "Werk je lokaal? Zet er een publieke tunnel voor (bv. ngrok of een VS Code dev tunnel) en gebruik die URL. Voor lokaal testen zónder handtekening-verificatie kun je TEAMS_DEV_INSECURE_WEBHOOKS=true zetten — nooit in productie.",
          credentialsTitle: "Vul de credentials in FixControl in",
          credentialsBody: "Plak de tenant-ID, App-ID en client secret uit Azure en sla op. Secrets worden versleuteld opgeslagen.",
          installTitle: "Voeg de bot toe in Teams en kies een kanaal",
          installBody: "Voeg in je Azure Bot → Channels 'Microsoft Teams' toe. Installeer de bot in je team en @-mention hem één keer in het kanaal waar meldingen moeten landen — FixControl vangt dan automatisch de conversatie-ID en service-URL op. Lukt dat niet? Plak ze hieronder handmatig.",
          testTitle: "Test de verbinding",
          testBody: "Stuur een testmelding naar het gekozen kanaal. Zie je hem in Teams verschijnen, dan staat de koppeling.",
        },
      },
    },
    office365: {
      pageTitle: "Office 365 / Outlook",
      pageSubtitle: "Importeer supportmails uit Outlook en zet ze om in issues.",
      statusTitle: "Status",
      statusConnected: "Verbonden",
      statusAccountSuffix: "— {email}",
      statusDisabled: "Uitgeschakeld — verbind opnieuw om te hervatten",
      statusNotConnected: "Niet verbonden",
      connectionsCount: "{count} account(s) gekoppeld",
      connectAnother: "Nog een account koppelen",
      oauthMissing: "Microsoft OAuth is niet geconfigureerd op deze server. Stel MICROSOFT_OAUTH_CLIENT_ID, MICROSOFT_OAUTH_CLIENT_SECRET en MICROSOFT_OAUTH_REDIRECT_URI in voordat je verbindt.",
      envClientId: "Client-ID:",
      envRedirectUri: "Redirect-URI:",
      envTenant: "Tenant:",
      envScopes: "Scopes:",
      connect: "Verbinden met Microsoft",
      reconnect: "Microsoft opnieuw verbinden",
      disable: "Uitschakelen",
      disableConfirm: "Office 365-integratie uitschakelen? E-mailintake stopt totdat je opnieuw verbindt.",
      connectOk: "Microsoft-account verbonden.",
      connectErr: "Verbinden mislukt: {reason}",
      mailboxTitle: "Postvak",
      mailboxSub: "Het Outlook-postvak waaruit we lezen. Standaard de verbonden gebruiker; gebruik een shared mailbox-UPN als het verbonden account gedelegeerde toegang heeft.",
      fields: {
        mailbox: "Postvakadres",
        mailboxHint: "E-mailadres (UPN) van het postvak dat gelezen moet worden. Gebruik een shared mailbox-UPN bij delegatie.",
      },
      saving: "Opslaan…",
      testing: "Testen…",
      testConnection: "Verbinding testen",
      testOk: "Verbonden als {email}, postvak {mailbox}.",
      testFailed: "Mislukt: {reason}",
      testNetworkError: "Netwerkfout.",
      recentTitle: "Recente berichten",
      recentSub: "De laatste e-mails die Microsoft Graph zou leveren. Klik Importeren om er één om te zetten naar een issue.",
      loadRecent: "Recente berichten laden",
      recentEmpty: "Geen berichten gevonden. (Leeg postvak of filter actief.)",
      noSubject: "(geen onderwerp)",
      import: "Importeren",
      importQueued: "Geïmporteerd in de inbox. Intake bepaalt of het een issue wordt.",
      importDeduped: "Al geïmporteerd. Geen duplicaat aangemaakt.",
      importFailed: "Importeren mislukt: {reason}",
      syncTitle: "Postvak synchroniseren",
      syncSub: "Haal de laatste 10 berichten op uit het verbonden postvak en verwerk ze via intake. Hetzelfde e-mailbericht levert nooit twee issues op.",
      syncNow: "Nu synchroniseren",
      syncing: "Synchroniseren…",
      syncDone: "Opgehaald {fetched}: {queued} nieuw in de inbox (intake bepaalt issue of support), {duplicates} al geïmporteerd.",
      syncFailed: "Synchroniseren mislukt: {reason}",
      syncNetworkError: "Netwerkfout tijdens synchroniseren.",
      liveSyncTitle: "Live-sync (Microsoft Graph)",
      liveSyncSub: "Met live-sync ontvangt FixControl een Microsoft Graph-melding zodra een nieuwe e-mail in het verbonden postvak binnenkomt en verwerkt deze direct via intake. Abonnementen verlopen elke ~3 dagen en worden automatisch verlengd.",
      liveSyncEnabled: "Live-sync staat aan",
      liveSyncDisabled: "Live-sync staat uit",
      liveSyncSubscriptionId: "Subscription-ID:",
      liveSyncExpiresAt: "Verloopt op:",
      liveSyncLastWebhookAt: "Laatst ontvangen webhook:",
      liveSyncLastError: "Laatste fout: {reason}",
      liveSyncEnable: "Live-sync aanzetten",
      liveSyncEnabling: "Aanzetten…",
      liveSyncEnableOk: "Live-sync ingeschakeld.",
      liveSyncEnableFailed: "Kan live-sync niet aanzetten: {reason}",
      liveSyncDisable: "Live-sync uitzetten",
      liveSyncDisabling: "Uitzetten…",
      liveSyncDisableOk: "Live-sync uitgeschakeld.",
      liveSyncDisableFailed: "Kan live-sync niet uitzetten: {reason}",
      liveSyncDisableConfirm: "Live-sync uitzetten? Outlook-mails komen alleen via handmatige sync binnen totdat je het weer aanzet.",
      liveSyncRenew: "Nu verlengen",
      liveSyncRenewing: "Verlengen…",
      liveSyncRenewOk: "Abonnement verlengd.",
      liveSyncRenewFailed: "Verlengen mislukt: {reason}",
      liveSyncNetworkError: "Netwerkfout.",
      scopesTitle: "Microsoft Graph-scopes",
      scopesSub: "Toegekend bij consent. Minimale rechten voor de MVP — geen verzenden, geen schrijven.",
      scopesNote: "Scopes toevoegen vereist opnieuw consent: ontkoppel en verbind opnieuw zodra nieuwe scopes worden uitgerold.",
      derivedConnected: "Office365 is geconfigureerd. De live capability-status staat hieronder.",
      derivedRenewalExpiring: "Live-sync abonnement verloopt bijna. De verlenging zou automatisch moeten draaien.",
      derivedRenewalFailed: "Live-sync verlenging is herhaaldelijk mislukt. Controleer de cron en Microsoft-rechten.",
      derivedSyncError: "Mailsynchronisatie liep tegen een fout aan. FixControl probeert het opnieuw waar mogelijk.",
      derivedReconnectRequired: "Verbind je Microsoft-account opnieuw om sync te herstellen.",
      derivedDisconnected: "Verbind Office365 om supportmails te importeren.",
      opLastSyncAt: "Laatste sync:",
      opLastSuccessfulRenewal: "Laatste geslaagde verlenging:",
      opSubscriptionExpiry: "Abonnement verloopt op:",
      opRenewalFailedCount: "Achtereenvolgende verlengingsfouten:",
      opWebhookFailedCount: "Webhook-leveringen opgegeven:",
      opLastError: "Laatste fout: {reason}",
      opLastErrorAt: "(op {at})",
      opWebhookUrl: "Webhook-URL:",
      opNever: "nog niet",
    },
  },

  settingsWebhooks: {
    eyebrow: "Instellingen · Integraties",
    title: "Uitgaande webhooks",
    subtitle: "Stuur FixControl-events door naar elk HTTPS-endpoint dat jouw team beheert.",
    addWebhook: "Webhook toevoegen",
    noWebhooks: "Nog geen webhooks ingesteld.",
    noWebhooksHint: "Klik op \"Webhook toevoegen\" om events naar een endpoint van een teamgenoot te sturen.",
    table: {
      name: "Naam",
      url: "URL",
      events: "Events",
      enabled: "Actief",
      created: "Aangemaakt",
      actions: "Acties",
      secret: "Secret",
    },
    actions: {
      edit: "Bewerken",
      delete: "Verwijderen",
      test: "Test versturen",
      viewLog: "Leveringen bekijken",
      enable: "Inschakelen",
      disable: "Uitschakelen",
    },
    create: {
      title: "Uitgaande webhook aanmaken",
      sub: "Kies welke events je doorstuurt en waarheen.",
      name: "Naam",
      namePlaceholder: "PagerDuty-koppeling",
      url: "Endpoint-URL",
      urlPlaceholder: "https://your-app.example.com/fixcontrol-webhook",
      urlHint: "Moet POST accepteren. Gebruik HTTPS in productie.",
      events: "Events",
      eventsHint: "Kies minstens één event-type.",
      cancel: "Annuleren",
      save: "Webhook aanmaken",
      saving: "Aanmaken…",
    },
    edit: {
      title: "Webhook bewerken",
      save: "Wijzigingen opslaan",
      saving: "Opslaan…",
    },
    confirmDelete: "Deze webhook verwijderen? Eerdere leverlogs blijven bewaard voor audit.",
    secretModal: {
      title: "Kopieer je webhook-secret",
      sub: "FixControl toont deze precies één keer. Bewaar hem veilig — er is geen herstelmogelijkheid.",
      copy: "Secret kopiëren",
      copied: "Gekopieerd",
      done: "Ik heb hem opgeslagen",
      preview: "Voorbeeld",
    },
    deliveries: {
      title: "Recente leveringen",
      empty: "Nog geen leveringen.",
      event: "Event",
      status: "Status",
      code: "Code",
      attempts: "Pogingen",
      error: "Fout",
      time: "Tijd",
      requestId: "Request-ID",
    },
    status: {
      pending: "In wachtrij",
      retrying: "Opnieuw proberen",
      success: "Geslaagd",
      failed: "Mislukt",
      gave_up: "Opgegeven",
    },
    events: {
      "issue.created": "Issue aangemaakt",
      "issue.updated": "Issue bijgewerkt",
      "patch.ready": "Patch klaar",
      "patch.approved": "Patch goedgekeurd",
      "patch.failed": "Patch mislukt",
      "pr.created": "Pull request geopend",
      "integration.error": "Integratiefout",
    },
    errors: {
      missingTenant: "Kies eerst een workspace.",
      loadFailed: "Kan webhooks niet laden.",
      saveFailed: "Kan webhook niet opslaan.",
      testFailed: "Test-levering kon niet worden ingepland.",
      deleteFailed: "Kan webhook niet verwijderen.",
    },
    testDispatched: "Test-levering ingepland — open de leveringslog om te bevestigen.",
  },

  settingsAudit: {
    eyebrow: "Instellingen",
    title: "Audit-log",
    subtitle: "Beveiligingsgevoelige acties — wie deed wat, wanneer, en met welk resultaat",
    fromMission: "Missie",
    exportJson: "JSON exporteren",
    exportTitle: "Download de momenteel zichtbare rijen als JSON",
    refresh: "Vernieuwen",
    accessRequired: "Audit-toegang vereist",
    accessRequiredDetail: "Alleen admins en managers kunnen het audit-log bekijken.",
    failedToLoad: "Kan audit-log niet laden",
    eventPlaceholder: "Exact event, bv. patch.applied",
    searchPlaceholder: "Zoeken…",
    sinceLast1h: "Laatste 1 uur",
    sinceLast24h: "Laatste 24 uur",
    sinceLast7d: "Laatste 7 dagen",
    sinceLast30d: "Laatste 30 dagen",
    sinceAllTime: "Alle tijd",
    byEvent: "Per event",
    headers: {
      time: "Tijd",
      event: "Event",
      user: "Gebruiker",
      tenant: "Organisatie",
      target: "Doel",
      fields: "Velden",
    },
    noRows: "Geen rijen",
    bulk: {
      exportSelected: "Geselecteerde exporteren",
    },
    groups: {
      all: "Alle",
      auth: "Auth",
      governance: "Governance",
      workflow: "Workflow",
      orchestration: "Orchestratie",
      devops: "DevOps",
      patch: "Patch",
      secrets: "Secrets",
      settings: "Instellingen",
      user: "Gebruiker",
      agent: "Agent",
      codebase: "Codebase",
      // Behouden voor oudere bladwijzers / gespoolde rijen met deze prefixes.
      connection: "Verbinding",
      infra: "Infra",
      runFix: "Run-fix",
      maintenance: "Onderhoud",
      cli: "CLI",
    },
    drawer: {
      eyebrow: "Audit-event",
      userId: "Gebruikers-id",
      tenant: "Organisatie",
      target: "Doel",
      auditId: "Audit-id",
      summary: "Samenvatting",
      fields: "Velden",
      closeAria: "Sluiten",
    },
    spool: {
      header: "In wachtrij voor herhaling",
      refresh: "Vernieuwen",
      databaseSpool: "Database-spool",
      webhookPrefix: "Webhook: {sink}",
      eventCount: "{count} event",
      eventCountPlural: "{count} events",
      oldest: "oudste {ts}",
      newest: " — nieuwste {ts}",
    },
    pills: {
      outcome: "Resultaat",
      action: "Actie",
      newStatus: "Nieuwe status",
      workspace: "Werkruimte",
      hasNote: "Heeft notitie",
      mode: "Modus",
      pr: "PR",
      mr: "MR",
      branch: "Branch",
      files: "Bestanden",
      path: "Pad",
      overall: "Totaal",
      patchCount: "Aantal patches",
      changedFields: "Gewijzigde velden",
      rotatedSecrets: "Geroteerde secrets",
      fingerprint: "Fingerprint",
      carriedKeys: "Meegenomen keys",
      applies: "Toepassing",
      onRestart: "bij herstart",
      now: "nu",
      runsRetention: "Runs-retentie",
      auditRetention: "Audit-retentie",
      runsDeleted: "Runs verwijderd",
      auditDeleted: "Audit verwijderd",
      pass: "geslaagd",
      fail: "mislukt",
      yes: "ja",
      no: "nee",
      provider: "Provider",
      kind: "Soort",
      account: "Account",
      decision: "Besluit",
      channel: "Kanaal",
      gateType: "Gate",
      riskLevel: "Risico",
      fromState: "Van",
      toState: "Naar",
      reason: "Reden",
    },
  },

  // De weigeringspagina's van de preview-gateway — zie en.ts.
  previewGateway: {
    pending: {
      title: "De omgeving wordt gestart",
      body: "We halen de images op, starten de services en wachten tot de applicatie antwoordt.",
      /** Onder de spinner: waarom de pagina vanzelf verspringt. */
      retrying: "Dit duurt meestal een halve minuut. Deze pagina ververst zichzelf en toont de applicatie zodra hij draait.",
      /** Zelfde plek, nadat het automatisch verversen is gestopt. */
      retryStopped: "Het duurt langer dan verwacht. We zijn gestopt met automatisch verversen.",
      retryNow: "Nu opnieuw proberen",
    },
    ended: {
      title: "Dit voorbeeld is beëindigd",
      body: "Een voorbeeld is een tijdelijke validatieomgeving en die is nu gestopt. Open een nieuwe via Code & omgevingen bij het project.",
    },
    unavailable: {
      title: "Dit voorbeeld is niet bereikbaar",
      body: "De tijdelijke validatieomgeving is op dit moment niet bereikbaar. Probeer het zo nog eens, of open een nieuw voorbeeld bij het project.",
    },
    stopped: {
      title: "Dit voorbeeld draait niet meer",
      body: "De applicatie in deze tijdelijke validatieomgeving is gestopt. Open een nieuw voorbeeld via Code & omgevingen bij het project.",
    },
    /** Eén pagina voor elke weigering — zie en.ts. */
    unauthorized: {
      title: "Open dit voorbeeld vanuit FixControl",
      body: "Een voorbeeld hoort bij je FixControl-sessie, en de aanmelding op dit adres is kortlopend. Open het voorbeeld opnieuw via de sandboxrun waar het bij hoort.",
    },
  },
  // Waar het werk van een project mag plaatsvinden, en de weigeringen die
  // daaruit volgen. Zie en.ts voor de toelichting.
  residency: {
    refusal: {
      residency_execution_downgrade:
        "Dit project is ingesteld om validatie in je eigen omgeving te draaien, maar de validatie die hiervoor nodig is zou op de infrastructuur van FixControl draaien. Er is niets verplaatst of gedraaid. Zet de validatie van dit project op je eigen omgeving, of haal de eis weg.",
      residency_runner_unavailable:
        "Dit project is ingesteld om validatie in je eigen omgeving te draaien, en er is op dit moment geen runner in je omgeving beschikbaar. Er is niets gedraaid. Breng de FixControl-agent weer online en probeer het opnieuw.",
      strict_source_residency_unavailable:
        "Broncode volledig binnen je eigen omgeving houden is nog niet beschikbaar. Validatie in je eigen omgeving draaien kan vandaag wel.",
    },
    section: {
      title: "Waar dit project draait",
      loadError: "Kon niet laden waar dit project mag draaien.",
      none: "Geen locatie-eis ingesteld — dit project draait op infrastructuur die FixControl beheert.",
      execution: "Code-uitvoering",
      aiProcessing: "AI-verwerking",
      executionCustomer: "Je eigen omgeving",
      executionHosted: "Beheerd door FixControl",
      aiHosted: "Beheerd door FixControl",
      aiCustomerProvider: "Je eigen AI-provider",
      aiCustomerLocal: "Een model binnen je eigen omgeving",
      readOnly: "Afgesproken met je team tijdens de onboarding. Hier niet aanpasbaar.",
      docsCta: "Hoe validatie draait",
    },
  },
  // De drie validatiestrategieën, benoemd voor wie er een kiest. Zie en.ts.
  validationStrategy: {
    label: {
      docker: "Geïsoleerde Docker",
      remote_kubernetes: "Je eigen Kubernetes-omgeving",
      docker_then_remote: "Eerst Docker, daarna je eigen omgeving",
      /** Een waarde die deze build niet kent. Ingekaderd, nooit kaal. */
      other: "Eigen opzet ({value})",
    },
    explainer: {
      docker: "Snelle, geïsoleerde validatie op infrastructuur die FixControl beheert.",
      remote_kubernetes: "Valideer tegen de omgeving waarin de wijziging echt gaat draaien.",
      docker_then_remote: "De geïsoleerde run draait eerst; pas na een groen resultaat start de run in je eigen omgeving. De poort vereist dan beide.",
    },
  },
  projectControl: {
    loadError: "Het overzicht van dit project kon niet worden geladen.",
    retry: "Opnieuw proberen",
    saveError: "Je wijzigingen konden niet worden opgeslagen ({status}). Er is niets gewijzigd.",
    deleteError: "Project kon niet worden verwijderd ({status}).",
    nav: {
      sectionsLabel: "Projectonderdelen",
      overview: "Instellen & status",
      intake: "Binnenkomend werk",
      code: "Code & omgevingen",
      testEnvironments: "Omgevingen",
      knowledge: "Kennis",
      updates: "Updates & meldingen",
      team: "Leden",
      automation: "AI-team",
      activity: "Activiteit",
      advanced: "Geavanceerd",
      connections: "Verbindingen",
      governance: "Goedkeuringen & regels",
      communication: "Klantcommunicatie",
      websiteChat: "Chat",
      hr: "HR-governance",
      /** De twee groepen in de zijbalk. "Hoort bij dit project" zijn de
       *  onderdelen die de actieve use cases van dit project zelf noemen;
       *  de rest blijft gewoon bereikbaar onder "Ook beschikbaar". */
      groups: {
        relevant: "Werk",
        additional: "Ook beschikbaar",
        pages: "Aparte pagina's",
      },
      /** Eén regel onder elk menu-item: elf kale zelfstandige naamwoorden
       *  laten een nieuwe klant raden waar iets staat. */
      hint: {
        overview: "Is dit project klaar, en wat is de volgende stap.",
        intake: "Waar nieuwe vragen en verzoeken vandaan komen.",
        issues: "De issues van dit project.",
        missions: "Waar de AI nu voor dit project aan werkt.",
        patches: "Voorgestelde codewijzigingen die op een besluit wachten.",
        workspaces: "De sessies waarin de AI aan deze code werkt.",
        websiteChat: "Chat op je website, of intern voor collega's.",
        code: "De repositories waar dit project aan werkt.",
        testEnvironments: "Waar wijzigingen veilig worden uitgeprobeerd voordat iemand ze goedkeurt.",
        knowledge: "Documentatie waar de AI uit put.",
        updates: "Waar voortgang naartoe wordt teruggemeld.",
        team: "Wie aan dit project werkt, en met welke rol.",
        automation: "Wat de AI hier zelf mag doen.",
        activity: "Wat er is gebeurd.",
        advanced: "Technische details en probleemoplossing.",
        connections: "E-mail, chat, supportsoftware en code die dit project gebruikt.",
        governance: "Wat op dit project eerst door iemand goedgekeurd moet worden.",
        communication: "Hoe antwoorden aan klanten klinken, eruitzien en ondertekend worden.",
      },
    },
    unsaved: {
      /** Het formulier staat verspreid over meerdere pagina's, dus de balk
       *  moet zeggen WAAR de wijziging staat — anders ga je zoeken. */
      messageOn: "Niet-opgeslagen wijzigingen bij: {sections}.",
      revert: "Terugdraaien",
      saved: "Wijzigingen opgeslagen.",
      saveHint: "Wijzigingen gelden pas als je ze onderaan opslaat met “Wijzigingen opslaan”.",
      sectionHint: "Hier staan wijzigingen die nog niet zijn opgeslagen.",
    },
    /** Een sectie waar dit project geen recht op heeft (of die de resolver
     *  voor dit projecttype weglaat) is nu een URL die iemand kan typen. */
    sectionUnavailable: {
      title: "Dit onderdeel geldt niet voor dit project.",
      body: "Deze pagina hoort niet bij dit soort project. De rest van het project blijft gewoon bereikbaar.",
      noPermissionTitle: "Je kunt dit onderdeel van het project niet openen.",
      noPermissionBody: "Je rol in dit project geeft daar geen toegang toe. Een beheerder van dit project kan je toegang geven.",
      back: "Terug naar het overzicht",
    },
    team: {
      title: "Teamtoegang",
      subtitle: "Leden zien de issues van dit project in de app en de interne ticketdetails in Slack- en Teams-antwoorden. Admins en managers hebben altijd organisatiebrede toegang; operators zien alleen projecten waarvan ze lid zijn en tickets die aan hen zijn toegewezen of door hen zijn aangemaakt.",
      empty: "Nog geen leden.",
      emptyHint: "Operators zien de issues van dit project niet (in de app of in chatantwoorden) totdat je ze toevoegt. Admins en managers hebben altijd toegang.",
      emailPlaceholder: "collega@bedrijf.nl",
      add: "Lid toevoegen",
      remove: "Lid verwijderen",
      userNotFound: "Geen actieve gebruiker met e-mailadres {email} in deze organisatie. Nodig collega's eerst uit via Instellingen → Gebruikers; daarna kun je ze hier toevoegen.",
      addFailed: "Dit lid kon niet worden toegevoegd. Er is niets gewijzigd.",
      removeConfirmTitle: "{name} uit dit project verwijderen?",
      removeConfirm: "Diegene ziet de issues en ticketdetails van dit project daarna niet meer.",
      removeFailed: "Dit lid kon niet worden verwijderd. Er is niets gewijzigd.",
      loadError: "De ledenlijst kon niet worden geladen.",
      manageUsers: "Gebruikers beheren of uitnodigen",
    },
    status: {
      connected: "Verbonden",
      degraded: "Vereist aandacht",
      notConfigured: "Uitgeschakeld",
      none: "Niet ingesteld",
    },
    statusReason: {
      providerDisconnected: "{provider} is nog niet verbonden op organisatieniveau — verbind hem eerst, daarna werkt deze koppeling.",
      providerUnhealthy: "De verbinding met {provider} werkt op dit moment niet — herstel de verbinding om deze koppeling weer werkend te krijgen.",
      connectProvider: "Verbinden",
      fixConnection: "Verbinding herstellen",
      deliveryFailing: "Recente updates naar deze bron zijn niet afgeleverd",
      deliveryDetail: "Laatste fout: {detail}",
      viewActivity: "Bekijk activiteit",
      disabled: "Deze koppeling staat uit — er komt geen werk binnen en er gaan geen updates naartoe totdat je hem inschakelt.",
      enable: "Inschakelen",
      enableFailed: "Kon de koppeling niet inschakelen.",
    },
    addResource: {
      title: {
        intake: "Werkbron toevoegen",
        code: "Runtime of repository koppelen",
        updates: "Updateroute toevoegen",
        approvals: "Goedkeuringskanaal toevoegen",
      },
      intro: {
        intake: "Kies waar het werk vandaan komt.",
        code: "Kies waar de code staat of waar hij draait.",
        updates: "Kies waar voortgang naartoe gaat.",
        approvals: "Kies waar goedkeuringsverzoeken landen.",
      },
      group: {
        humanReported: {
          title: "Iemand meldt het",
          hint: "Een bericht, ticket, issue of e-mail wordt hier werk in dit project.",
        },
        systemReported: {
          title: "Een systeem meldt het zelf",
          hint: "Een storing of alert uit je eigen omgeving opent werk, zonder dat iemand iets hoeft te melden.",
        },
        sourceCode: {
          title: "Waar de code staat",
          hint: "De repository waar agents aan werken.",
        },
        runtime: {
          title: "Waar de code draait",
          hint: "De omgeving waar een wijziging naartoe gaat, en de deployment-events die daaruit komen.",
        },
        backToReporter: {
          title: "Terug naar wie het meldde",
          hint: "Voortgang en status landen op het ticket of issue waar het werk vandaan kwam.",
        },
        toYourTeam: {
          title: "Naar je eigen team",
          hint: "Updates komen binnen in een kanaal of mailbox van je team.",
        },
        toYourSystems: {
          title: "Naar je eigen systemen",
          hint: "De uitkomst gaat terug naar de repository, pipeline of het endpoint dat erop wacht.",
        },
        humanApproves: {
          title: "Waar mensen beslissen",
          hint: "Het goedkeuringsverzoek komt hier binnen; iemand keurt goed of af.",
        },
        deployGate: {
          title: "Bij de uitrol zelf",
          hint: "De uitrol pauzeert tot iemand groen licht geeft.",
        },
        other: {
          title: "Overig",
          hint: "Ook te koppelen in dit onderdeel.",
        },
        inert: {
          title: "Levert hier nog niets af",
          hint: "Koppelen mag — het helpt binnenkomend werk aan dit project toe te wijzen — maar FixControl stuurt hier vandaag nog niets naartoe.",
        },
      },
      statusUnverified: "Kon niet controleren welke systemen verbonden zijn — kies een systeem, of verbind er eerst een via Instellingen → Kanalen.",
      notConnected: "Niet verbonden",
      connectFirst: "Verbind dit systeem eerst op organisatieniveau, daarna kun je het hier koppelen.",
      connectLink: "Verbinden",
    },
    refKind: {
      channel: "kanaal",
      userGroup: "gebruikersgroep",
      operatorGroup: "operatorgroep",
      ticketGroup: "ticketgroep",
      product: "product",
      tag: "label",
      account: "hele account",
      issueProject: "project",
      component: "component",
      team: "team",
      repository: "repository",
      cluster: "cluster",
      namespace: "namespace",
      service: "service",
      labelSelector: "labelselectie",
      application: "applicatie",
      rollout: "rollout",
      argoProject: "Argo-project",
      mailbox: "mailbox",
      webhook: "webhook",
    },
    usedFor: "Gebruikt voor",
    fromProvider: "via {provider}",
    overview: {
      title: "Instellen & status",
      subtitle: "Alles wat dit project nodig heeft, in één lijst.",
      health: {
        ready: "Project klaar voor gebruik",
        needsSetup: "Instellen nog niet af",
        degraded: "Vraagt aandacht",
        setupRecommended: "Project klaar voor gebruik",
        setupRecommendedHint: "Alles wat nodig is werkt. Een aanbevolen stap staat nog open — zie de lijst.",
        stepSeparator: "·",
        readyHint: "Alles wat dit project nodig heeft is ingesteld en werkt.",
        needsSetupHint: "Er staat nog een verplichte stap open — de lijst laat zien welke.",
        degradedHint: "Alles wat nodig is, is ingesteld, maar nog niet alles werkt of is getest.",
      },
      attentionTitle: "Vraagt aandacht",
      attentionDisconnected: "{provider} is nog niet verbonden — verbind hem eerst.",
      attentionUnhealthy: "de verbinding met {provider} werkt op dit moment niet.",
      attentionDelivery: "updates naar {provider} worden niet afgeleverd.",
      attentionView: "Bekijken",
      setup: {
        title: "Aan de slag",
        subtitle: "Nog een paar stappen en dit project draait: werk komt binnen, agents hebben kennis om mee te werken, en goedkeuringen landen op de juiste plek.",
        steps: {
          workSource: {
            title: "Verbind waar werk binnenkomt",
            hint: "Een Slack-kanaal, servicedesk, issuetracker of mailbox — of een alert uit je eigen systemen.",
            action: "Werkbron toevoegen",
          },
          codebase: {
            title: "Koppel de code",
            hint: "De repository of codebase waar agents aan werken.",
            action: "Codebase toevoegen",
          },
          knowledge: {
            title: "Voeg kennis toe",
            hint: "Documentatie waar agents uit putten — of laat een gekoppelde codebase inspecteren.",
            action: "Naar kennis",
          },
          approvals: {
            title: "Kies wie goedkeurt",
            hint: "Waar goedkeuringsverzoeken landen voordat risicovolle acties doorgaan.",
            action: "Goedkeuringskanaal toevoegen",
          },
        },
      },
      connected: "Verbonden",
      connectedNone: "Nog niets verbonden.",
      agentsUse: "Agents gebruiken",
      counts: {
        knowledge: "{count} kennisbron",
        knowledgePlural: "{count} kennisbronnen",
        codebases: "{count} codebase",
        codebasesPlural: "{count} codebases",
        intake: "{count} werkbron",
        intakePlural: "{count} werkbronnen",
        approvalRules: "{count} goedkeuringsregel",
        approvalRulesPlural: "{count} goedkeuringsregels",
        updateRoutes: "{count} updateroute",
        updateRoutesPlural: "{count} updateroutes",
      },
    },
    intake: {
      title: "Binnenkomend werk",
      subtitle: "Waar werk voor dit project binnenkomt.",
      empty: "Nog geen werkbronnen.",
      emptyHint: "Verbind een Slack-kanaal, servicedesk, issuetracker of mailbox zodat nieuw werk hier terechtkomt — of laat je eigen systemen een storing melden.",
      add: "Werkbron toevoegen",
      addMailbox: "Mailbox koppelen",
      mailboxHint: "Met een gekoppelde mailbox wordt elke mail aan dat adres een issue in dit project. Een antwoord aan de afzender gaat er pas uit nadat iemand het heeft goedgekeurd.",
      manage: "Binnenkomend werk beheren",
    },
    code: {
      title: "Code & omgevingen",
      subtitle: "De code waar agents aan werken en de runtime waar het naartoe gaat.",
      codebasesTitle: "Codebases",
      runtimeTitle: "Runtime & deployment",
      runtimeEmpty: "Geen runtime gekoppeld.",
      runtimeEmptyHint: "Koppel een Kubernetes-namespace of Argo-app om deployments en incidenten aan dit project te koppelen.",
      inspected: "Geïnspecteerd",
      notInspected: "Niet geïnspecteerd",
      inspectNow: "Nu inspecteren",
      defaultBadge: "standaard",
      addCodebase: "Codebase toevoegen",
      addRuntime: "Runtime toevoegen",
      unlinkCodebase: "Codebase ontkoppelen",
      manageRuntime: "Runtime beheren",
    },
    testEnvironments: {
      title: "Testomgevingen",
      subtitle:
        "Start een exacte gevalideerde revisie van dit project wanneer je wilt en bekijk hem. "
        + "Elke omgeving is de exacte commit én de exacte geverifieerde omgeving die de "
        + "validatie heeft bewezen — bij het starten wordt niets opnieuw onderzocht.",
      runningOfMax: "{running} van {max} draaiend",
      baseline: "Hoofdbranch",
      revisionOnly: "Revisie",
      staleBadge: "vervangen",
      open: "Openen",
      start: "Starten",
      stop: "Stoppen",
      restart: "Herstarten",
      rebuild: "Opnieuw opbouwen",
      revalidate: "Opnieuw valideren",
      more: "Meer acties",
      stepOf: "stap {done} van {total}",
      policyLabel: "Wanneer testomgevingen worden aangemaakt",
      policy: {
        disabled: "Uit",
        on_demand: "Op verzoek",
        after_validation: "Na een geslaagde validatie",
        auto_start: "Automatisch starten",
      },
      keepAlive: "Een uur openhouden",
      releaseHold: "Openhouden stoppen",
      viewValidation: "Bekijk de validatie",
      viewPatch: "Bekijk de wijziging",
      loadFailed: "De testomgevingen konden niet worden geladen.",
      actionFailed: "Dat is niet gelukt. Er is niets gewijzigd.",
      emptyDisabled: "Testomgevingen staan uit voor dit project.",
      emptyNoValidation:
        "Er is nog geen testomgeving beschikbaar, omdat geen revisie van dit project is "
        + "gevalideerd met een startbaar applicatiedoel.",
      addRevision: "Revisie toevoegen",
      pickerTitle: "Kies de revisie die je wilt starten",
      pickerHint:
        "Een branch is hier een manier om een commit te vinden, geen doel dat blijft "
        + "bewegen: we zetten de commit vast waar de branch nu op staat. Die commit "
        + "blijft deze omgeving, ook als de branch morgen verder gaat.",
      pickerSearch: "Zoek een branch",
      pickerBranches: "Branches",
      pickerCommit: "Of een exacte commit",
      pickerCommitPlaceholder: "40-teken commit",
      pickerDefaultBranch: "hoofdbranch",
      pickerNotFetched: "nog niet opgehaald",
      pickerRemoteUnreachable:
        "De remote kon niet worden bevraagd, dus dit zijn alleen de branches die al "
        + "lokaal staan.",
      pickerNoBranches: "Er zijn geen branches gevonden in de repository van dit project.",
      pickerBranchesFailed: "De branches konden niet worden opgehaald.",
      pin: "Vastzetten",
      pinning: "Bezig met vastzetten…",
      cancel: "Annuleren",
      status: {
        starting: "Bezig met starten",
        stopping: "Bezig met stoppen",
        running: "Draait",
        startFailed: "Applicatie start niet",
        validationFailed: "Validatie niet geslaagd",
        needsRevalidation: "Moet opnieuw gevalideerd worden",
        revisionUnavailable: "Revisie niet meer beschikbaar",
        previewUnavailable: "Geen draaiende preview voor dit doel",
        stale: "Vervangen",
        expired: "Verlopen",
        readyToStart: "Klaar om te starten",
        notValidated: "Nog niet gevalideerd",
      },
    },
    environment: {
      title: "Geverifieerde omgeving",
      verified: "Geverifieerd — omgeving v{version}, {date}",
      startTargets: "Start",
      recommendation: "Aanbevolen validatie: {strategy}",
      running: "Bezig met uitzoeken wat dit project nodig heeft om te draaien…",
      blocked: "Er is een antwoord nodig voordat deze omgeving geverifieerd kan worden",
      observed: "Wat we vonden",
      tried: "Wat we probeerden",
      error: "De laatste verificatiepoging is niet afgerond.",
      cancelled: "De laatste verificatie is geannuleerd.",
      none: "De omgeving van dit project is nog niet geverifieerd.",
      noneHint: "FixControl kan uitzoeken wat de repository nodig heeft om te draaien en dat bewijzen door het één keer te starten. Er is niets geverifieerd totdat een run slaagt.",
      verify: "Omgeving verifiëren",
      verifying: "Bezig met verifiëren…",
      verifyFailed: "Kon de omgevingsverificatie niet starten ({status}). Er is niets gewijzigd.",
      loadError: "Kon de omgevingsstatus van dit project niet laden.",
      /** Een geverifieerd contract zonder startcommando: er start niets, dus er is geen voorbeeld. */
      noStartTarget: "Geverifieerd zonder startcommando — de tests draaiden, er is niets gestart, dus er is nog geen voorbeeld.",
      /**
       * Hetzelfde lege startcommando, maar het contract weet al WAT dit project
       * draait, en het is niets wat een preview kan tonen. Dan geen
       * "startcommando laten zoeken" — er valt niets te vinden. Eén zin per
       * soort uit `@/lib/artifact-kind`; ze zeggen "nog niet", want dat is de
       * stand van de gateway, geen eigenschap van het project.
       */
      notPreviewable: {
        android_app: "Dit project bouwt een Android-app. FixControl kan die nog niet tonen — daar is een emulator voor nodig, en die omgeving bestaat nog niet.",
        ios_app: "Dit project bouwt een iOS-app. FixControl kan die nog niet tonen — daar is een simulator op Apple-hardware voor nodig, en die omgeving bestaat nog niet.",
        desktop_app: "Dit project bouwt een desktopapplicatie. FixControl kan die nog niet tonen — daar is een scherm voor nodig in plaats van een poort, en die omgeving bestaat nog niet.",
        http: "Dit project draait een webapplicatie, maar deze omgeving heeft er geen gestart.",
      },
      findStartTarget: "Startcommando laten zoeken",
      /**
       * Een applicatie die uit meer processen bestaat dan deze omgeving start.
       * Geen mislukking — de omgeving is precies wat bewezen is — maar de
       * preview toont een deel van een applicatie, en zonder deze regel is het
       * enige symptoom een pagina die blijft laden.
       */
      partialApplication: "Deze applicatie bestaat uit {declared} processen: {all}. Deze omgeving start er {started}: {startedNames}. Laat FixControl de rest voorstellen, of vul ze zelf aan bij Omgevingsinstellingen.",
      /** De knop eronder: dezelfde vraag, maar aan de planner gesteld. Een
       *  nieuwe verificatie moet het voorstel alsnog bewijzen. */
      partialApplicationAction: "De rest invullen uit het compose-bestand",
      /**
       * Services die de repository expliciet vereist en die deze omgeving
       * nergens mee heeft ingevuld. Geen mislukking — de omgeving is wat een
       * groene run bewees — maar een applicatie zonder zijn wachtrij of
       * objectopslag faalt op een manier die eruitziet alsof de code stuk is.
       */
      missingServices: "De repository vraagt om services die FixControl niet kan starten: {services}. Er is geen image voor deze soort, of het budget van deze omgeving is vol. Vul ze zelf aan bij Omgevingsinstellingen, of laat ze weg als de validatie ze niet nodig heeft.",
      /** Idem. Genoemd naar wat er gebeurt: ze worden voorgesteld en moeten
       *  door een run omhoogkomen, niet zomaar bijgezet. */
      declaredCoveringServices: "De validatie bewees deze services niet, maar jouw omgevingsinstellingen starten ze bij elke controle: {services}.",
      disabledServices: "Uitgezet door jou, dus ze draaien niet mee: {services}. De repository vraagt er wel om.",
      environmentSupplementsAction: "Uitzetten wat je niet nodig hebt",
      environmentSupplements: "FixControl start deze er bij elke controle en elk voorbeeld bij, omdat de repository erom vraagt en de validatie ze niet bewees: {services}. Dat kost tijd per run.",
      /**
       * Wat een preview er bovenop zet. Dit is de plek waar de belofte "de
       * exacte geverifieerde omgeving" wordt bijgesteld: voor een preview geldt
       * die niet meer helemaal, en dan hoort het er te staan.
       */
      /** De editor voor omgevingsinstellingen per project (migratie 281). */
      settings: {
        title: "Omgevingsinstellingen",
        switches: "Wat er naast je omgeving draait",
        switchesHint: "FixControl start de services waar je repository om vraagt en die de validatie niet bewees. Zet uit wat dit project niet nodig heeft: dat scheelt tijd bij elke controle en elk voorbeeld. Uitzetten betekent niet dat de repository er niet meer om vraagt — het betekent dat wij hem niet starten.",
        switchesNone: "Er draait niets extra naast je omgeving.",
        switchesHere: "Hieronder staat wat er naast je omgeving draait. Zet uit wat je niet nodig hebt en sla op.",
        switchSource: {
          repository: "gevraagd door de repository",
          declared: "door jou toegevoegd",
        },
        targetPath: "Pad waarop een voorbeeld dit proces bereikt",
        targetPathHint: "Bijvoorbeeld /api. Zonder pad wordt dit proces door een voorbeeld niet gestart — alleen het eerste proces start vanzelf.",
        targetPathHintPrimary: "Het eerste proces is het proces dat het voorbeeld toont; een pad is hier alleen nodig als het onder een prefix draait.",
        seeded: "Toegevoegd uit de repository: {added}. Het commando, de poort en — waar het compose- of proxybestand die noemt — het pad zijn ingevuld. Controleer het, vul een leeg pad aan (zonder pad start een voorbeeld dat proces niet) en sla op.",
        seededLeftOut: "Niet toegevoegd: {leftOut}.",
        seededNone: "Er was niets toe te voegen. Niet toegevoegd: {leftOut}.",
        /** Why a declared process was not turned into a start target. */
        seedReason: {
          no_port: "geen poort in het compose-bestand, dus niets om op te wachten",
          no_command: "geen commando in het compose-bestand",
          shell_command: "het commando is een shell-regel, die hier niet kan draaien",
          no_room: "er is geen ruimte meer; een omgeving start er hoogstens zes",
          already_started: "start al",
        },
        hint: "Wat de ontdekking niet uit de repository kan lezen: hoe de applicatie start, poort en readiness-pad, extra omgevingsvariabelen, systeempakketten en installatiestappen. Gelden voor elke verificatie, worden door hetzelfde beleid beoordeeld als de commando's van de planner en moeten nog steeds door de run bewezen worden.",
        open: "Omgevingsinstellingen bewerken",
        close: "Verbergen",
        prefilled: "Ingevuld vanuit de geverifieerde omgeving — de gemarkeerde onderdelen hieronder laten zien waarmee je project nu draait. Pas aan wat niet klopt.",
        fromVerified: "nu in gebruik",
        envPrefilledNote: "Deze {count} variabelen komen uit de geverifieerde omgeving. Pas er een aan en vanaf de volgende controle geldt jouw waarde.",
        /**
         * De alleen-lezen helft van het paneel: wat het platform vóór de klant
         * heeft besloten en gebruikt. Eerst gewone taal, daarna de eigen
         * herkomstregels van de resolver achter de vouw "Waarom deze?".
         */
        resolved: {
          title: "Waarmee je project nu draait",
          explain: "FixControl heeft dit uitgezocht door je applicatie één keer te starten en te kijken of ze opkwam. Het zijn uitkomsten, geen instellingen: ze worden opnieuw bepaald zodra de omgeving weer geverifieerd wordt.",
          image: "Machine waarop je code draait",
          imageHelp: "Je code draait op een schone wegwerpmachine die na elke controle wordt weggegooid, zodat niets ervan bij je eigen servers kan komen. FixControl kiest de versie op basis van wat je repository vraagt; wil je een andere, pas dat daar aan (of leg een versie vast bij de runtime-instellingen van het project) en verifieer opnieuw.",
          imageFrom: {
            catalog: "Een kant-en-klare image uit de catalogus van FixControl: {image}",
            derived: "De catalogus heeft geen {language}-image voor deze versie, dus is ze samengesteld volgens de officiële naamgeving: {image}",
            project_override: "De image die dit project zelf vastlegt: {image}",
            operator_default: "De standaardimage van deze workspace: {image}",
            toolchain: "Door FixControl gebouwd bovenop {image}, met de extra software hieronder erbij",
            unknown: "Image: {image}",
          },
          versionAsked: "Je repository vroeg om versie {constraint}.",
          uncertain: "Een deel hiervan was de best mogelijke inschatting — de redenen staan hieronder.",
          pinned: "Vastgezet op precies deze build: {digest}",
          why: "Waarom deze?",
          services: "Wordt ernaast gestart",
          servicesHelp: "Je applicatie heeft deze nodig om te draaien — een database, een cache. Voor elke controle wordt een gloednieuwe lege versie gestart en daarna weggegooid, dus je echte gegevens komen er nooit aan te pas.",
          packages: "Extra software geïnstalleerd",
          packagesHelp: "Je code bouwen vraagt meer dan de taal alleen; dit zijn de bibliotheken die FixControl erbij heeft gezet. Mislukt een build ooit doordat er iets ontbreekt, noem het dan hieronder bij Systeempakketten, dan komt het er ook bij.",
          tools: "Gereedschap: {tools}",
          extensions: "Taaluitbreidingen: {extensions}",
          install: "Klaargezet voordat je applicatie start",
          installHelp: "Deze halen en zetten alles klaar waar je code van afhangt. Ze draaien al — wat je onder Extra installatiestappen zet, draait daarna, dus je hoeft ze niet te herhalen.",
          checks: "Draait om een wijziging te controleren",
          checksHelp: "De build, tests, typecontroles en lint uit je eigen repository. Een voorgestelde wijziging wordt pas ter goedkeuring aangeboden als deze in deze omgeving slagen.",
          required: "Moet slagen.",
          advisory: "Adviserend — een fout wordt gemeld, maar blokkeert niet.",
          autoEnv: "Wordt automatisch ingevuld",
          autoEnvHelp: "FixControl zet deze zelf bij het starten van je applicatie. Ze krijgen bij elke controle een nieuwe waarde — het adres van een net gestarte service, een vers aangemaakt wachtwoord — dus je kunt ze hieronder niet invullen en er wordt hier geen waarde bewaard.",
          fromService: "Het adres van {service} (poort {port}), dat bij de start wordt doorgegeven.",
          fromServiceNoPort: "Het adres van {service}, dat bij de start wordt doorgegeven.",
          fromGenerated: "Een wachtwoord dat voor die ene run wordt aangemaakt.",
          fromSecret: "Wordt gelezen uit de geheimenkluis van je eigen cluster — de waarde bereikt FixControl nooit.",
          forTarget: "voor {target}",
        },
        startTargets: "Startcommando's",
        addTarget: "Startcommando toevoegen",
        services: "Extra services",
        servicesHint: "Images die naast de geverifieerde omgeving draaien: bij elke controle van dit project en in elk voorbeeld. Ze horen niet bij wat de validatie bewees — een testomgeving start ze dus niet — en omdat het image niet uit deze repository gebouwd wordt, zie je een wijziging in de code die het bedient er niet in terug.",
        serviceName: "Naam",
        serviceNameHint: "De hostnaam waarop hij antwoordt binnen de run. Gebruik de naam die de applicatie al zoekt — die uit je compose-bestand.",
        serviceImage: "Image",
        serviceImageHint: "Een gepinde verwijzing, getoetst aan de registry-allowlist van deze installatie.",
        servicePort: "Poort",
        servicePortHint: "De poort binnen de container, en waar de readiness op wacht.",
        servicePath: "Bereikbaar op",
        servicePathHint: "Een pad dat het voorbeeld hierheen stuurt in plaats van naar de applicatie, bijv. /api — gebruik het pad dat de frontend al aanroept. Leeg laten en de service is alleen binnen de run bereikbaar.",
        invalidTargetPath: "Proces \"{target}\": \"{prefix}\" kan niet als pad — begin met / en gebruik geen jokertekens of query.",
        invalidServicePath: "Service \"{name}\": \"{prefix}\" kan niet als pad — begin met / en gebruik geen jokertekens of query.",
        serviceInitCommand: "Eerst eenmalig draaien",
        serviceInitCommandHint: "Draait in dit image tot voltooiing vóór de service start, en moet exit 0 geven — een migratie, een seed, een schema. Leeg laten als er niets hoeft.",
        serviceCommand: "Commando",
        serviceCommandHint: "Vervangt het commando van het image zelf. Leeg laten om dat te gebruiken.",
        serviceEnv: "Omgeving",
        serviceEnvHint: "KEY=waarde per regel. De database, cache en queue van deze run zijn al aangesloten — zet hier alleen iets om een naam te corrigeren die deze applicatie anders schrijft. Nooit een wachtwoord.",
        removeService: "Service verwijderen",
        addService: "Service toevoegen",
        serviceNameRequired: "Elke extra service heeft een naam nodig.",
        invalidServiceName: "\"{name}\" kan niet als servicenaam — kleine letters, cijfers en koppeltekens, niet beginnend of eindigend met een koppelteken.",
        serviceImageRequired: "Service \"{name}\" heeft een image nodig.",
        invalidServicePort: "Service \"{name}\" heeft een poort tussen 1 en 65535 nodig.",
        invalidServiceEnv: "Service \"{name}\": \"{line}\" is geen KEY=waarde.",
        duplicateServiceName: "Twee extra services heten allebei \"{name}\".",
        removeTarget: "Verwijderen",
        targetName: "Naam",
        command: "Commando",
        commandHint: "Eén commandoregel, geen shell: aanhalingstekens groeperen, niets wordt geëxpandeerd.",
        port: "Poort",
        readinessPath: "Readiness-pad",
        readinessTimeout: "Readiness-timeout (s)",
        env: "Omgevingsvariabelen",
        envHint: "Eén NAAM=waarde per regel. Alleen niet-geheime waarden; inloggegevens horen in env-bindingen.",
        systemPackages: "Systeempakketten",
        systemPackagesHint: "Namen, gescheiden door komma's, uit de receptenallowlist (bijv. libldap2-dev).",
        installSteps: "Extra installatiestappen",
        installStepsHint: "Eén commando per regel, uitgevoerd na de eigen installatiestappen van de planner.",
        /** Eerder beantwoorde blokkeervragen — onthouden op het project. */
        answers: {
          title: "Wat je eerder hebt geantwoord",
          hint: "Deze antwoorden krijgt de planner bij elke verificatie mee, zodat dezelfde vraag niet opnieuw wordt gesteld. Verwijder er een als hij niet meer klopt.",
          forget: "Dit antwoord vergeten",
        },
        notes: "Notities voor de planner",
        save: "Opslaan",
        saveAndVerify: "Opslaan en verifiëren",
        saved: "Opgeslagen.",
        savedVerifying: "Opgeslagen — verificatie gestart.",
        saveFailed: "Kon de omgevingsinstellingen niet opslaan ({status}). Er is niets gewijzigd — probeer het opnieuw.",
        invalidCommand: "\"{target}\" heeft geen startcommando. Typ het commando dat het start, of verwijder dit startcommando.",
        invalidPort: "De poort voor \"{target}\" moet een heel getal tussen 1 en 65535 zijn — de poort waarop de applicatie luistert.",
        invalidReadiness: "De wachttijd voor \"{target}\" moet tussen 1 en 600 seconden liggen — hoelang de applicatie mag doen over het eerste antwoord.",
        invalidEnv: "Elke omgevingsregel moet NAAM=waarde zijn. Deze niet: {line}",
        /** Resourcelimieten per project. Geschreven voor iemand die nog nooit
         *  van een container heeft gehoord: "machine", "geheugen",
         *  "programma's tegelijk" \u2014 nooit "cgroup" of "pids-limit". */
        resources: {
          title: "Hoeveel machine de controles krijgen",
          hint: "Elke controle draait op een eigen kleine machine, die daarna wordt weggegooid. Deze velden tonen wat dit project vandaag krijgt. Pas er alleen iets aan als een controle te weinig geheugen of tijd heeft; laat je een veld op de getoonde waarde staan, dan blijft het de server volgen.",
          cpus: "Processorkernen",
          memory: "Geheugen (MB)",
          pids: "Programma's tegelijk",
          stepTimeout: "Langste stap (min)",
          runTimeout: "Langste hele controle (min)",
          explainCpus: "Hoeveel processorkernen de controle tegelijk mag gebruiken. Met meer kernen is een build eerder klaar; een test die faalt wordt er niet groen van.",
          explainMemory: "Werkgeheugen voor de controle, in megabytes (1024 MB is 1 GB). Verhoog dit als een build of test stopt met \u201cout of memory\u201d \u2014 dan is de machine te klein, niet je code verkeerd.",
          explainPids: "Hoeveel programma's de controle tegelijk mag draaien. Het is een vangnet tegen een proces dat de host volloopt; verhoog het alleen als een build meldt dat hij geen proces meer kan starten.",
          explainStepTimeout: "Hoelang \u00e9\u00e9n stap \u2014 installeren, testen, bouwen \u2014 mag duren voordat we hem afbreken en als mislukt tellen. Verhoog dit als een trage testsuite halverwege wordt afgekapt.",
          explainRunTimeout: "Hoelang alle stappen samen mogen duren. Een stap kan nooit langer draaien dan wat hiervan over is, dus een stap hoger zetten dan dit verandert niets.",
          fromServer: "Nu: de serverstandaard voor elk project.",
          fromProject: "Nu: ingesteld voor dit project.",
          clamped: "Ingesteld voor dit project, maar hoger dan deze server toestaat \u2014 het lagere getal draait.",
          invalid: {
            cpus: "Processorkernen moet een getal boven nul zijn, bijvoorbeeld 2 of 1,5.",
            memory: "Geheugen moet een heel aantal megabytes zijn, bijvoorbeeld 8192 voor 8 GB.",
            pids: "Programma's tegelijk moet een heel getal zijn, bijvoorbeeld 512.",
            stepTimeout: "De langste stap moet een heel aantal minuten zijn, bijvoorbeeld 25.",
            runTimeout: "De langste controle moet een heel aantal minuten zijn, bijvoorbeeld 60.",
          },
          tooHigh: {
            cpus: "Deze server staat maximaal {max} processorkernen per controle toe. Verlaag het getal of vraag een platformbeheerder om de limiet te verhogen.",
            memory: "Deze server staat maximaal {max} MB geheugen per controle toe. Verlaag het getal of vraag een platformbeheerder om de limiet te verhogen.",
            pids: "Deze server staat maximaal {max} programma's tegelijk per controle toe. Verlaag het getal of vraag een platformbeheerder om de limiet te verhogen.",
            stepTimeout: "Deze server laat \u00e9\u00e9n stap maximaal {max} minuten draaien. Verlaag het getal of vraag een platformbeheerder om de limiet te verhogen.",
            runTimeout: "Deze server laat een hele controle maximaal {max} minuten duren. Verlaag het getal of vraag een platformbeheerder om de limiet te verhogen.",
          },
        },
        clear: "Alle instellingen wissen",
        empty: "Geen omgevingsinstellingen — de ontdekking leidt alles af uit de repository.",
        active: "Omgevingsinstellingen gelden voor elke verificatie.",
      },
      previewTarget: "Voorbeeld van de applicatie",
      previewStarting: "Wordt gestart…",
      previewElapsed: "al {elapsed} bezig",
      /** Onder de stepper terwijl een ontdekking loopt: hoe lang de huidige fase al duurt. */
      phaseAttempt: "poging {n} — de stappen beginnen per poging opnieuw",
      phaseElapsed: "al {elapsed} in deze fase",
      attemptOf: "poging {n} van {total}",
      reverifyNote: "de geverifieerde omgeving blijft gelden tot deze run haar vervangt",
      /** Waar een opstartend voorbeeld is, uit de voortgangsnotitie van de job. */
      previewProgress: {
        preparing: "Geverifieerde omgeving en werkruimte worden gecontroleerd…",
        environment: "Taak wordt gestart en image opgehaald…",
        steps: "Stap {done} van {total} klaar: {name}",
        stepFailed: "Stap mislukt: {name}",
        startingTarget: "{target} wordt gestart, wacht op antwoord…",
        failed: "Niet gestart: {reason}",
      },
      /** Waar de taak is, gelezen uit het logboek van de runner zelf. */
      previewMilestone: {
        checked_out: "Revisie uitgecheckt, afhankelijkheden volgen…",
        deps_restored: "Afhankelijkheden hersteld uit cache ({mb} MB), bouwen…",
        deps_cold: "Geen bruikbare cache — afhankelijkheden worden volledig geïnstalleerd…",
        patch_applied: "Patch toegepast, omgeving wordt gebouwd…",
        app_starting: "Applicatie wordt gestart…",
      },
      previewFailedRecently: "Het laatste voorbeeld van {target} is niet gestart: {reason}",
      /** De weigeringscodes die de preview-job vastlegt, als zinnen. */
      previewFailure: {
        start_target_not_ready: "de applicatie is gestart maar heeft nooit geantwoord",
        contract_fingerprint_mismatch: "de configuratie van de repository is veranderd sinds de verificatie — de omgeving wordt opnieuw ontdekt",
        preview_run_failed: "de sandbox-run is mislukt voordat de applicatie startte",
        no_active_contract: "dit project heeft geen geverifieerde omgeving",
        contract_has_no_start_target: "de geverifieerde omgeving heeft niets om te starten",
        project_codebase_unresolved: "de codebase van het project is niet op schijf gevonden",
        patch_overlay_unavailable: "de patch die dit voorbeeld moest tonen is niet meer beschikbaar",
        supplement_service_unavailable: "een service die de repository declareert, maar die de verificatie niet heeft bewezen, kon niet worden gestart",
        preview_disabled: "voorbeelden staan uit voor deze workspace",
        discovery_disabled: "omgevingsontdekking staat uit voor deze workspace",
      },
      previewLive: "Draait",
      previewEnded: "Beëindigd",
      expiresIn: "Stopt over {minutes} min",
      open: "Voorbeeld openen",
      stop: "Voorbeeld stoppen",
      stopFailed: "Kon dit voorbeeld niet stoppen ({status}).",
      accept: "Dit ziet er goed uit",
      reject: "Dit ziet er niet goed uit",
      reviewFailed: "Kon je oordeel niet vastleggen ({status}).",
      reviewHint: "Je oordeel wordt náást de automatische validatie vastgelegd als menselijk bewijs — het vervangt nooit een testresultaat en opent op zichzelf nooit een poort.",
      verdict: {
        accepted: "beoordeeld: ziet er goed uit",
        rejected: "beoordeeld: ziet er niet goed uit",
      },
      // Zie en.ts: de ruwe faalvocabulaire (ValidationFailureKind) in gewone taal.
      whereItStopped: "Waar het stokte",
      technicalDetails: "Technische details",
      noAttempt: "Er is geen poging gedaan",
      failureKind: {
        test_failure: "Een test faalde",
        startup_failure: "De applicatie startte niet",
        readiness_timeout: "De applicatie werd niet op tijd bereikbaar",
        image_pull: "Een container-image kon niet worden opgehaald",
        scheduling: "Er was geen capaciteit om de omgeving te draaien",
        infrastructure: "Een infrastructuurprobleem stopte de run",
        transport: "De verbinding met de runner brak",
        cancelled: "De poging is geannuleerd",
        runtime_incompatible: "De runtime-versie past niet bij dit project",
        dependency_installation: "Het installeren van de afhankelijkheden mislukte",
        missing_runtime_dependency: "Iets dat de applicatie nodig heeft ontbreekt",
        service_boot: "Een ondersteunende service (database, cache, …) startte niet",
        service_connection: "De applicatie kon een ondersteunende service niet bereiken",
        environment_configuration: "De omgevingsconfiguratie is onvolledig",
        application_boot: "De applicatie crashte tijdens het starten",
        build_failure: "De build mislukte",
        policy_refusal: "De voorgestelde omgeving is door beleid geweigerd",
        network_policy: "Netwerkregels blokkeerden de run",
        resource_budget: "De run ging over zijn resourcebudget heen",
        unsupported_environment: "Geen enkele uitvoeringsomgeving kan leveren wat dit project nodig heeft",
        toolchain_build: "De toolchain-image voor dit project kon niet worden gebouwd",
        external_dependency_required: "Dit project is afhankelijk van iets dat FixControl niet zelf kan leveren",
        dependency_lock_mismatch: "Het dependency-manifest en het lockbestand spreken elkaar tegen",
        dependency_resolution_failure: "De opgegeven afhankelijkheden zijn niet oplosbaar",
        tests_not_executed: "De testrun heeft geen enkele test uitgevoerd — het testcommando selecteerde niets van deze wijziging",
        unknown: "De reden kon niet worden vastgesteld",
      },
      runtimesLabel: "Runtime",
      servicesLabel: "Services",
      previewCreate: "Voorbeeld openen",
      previewCreating: "Bezig met openen…",
      previewCreateFailed: "Kon geen voorbeeld openen ({status}). Er is niets gestart.",
      previewTemporary: "Tijdelijke validatieomgeving — niet je productiesysteem.",
      previewRefusal: {
        preview_disabled: "Voorbeelden staan niet aan voor deze werkruimte.",
        discovery_disabled: "Omgevingsverificatie staat niet aan voor deze werkruimte, dus een voorbeeld kan niet worden gestart.",
        no_active_contract: "Dit project heeft nog geen geverifieerde omgeving — verifieer eerst de omgeving.",
        contract_has_no_start_target: "De geverifieerde omgeving van dit project start geen applicatie, dus er valt niets te tonen. Laat FixControl een startcommando zoeken, of vul er een in bij Omgevingsinstellingen, en verifieer opnieuw.",
        preview_limit_reached: "Het maximale aantal voorbeelden draait al. Stop er eerst één voordat je een nieuwe opent.",
        run_not_passed: "Deze run heeft de patch niet geverifieerd, dus hij kan niet als draaiende applicatie worden getoond.",
        run_has_no_patch: "Deze run verifieerde geen patch — open in plaats daarvan een voorbeeld van de projectomgeving.",
        run_has_no_project: "Deze run hangt niet aan een project, dus er is geen geverifieerde omgeving om hem in te draaien.",
        patch_overlay_unavailable: "De bestanden van deze patch zijn niet meer leesbaar, dus het voorbeeld zou de ongepatchte code tonen.",
      },
      discoveryRefusal: {
        discovery_disabled: "Omgevingsverificatie staat niet aan voor deze werkruimte.",
        residency_customer_hosted_unsupported: "Dit project moet op je eigen runner worden gevalideerd, en er is geen runner die FixControl kan bereiken die dat aankan. Registreer een container-capabele runner voor dit project, of zet externe validatie ervoor aan.",
        discovery_in_progress: "FixControl is al bezig dit project uit te zoeken. De voortgang staat hierboven.",
        discovery_resume_exhausted: "Deze omgeving is het maximale aantal keren beantwoord en opnieuw geprobeerd; verifieer opnieuw vanaf nul of pas de validatie-instellingen van het project aan.",
      },
      // ── Onboardingfases ─────────────────────────────────
      phase: {
        title: "Dit project klaarmaken",
        analyzing: "Codebase analyseren",
        resolving: "Omgeving bepalen",
        preparing_runtime: "Runtime voorbereiden",
        starting_services: "Services starten",
        verifying: "Applicatie verifiëren",
        ready: "Klaar",
        stoppedHere: "Hier gestopt",
        inProgress: "Bezig",
      },
      // ── Geblokkeerd: één zin, één vraag, en de uitwegen ─────────────
      blockedDependency: "FixControl kon één afhankelijkheid niet leveren: {name}",
      blockedQuestionTitle: "Wat FixControl moet weten",
      questionLabel: {
        runtime_version: "Op welke runtimeversie draait dit project?",
        system_packages: "Welke systeempakketten heeft dit project nodig?",
        services: "Welke services heeft dit project nodig (database, cache, queue)?",
        env_vars: "Welke omgevingsvariabelen heeft dit project nodig om te starten?",
        start_command: "Hoe wordt deze applicatie gestart?",
        build_command: "Hoe wordt dit project gebouwd?",
        unsupported_environment: "Wat heeft dit project nodig dat FixControl niet kon leveren?",
        external_dependency: "Van welk extern systeem is dit project afhankelijk, en hoe moet de sandbox dat vervangen?",
        generic: "Jouw antwoord",
      },
      unanswerableTitle: "Hier helpt een antwoord niet",
      unanswerableWhatHelps: "Deze eis staat in de repository zelf, en geen enkele beschikbare uitvoeringsomgeving levert hem — een antwoord in een tekstveld verandert dat niet. Wat wel werkt: een runner registreren die dit kan en validatie op afstand voor dit project aanzetten, of de repository zo aanpassen dat de eis vervalt.",
      answerPlaceholder: "Bijvoorbeeld: PHP 8.1, de queue worker heeft Redis nodig, starten met bin/console app:serve",
      answerSubmit: "Antwoorden en opnieuw proberen",
      answerSubmitting: "Starten…",
      answerFailed: "Kon geen nieuwe poging starten ({status}). Er is niets gewijzigd.",
      answerStarted: "Bedankt — er is een nieuwe poging gestart met jouw antwoord.",
      answerNotCarried: "Er is een nieuwe poging gestart, maar deze versie kan jouw antwoord nog niet aan de planner doorgeven.",
      answerCharsLeft: "Nog {count} tekens",
      // ── Klant-gehoste uitwijk ─────────────────────────────
      fallbackTitle: "Jullie eigen runner kan dit aan",
      fallbackAllowed: "{agent} biedt alles wat dit project nodig heeft. Daar valideren verandert niets aan waar jullie broncode staat.",
      fallbackValidate: "Valideren op {agent}",
      fallbackValidating: "Starten…",
      fallbackBlocked: "{agent} zou deze omgeving kunnen draaien, maar validatie op afstand staat uit voor dit project. De instelling heet “Remote validatie voor dit project” onder Hoe wijzigingen worden gevalideerd.",
      fallbackBlockedCta: "Ga naar de instelling voor validatie op afstand",
      skippedRunner: "Er is een runner met de naam {name} geregistreerd, maar die heeft niet opgegeven wat hij kan, dus is hij niet geprobeerd.",
      strategyLink: "Strategie voor validatie op afstand",
      // ── Geavanceerd ───────────────────────────────────
      attemptsTitle: "Pogingen",
      attemptLine: "Poging {n}: {kind}",
      /** Het verificatielogboek op de projectkaart: per poging wat er misging. */
      attemptLog: {
        title: "Verificatielogboek ({count})",
        /** Poging 0 is de capaciteitscontrole vóór de eerste echte poging. */
        beforeFirstAttempt: "Vóór de eerste poging: {kind}",
        failedStep: "Gestruikeld op: {name} ({kind})",
        exitCode: "afsluitcode {code}",
        servicesNotReady: "Niet gereed: {services}",
        logWithheld: "De uitvoer van deze poging is alleen zichtbaar voor wie dit project mag beheren.",
      },
    },
    // Welke validatie de wijzigingen van dit project moeten doorstaan.
    validation: {
      title: "Hoe wijzigingen worden gevalideerd",
      subtitle: "Waar de wijzigingen van dit project worden gebouwd en getest voordat een poort open kan.",
      loadError: "Kon de validatie-instelling van dit project niet laden.",
      saveFailed: "Kon de validatie-instelling niet opslaan ({status}). Er is niets gewijzigd.",
      saved: "Opgeslagen.",
      enableLabel: "Gebruik een validatiestrategie voor dit project",
      enableHelp: "Uit betekent dat dit project de standaard van de werkruimte volgt.",
      sourceProject: "Projectinstelling",
      sourceDefault: "Standaard van de werkruimte",
      readOnly: "Je hebt geen rechten om de instellingen van dit project te wijzigen.",
      runtimeOverrides: {
        title: "Runtimeversies voor dit project",
        help: "Leg vast waar dit project echt op draait. Geef een taal en een versie, of een taal en een image als de versie alleen niet genoeg is. Laat leeg om FixControl het uit de repository te laten afleiden.",
        language: "Taal",
        version: "Versie",
        image: "Image (optioneel)",
        languagePlaceholder: "php",
        versionPlaceholder: "8.1",
        imagePlaceholder: "registry.example.com/php:8.1-fpm",
        add: "Runtime toevoegen",
        remove: "Verwijderen",
        empty: "Geen runtime vastgelegd — FixControl leidt dit af uit de repository.",
        max: "Er kunnen maximaal {count} runtimes worden vastgelegd.",
        unsupported: "Deze versie van FixControl kan runtimepins nog niet opslaan.",
      },
      startVerification: {
        title: "Bewijs dat de applicatie nog start",
        help: "Voordat een wijziging wordt toegepast, start FixControl de applicatie van dit project en wacht tot die antwoordt. Slagende tests zijn niet hetzelfde als een applicatie die nog opkomt, en dit is de controle die het verschil aantoont.",
        required: "Verplicht — een wijziging wordt pas toegepast als de applicatie is gestart en heeft geantwoord.",
        waived: "Niet verplicht — wijzigingen worden toegepast op alleen de testresultaten.",
        waivedBy: "Geaccepteerd door {who} op {date}.",
        waiveLabel: "Wijzigingen toestaan zonder dit bewijs",
        waiveHelp: "Alleen voor een project dat op je eigen runner wordt gevalideerd, die de applicatie niet kan starten en controleren. Je accepteert daarmee dat een wijziging kan worden toegepast terwijl niemand heeft bevestigd dat de applicatie nog opkomt. Je naam en de datum van vandaag worden vastgelegd naast elke goedkeuring die erop steunt.",
        waiveConfirm: "Ja, wijzigingen toepassen zonder dit bewijs",
        readOnly: "Alleen beheerders en managers kunnen dit wijzigen.",
        saveFailed: "Kon deze instelling niet opslaan ({status}). Er is niets gewijzigd.",
      },
      remotePermissionLabel: "Remote validatie voor dit project",
      remotePermissionHelp:
        "Of dit project zijn validatie in jouw Kubernetes-cluster mag draaien. " +
        "Overnemen volgt de instelling van de werkruimte; de andere twee beslissen alleen voor dit project.",
      remotePermissionInherit: "Instelling van de werkruimte overnemen",
      remotePermissionOn: "Toegestaan voor dit project",
      remotePermissionOff: "Niet toegestaan voor dit project",
    },
    updates: {
      title: "Updates & meldingen",
      subtitle: "Waar FixControl voortgang over het werk van dit project naartoe stuurt.",
      originTitle: "Updates terug naar bron",
      originSubtitle: "Voortgang en status gaan terug naar waar het werk vandaan kwam.",
      originEmpty: "Geen updateroutes ingesteld.",
      originEmptyHint: "Updates vallen terug op in-app meldingen totdat je een kanaal of ticketsysteem verbindt.",
      approvalsTitle: "Goedkeuringsregels",
      approvalsSubtitle: "Wie moet aftekenen voordat risicovolle acties doorgaan.",
      approvalsEmpty: "Geen goedkeuringsregels ingesteld.",
      approvalsEmptyHint: "Risicovolle acties wachten op in-app goedkeuring totdat je goedkeuringsregels instelt.",
      requiresApproval: "Vereist goedkeuring",
      noApproval: "Geen goedkeuring nodig",
      manage: "Goedkeuringsregels instellen",
      manageUpdates: "Updateroutes beheren",
      addRoute: "Updateroute toevoegen",
      addApprovalChannel: "Goedkeuringskanaal toevoegen",
      riskAny: "elk risico",
      risk: { low: "laag risico", medium: "gemiddeld risico", high: "hoog risico", critical: "kritiek risico" },
      channel: { inapp: "in de app", email: "e-mail", slack: "Slack", teams: "Teams", webhook: "webhook" },
    },
    automation: {
      title: "AI-team",
      subtitle: "Hoe de AI aan dit project werkt: welke agents helpen, wat ze zelf mogen doen en welke regels ze altijd volgen.",
      readOnlyNotice: "Je kunt hier alleen meekijken. Alleen admins en managers passen deze instellingen aan.",
      allowed: "Toegestaan",
      requiresApproval: "Vereist goedkeuring",
      disabled: "Uitgeschakeld",
      /** De keuze staat bovenaan, het gevolg eronder. Andersom las de pagina
       *  als een tabel met uitkomsten waarvan de oorzaak eronder verstopt zat. */
      accessTitle: "Wat mogen agents met je code doen?",
      accessHint: "Dit is de belangrijkste keuze op deze pagina: hij bepaalt het lijstje hieronder.",
      accessReadOnly: "FixControl leest je code, maar verandert er nooit iets aan.",
      accessSandbox: "FixControl probeert wijzigingen uit in een aparte, veilige kopie en stelt ze voor. Je eigen code blijft onaangeraakt.",
      accessWritable: "Zodra iemand een wijziging goedkeurt, past FixControl die toe op de gekoppelde codebase.",
      effectTitle: "Wat dat in de praktijk betekent",
      effectHint: "Dit volgt automatisch uit je keuze hierboven; je stelt het niet los in.",
      /** De volledige naam van elke modus, op de instellingenpagina en als
       *  tooltip; wat hij precies betekent staat in accessReadOnly/Sandbox/Writable. */
      mode: { read_only: "Alleen kijken", sandbox: "Wijzigingen proberen in een veilige kopie", writable: "Goedgekeurde wijzigingen toepassen" },
      /** Kort genoeg voor een pill in de projectenlijst en een filtertab. */
      modeShort: { read_only: "Alleen kijken", sandbox: "Veilige kopie", writable: "Toepassen" },
      behaviourTitle: "Hoe dit project met binnenkomend werk omgaat",
      behaviourHint: "Twee keuzes die alleen voor dit project gelden.",
      guardrailsTitle: "Vaste regels voor agents",
      guardrailsHint: "Regels in je eigen woorden die agents in dit project altijd volgen — bijvoorbeeld welke mappen ze met rust laten. Ze gelden bovenop de instellingen hierboven.",
      guardrailsEmpty: "Nog geen regels. Voeg er een toe die agents in dit project altijd volgen.",
      guardrailsExamplesLabel: "Klik een voorbeeld om het over te nemen:",
      guardrailsExamples: "Raak nooit /payments aan zonder review|Werk altijd de tests bij|Geen wijzigingen aan databasemigraties zonder goedkeuring",
      guardrailsAdd: "Regel toevoegen",
      guardrailsPlaceholder: "bijv. Raak nooit productie-config aan",
      guardrailsRemove: "Regel “{rule}” verwijderen",
      guardrailsReadOnly: "Alleen admins en managers kunnen deze regels wijzigen.",
      supportDeskTitle: "Alleen support, geen codewerk",
      supportDeskHint: "Binnenkomende vragen worden tickets die netjes worden bijgehouden, maar er start nooit vanzelf een agent die code aanpast. Is er tóch codewerk nodig, dan gebruik je “Escaleer naar engineering”. Zolang dit aanstaat, zijn Patches en Werksessies verborgen en past het project geen code aan.",
      llmFirstTitle: "Chatberichten door AI laten begrijpen",
      llmFirstHint: "Berichten in de Slack- en Teams-kanalen van dit project worden eerst door AI gelezen in plaats van door vaste woordpatronen. Vrije formuleringen worden dan veel vaker begrepen en één bericht mag om meerdere dingen tegelijk vragen. Kost iets meer tijd en AI-kosten per bericht. Alleen het begrijpen verandert: elke actie vraagt nog steeds dezelfde bevestiging en goedkeuring, en valt de AI uit, dan nemen de vaste patronen het weer over.",
      cap: {
        readKnowledge: "Projectkennis lezen",
        inspectCodebases: "Gekoppelde codebases inspecteren",
        postProgress: "Voortgangsupdates plaatsen",
        createPatch: "Patchvoorstellen maken",
        openPullRequests: "Pull requests openen",
        productionChanges: "Productiewijzigingen",
        securityChanges: "Beveiligingsgevoelige wijzigingen",
        customerReplies: "Antwoorden naar klanten",
        directProductionWrites: "Rechtstreeks naar productie schrijven",
        automaticEmailReplies: "Automatische e-mailantwoorden",
      },
      modeTitle: "Standaard agent-modus",
    },
    activity: {
      title: "Activiteit & audit",
      subtitle: "Recente gebeurtenissen rond binnenkomst, agents en updates voor dit project.",
      empty: "Nog geen activiteit vastgelegd.",
      emptyHint: "Zodra werk door dit project loopt, verschijnen hier resolutie-, sync- en leergebeurtenissen.",
      viewAudit: "Volledige auditlog bekijken",
      showMore: "Toon {count} meer",
      showLess: "Toon minder",
      events: {
        project_context_resolved: "Gebeurtenis aan dit project gekoppeld",
        project_context_ambiguous: "Gebeurtenis kan op meerdere projecten passen",
        project_context_missing: "Gebeurtenis kon niet aan een project worden gekoppeld",
        external_sync_delivered: "Update afgeleverd bij bron",
        external_sync_skipped: "Update overgeslagen",
        external_sync_failed: "Afleveren van update mislukt",
        learning_captured: "Learning vastgelegd",
        approval_requested: "Goedkeuring aangevraagd",
        notification_route_resolved: "Melding gerouteerd",
        codebase_inspected: "Codebase geïnspecteerd",
        knowledge_source_added: "Kennisbron toegevoegd",
        unknown: "Gebeurtenis",
      },
    },
    advanced: {
      title: "Geavanceerde instellingen",
      subtitle: "De ruwe verbindingseditor en labels. Heb je dit niet nodig, dan hoef je hier niets te doen.",
      warning: "Dit is de technische weergave van álle koppelingen van dit project. Een systeem toevoegen doe je gewoon via de gewone onderdelen in het menu; hier bewerk, test of voer je bestaande koppelingen handmatig in.",
      show: "Geavanceerde instellingen tonen",
      hide: "Geavanceerde instellingen verbergen",
      connectedSystemsTitle: "Verbonden systemen (ruwe bindings)",
      governanceTitle: "Governance",
      tagsTitle: "Labels",
      tagsHint: "Vrije labels om projecten terug te vinden in lijsten. Ze veranderen niets aan wat agents mogen.",
      removeTag: "Tag {tag} verwijderen",
      readOnly: "Je hebt geen rechten om geavanceerde instellingen te wijzigen.",
    },
  },

  projectIntegrations: {
    title: "Gekoppelde systemen",
    help: "Kies welke externe resources bij dit project horen. Events, goedkeuringen en notificaties komen via deze koppelingen op dit project terecht.",
    vocabConnection: "Connectie = toegang op organisatieniveau tot een extern systeem",
    vocabBinding: "Projectkoppeling = externe resource gekoppeld aan dit project",
    vocabRouting: "Routering = regels die bepalen waar goedkeuringen en notificaties heen gaan",
    loadFailed: "Kon projectkoppelingen niet laden.",
    saveFailed: "Kon de koppeling niet opslaan.",
    removeFailed: "Kon de koppeling niet verwijderen.",
    toggleFailed: "Kon de koppeling niet bijwerken.",
    noPermission: "Je hebt de beheerrechten voor projectintegraties nodig om dit te wijzigen.",
    conflict: "Deze resource is al aan een ander project gekoppeld.",
    conflictHint: "Verwijder die koppeling voordat je de resource hier koppelt.",
    addBinding: "Toevoegen",
    saveBinding: "Opslaan",
    refIdPlaceholder: "Extern id (channel-id, groep-id, owner/repo, namespace, app-naam…)",
    refNamePlaceholder: "Weergavenaam (optioneel, bijv. #payments-platform)",
    enabled: "Actief",
    disabled: "Uit",
    testRoute: "Test route",
    participation: {
      inbound: "Inkomend → project",
      inboundPlanned: "Inkomend: gepland",
      outboundLive: "Terugkoppeling: live",
      outboundPlanned: "Terugkoppeling: gepland",
      learning: "Learning capture",
    },
    cap: {
      progress: "Voortgang",
      status: "Status",
      patchLinks: "Patch-links",
      comments: "Reacties",
      transitions: "Transities",
      incidents: "Incidenten",
    },
    testOk: "Resolutie OK — events van deze resource landen op dit project.",
    testMismatch: "Resolutie landt NIET op dit project:",
    testOtherProject: "Resolueert naar een ander project: {project}",
    testAmbiguous: "Ambigu — meerdere projecten matchen deze resource.",
    testNoMatch: "Geen project gematcht — events van deze resource vragen om handmatige selectie.",
    testFailed: "Test mislukt — kon de resolutie niet uitvoeren.",
    routingPreviewTitle: "Routering voor dit project:",
    readOnly: "Je hebt de beheerrechten voor projectintegraties nodig om dit te wijzigen.",
    configure: "Configureren",
    addAnother: "Nog een koppeling toevoegen",
    configureTitle: "{provider} configureren",
    usedFor: "Gebruikt voor:",
    remove: "Verwijderen",
    useFor: "Gebruiken voor",
    pickOne: "Selecteer…",
    connected: "Verbonden",
    notConnected: "Niet verbonden",
    configuredUnverified: "Gekoppeld \u00b7 nog niet geverifieerd",
    connectedTo: "Verbonden met {name}",
    slackConnectedTo: "Verbonden met workspace {team}",
    notConnectedHint: "{provider} is niet verbonden op organisatieniveau. Vraag een admin om de koppeling te maken via Instellingen → Kanalen.",
    webhookAlwaysAvailable: "Beschikbaar — gesigneerde inkomende webhooks",
    pickerLoadFailed: "Verbonden, maar resources konden niet geladen worden. Probeer opnieuw of gebruik de geavanceerde modus hieronder.",
    resourceRequired: "Kies eerst een resource.",
    catalogueEmpty: "Verbonden, maar nog niets te kiezen — dit account heeft geen items van dit type. Maak er een aan in het bronsysteem, of gebruik de Geavanceerd-modus hieronder.",
    moreOptions: "Meer opties",
    resourceRequiredBy: {
      argo: "Vul eerst de Argo-applicatie- of rollout-naam in (exact zoals in Argo).",
      kubernetes: "Vul eerst de Kubernetes-resource in (namespace, cluster of label selector).",
      teams: "Vul eerst het Teams-kanaal-id in.",
      topdesk: "Kies eerst een TOPdesk-operatorgroep.",
      webhook: "Vul eerst de webhook-endpoint-naam in.",
    },
    purposeRequired: "Selecteer minimaal één doel.",
    // Zie en.ts.
    purposeInert: "hier bezorgt nog niets",
    purposeInertHint: "Je kunt het opslaan, maar geen enkel onderdeel van FixControl verstuurt vandaag iets voor deze combinatie.",
    purposeInertIntake: "geen nieuwe issues",
    purposeInertIntakeHint: "Een nieuw ticket wordt geen issue. Tickets die aan een FixControl-issue gekoppeld zijn, horen bij dit project en de status loopt mee.",
    partialSave: "Sommige koppelingen konden niet opgeslagen worden —",
    advancedToggle: "Geavanceerd: externe referentie handmatig invoeren",
    advancedWarning: "Gebruik dit alleen wanneer de resource niet vindbaar is via de verbonden integratie.",
    advancedRefType: "Referentietype",
    advancedRefId: "Extern id",
    advancedConfig: "Config (JSON, optioneel)",
    advancedRefIdRequired: "Geavanceerde modus vereist een extern id.",
    configJsonInvalid: "Config moet een geldig JSON-object zijn.",
    searchChannels: "Zoek kanalen…",
    slackMissingScope: "De Slack-app mist de kanaallijst-permissie (channels:read). Verbind Slack opnieuw via Instellingen → Kanalen om die toe te kennen, of gebruik de geavanceerde modus hieronder.",
    slackChannel: "Kanaal",
    slackWorkspace: "Workspace: {team}",
    slackUseThread: "Gebruik de thread van het oorspronkelijke issue voor voortgangsupdates",
    slackAskProject: "Vraag om een project wanneer een bericht binnenkomt in een niet-gekoppeld kanaal",
    slackPreview: "Berichten uit {channel} maken issues aan in {project}.",
    freshdeskMatchBy: "Match tickets op",
    freshdeskTag: "Tag",
    freshdeskMirrorNote: "Freshdesk spiegelt voortgang en status, tenzij approval-routering expliciet is ingeschakeld.",
    freshdeskNoGroups: "Er bestaan geen agent-groepen in dit Freshdesk-account. Maak er een aan via Freshdesk → Admin → Groups, of koppel tickets via Product of Tag.",
    freshdeskNoProducts: "Er bestaan geen producten in dit Freshdesk-account (producten vereisen een Freshdesk Estate/Enterprise-plan). Koppel tickets via Groep of Tag.",
    freshdeskCatchAllNote: "Elk ticket uit dit Freshdesk-account gaat naar dit project, tenzij een specifiekere Groep-, Product- of Tag-koppeling het ergens anders heen stuurt.",
    githubExplain: "Repository-toegang, PR-oplevering en webhook-events. Repositories koppel je doorgaans als codebase — een koppeling hier routeert daarnaast webhook-events naar dit project.",
    githubConnection: "GitHub-connectie",
    githubRepo: "Repository",
    githubCodebaseHint: "Wil je een repository als codecontext voor de agent gebruiken, voeg die dan als codebase toe via de sectie Codebases hierboven.",
    githubAutoDeliver: "Code automatisch leveren — open een PR op deze repo na patch-goedkeuring",
    githubAutoDeliverHint: "Bij goedkeuren past FixControl de patch toe en opent een pull request (geen push naar main). Vereist dat de repo als GitHub-codebase is gekoppeld.",
    k8sBindingType: "Koppelingstype",
    k8sCluster: "Cluster",
    anyCluster: "Elk cluster",
    k8sLegacyClusterWarning:
      "Geen cluster gekozen. Met meer dan één cluster is een namespace die aan “elk cluster” is gekoppeld dubbelzinnig — kies een cluster zodat events naar het juiste project gaan.",
    environment: "Omgeving",
    environments: {
      development: "Development",
      staging: "Staging",
      production: "Productie",
    },
    k8sPreview: "Events van {resource} resolven naar {project}.",
    k8sDiscoveryNote: "Namespace-discovery is niet beschikbaar — voer de namespace exact in zoals die in het cluster heet.",
    argoInstance: "Argo-connectie",
    argoManualNote: "Argo-applicaties zijn niet automatisch vindbaar — voer de naam exact in zoals die in Argo staat.",
    argoPromotionApprovals: "Promotie-goedkeuringen — pauzeer rollouts tot goedkeuring in FixControl",
    argoAutoDeployGate: "Deployment-gate automatisch openen — na patch-goedkeuring een Argo-gebonden deploy-goedkeuring openen",
    argoAutoDeployHint: "Bij goedkeuren hervat FixControl de gepauzeerde rollout hieronder. Vereist namespace + rollout.",
    argoNamespace: "Rollout-namespace",
    argoRollout: "Rollout-naam",
    emailAlias: "Mailbox / alias",
    webhookEndpoint: "Endpoint- / bronnaam",
    webhookEventType: "Eventtype (optioneel)",
    jiraProject: "Jira-project",
    linearTeam: "Linear-team",
    jiraMirrorNote: "Issue-trackers spiegelen voortgang en status; intake maakt issues aan in dit project.",
    topdeskOperatorGroup: "Operatorgroep",
    topdeskMirrorNote: "Meldingen die bij deze operatorgroep liggen landen in dit project; voortgang en status worden teruggespiegeld naar de melding.",
    teamsChannelId: "Kanaal-id",
    teamsManualNote: "Teams-kanalen zijn niet vindbaar met de bot-credential. In Teams: kanaal → ⋯ → Link naar kanaal ophalen — het id is het 19:…@thread-deel.",
    teamsChannel: "Teams-kanaal",
    teamsManualToggle: "Kanaal niet in de lijst? Voer het id handmatig in",
    teamsNoChannelsDiscoverable: "Voeg de FixControl-bot toe aan een Teams-kanaal (@-vermeld hem één keer) en het verschijnt hier. Voer tot die tijd het kanaal-id handmatig in.",
    notConnectedList: "Niet verbonden op organisatieniveau:",
    notConnectedListHint: "verbind ze via Instellingen → Kanalen, dan verschijnen ze hier.",
    checkingConnections: "Bezig met controleren welke systemen verbonden zijn…",
    statusUnverified: "Kon organisatieverbindingen niet verifiëren — alleen systemen met bestaande koppelingen worden getoond.",
    unverifiedList: "Niet geverifieerd:",
    expandCard: "Uitklappen",
    collapseCard: "Inklappen",
    boundCountOne: "{count} resource gekoppeld",
    boundCountOther: "{count} resources gekoppeld",
    providers: {
      slack: {
        title: "Slack",
        help: "Slack is op workspace-niveau verbonden. Kies welke kanalen bij dit project horen — intakekanalen routeren nieuwe issues hierheen; een goedkeuringskanaal ontvangt beslissingskaarten.",
        example: "Voorbeeld — koppel #payments-support zodat berichten daar issues in dit project openen.",
      },
      teams: {
        title: "Microsoft Teams",
        help: "Koppel Teams-kanalen aan dit project zodat berichten en goedkeuringen juist routeren. Kanalen van elk team waarin de bot is @-vermeld verschijnen in de lijst; voor de rest plak je het kanaal-id uit Teams.",
        example: "Voorbeeld — koppel het kanaal ‘Payments – General’ zodat berichten hierheen gaan.",
      },
      freshdesk: {
        title: "Freshdesk",
        help: "Koppel Freshdesk-groepen, -producten of -tags aan dit project. Freshdesk spiegelt voortgang en status, tenzij approval-routering expliciet is ingeschakeld.",
        example: "Voorbeeld — stuur tickets met tag fc-payments, of alle tickets, naar dit project.",
      },
      odoo: {
        title: "Odoo",
        help: "Koppel Odoo-helpdeskteams aan dit project. Odoo spiegelt voortgangs- en statusupdates; tickets van een gekoppeld team landen hier.",
        example: "Voorbeeld — koppel het team Customer Care zodat zijn tickets naar dit project routeren.",
      },
      topdesk: {
        title: "TOPdesk",
        help: "Koppel TOPdesk-operatorgroepen aan dit project. TOPdesk spiegelt voortgangs- en statusupdates; meldingen die bij een gekoppelde groep liggen landen hier.",
        example: "Voorbeeld — koppel de operatorgroep Servicedesk Betalingen zodat haar meldingen naar dit project routeren.",
      },
      jira: {
        title: "Jira",
        help: "Koppel Jira-projecten aan dit project. Jira spiegelt voortgang en status; issues uit een gekoppeld project landen hier.",
        example: "Voorbeeld — koppel het PAY Jira-project zodat issues in dit project spiegelen.",
      },
      linear: {
        title: "Linear",
        help: "Koppel Linear-teams aan dit project. Linear spiegelt voortgang en status; issues uit een gekoppeld team landen hier.",
        example: "Voorbeeld — koppel het ENG Linear-team zodat issues in dit project spiegelen.",
      },
      github: {
        title: "GitHub",
        help: "Repositories koppel je doorgaans als codebases (hierboven). Een repo-koppeling hier routeert daarnaast webhook-events naar dit project.",
        example: "Voorbeeld — koppel acme/ledger zodat webhook-events naar dit project gaan.",
      },
      gitlab: {
        title: "GitLab",
        help: "Projecten koppel je doorgaans als codebases (hierboven). Een projectkoppeling hier routeert daarnaast webhook-events naar dit project.",
        example: "Voorbeeld — koppel acme/ledger zodat webhook-events naar dit project gaan.",
      },
      bitbucket: {
        title: "Bitbucket",
        help: "Repositories koppel je doorgaans als codebases (hierboven). Een repo-koppeling hier routeert daarnaast webhook-events naar dit project.",
        example: "Voorbeeld — koppel acme/ledger zodat webhook-events naar dit project gaan.",
      },
      kubernetes: {
        title: "Kubernetes",
        help: "Koppel clusters, namespaces of services aan dit project zodat deployment-events en guardrails in de juiste context terechtkomen.",
        example: "Voorbeeld — koppel namespace payments zodat deployments daar bij dit project horen.",
        exampleIn: {
          intake: "Voorbeeld — een vastgelopen rollout of crashende pod in namespace payments komt hier binnen als werk.",
          updates: "Voorbeeld — koppel cluster prod zodat deploystatus daaruit dit project bijwerkt.",
        },
      },
      argo: {
        title: "Argo",
        help: "Koppel Argo-applicaties of rollouts aan dit project zodat deployment-goedkeuringen en promotie-events juist gerouteerd worden.",
        example: "Voorbeeld — koppel de Argo-applicatie ledger-api zodat promotie-approvals in dit project landen.",
        exampleIn: {
          code: "Voorbeeld — koppel de Argo-applicatie ledger-api als de runtime van dit project.",
          updates: "Voorbeeld — koppel de Argo-applicatie ledger-api zodat deploystatus daaruit dit project bijwerkt.",
        },
      },
      email: {
        title: "E-mail",
        help: "Koppel inkomende e-mailaliassen aan dit project zodat e-mailintake op het juiste project landt.",
        example: "Voorbeeld — koppel support@jouwbedrijf.nl zodat e-mails daar issues in dit project openen.",
      },
      webhook: {
        title: "Webhooks",
        help: "Routeer inkomende webhook-events van een benoemde bron naar dit project — deployment-events, CI-signalen, incidentmeldingen.",
        example: "Voorbeeld — koppel ci-pipeline zodat deployment-events naar dit project gaan.",
        exampleIn: {
          intake: "Voorbeeld — koppel monitoring-alerts zodat een alert hier werk in dit project opent.",
        },
      },
    },
    purposes: {
      intake: "Nieuwe issues",
      approvals: "Goedkeuringsverzoeken",
      notifications: "Voortgangsupdates",
      status: "Statusupdates",
      delivery: "PR- / patch-oplevering",
      deployment: "Deployment-events",
      incidents: "Incidentmeldingen",
      code: "Codecontext",
    },
    purposeLabels: {
      freshdesk: {
        intake: "Nieuwe tickets",
        notifications: "Voortgangsnotities",
      },
      topdesk: {
        intake: "Nieuwe meldingen",
        notifications: "Voortgangsnotities",
      },
      github: {
        notifications: "Webhook-events",
      },
      kubernetes: {
        status: "Runtime-context",
      },
      argo: {
        deployment: "Rollout- / promotie-events",
        approvals: "Promotie-goedkeuringen",
        status: "Deployment-status",
      },
    },
    purposeDescriptions: {
      intake: "Inkomende items van deze resource maken issues aan in dit project.",
      approvals: "Goedkeuringsverzoeken en beslissingskaarten worden hierheen gestuurd.",
      notifications: "Voortgangsupdates worden op deze resource geplaatst.",
      status: "Statuswijzigingen worden naar deze resource gespiegeld.",
      delivery: "Patches en pull requests worden via deze resource opgeleverd.",
      deployment: "Deployment-events van deze resource horen bij dit project.",
      incidents: "Incidentmeldingen van deze resource landen op dit project.",
      code: "Deze resource levert codecontext voor de agent.",
    },
    refTypes: {
      slack_channel: "Kanaal",
      slack_user_group: "Gebruikersgroep",
      teams_channel: "Kanaal",
      freshdesk_group: "Groep",
      freshdesk_product: "Product",
      freshdesk_tag: "Tag",
      freshdesk_account: "Alle tickets",
      odoo_team: "Helpdeskteam",
      topdesk_operator_group: "Operatorgroep",
      jira_project: "Jira-project",
      jira_component: "Component",
      linear_team: "Team",
      linear_project: "Linear-project",
      github_repo: "Repository",
      gitlab_repo: "Repository",
      bitbucket_repo: "Repository",
      kubernetes_cluster: "Cluster",
      kubernetes_namespace: "Namespace",
      kubernetes_service: "Service",
      kubernetes_label_selector: "Label-selector",
      argo_application: "Applicatie",
      argo_rollout: "Rollout",
      argo_project: "Argo-project",
      argo_namespace: "Namespace",
      email_alias: "Alias",
      webhook_endpoint: "Endpoint",
    },
  },

  projectCodebaseWizard: {
    title: "Codebase toevoegen",
    openButton: "Codebase toevoegen…",
    linkExisting: "Bestaande codebase koppelen…",
    stepLabel: "Stap {step} van {total}",
    steps: {
      source: "Kies bron",
      connection: "Connectie",
      select: "Kies repo of map",
      configure: "Toegang instellen",
      attach: "Aan project koppelen",
      done: "Codebase toegevoegd",
    },
    explainer: "Een connectie regelt toegang (de credential, bijv. een GitHub App-installatie). Een codebase is de concrete repo of map waarop agents werken. Channels en integraties verzorgen intake en delivery — Slack, PR-delivery, webhooks.",
    sources: { github: "GitHub", gitlab: "GitLab", bitbucket: "Bitbucket", local: "Lokale map", ssh: "SSH-server" },
    sourceHints: {
      github: "Repository op github.com",
      gitlab: "Repository op GitLab",
      bitbucket: "Repository op Bitbucket",
      local: "Map op de FixControl-host",
      ssh: "Repository of map op een externe server",
    },
    connectionHelp: "Een connectie is de credential waarmee FixControl bij je code kan. Kies een bestaande of koppel een nieuw account.",
    connected: "Verbonden",
    noConnections: "Nog geen {source}-connectie.",
    connectCta: "{source} koppelen",
    repoSearch: "Zoek repositories…",
    noRepoMatches: "Geen repositories gevonden voor je zoekopdracht.",
    repoFreeFormLabel: "Repository (owner/naam)",
    branch: "Branch",
    localPathLabel: "Map-pad",
    localBaseHint: "Basispad van de connectie: {base}",
    remotePathLabel: "Extern pad",
    nameLabel: "Codebase-naam",
    sourceLabel: "Bron",
    accessMode: "Toegangsmodus",
    modeHelp: {
      read_only: "Agents kunnen de code inspecteren en analyseren maar nooit wijzigen.",
      sandbox: "Agents stellen patches voor die in een geïsoleerde sandbox worden geverifieerd; wijzigingen gaan via review. Aanbevolen.",
      writable: "Agents mogen direct in de working tree schrijven. Met beleid gebruiken.",
    },
    attachProject: "Project",
    useAsDefault: "Gebruik als standaard-codebase voor dit project",
    addCodebase: "Codebase toevoegen",
    adding: "Toevoegen…",
    retryAttach: "Koppelen opnieuw proberen",
    partialFailureTitle: "Codebase aangemaakt, maar niet gekoppeld",
    partialFailureBody: "\u201c{name}\u201d bestaat, maar het koppelen aan {project} is mislukt: {error}. Probeer het koppelen opnieuw — er wordt geen dubbele codebase aangemaakt.",
    noPermission: "Codebases toevoegen vereist de admin-rol — vraag een admin om er een toe te voegen.",
    doneSub: "\u201c{name}\u201d is gekoppeld aan {project}.",
    nextInspect: "Codebase-inspectie uitvoeren",
    nextPosture: "Codebase-posture bekijken",
    nextGuardrails: "Guardrails instellen",
    close: "Klaar",
    back: "Terug",
    next: "Verder",
  },
  settingsCodebases: {
    eyebrow: "Instellingen",
    title: "Codebases",
    subtitle: "Code-repositories en mappen waarop agents kunnen werken voor {company}.",
    subtitleDefault: "jouw organisatie",
    addCodebase: "Codebase toevoegen",
    deleteConfirm: "Codebase \"{name}\" verwijderen? De verbinding met de repository wordt verbroken. Historische commit-referenties, patch-lineage en verificatiebewijzen blijven bewaard, en een beheerder kan hem terugzetten.",
    deletedToast: "{name} verwijderd",
    testFailedToast: "Verbindingstest voor {name} mislukt — netwerkfout.",
    createdToast: "{name} aangemaakt",
    updatedToast: "{name} bijgewerkt",
    empty: "Geen codebases geconfigureerd",
    emptyHint: "Voeg een codebase toe om agents te vertellen waar je code staat.",
    needInfra: "Maak eerst een infrastructuur-verbinding (Instellingen → Infrastructuur).",
    needInfraStepLabel: "Eerst dit",
    needInfraStep: "Vertel FixControl waar je code staat. Daarna kun je hier codebases toevoegen.",
    needInfraCta: "Infrastructuur verbinden",
    sandboxDb: {
      label: "Database voor verificatieruns",
      hint: "Of een verificatierun de databasemigraties van deze codebase toepast voordat hij de wijziging test. Zonder database worden migraties overgeslagen en kan een wijziging die er een nodig heeft niet worden bewezen.",
      inherit: "Standaard (systeeminstelling gebruiken)",
      none: "Geen — databasemigraties overslaan",
      url: "URL — gebruik een database-URL voor deze codebase",
      docker: "Elke run een verse wegwerp-database",
      engineLabel: "Sidecar-database-engine",
      engineInherit: "Standaard (systeeminstelling gebruiken)",
      enginePostgres: "PostgreSQL",
      engineMysql: "MySQL",
      engineHint: "Kies MySQL voor Laravel/Magento-codebases waarvan de migraties MySQL-specifieke SQL gebruiken. Laat het op Standaard en deze codebase volgt de keuze voor de hele workspace, die PostgreSQL is tenzij een beheerder dat heeft gewijzigd.",
      urlLabel: "Database-URL voor deze codebase",
      urlPlaceholderUnset: "postgres://user:pass@host:5432/db",
      urlPlaceholderSet: "•••••••• opgeslagen — laat leeg om te behouden",
      urlHint: "Versleuteld opgeslagen en alleen voor deze codebase. Laat het leeg en deze codebase gebruikt de database voor de hele workspace. Wijs naar een geïsoleerde wegwerp-database — de verificatie draait migraties tegen wat hier staat.",
      urlClear: "Opgeslagen URL wissen",
    },
    fields: {
      projectFile: "Projectbestand",
      projectRoot: "Projectroot",
      scm: "SCM",
      analyses: "Analyses",
      techDocs: "Tech-docs",
      access: "Toegang",
      repository: "Repository",
      path: "Pad",
      remotePath: "Remote pad",
      branch: "Branch",
      mode: "Modus",
      via: "Via",
      unknown: "Onbekend",
      analysisCount: "{count} bestand",
      analysisCountPlural: "{count} bestanden",
      docCount: "{count} ref",
      docCountPlural: "{count} refs",
    },
    multiEntry: {
      add: "Toevoegen",
    },
    actions: {
      viewPosture: "Posture bekijken",
      testConnection: "Verbinding testen",
      edit: "Bewerken",
      delete: "Verwijderen",
    },
    modal: {
      titleEdit: "{name} bewerken",
      titleAddWindev: "WINDEV-codebase toevoegen",
      titleAdd: "Codebase toevoegen",
      subEdit: "Codebase-configuratie bijwerken",
      subAdd: "Koppel een codebase aan een bestaande infrastructuur-verbinding",
      submitSave: "Opslaan",
      submitAddWindev: "WINDEV-project toevoegen",
      submitAdd: "Codebase toevoegen",
      noConnections: "Geen infrastructuur-verbindingen geconfigureerd",
      noConnectionsHint: "Maak eerst een verbinding in Instellingen → Infrastructuur, kom daarna terug.",
      infrastructureConnection: "Infrastructuur-verbinding",
      codebaseName: "Codebase-naam",
      codebaseNamePlaceholderWindev: "bv. TaurusKassa",
      codebaseNamePlaceholderGeneric: "bv. POS Backend",
      description: "Omschrijving",
      descriptionOptional: "(optioneel)",
      descriptionPlaceholder: "Wat bevat deze codebase?",
      platformType: "Platformtype",
      repository: "Repository",
      repoSelectPlaceholder: "— kies een repository —",
      repoPrivate: " (privé)",
      repoSelectFooter: "{count} repo toegankelijk voor deze installatie.",
      repoSelectFooterPlural: "{count} repo's toegankelijk voor deze installatie.",
      repoSelectManageLink: "Repo's toevoegen of verwijderen op GitHub",
      repoLoading: "Repositories laden…",
      repoLoadFailed: "Kan repo's niet laden van GitHub: {error}",
      repoPlaceholder: "org/repo-naam",
      defaultBranch: "Default-branch",
      branchPlaceholder: "main",
      windevHeader: "WINDEV-projectconfiguratie",
      windevProjectRootPlaceholderSsh: "/opt/windev/TaurusKassa",
      windevProjectRootPlaceholderLocal: "C:/WINDEV/Projects/TaurusKassa",
      windevProjectFile: "Projectbestand (.wdp)",
      windevProjectFilePlaceholder: "TaurusKassa.wdp",
      windevSourceControl: "Versiebeheer",
      windevAnalysisLabel: "Analysebestanden (.wda)",
      windevAnalysisHint: "Voeg paden toe relatief aan de projectroot",
      windevAnalysisPlaceholder: "bv. TaurusKassa.wda",
      windevDocsLabel: "Technische documentatie",
      windevDocsHint: "Verwijzingen naar geëxporteerde docs of geïmporteerde docbronnen voor kennisretrieval",
      windevDocsPlaceholder: "bv. docs/architectuur-overzicht.pdf",
      defaultMode: "Standaardmodus",
    },
  },

  settingsInfra: {
    eyebrow: "Instellingen",
    title: "Infrastructuur",
    subtitle: "Koppel een credential en kies op welke repositories je agents werken. Agents van {company} hebben alleen toegang tot codebases die hieronder zijn geactiveerd.",
    subtitleDefault: "Jouw organisatie",
    activeConnections: "Actieve verbindingen",
    addConnection: "Verbinding toevoegen",
    whereDoesCodeLive: "Waar staat je code?",
    desktopRequired: "Desktop-app vereist",
    downloadDesktop: "Desktop-app downloaden",
    addAnother: "Nog één toevoegen",
    setUp: "Instellen",
    connected: "Gekoppeld",
    githubAppInstalledToast: "GitHub-app geïnstalleerd",
    githubInstallFailedToast: "GitHub-installatie mislukt: {reason}",
    gmailConnectedToast: "Gmail gekoppeld",
    gmailConnectFailedToast: "Gmail-koppeling mislukt: {reason}",
    jiraConnectedToast: "Jira gekoppeld",
    jiraConnectFailedToast: "Jira-koppeling mislukt: {reason}",
    linearConnectedToast: "Linear gekoppeld",
    linearConnectFailedToast: "Linear-koppeling mislukt: {reason}",
    deletedToast: "{name} verwijderd",
    createdToast: "{name} aangemaakt",
    updatedToast: "{name} bijgewerkt",
    deleteConfirm: "Verbinding \"{name}\" verwijderen? Codebases die hem gebruiken werken niet meer tot hij opnieuw is verbonden. De audithistorie en de verificatiebewijzen van eerdere runs blijven bewaard.",
    localTestNeedsDesktop: "Lokale verbindingen kunnen alleen vanuit de desktop-app worden getest.",
    localMissingBasePath: "Basis-pad ontbreekt — bewerk de verbinding en stel er één in.",
    localNeedsDesktopToCreate: "Lokale verbindingen vereisen de desktop-app.",
    repoActiveCount: "· {active}/{total} repo actief",
    repoActiveCountPlural: "· {active}/{total} repo's actief",
    codebaseCount: "· {count} codebase",
    codebaseCountPlural: "· {count} codebases",
    statusLabels: {
      connected: "Gekoppeld",
      pending: "In afwachting",
      error: "Fout",
      not_configured: "Niet geconfigureerd",
    },
    githubHealth: {
      reconnectRequiredTitle: "Opnieuw verbinden vereist",
      reconnectRequiredHint: "Recente GitHub-aanroepen kregen een permanente auth-fout. Installeer de GitHub App opnieuw om PR-levering te herstellen.",
      reconnectRequiredWithReason: "Recente GitHub-aanroepen mislukten: {reason}. Installeer de App opnieuw of wis de waarschuwing als je toegang al hersteld hebt.",
      lastErrorLabel: "Laatste GitHub-fout:",
      failedCount: "{count} opeenvolgende fouten",
      lastPrPrefix: "Laatste PR-levering:",
      reinstall: "GitHub App opnieuw installeren",
      clearPin: "Opgelost, waarschuwing wissen",
      cleared: "Reconnect-waarschuwing gewist",
    },
    providerHealth: {
      reconnectRequiredTitle: "Opnieuw verbinden vereist",
      reconnectRequiredHint: "Recente aanroepen naar deze integratie kregen een permanente auth-fout. Doorloop de verbindingsflow opnieuw om toegang te herstellen.",
      reconnectRequiredWithReason: "Recente aanroepen mislukten: {reason}. Doorloop de verbindingsflow opnieuw om toegang te herstellen.",
      lastErrorLabel: "Laatste fout:",
      failedCount: "{count} opeenvolgende fouten",
      lastDeliveryPrefix: "Laatste geslaagde levering:",
      reconnect: "Opnieuw verbinden",
    },
    githubRepos: {
      loading: "Repositories laden…",
      loadFailed: "Kan repositories niet laden: {error}",
      noneYetPrefix: "Deze installatie heeft nog geen toegang tot repositories.",
      addOnGithub: "Voeg er een paar toe op GitHub",
      manageOnGithub: "Repo's toevoegen of verwijderen op GitHub",
      removeCodebaseTitle: "Codebase verwijderen",
      activate: "Activeren",
      activateFailed: "Activeren mislukt ({status})",
      deleteFailed: "Verwijderen mislukt ({status})",
      activatedToast: "{name} geactiveerd",
      removedToast: "{name} verwijderd",
      removeConfirm: "\"{name}\" verwijderen uit actieve codebases? De verbinding wordt verbroken. Historische commit-referenties, patch-lineage en verificatiebewijzen blijven bewaard, en een beheerder kan hem terugzetten.",
      activeBadge: "Actief",
    },
    otherCodebases: {
      empty: "Nog geen codebases voor deze verbinding.",
      removeCodebaseTitle: "Codebase verwijderen",
      addCodebase: "Codebase toevoegen",
    },
    addModal: {
      title: "{label}-verbinding toevoegen",
      connectionName: "Verbindingsnaam",
    },
    editModal: {
      title: "{label}-verbinding bewerken",
    },
    field: {
      browse: "Bladeren",
    },
    github: {
      connectTitle: "GitHub koppelen",
      connectSub: "Installeer de FixControl GitHub-app op geselecteerde repositories. Pull requests verschijnen onder de bot-identiteit en tokens roteren automatisch.",
      installTitle: "Installeren op GitHub",
      installBody: "Aanbevolen. Je wordt naar GitHub doorgestuurd om te kiezen welke repositories de app mag benaderen. PR's worden toegeschreven aan de FixControl-bot.",
      continueOnGithub: "Doorgaan op GitHub",
      deviceTitle: "Gebruik een device-code",
      deviceSub: "Voor headless-installaties of als je in deze browser niet bij FixControl kunt inloggen. PR's worden toegeschreven aan jouw GitHub-gebruiker.",
      patTitle: "Geavanceerd: plak een Personal Access Token",
      patSub: "Gebruik je eigen token. Benodigde scopes: repo, workflow, read:user.",
      getDeviceCode: "Device-code aanvragen",
      deviceFailedStart: "Kan device-flow niet starten",
      userCode: "Gebruikerscode",
      verifyStep1Prefix: "1. Open ",
      verifyStep2: "2. Plak de code en keur goed. Deze pagina pikt het automatisch op.",
      waitingForApproval: "Wachten op goedkeuring…",
      deviceFailed: "Device-flow mislukt",
      patName: "GitHub (PAT)",
      patConnectionName: "Verbindingsnaam",
      patTokenLabel: "Personal Access Token",
      patTokenPlaceholder: "ghp_...",
      patHelpPrefix: "Aanmaken op ",
      patHelpSuffix: " met scopes: repo, workflow.",
      patUserLabel: "GitHub-gebruikersnaam (optioneel)",
      patUserPlaceholder: "jouw-gebruikersnaam",
      saveToken: "Token opslaan",
      genericFailed: "Mislukt",
      networkError: "Netwerkfout",
    },
    gitlab: {
      connectTitle: "GitLab koppelen",
      connectSub: "Sta FixControl toe patches te pushen en merge requests te openen op je GitLab-projecten. Refresh-tokens worden versleuteld opgeslagen; access-tokens roteren automatisch.",
      oauthTitle: "Inloggen met GitLab",
      oauthBody: "Aanbevolen. Je wordt naar GitLab doorgestuurd om de scopes api en read_user te verlenen. Merge requests worden toegeschreven aan jouw GitLab-gebruiker en tokens worden automatisch ververst.",
      continueWithGitlab: "Doorgaan met GitLab",
      tokenTitle: "Geavanceerd: plak een Personal Access Token",
      tokenSub: "Gebruik je eigen token. Vereist voor self-hosted GitLab-instances die de FixControl OAuth-client niet hosten.",
      tokenName: "GitLab (PAT)",
      tokenConnectionName: "Verbindingsnaam",
      tokenBaseUrlLabel: "GitLab-URL (leeg voor gitlab.com)",
      tokenBaseUrlHint: "Voor self-hosted instances. Cloudgebruikers laten dit leeg.",
      tokenLabel: "Personal Access Token",
      tokenHelpPrefix: "Aanmaken op ",
      tokenHelpSuffix: " met scope: api.",
      saveToken: "Token opslaan",
    },
    bitbucket: {
      connectTitle: "Bitbucket koppelen",
      connectSub: "Sta FixControl toe patches te pushen en pull requests te openen op je Bitbucket-repositories. Refresh-tokens worden versleuteld opgeslagen; access-tokens roteren automatisch.",
      oauthTitle: "Inloggen met Bitbucket",
      oauthBody: "Aanbevolen. Je wordt naar Bitbucket doorgestuurd om de scopes te verlenen die op de OAuth Consumer staan (meestal repository, repository:write, pullrequest, pullrequest:write).",
      continueWithBitbucket: "Doorgaan met Bitbucket",
      tokenTitle: "Geavanceerd: plak een access token of App Password",
      tokenSub: "Gebruik een workspace/repository access token (aanbevolen) of een App Password gekoppeld aan een gebruikersnaam. Vereist als de FixControl OAuth Consumer niet kan worden geregistreerd.",
      tokenName: "Bitbucket (token)",
      tokenConnectionName: "Verbindingsnaam",
      tokenBaseUrlLabel: "Bitbucket API-URL (leeg voor Cloud)",
      tokenBaseUrlHint: "Voor forward-compat met Atlassian-gehoste proxy's. Cloudgebruikers laten dit leeg.",
      tokenUsernameLabel: "Bitbucket-gebruikersnaam (alleen App Password)",
      tokenUsernameHint: "Laat leeg bij een workspace/repository access token. Vereist voor App Passwords.",
      tokenLabel: "Access Token, OAuth Bearer of App Password",
      saveToken: "Token opslaan",
    },
    jira: {
      connectTitle: "Jira koppelen",
      connectSub: "Sta FixControl toe BUG- en FEATURE-issues te spiegelen naar een Jira Cloud-project en geleverde pull requests terug te koppelen aan het ticket. Refresh-tokens worden versleuteld opgeslagen; access-tokens roteren automatisch.",
      oauthTitle: "Inloggen met Atlassian",
      oauthBody: "Aanbevolen. Je wordt naar Atlassian doorgestuurd om read:jira-work, write:jira-work en offline_access toe te kennen. Issues worden toegeschreven aan jouw Atlassian-gebruiker.",
      continueWithJira: "Doorgaan met Atlassian",
      tokenTitle: "Geavanceerd: plak e-mail + API-token",
      tokenSub: "Gebruik je Atlassian-account-e-mail en een Jira API-token. Vereist als de FixControl OAuth-app niet voor deze deployment is geregistreerd, of voor self-hosted Jira (DC/Server).",
      tokenName: "Jira (API-token)",
      tokenConnectionName: "Verbindingsnaam",
      tokenBaseUrlLabel: "Jira-site-URL",
      tokenEmailLabel: "Atlassian-account-e-mail",
      tokenLabel: "API-token",
      tokenHelpPrefix: "Maak aan op ",
      tokenHelpSuffix: ".",
      defaultProjectLabel: "Standaard projectkey (bijv. POS)",
      saveToken: "Token opslaan",
    },
    linear: {
      connectTitle: "Linear koppelen",
      connectSub: "Sta FixControl toe BUG- en FEATURE-issues te spiegelen naar een Linear-team en geleverde pull requests aan de issue te koppelen.",
      oauthTitle: "Inloggen met Linear",
      oauthBody: "Aanbevolen. Je wordt naar Linear doorgestuurd om read- en write-scopes toe te kennen. Issues worden toegeschreven aan jouw Linear-gebruiker.",
      continueWithLinear: "Doorgaan met Linear",
      tokenTitle: "Geavanceerd: plak een API-key",
      tokenSub: "Gebruik een persoonlijke Linear API-key. Vereist als de FixControl OAuth-app niet voor deze deployment is geregistreerd.",
      tokenName: "Linear (API-key)",
      tokenConnectionName: "Verbindingsnaam",
      tokenLabel: "API-key",
      tokenHelpPrefix: "Maak aan op ",
      tokenHelpSuffix: ".",
      defaultTeamLabel: "Standaard team-key (bijv. ENG)",
      saveToken: "Token opslaan",
    },
    gmail: {
      connectTitle: "Gmail koppelen",
      connectSub: "Sta FixControl toe binnenkomende mail van een Google-account te lezen. Refresh-tokens worden versleuteld opgeslagen; access-tokens roteren automatisch.",
      signInTitle: "Inloggen met Google",
      signInBody: "Je wordt naar Google doorgestuurd om de scopes {readScope} en {modifyScope} te verlenen (de laatste activeert push-notificaties via Pub/Sub indien geconfigureerd).",
      continueWithGoogle: "Doorgaan met Google",
      footnote: "Bewerk na koppeling de verbinding om routing-regels voor ontvangers toe te voegen — bijvoorbeeld {example} koppelen aan een specifieke organisatie. Zonder regels gaat elk binnenkomend bericht naar de organisatie die deze verbinding bezit.",
      editTitle: "Gmail-verbinding bewerken",
      connectedAs: "Gekoppeld als",
      reconnect: "Opnieuw koppelen",
      recipientRouting: "Ontvanger-routing",
      recipientRoutingHelp: "Koppel een To:-adres aan een organisatie. Zonder regels gaat elk bericht naar {tenant}.",
      addRule: "Regel toevoegen",
      noRules: "Geen regels geconfigureerd. Alle binnenkomende mail gaat naar {tenant}.",
      rulePlaceholder: "support+flirt@fixcontrol.nl",
      tenantUnknown: "{id} (onbekend)",
      lastSync: "Laatste sync {time}",
      syncNow: "Nu synchroniseren",
      syncQueued: "In wachtrij",
      syncFailed: "Mislukt",
      syncDisabledReason: "Opnieuw verbinden om te synchroniseren",
      derived: {
        // Bewust "Geconfigureerd": credentials + geen opgeslagen fout is geen
        // geverifieerde verbinding — live groen komt uit het health-paneel.
        connected: "Geconfigureerd",
        sync_error: "Sync-fout",
        reconnect_required: "Opnieuw verbinden vereist",
        disconnected: "Niet verbonden",
      },
    },
  },

  settingsSecurity: {
    eyebrow: "Instellingen",
    title: "Beveiliging",
    subtitle: "Je eigen inloggegevens en tweestapsverificatie; beheerders beheren hier ook de organisatiebeveiliging.",
    personalSubtitle: "Je eigen wachtwoord en tweestapsverificatie.",
    organizationSubtitle: "Tweestapsbeleid en sessie-intrekking voor je hele organisatie.",
    platformSubtitle: "JWT-sleutels en rate-limits — FixControl-platforminterne zaken.",
    sections: {
      personal: "Mijn beveiliging",
      organization: "Organisatiebeveiliging",
      platform: "FixControl-platformbeheer",
    },
    accessRequired: "Admin-toegang vereist",
    accessRequiredDetail: "Alleen admins kunnen beveiligingsoperaties uitvoeren.",
    adminOnlyHint: "Sessie-intrekking, JWT-rotatie en rate-limit-inspectie zijn admin-only en niet van toepassing op je account.",
    platformOnlyHint: "Sleutelrotatie en de rate-limit-inspectie zijn FixControl-platformoperaties, alleen beschikbaar voor platformmedewerkers.",
    password: {
      header: "Wachtwoord wijzigen",
      description: "Gebruik een uniek wachtwoord — je login is je e-mail en die delen we nooit met andere sites.",
      currentLabel: "Huidig wachtwoord",
      newLabel: "Nieuw wachtwoord",
      confirmLabel: "Bevestig nieuw wachtwoord",
      requirement: "Minimaal {min} tekens.",
      save: "Wachtwoord bijwerken",
      saving: "Bijwerken…",
      saved: "Wachtwoord bijgewerkt",
      errors: {
        badCurrent: "Je huidige wachtwoord is onjuist.",
        samePassword: "Kies een wachtwoord dat verschilt van het huidige.",
        weak: "Wachtwoord moet minimaal {min} tekens zijn.",
        mismatch: "Wachtwoorden komen niet overeen.",
        network: "Netwerkfout — probeer het opnieuw.",
      },
    },
    revoke: {
      header: "Sessies intrekken",
      description: "Logt gebruikers uit door alle sessietokens van vóór dit moment te weigeren. Gebruik dit om de sessies van een gebruiker of organisatie per direct te beëindigen — getroffen gebruikers loggen daarna gewoon opnieuw in.",
      scope: "Scope",
      scopeGlobal: "Iedereen (heel platform)",
      scopeTenant: "Alle leden van één organisatie",
      scopeUser: "Eén gebruiker",
      tenantId: "Organisatie-id",
      tenantPlaceholder: "bv. FixControl",
      userId: "Gebruikers-id",
      userPlaceholder: "bv. u-abc123",
      findUsersPrefix: "Zoek gebruikers-ids in",
      findUsersLink: "Gebruikers",
      revokeButton: "Intrekken…",
      confirmGlobal: "Iedereen uitloggen",
      confirmTenant: "Alle leden van organisatie \"{id}\" uitloggen",
      confirmUser: "Gebruiker \"{id}\" uitloggen",
      confirmPlaceholder: "…",
      confirmTrailing: ". Dit kan niet ongedaan worden gemaakt — getroffen gebruikers moeten opnieuw inloggen.",
      confirmRevoke: "Bevestig intrekken",
      revoking: "Intrekken…",
      revokedMessage: "Ingetrokken (cutoff {cutoff}). Overeenkomende sessies worden geweigerd bij hun volgende verzoek.",
      networkError: "Netwerkfout",
    },
    rateLimit: {
      header: "Rate-limit-buckets",
      refresh: "Vernieuwen",
      failedToLoad: "Laden mislukt",
      unsupported: "De huidige rate-limit-store ondersteunt geen enumeratie (waarschijnlijk een Redis-adapter zonder snapshot-helper). Controleer Redis direct om buckets te inspecteren.",
      empty: "Geen getrackte buckets — er is nog geen verkeer, of elke bucket zit vol.",
      headers: {
        bucket: "Bucket",
        subject: "Onderwerp",
        tokens: "Tokens",
        fill: "Vulling",
        updated: "Bijgewerkt",
      },
      ageSeconds: "{n}s geleden",
      ageMinutes: "{n}m geleden",
      ageHours: "{n}u geleden",
    },
    rotation: {
      header: "JWT-key roteren",
      description: "Genereert een nieuwe sterke signing-key en past hem toe volgens de gekozen modus. De daadwerkelijke secret-waarde wordt geschreven naar {file} (of in db-modus naar de DB) — nooit teruggegeven in de respons.",
      mode: "Modus",
      modeDb: "DB-backed (geen herstart)",
      modeMulti: "Multi-secret (sessies overleven)",
      modeSingle: "Single (forceer opnieuw inloggen)",
      blurbDb: "Schrijft de nieuwe key naar de DB. Node-routes pakken hem op binnen 60s — geen herstart nodig. Edge-middleware leest env nog bij cold-start en verifieert tokens met de nieuwe key pas zodra de worker recyclet.",
      blurbMulti: "Stagged een nieuw env-manifest dat de oude key meeneemt. Zowel Node als Edge moeten herstarten, maar bestaande sessies overleven (geverifieerd tegen de oude key) tot ze verlopen.",
      blurbSingle: "Stagged één nieuwe secret. Alle bestaande sessies worden ongeldig bij herstart; elke gebruiker moet opnieuw inloggen.",
      rotateButton: "Roteren…",
      confirmPrefix: "Roteren met modus",
      confirmSingle: "Alle actieve sessies worden ongeldig. Elke gebruiker moet opnieuw inloggen.",
      confirmMulti: "Herstart vereist; bestaande sessies overleven via de meegenomen key.",
      confirmDb: "Geen herstart voor Node; Edge loopt achter tot de worker recyclet.",
      confirmTypePrefix: "Typ",
      confirmTypeSuffix: "om de bevestigknop te activeren:",
      confirmRotate: "Bevestig roteren",
      rotating: "Roteren…",
      stagedMessage: "Rotatie ingesteld. Volg de instructies hieronder om te activeren.",
      activeMessage: "Rotatie actief voor Node-routes. Edge heeft nog een herstart nodig.",
      fingerprint: "Nieuwe key-fingerprint:",
      networkError: "Netwerkfout",
    },
    history: {
      header: "Recente intrekkingen",
      refresh: "Vernieuwen",
      failedToLoad: "Laden mislukt",
      empty: "Geen intrekkingen geregistreerd in het audit-log.",
      headers: {
        when: "Wanneer",
        scope: "Scope",
        target: "Doel",
        by: "Door",
        cutoff: "Cutoff",
      },
    },
  },

  settingsAgents: {
    eyebrow: "Instellingen",
    title: "Agentgedrag",
    subtitle: "Beleid voor de CTO-geleide missie-orkestratie, per organisatie. Standaarden laten het huidige gedrag ongewijzigd — een knop omzetten is opt-in.",
    loading: "Laden…",
    policy: "Missiebeleid",
    overridden: "Aangepast",
    resetToDefault: "Terug naar standaard",
    saved: "Opgeslagen",
  },

  settingsGovernance: {
    loadFailed: "Governancebeleid kon niet worden geladen",
    signInAgain: "Opnieuw inloggen",
    setUpTwoFactor: "Tweefactorauthenticatie instellen",
    loading: "Governancebeleid laden…",
    eyebrow: "Governance",
    title: "Governance & beleid",
    subtitle: "Hoe deze organisatie AI-operaties bestuurt — goedkeuringen, autonomie, risico, verificatie, communicatie, escalatie en bewaartermijnen.",
    domains: {
      approvalTitle: "Goedkeuringsbeleid",
      approvalDesc: "Wanneer menselijke goedkeuring vereist is, wie goedkeurt en wat er gebeurt als niemand op tijd reageert.",
      notificationTitle: "Meldingsbeleid",
      notificationDesc: "Wie wat te horen krijgt, via welk kanaal, wanneer er aandacht nodig is.",
      autonomyTitle: "AI-autonomiebeleid",
      autonomyDesc: "Hoeveel de AI zelfstandig mag doen — van voorzichtig tot voortvarend.",
      riskTitle: "Risicobeleid",
      riskDesc: "Welke soorten wijzigingen gevoelig zijn en welke controles ze in werking stellen.",
      verificationTitle: "Verificatiebeleid",
      verificationDesc: "Wat bewezen moet worden — sandbox, tests, QA-goedkeuring — voordat een wijziging live mag.",
      communicationTitle: "Klantcommunicatiebeleid",
      communicationDesc: "Of en hoe de AI met klanten mag communiceren, en wat de AI nooit mag aanraken.",
      escalationTitle: "Escalatiebeleid",
      escalationDesc: "Wat er gebeurt wanneer werk stilvalt, verloopt of een SLA overschrijdt.",
      auditTitle: "Audit- en bewaarbeleid",
      auditDesc: "Hoe lang traces, goedkeuringen, audit, runtime-logs en klantberichten bewaard worden.",
    },
    autonomyModes: {
      conservativeLabel: "Conservatief",
      conservativeDesc: "Maximaal toezicht. Plannen vereisen altijd goedkeuring, de AI pauzeert bij elke poort, en revisie-/herhaalketens zijn kort.",
      balancedLabel: "Gebalanceerd",
      balancedDesc: "De aanbevolen standaard. Goedkeuring bij riskant of kritiek werk; de AI gaat door bij routinewerk.",
      aggressiveLabel: "Agressief",
      aggressiveDesc: "Maximale doorvoer. Geen planpoort, de AI bouwt door langs poorten, en goedkeuringen accepteren werk zoals het is.",
      customLabel: "Aangepast",
      customDesc: "Stel de onderliggende controles handmatig in. Gebruik Geavanceerd hieronder als een preset niet past.",
    },
    approvalRequirements: {
      never: "Nooit",
      risky_only: "Alleen riskante wijzigingen",
      production_impact: "Productie-impact",
      security_sensitive: "Beveiligingsgevoelig",
      external_integration: "Externe integraties",
      database_change: "Databasewijzigingen",
      always: "Altijd",
    },
    planApprovalPolicies: {
      alwaysLabel: "Altijd plangoedkeuring vereisen",
      alwaysDesc: "Elke missie pauzeert na analyse tot het uitvoeringsplan is goedgekeurd.",
      risky_or_criticalLabel: "Alleen bij riskant of kritiek werk",
      risky_or_criticalDesc: "Risico-gemarkeerde of kritieke missies pauzeren voor plangoedkeuring; routinewerk gaat door.",
      neverLabel: "Nooit plangoedkeuring vereisen",
      neverDesc: "De AI mag uitvoeren zonder plangoedkeuring.",
    },
    timeoutActions: {
      pause: "Missie pauzeren",
      escalate: "Escaleren",
      auto_reject: "Automatisch afwijzen",
      notify_fallback: "Reserve-goedkeurder informeren",
    },
    verificationStrictness: {
      lenientLabel: "Soepel",
      lenientDesc: "Verifieer de werkmap; auto-reviseer bij een mislukte sandboxrun.",
      standardLabel: "Standaard",
      standardDesc: "Het standaard sandboxgedrag.",
      strictLabel: "Streng",
      strictDesc: "Verifieer een schone kloon vastgezet op de commit van de run; geen auto-revisie.",
    },
    commsModes: {
      never_autosendLabel: "Nooit automatisch verzenden",
      never_autosendDesc: "De AI stelt op; een mens verzendt altijd.",
      draft_onlyLabel: "Alleen concept",
      draft_onlyDesc: "De AI bereidt antwoorden voor ter beoordeling.",
      autosend_high_confidenceLabel: "Verzend bij hoge zekerheid",
      autosend_high_confidenceDesc: "Verzenden zodra de zekerheid de drempel haalt.",
      autosend_low_riskLabel: "Verzend laag-risico",
      autosend_low_riskDesc: "Verzend routinematige, laag-risico antwoorden automatisch.",
    },
    riskLevels: { low: "Laag", medium: "Middel", high: "Hoog", critical: "Kritiek" },
    riskCategories: {
      auth: "Authenticatiewijzigingen",
      billing: "Facturatiewijzigingen",
      database_schema: "Databaseschema-wijzigingen",
      security_files: "Beveiligingsgevoelige bestanden",
      external_api: "Externe API-integraties",
      deployment: "Deployment / release-acties",
      infrastructure: "Infrastructuurwijzigingen",
      terraform: "Terraform apply",
      kubernetes: "Kubernetes-clusterwijzigingen",
      secret_rotation: "Secret-rotatie",
      dns: "DNS-wijzigingen",
      feature_flag: "Feature flag-uitrol",
    },
    notificationTriggers: {
      approval_required: "Goedkeuring vereist",
      patch_failed: "Patch mislukt",
      sandbox_failed: "Sandbox mislukt",
      mission_completed: "Missie voltooid",
      mission_failed: "Missie mislukt",
      retry_exhausted: "Herhalingen uitgeput",
      risky_patch_detected: "Riskante patch gedetecteerd",
      customer_reply_pending: "Klantantwoord in afwachting",
    },
    escalationTriggers: {
      retry_exhausted: "Herhalingen uitgeput",
      approval_timeout: "Goedkeuring verlopen",
      inactivity: "Inactiviteit",
      sla_breach: "SLA-overschrijding",
      stuck_mission: "Vastgelopen missie",
    },
    escalationActions: {
      notify_fallback: "Melden in FixControl",
      reassign: "Issue opnieuw toewijzen en melden",
      auto_reject: "Automatisch afwijzen",
      page: "Melden in FixControl en per e-mail",
    },
    escalationTriggerHints: {
      approval_timeout: "Een goedkeuring wachtte langer dan dit op een beslissing. Herinneringen gaan naar de doelpersoon van de regel als die is ingesteld, anders naar de managers, of naar de beheerders als er geen managers zijn (de beheerders als de regel Admin als doel heeft); automatisch afwijzen wordt vastgelegd als besloten door de time-out.",
      stuck_mission: "Een lopende missie boekte zo lang geen voortgang. Herhaalt hooguit één keer per periode zolang hij vastzit. Staat deze aan, dan vervangt hij de MISSION_STALL_MINUTES-melding.",
      inactivity: "Een open issue waaraan zo lang niemand iets veranderde. Eén keer per stille periode; elke wijziging aan het issue start een nieuwe.",
      sla_breach: "Een open issue dat zo lang na binnenkomst nog niet klaar is. Eén keer per issue.",
      retry_exhausted: "Een missie werd gestopt omdat de retries op waren. Op dat moment zelf.",
    },
    escalationAppliedWhen: "Escalatieregels doen alleen zelf iets als “Goedkeuringstime-outs en escalaties automatisch toepassen” aan staat (tab Goedkeuring), of als de governance-sweep draait.",
    channels: { inapp: "FixControl", email: "E-mail", slack: "Slack", teams: "Teams", webhook: "Webhook" },
    commsChannels: { portal: "Portaal", email: "E-mail", slack: "Slack", api: "API" },
    roles: {
      cto: "CTO", qa: "QA", release_manager: "Release Manager", team_lead: "Teamlead",
      manager: "Manager", admin: "Beheerder", support: "Support",
      platform_engineer: "Platform Engineer", security_officer: "Security Officer",
      compliance_officer: "Compliance Officer", incident_commander: "Incident Commander",
      deployment_approver: "Deployment Approver",
    },
    fields: {
      simpleApprovers: "Wie mag goedkeuren",
      simpleApproversHint: "De rol(len) waarnaar goedkeuringsverzoeken standaard worden gerouteerd, in volgorde. Specifiekere regels hieronder blijven voorgaan voor de situaties die ze afdekken. Met goedkeuring in fasen uit beslist één beheerder of manager; met goedkeuring in fasen aan is elke rol een fase die op volgorde door een andere persoon wordt beslist.",
      simpleApproversAdd: "Rol toevoegen…",
      simpleApproversDefaultNote: "Er is nog geen standaardregel ingesteld. Zonder eigen keuze worden verzoeken als suggestie naar CTO en daarna Admin gerouteerd en beslist elke beheerder of manager; deze ingebouwde keten wordt nooit in fasen gezet.",
      simpleApproversSpecificNote: "Er bestaan ook specifiekere regels; die winnen voor de risiconiveaus of soorten wijzigingen die ze benoemen.",
      planApproval: "Plangoedkeuring vóór uitvoering",
      planApprovalHint: "Of de AI zijn uitvoeringsplan moet laten goedkeuren voordat hij begint met implementeren. Dit dwingt de ORCH_PLAN_APPROVAL_POLICY-poort af.",
      planApprovalOutcomeLabel: "Beleidsuitkomst:",
      planApprovalOutcome: {
        always: "Al het werk pauzeert na analyse tot een workspace-goedkeurder het uitvoeringsplan goedkeurt. Implementatie is tot dan geblokkeerd.",
        risky_or_critical: "Riskant en kritiek werk pauzeert na analyse tot een workspace-goedkeurder het uitvoeringsplan goedkeurt; routinewerk gaat door zonder plangoedkeuring.",
        never: "De AI mag direct na analyse uitvoeren — er is geen plangoedkeuring vereist.",
      },
      approvalWhen: "Wanneer is menselijke goedkeuring vereist?",
      approvalWhenHint: "De basispoort voordat AI-werk live gaat. Routeringsregels hieronder kunnen dit verfijnen.",
      timeout: "Als niemand op tijd reageert",
      timeoutHint: "Wat er gebeurt met een verzoek waarover niemand op tijd beslist. 0 minuten = onbeperkt wachten. Een herinnering gaat naar de managers, of naar de beheerders als er geen managers zijn; pauzeren laat het verzoek wachten en voegt niemand toe. Een actieve escalatieregel “Goedkeuring verlopen” met een eigen wachttijd vervangt dit.",
      timeoutAutomatic: "Wordt elke vijf minuten automatisch gecontroleerd.",
      timeoutManual: "Wordt niet automatisch gecontroleerd: zet “Goedkeuringstime-outs en escalaties automatisch toepassen” aan (organisatieniveau, hieronder), of draai de governance-sweep. Tot dan blijft een te laat verzoek gewoon wachten.",
      timeoutAppliedUnknown: "Wordt alleen toegepast als “Goedkeuringstime-outs en escalaties automatisch toepassen” aan staat, of als iemand de governance-sweep draait.",
      routing: "Goedkeuringsroutering & fasen",
      routingHint: "Koppel goedkeuringen op risico aan een goedkeurketen (bijv. CTO → QA → Release Manager) en kies de kanalen waarlangs verzoeken gaan. De keten wordt alleen afgedwongen met goedkeuring in fasen aan: elke fase wordt dan op volgorde door een andere persoon beslist, dus een route met N fases heeft N mensen nodig die kunnen beslissen. Anders is het alleen routering en beslist één beheerder of manager.",
      routingStagedOn: "Goedkeuring in fasen staat aan: een nieuw verzoek waarvan de route fases heeft, wordt fase voor fase beslist, één persoon per fase.",
      routingStagedOff: "Goedkeuring in fasen staat uit: deze ketens kiezen kanalen en stellen voor wie beslist, maar één beheerder of manager beslist over een verzoek. Zet “Goedkeuring in fasen afdwingen” hieronder aan om de fases af te dwingen.",
      routeWebhookNotDelivered: "Webhook: goedkeuringsverzoeken hebben geen webhook-verzender, dus via dit kanaal wordt niets verstuurd.",
      routeEmailOrgWide: "E-mail: een actieve regel met e-mail zet goedkeurings-e-mail aan voor de hele organisatie, ook voor verzoeken waar geen regel met eigen kanalen op past. Die gaat naar elke actieve beheerder en manager, niet naar de rollen in deze regel.",
      approvalEmailOrgWide: "Goedkeurings-e-mail gaat naar elke actieve beheerder en manager, welke rol deze regel ook noemt, en deze regel zet die aan voor de hele organisatie.",
      stageInheritsTimeout: "geen eigen deadline: neemt de deadline en actie van het bedrijf over",
      routingEmpty: "Geen routeringsregels — de basis hierboven geldt voor alles.",
      ruleName: "Regelnaam",
      risk: "Risico:",
      channelsLabel: "Kanalen:",
      approvalMode: "Modus:",
      minApprovers: "min. goedkeurders",
      resolvedChain: "Opgeloste keten",
      stage: "Fase {n}",
      notificationsHint: "Routeer bij elke trigger een melding naar een rol via een kanaal.",
      // Zie en.ts.
      channelNotDelivered: "Wordt niet bezorgd bij deze trigger — FixControl heeft nog geen verzender voor dit kanaal bij missie-statusmeldingen. De route blijft staan en wordt vastgelegd als overgeslagen; in-app en Slack bezorgen wel.",
      destinationInapp: "Gaat naar: de {role}s in deze werkruimte, in hun meldingenlijst.",
      via: "via",
      autonomyAdvanced: "Geavanceerde controles",
      autonomyPlanNote: "De plangoedkeuringspoort wordt geregeld door Goedkeuringsbeleid hierboven.",
      autonomyOrgDefaultHeader: "Dit stelt je organisatiebrede standaard in.",
      autonomyOrgDefaultBody: "Retry-caps, halt/release/resume-semantiek en de gate-approve-semantiek hier gelden voor de hele organisatie. Ander gedrag nodig voor een specifieke workspace, project of omgeving? Overschrijf het per scope in de Hiërarchie-editor — dat heeft voorrang op deze standaard.",
      autonomyOrgDefaultCta: "Per-scope overrides in de Hiërarchie-editor",
      verifyStrictness: "Verificatiestrengheid",
      requireSandbox: "Vereis een geslaagde sandbox vóór goedkeuring",
      requireSandboxHint: "Een wijziging moet in de sandbox verifiëren voordat ze goedgekeurd kan worden.",
      requireTests: "Vereis dat de testsuite voltooit",
      requireTestsHint: "Blokkeer goedkeuring totdat tests draaien.",
      requireQa: "Vereis QA-goedkeuring",
      requireQaHint: "Staat op het uitvoeringsplan als vereiste controle. Geen gate wacht nog op een QA-beslissing.",
      blockProd: "Blokkeer productie-impact zonder QA",
      blockProdHint: "Wordt bij het beleid vastgelegd. Geen gate dwingt het nog af: productiewijzigingen worden niet vastgehouden voor QA.",
      commsEnforce: "Dit communicatiebeleid afdwingen",
      commsEnforceHint: "Standaard uit. Indien aan, beperken de onderstaande regels AI-autoverzending organisatiebreed (kan verzending alleen beperken, nooit het beleid van een mailbox versoepelen).",
      commsBehaviour: "AI-antwoordgedrag",
      commsThresholds: "Zekerheidsdrempels",
      commsThresholdsHint: "Op/boven auto-verzend → AI mag verzenden. Tussen beide → menselijke goedkeuring. Onder goedkeuring → escaleren.",
      commsAutosend: "Auto-verzend ≥",
      commsApproval: "· Menselijke goedkeuring ≥",
      commsChannelsLabel: "Toegestane kanalen",
      commsChannelsHint: "Waar de AI extern mag communiceren.",
      restrictedTopics: "Beperkte onderwerpen",
      restrictedTopicsHint: "Deze mag de AI nooit automatisch afhandelen. Klik op een preset om toe te voegen, × om te verwijderen.",
      auditNote: "Bewaartermijn in dagen. 0 = onbeperkt bewaren.",
      platformManaged: "Platformbeheerd",
      auditEventsPlatformHint: "Platformbeheerd: auditretentie wordt platformbreed afgedwongen met een ondergrens van 90 dagen. De audit-hashketen is één globale reeks, dus dit venster is geen instelling per organisatie.",
      runtimeLogsHint: "Runtime-logs worden na {n} dagen verwijderd.",
      customerCommsHint: "Berichtinhoud van klantcommunicatie wordt na {n} dagen geredigeerd; metadata en hashes blijven.",
      riskApproval: "goedkeuring",
      riskVerify: "verifiëren",
      riskEscalate: "escaleren",
      escalationAfter: "na",
      stageFallback: "terugval",
      stageFallbackNone: "elke beheerder of manager",
      escalationTo: "naar",
    },
    buttons: {
      addRoute: "Routeringsregel toevoegen",
      addStage: "Fase toevoegen",
      addNotification: "Meldingsregel toevoegen",
      remove: "Verwijderen",
    },
    knobs: {
      autoReviseLabel: "Auto-revisielimiet",
      autoReviseHint: "Max. CTO-feedback auto-revisies per patch. 0 schakelt uit.",
      retryLabel: "Herhalingslimiet",
      retryHint: "Max. door mensen goedgekeurde herhalingen van een mislukte taak vóór afbreken.",
      haltLabel: "Werk pauzeren bij een poort",
      haltHint: "Stop lopend dev-werk terwijl een goedkeuring in afwachting is.",
      rejectLabel: "Afwijzen breekt de missie af",
      rejectHint: "Uit → er opent een herhalingspoort in plaats van afbreken.",
      releaseLabel: "Releasestap toevoegen",
      releaseHint: "Plan een release-/deployment-goedkeuring aan het eind.",
      resumeLabel: "Revisie hervatten bij voltooiing",
      resumeHint: "Laat een 'wijzigingen gevraagd'-patch niet stranden.",
      semanticsLabel: "Een gemarkeerde patch goedkeuren",
      semanticsHint: "revise: agent past het aan. accept: gaat zo live.",
      followupsLabel: "Wanneer opvolgtaken aanmaken",
      followupsHint: "approve: bij elke positieve beslissing (incl. goedkeuren-met-revisie). applied: pas zodra de patch is toegepast/uitgerold.",
    },
    audit: {
      trace: "Agent-traces",
      approvals: "Goedkeuringen",
      auditEvents: "Auditgebeurtenissen",
      runtimeLogs: "Runtime-logs",
      customerComms: "Klantcommunicatie",
      days: "dagen",
      minutes: "min",
      percent: "%",
    },
    tabs: {
      overview: "Overzicht",
      simulator: "Simulator",
      approval: "Goedkeuringen",
      notification: "Meldingen",
      autonomy: "AI-autonomie",
      risk: "Risico",
      verification: "Verificatie",
      communication: "Klantcommunicatie",
      escalation: "Escalaties",
      audit: "Audit & bewaring",
      safety: "Veiligheidscontroles",
    },
    console: {
      scopeLabel: "Beleidsbereik",
      scope_organization: "Organisatiebreed",
      scope_workspace: "Werkruimtebereik",
      scope_mailbox: "Omgevingsbereik",
      scopeHint: "Dit beleid geldt op het gekozen bereik en plant zich voort naar zijn descendants.",
      scopeFuture: "Kies een bereik links om het te bewerken. Locks pinnen een veld vast op het bereik waar ze zijn gezet — geen enkele descendant kan ze overschrijven.",
      posture: "Governance-houding",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      saveError: "Opslaan mislukt — opnieuw",
      savedIdle: "Alle wijzigingen opgeslagen",
    },
    postures: {
      conservativeLabel: "Conservatief",
      conservativeDesc: "Maximaal toezicht. Werk met productie-impact vereist goedkeuring, verificatie is streng, en de AI verzendt nooit zelf naar klanten.",
      balancedLabel: "Gebalanceerd",
      balancedDesc: "De aanbevolen standaard. Riskant werk wordt afgeschermd en geverifieerd; routinewerk gaat door, en klantantwoorden worden als concept voorbereid.",
      aggressiveLabel: "Agressief",
      aggressiveDesc: "Maximale doorvoer. Minimale afscherming, soepele verificatie, en laag-risico klantantwoorden mogen automatisch verzenden. De sandbox draait nog steeds vóór livegang.",
      customLabel: "Aangepast",
      customDesc: "Je beleid is een aangepaste mix die niet bij een preset past. Kies een houding om de basis te resetten, of stem elk domein hieronder af.",
      applyTitle: "De {name}-houding toepassen?",
      applyBody: "Dit werkt de organisatiebrede standaarden bij voor autonomie, goedkeuringen, verificatie, communicatie en bewaring. Je routerings-, risico- en escalatieregels blijven onaangeroerd. Het wordt direct toegepast.",
      applyConfirm: "Houding toepassen",
    },
    overview: {
      intro: "Hoe de AI zich momenteel in deze organisatie gedraagt. Klik op een kaart om dat beleid aan te passen.",
      postureTitle: "Effectieve houding",
      postureHint: "hoe de AI zich nu in elk beleidsdomein gedraagt — klik op een kaart om het aan te passen",
      approvalTimeout: "Verloopt na {n} min",
      approvalTimeoutManual: "Time-out van {n} min ingesteld, niet automatisch toegepast",
      approvalTimeoutUnknown: "Time-out van {n} min, toegepast als automatische time-outs aan staan",
      approvalNoTimeout: "Wacht onbeperkt",
      vSandbox: "Sandbox",
      vTests: "Tests",
      vQa: "QA-goedkeuring",
      verificationNone: "Alleen werkmapcontrole",
      commsEnforced: "Organisatiebreed afgedwongen",
      commsAdvisory: "Adviserend — niet afgedwongen",
      riskValue: "{n} van {total} categorieën afgeschermd",
      riskHint: "Gevoelige wijzigingsklassen met een controle.",
      escalationValue: "{trigger} na {n} min",
      escalationNone: "Geen escalaties actief",
      escalationNoneHint: "Er wordt nog niets automatisch geëscaleerd.",
      notificationsValue: "{n} actieve regels",
      notificationsHint: "Trigger → kanaal → ontvanger-routes.",
      retentionForever: "Onbeperkt bewaard",
      retentionPlatformManaged: "Auditretentie: platformbeheerd",
      retentionHint: "Traces {trace}d · klantberichten {comms}d",
      effectiveTitle: "Effectief beleid",
      effectiveHint: "wat er gebeurt als de AI elke actie probeert, onder het huidige beleid",
    },
    actionDomains: {
      code: "Codewijziging",
      deployment: "Deployment",
      communication: "Klantcommunicatie",
      data: "Data / schema",
      infrastructure: "Infrastructuur",
      security: "Beveiligingsgevoelig",
      integration: "Externe integratie",
      cicd: "CI/CD-pipeline",
      kubernetes: "Kubernetes",
      terraform: "Terraform",
      secret: "Secret-beheer",
      db_migration: "Databasemigratie",
      freeze_window: "Freeze-venster",
    },
    environments: {
      development: "Ontwikkeling",
      staging: "Staging",
      production: "Productie",
    },
    scenarios: {
      routineCodefixLabel: "Routine-codefix",
      routineCodefixDesc: "Een kleine, geteste wijziging in ontwikkeling.",
      largeRefactorLabel: "Grote refactor",
      largeRefactorDesc: "Een brede wijziging zonder tests.",
      schemaChangeLabel: "Databaseschemawijziging",
      schemaChangeDesc: "Een onomkeerbare schemawijziging gericht op productie.",
      productionDeployLabel: "Productie-deployment",
      productionDeployDesc: "Een build naar productie brengen.",
      authChangeLabel: "Authenticatiewijziging",
      authChangeDesc: "Raakt auth en beveiligingsgevoelige bestanden in productie.",
      refundEmailLabel: "Gevoelig klantantwoord",
      refundEmailDesc: "Een uitgaand klantantwoord dat facturering raakt, met lage AI-zekerheid.",
      routineReplyLabel: "Routine-klantantwoord",
      routineReplyDesc: "Een uitgaand antwoord met hoge zekerheid.",
      infraScaleLabel: "Infrastructuur opschalen",
      infraScaleDesc: "Een infrastructuurwijziging in productie.",
      externalIntegrationLabel: "Externe integratie-aanroep",
      externalIntegrationDesc: "Een actie via een externe API.",
    },
    simulator: {
      intro: "Voer elke actie droog uit tegen het live beleid. Er wordt niets opgeslagen — kies een scenario of bouw er een, en zie precies wat governance zou doen.",
      scenariosLabel: "Scenario's",
      actionLabel: "Actie",
      reset: "Reset",
      domain: "Actiedomein",
      environment: "Doelomgeving",
      signalsLabel: "Signalen",
      blastRadius: "Bestanden / resources",
      confidence: "AI-zekerheid",
      restrictedTopic: "Beperkt onderwerp",
      noRestrictedTopic: "Geen",
      preview: "preview",
      signals: {
        touchesAuth: "Raakt auth",
        touchesBilling: "Raakt facturatie",
        touchesDatabaseSchema: "DB-schemawijziging",
        touchesSecurityFiles: "Beveiligingsbestanden",
        touchesExternalApi: "Externe API",
        touchesInfrastructure: "Infrastructuur",
        isDeployment: "Is een deployment",
        isRollback: "Is een rollback",
        isCustomerComms: "Klantcommunicatie",
        touchesTerraform: "Terraform apply",
        touchesKubernetes: "Kubernetes-wijziging",
        touchesSecrets: "Secret-rotatie",
        touchesDns: "DNS-wijziging",
        touchesFeatureFlag: "Feature flag",
        hasTests: "Heeft tests",
        sandboxPassed: "Sandbox geslaagd",
        externalRecipient: "Externe ontvanger",
        irreversible: "Onomkeerbaar",
      },
    },
    decision: {
      outcome: {
        auto_execute: "Voert automatisch uit",
        draft_only: "Concept voor handmatig verzenden",
        approval_required: "Goedkeuring vereist",
        blocked: "Geblokkeerd door beleid",
      },
      riskScore: "Risicoscore",
      categories: "In spel:",
      whyTitle: "Waarom — beslissingsspoor",
      noTriggers: "Geen beleid verhoogde de drempel — deze actie loopt vrij.",
      conflictTitle: "Conflicten opgelost",
      conflictOver: "overschreef",
      approvalTitle: "Goedkeuringsketen",
      noApproval: "Geen goedkeuring vereist",
      verificationTitle: "Verificatie",
      escalationTitle: "Escalatie",
      noEscalation: "Geen escalatie geactiveerd",
      auditTitle: "Gegenereerde audit-events",
      autoExecYes: "Automatische uitvoering toegestaan",
      autoExecNo: "Automatische uitvoering vastgehouden",
      fallback: "fallback",
      mode_sequential: "Sequentieel",
      mode_parallel: "Parallel",
      minApprovers: "{n} goedkeurders (afgedwongen met goedkeuring in fasen)",
      sla: "{n} min SLA",
      via: "via",
    },
    scopes: {
      selectLabel: "Beleidsbereik",
      selectHint: "Kies een node in de scope-boom. De vier-kind hiërarchie (organisatie → werkruimte → project → omgeving) betekent dat diepere scopes van hun parent erven en per veld kunnen overschrijven.",
      axis_organization: "Organisatie",
      axis_team: "Team",
      axis_workspace: "Werkruimte",
      axis_repository: "Repository",
      axis_environment: "Omgeving",
      axis_integration: "Integratie",
      axis_customer: "Klant",
      lockedHint: "First-lock-wins: een veld dat op één scope gelockt is, ligt vast voor elke descendant. Bewerk op de scope die de lock zette om hem op te heffen.",
      inheritsFrom: "Erft van parent",
      overriddenHere: "Hier overschreven:",
      resetAll: "Alles terug naar parent",
      noOverrides: "Geen overrides — erft alles van de parent-scope.",
      rulesOrgOnly: "Routerings-, risico- en escalatieregels zijn op organisatieniveau — wijzigingen hier gelden organisatiebreed.",
      pickerLabel: "Toepassingsniveau:",
      overriddenBadge: "{n} lokaal aangepast",
      lockedBadge: "{n} centraal vastgezet",
      advancedConsoleHint: "Structuur, agentgedrag-koppelingen, vastgezette velden of de runtime-simulator beheren?",
      advancedConsoleOpen: "Open geavanceerde console",
      editingBanner: "Je bewerkt nu het niveau {name}. Aanpassingen hier gelden alleen voor dit niveau; alles zonder aanpassing erft van het niveau erboven.",
      resetFieldTitle: "Zet {field} terug naar de geërfde waarde",
      lockFieldTitle: "Zet {field} op dit niveau vast zodat onderliggende niveaus het niet kunnen aanpassen",
      lockedByAncestor: "Centraal vastgezet op een hoger niveau",
      lockPrompt: "Zet \"{field}\" op dit niveau vast. Reden:",
    },
    templates: {
      title: "Beleidssjablonen",
      hint: "Pas in één keer een samenhangende preset toe over alle beleidsdomeinen. Omkeerbaar — pas altijd een andere toe.",
      autonomy: "Autonomie",
      friction: "Frictie",
      control: "Controle",
      apply: "Toepassen",
      active: "Actief",
      applyTitle: "Het {name}-sjabloon toepassen?",
      applyBody: "Dit stelt organisatiebrede standaarden in voor autonomie, goedkeuringen, verificatie, communicatie en bewaring volgens de gekozen preset. Je routerings-, risico- en escalatieregels blijven onaangeroerd. Het wordt direct toegepast.",
      applyConfirm: "Sjabloon toepassen",
      startupLabel: "Startup",
      startupDesc: "Snel bewegen met minimale afscherming — ideaal voor kleine teams die snel leveren.",
      startupTradeoffs: "Snel leveren · Weinig toezicht · Korte bewaring",
      balancedLabel: "Gebalanceerd",
      balancedDesc: "Riskant werk wordt afgeschermd en geverifieerd; routinewerk gaat door. De aanbevolen standaard.",
      balancedTradeoffs: "Solide standaard · Tests vereist · Gemiddelde frictie",
      enterpriseLabel: "Enterprise",
      enterpriseDesc: "Sterke goedkeuringen, strenge verificatie en langere bewaring voor grotere organisaties.",
      enterpriseTradeoffs: "Productie afgeschermd · QA vereist · Lange bewaring",
      soc2Label: "SOC 2",
      soc2Desc: "Goedkeuringen, verificatie en auditbewaring afgestemd op SOC 2-controles.",
      soc2Tradeoffs: "Audit-klaar · QA + tests · 1 jaar bewaring",
      iso27001Label: "ISO 27001",
      iso27001Desc: "Security-first goedkeuringen en strenge verificatie voor ISO 27001.",
      iso27001Tradeoffs: "Security-first · Strenge verificatie · 1 jaar bewaring",
      healthcareLabel: "Zorg (HIPAA)",
      healthcareDesc: "Maximale terughoudendheid bij klantcommunicatie en 6 jaar bewaring voor HIPAA.",
      healthcareTradeoffs: "Geen auto-verzending · 6 jaar bewaring · Altijd goedkeuren",
      financialLabel: "Financieel (SOX/PCI)",
      financialDesc: "Strakke goedkeuringen en 7 jaar bewaring afgestemd op financiële controles.",
      financialTradeoffs: "7 jaar bewaring · Productie afgeschermd · Geen auto-verzending",
      highSecurityLabel: "Hoge beveiliging",
      highSecurityDesc: "Maximaal toezicht — alles goedkeuren, strenge verificatie, geen auto-verzending.",
      highSecurityTradeoffs: "Alles goedkeuren · Strenge verificatie · Minimale autonomie",
    },
    deploymentCapabilities: {
      title: "Deployment-bevoegdheden",
      description: "Wat een deploymentgate op jouw systemen mag doen. Elke bevoegdheid staat uit tot je hem aanzet, en elke wijziging wordt met jouw reden geaudit.",
      writeBadge: "Schrijft naar buiten",
      recordOnlyBadge: "Legt alleen vast",
      phase: {
        disarmedLabel: "Niets aangezet.",
        disarmedBody: "Deploy-events komen binnen en worden beoordeeld, maar er opent geen gate voor een deployment die FixControl niet zelf heeft geleverd, en geen enkel oordeel kan jouw systemen bereiken.",
        observeOnlyLabel: "Alleen meekijken.",
        observeOnlyBody: "Gates openen en elk besluit wordt vastgelegd, maar geen oordeel verlaat FixControl — elke terugkoppeling wordt geweigerd als “disabled” en zegt dat ook op de gate. Dit is de fase die je draait vóór je FixControl laat schrijven.",
        armedLabel: "Aangezet om te schrijven ({n}).",
        armedBody: "Een goedgekeurde gate kan nu via onderstaande bevoegdheden op jouw systemen handelen. Wat hij niet kan: een deployment starten of terugdraaien.",
      },
      keys: {
        standaloneGates: {
          label: "Gates openen voor je eigen deployments",
          hint: "Open een goedkeuringsgate voor een deploy waar geen door FixControl geschreven wijziging achter zit — het normale geval wanneer je eigen pipelines je eigen code bouwen en uitrollen. Staat dit uit, dan wordt zo'n deploy overgeslagen als “no mission link” en opent er nooit een gate. Een gate openen legt een besluit vast; het schrijft niets.",
        },
        cicdCarryBack: {
          label: "Het oordeel terugkoppelen naar de CI/CD-host",
          hint: "Laat een goedkeuring of afwijzing de pipeline bereiken die de pauze vasthoudt — GitHub Actions, GitLab CI of Jenkins. Goedkeuren geeft de wachtende deployment vrij; afwijzen laat de run daar zichtbaar falen in plaats van hem te laten verlopen. Vereist een ingeschakelde verbinding en een credential die de host als reviewer accepteert.",
        },
        argoResume: {
          label: "Een gepauzeerde Argo Rollout promoveren",
          hint: "Laat een goedkeuring op een gate die uit een gepauzeerde Argo Rollout is geopend de volgende stap promoveren. Argo kent geen afwijzingsprimitief, dus een afwijzing schrijft niets en zegt dat ook — de pauze zelf blijft de handhaving. Vereist een Argo Rollouts-capability op de Kubernetes-verbinding.",
        },
        agentChannel: {
          label: "Het oordeel overdragen aan een fc-agent in je cluster",
          hint: "In plaats van dat FixControl je netwerk inbelt, publiceert een goedgekeurde gate een verlopende, eenmalige autorisatie die je eigen agent uitgaand ophaalt en lokaal alsnog mag weigeren. FixControl belt onder dit profiel nooit je cluster. Vereist een geregistreerde, ingeschakelde agent op de Kubernetes-verbinding.",
        },
      },
      confirm: {
        title: "“{name}” aanzetten?",
        bodyWrite: "Vanaf nu mag een goedgekeurde gate via deze bevoegdheid op je eigen systemen handelen. Het werkt direct, het wordt vastgelegd in het auditspoor, en je zet het hier met één klik weer uit.",
        bodyRecordOnly: "Er gaan gates openen voor deploys die FixControl niet zelf heeft geleverd. Dat legt een menselijk besluit vast — het schrijft uit zichzelf niets naar je systemen.",
        reasonPlaceholder: "Reden (vastgelegd in het auditspoor)",
        cta: "Bevoegdheid aanzetten",
      },
    },
    safety: {
      intro: "Organisatiebrede noodknoppen, elk geaudit. Ze werken nu in de previews op het overzicht, de simulator en beleidsevaluatie. Deployments, klantberichten, integraties en agent-runs controleren ze nog niet; alleen met “Risicoregels mogen een plangoedkeuring vereisen” aan houden Alle agents stoppen en Alleen-lezen modus een nieuw plan vast voor goedkeuring (of blokkeren het).",
      statusNormal: "Alle systemen normaal",
      statusEngaged: "{n} veiligheidscontrole(s) actief",
      activeBanner: "{n} noodveiligheidscontrole(s) zijn actief.",
      review: "Bekijken",
      off: "Uit",
      active: "Actief",
      expired: "Verlopen",
      autoLifts: "Heft automatisch op",
      engage: "Activeren",
      engaged: "Actief",
      reasonPlaceholder: "Reden (vastgelegd in het auditspoor)",
      autoLift: "Auto-opheffen",
      untilCleared: "Tot handmatig opgeheven",
      setBy: "Ingesteld door",
      controls: {
        stop_all_agents: { label: "Alle agents stoppen", desc: "Markeert elke AI-actie als geblokkeerd in beleidspreviews. Lopende agents worden niet gestopt." },
        read_only: { label: "Alleen-lezen modus", desc: "Markeert elke wijziging als geblokkeerd in beleidspreviews; analyseren blijft toegestaan. Agents controleren dit nog niet." },
        disable_deployments: { label: "Deployments uitschakelen", desc: "Markeert deployments als geblokkeerd in beleidspreviews. De deployment-gate controleert dit nog niet." },
        disable_comms: { label: "Klantcommunicatie uitschakelen", desc: "Markeert uitgaande klantberichten als geblokkeerd in beleidspreviews. Versturen controleert dit nog niet." },
        disable_integrations: { label: "Externe integraties uitschakelen", desc: "Markeert acties via externe integraties als geblokkeerd in beleidspreviews. Integraties controleren dit nog niet." },
        maintenance_mode: { label: "Onderhoudsmodus", desc: "Markeert deployments, data- en infrastructuurwijzigingen als geblokkeerd in beleidspreviews. Wordt nog niet gecontroleerd tijdens uitvoering." },
      },
    },
    auditExplorer: {
      title: "Auditspoor",
      chain: {
        verified: "Keten geverifieerd",
        verifiedHint: "Hash-geketend, append-only auditlog. Laatst geverifieerd {when}, {walked} regels nagelopen.",
        broken: "Ketenbreuk",
        brokenHint: "De verificatie stopte bij reeksnummer {seq}. De regels daarvóór kloppen nog; neem contact op met support met dat reeksnummer.",
        unverified: "Nog niet geverifieerd",
        unverifiedHint: "Hash-geketend, append-only auditlog. De verificatie loopt elk uur; voor deze keten is nog geen uitkomst vastgelegd.",
      },
      refresh: "Vernieuwen",
      export: "Exporteren",
      searchPlaceholder: "Zoek event, gebruiker, doel…",
      denied: "Je hebt geen rechten om het auditspoor te bekijken.",
      loading: "Audit-events laden…",
      empty: "Geen audit-events komen overeen met deze filters.",
      pageOf: "Pagina {page} van {total} · {count} events",
      prev: "Vorige",
      next: "Volgende",
      categories: {
        all: "Alle",
        policy: "Beleidswijzigingen",
        safety: "Veiligheidscontroles",
        approvals: "Goedkeuringen",
        comms: "Klantcommunicatie",
        deployments: "Deployments",
        auth: "Auth",
      },
    },
  },

  commandPalette: {
    title: "Snel zoeken",
    resultsLabel: "Zoekresultaten",
    placeholder: "Zoek of spring naar een pagina, wachtrij of issue…",
    nav: {
      inbox: "Naar Inbox", issues: "Naar Issues", patches: "Naar Patches",
      workspaces: "Naar Werksessies", missions: "Naar Missies", "follow-ups": "Naar Follow-ups",
      knowledge: "Naar Kennis", posture: "Naar Codebase-gezondheid", agents: "Naar Agents",
      runs: "Naar Agent-runs", operations: "Naar Operaties", governance: "Naar Governance",
      projects: "Naar Projecten", memory: "Naar AI-geheugen", notifications: "Naar Notificaties",
      devops: "Naar DevOps-verbindingen", audit: "Naar Audit-trail",
      dashboard: "Naar Dashboard", settings: "Naar Instellingen",
    },
    navDest: {
      inbox: "Inbox", issues: "Issues", patches: "Patches", workspaces: "Werksessies",
      missions: "Missies", "follow-ups": "Follow-ups", knowledge: "Kennis", posture: "Codebase-gezondheid",
      agents: "Agents", runs: "Agent-runs", operations: "Operaties", governance: "Governance",
      projects: "Projecten", memory: "AI-geheugen", notifications: "Notificaties",
      devops: "DevOps", audit: "Audit", dashboard: "Dashboard", settings: "Instellingen",
    },
  },
  /**
   * Het paneel met alle pogingen op deze melding. Eigen namespace omdat het
   * paneel gedeelde chrome is, geen onderdeel van één pagina.
   */
  missionListPanel: {
    title: "Alles wat FixControl op deze melding heeft geprobeerd",
    subtitle: "Elke poging, waarvoor die was en hoe ver die kwam. Niets verliet de sandbox zonder goedkeuring.",
    count: "{n} poging(en)",
    empty: "FixControl heeft nog niet aan deze melding gewerkt. Start het met de actie hierboven.",
    staleAttempt: "Gebaseerd op een eerdere versie van dit issue",
  },

  missions: {
    status: {
      queued: "in wachtrij",
      planning: "plannen",
      awaiting_plan_approval: "wacht op plangoedkeuring",
      executing: "uitvoeren",
      awaiting_gate: "wacht op poort",
      verifying: "verifiëren",
      succeeded: "geslaagd",
      failed: "mislukt",
      rolled_back: "teruggerold",
      cancelled: "geannuleerd",
      abandoned: "verlaten",
    },
    intent: {
      investigate: "onderzoeken",
      patch_proposal: "patch-voorstel",
      apply_patch: "patch toepassen",
      deploy: "uitrollen",
      rollback: "terugrollen",
      respond: "reageren",
      verify: "verifiëren",
      freeform: "vrije vorm",
    },
  },
  missionsPage: {
    eyebrow: "AI-operaties",
    title: "AI-missies",
    intro: "Elke beheerde AI-operationele poging — onderzoek, patch-voorstel, uitrol, terugrol. Open er een om het plan, de poorten, agents en telemetrie te zien.",
    statOpenGate: "Open poort",
    statExecuting: "Uitvoeren",
    statSucceeded: "Geslaagd",
    emptyTitle: "Nog geen missies",
    emptyBodyPrefix: "Missies zijn beheerde AI-operationele pogingen gestart vanuit een issue of vanuit de operator-chat. Open een issue en gebruik",
    emptyBodyAction: "Fix uitvoeren",
    emptyBodySuffix: "om er een te starten.",
    emptyAsk: "Wat kan FixControl met een melding doen?",
    deleteTitle: "Missie archiveren",
    deleteConfirm: "Deze missie archiveren? Ze verlaat de missielijst. De missie, haar poorten, goedkeuringen en geschiedenis blijven bewaard — je vindt haar terug onder \"Gearchiveerd\" en kunt haar terugzetten.",
    restoreTitle: "Missie terugzetten",
    archivedBadge: "Gearchiveerd",
    archivedBanner: "Deze missie is gearchiveerd — hij staat niet in de missielijst. Er is niets weggegooid.",
    emptyArchivedTitle: "Geen gearchiveerde missies",
    emptyArchivedBody: "Missies die je uit de lijst verwijdert komen hier terecht. Er wordt niets weggegooid — zet er een terug om hem weer in de lijst te krijgen.",
    searchPlaceholder: "Zoek op titel, instructie, issue, id…",
    viewAll: "Alle",
    viewActive: "Actief",
    viewDone: "Afgerond",
    viewArchived: "Gearchiveerd",
    exportCsv: "CSV exporteren",
    headers: {
      mission: "Missie",
      issue: "Issue",
      status: "Status",
      priority: "Prioriteit",
      completed: "Afgerond",
    },
    bulk: {
      delete: "Archiveren",
      deleted: "{count} gearchiveerd",
      deleteFailed: "{ok} gearchiveerd, {failed} mislukt",
      deleteConfirmTitle: "Missies archiveren",
      deleteConfirmLabel: "Archiveren",
      deleteConfirmBody: "{count} missie(s) archiveren? Ze verlaten de missielijst. Missies, hun poorten, goedkeuringen en geschiedenis blijven bewaard — je vindt ze terug onder \"Gearchiveerd\" en kunt ze terugzetten.",
      restore: "Terugzetten",
      restored: "{count} teruggezet in de lijst",
      restoreFailed: "{ok} teruggezet, {failed} mislukt",
    },
    pane: {
      title: "Missie-detail",
      enableSplit: "Gesplitste weergave aan",
      disableSplit: "Gesplitste weergave uit",
      resizeHandle: "Detailpaneel vergroten/verkleinen",
      open: "Openen",
      openFullPage: "Missie-cockpit openen",
      close: "Sluiten",
      emptyTitle: "Selecteer een missie",
      emptyBody: "Klik op een rij om die hier te bekijken zonder de lijst te verlaten.",
      issue: "Issue",
      priority: "Prioriteit",
      plan: "Plan",
      planVersions: "goedgekeurd v{approved} · laatste v{latest}",
      planNone: "Nog geen plan",
      started: "Gestart",
      completed: "Afgerond",
      instruction: "Instructie",
    },
    commandBarPolicy: "beleid v{version}",
    commandBarPolicyNotFrozen: "beleid nog niet vastgelegd",
    operatorChannel: "Operator-kanaal",
    chatEmptyTitle: "Nog geen operator-kanaal",
    chatEmptyBody: "Het chat-oppervlak wordt aangemaakt bij de eerste agent-dialoog of wanneer een operator een workspace start vanuit de issue.",
    chatBackToCockpit: "Terug naar missie-cockpit",
    chatOpenIssue: "Issue {key} openen",
  },
  settingsAi: {
    supportOnly: "Alleen FixControl-support",
    supportOnlyBody: "AI-providerconfiguratie wordt beheerd door het FixControl-team. Neem contact op met support als je een model of taak anders gerouteerd wilt voor je workspace.",
    loginTriggered: "Login gestart",
    loading: "Laden…",
    eyebrow: "Instellingen",
    title: "AI-providers",
    subtitle: "Kies welk model elke agent draait. Mix cloud + lokaal, gebruik je eigen sleutels, of gratis modellen.",
    save: "Opslaan", saving: "Opslaan…", saved: "Opgeslagen",
    routingTitle: "Agent-routing",
    routingSub: "Kies een provider + model per taak. Wijzigingen worden opgeslagen als je op Opslaan klikt.",
    overridesTitle: "Per-agent overrides",
    overridesSub: "Optioneel: geef een specifieke agent zijn eigen model, dat de taak-routing hierboven overschrijft. Laat op “Erven” staan om de taak te volgen. cto / security / release / ux zijn rule-based poorten en doen geen LLM-aanroep.",
    workspaceTitle: "Workspace-agent",
    workspaceSub: "Bepaalt welke Claude Code CLI-tools de in-app workspace-chat mag aanroepen. Bestandsmutatie-tools (Edit/Write/MultiEdit/NotebookEdit) zijn altijd geblokkeerd zodat wijzigingen via het patch-goedkeurpaneel lopen.",
    cacheTitle: "Contextefficiëntie + caching",
    cacheSub: "Bepaalt hoe agents stabiele context hergebruiken tussen runs. De cachebare prompt-prefix bevat nooit per-ticket-data of secrets — alleen statische prompts en operator-samengestelde contextpacks.",
    provider: "Provider", model: "Model",
    freeTierSuffix: " · gratis tier", freeSuffix: " · gratis", noToolUseParen: " (geen tool-use)",
    pillCliNotSignedIn: "CLI niet ingelogd", pillNeedsKey: "sleutel nodig", pillCliAuth: "CLI-auth",
    pillNoToolUse: "geen tool-use", pillOverride: "override", pillInherits: "erft · {task}",
    pillAnthropicOnly: "Alleen Anthropic", pillConfigured: "Geconfigureerd", pillNotConfigured: "Niet geconfigureerd",
    pillDesktop: "Desktop", pillCloud: "Cloud",
    inheritOption: "Erven ({provider} · {model})",
    trusted: "Vertrouwd",
    trustedDesc: "CLI mag shell-commando's en WebFetch draaien (het draait al met de eigen rechten van de gebruiker). Permissieprompts worden auto-omzeild zodat de chat niet vastloopt. Aanbevolen voor desktop / single-user-installaties.",
    restricted: "Beperkt",
    restrictedDesc: "Blokkeert ook Bash / PowerShell / KillShell / BashOutput / WebFetch en verwijdert de permissie-bypass. Aanbevolen voor gehoste / multi-tenant-deployments waar de agent tegen tenant-data op gedeelde infrastructuur draait.",
    getCredentials: "Credentials ophalen",
    browserLogin: "Browser-login (CLI)",
    useCliInstead: "Gebruik CLI in plaats van API-sleutel",
    openingBrowser: "Browser openen…",
    signInWith: "Inloggen met {name}",
    checkStatus: "Status controleren",
    cliNotInPath: "{cli} CLI niet in PATH. Installeer:",
    apiKey: "API-sleutel", baseUrl: "Basis-URL", optional: "(optioneel)",
    modelsKnown: "{n} modellen bekend", modelKnown: "{n} model bekend",
    test: "Test",
    replyIn: "Antwoord in {ms}ms:",
    providers: "Providers",
    cacheRowEnabled: "Context-caching",
    cacheRowEnabledDesc: "Laad tenant-contextpacks in de cachebare prefix en handhaaf per-fase token-budgetten. Contextmanifesten worden hoe dan ook vastgelegd.",
    cacheRowPrompt: "Prompt-caching voor Claude",
    cacheRowPromptDesc: "Markeer de stabiele prefix met Anthropic cache-control-breekpunten. Niet-Claude-providers ontvangen identieke blokken en negeren de vlag.",
    cacheRowCheap: "Goedkoop model voor samenvattingen verkiezen",
    cacheRowCheapDesc: "Gebruik het goedkope classify-taakmodel voor context- / thread-samenvattingen, met een deterministische afkap-fallback.",
    workspaceEnvHint: "Overschrijf op OS-niveau met WORKSPACE_AGENT_RESTRICTED=1 (forceer beperkt) of =0 (forceer vertrouwd) — de env-var wint van deze instelling.",
    cacheBudgetHint: "Per-fase token-budgetten komen standaard uit token-budget.ts en kunnen worden overschreven met CONTEXT_BUDGET_<PHASE> env-vars. Tokengebruik per run is zichtbaar op het context-cache-dashboard.",
    cacheBudgetLink: "context-cache-dashboard",
    capacityHint: "Weigeringen van de provider (facturering, authenticatie, context, rate limits), contextdruk en outputbudgetten over alle klanten staan in de",
    capacityLink: "AI-capaciteitscockpit",
    infoNoKey: "Geen API-sleutel? Anthropic en Codex ondersteunen CLI-auth — installeer de officiële CLI, klik “Inloggen met Claude/Codex” om een browser-login te openen, en schakel dan “Gebruik CLI in plaats van API-sleutel” in.",
    infoImplAgent: "Implementation Agent vereist tool-use (bestanden lezen, patch-voorstellen). Gebruik elke provider met tool-use voor Ticket Analyst, Support Agent en Financial Control.",
    infoKeys: "Sleutels worden opgeslagen in de app_settings-tabel en geredigeerd bij weergave. Env-vars worden als fallback gebruikt wanneer hier geen sleutel is geconfigureerd.",
    cliChecking: "controleren…", cliNotInstalled: "niet geïnstalleerd",
    cliInstalledNotSignedIn: "geïnstalleerd · niet ingelogd", cliSignedIn: "ingelogd",
    tasks: {
      classify: { label: "Ticket-analist", desc: "Classificeer inkomende e-mails tot gestructureerde issues." },
      reply: { label: "Support-agent", desc: "Stel klantgerichte antwoorden op." },
      coordinate: { label: "Operations-coördinator", desc: "Extraheer trackbare follow-up-acties per issue." },
      finance: { label: "Financiële controle", desc: "Controleer acties t.o.v. facturen. Conservatief." },
      impl: { label: "Implementatie-agent", desc: "Lees de repo, stel patches voor, open PR's. Vereist tool-use." },
    },
    roles: {
      qa: { label: "QA / Review", desc: "Verifieert patches; bepaalt het CTO-reviewoordeel." },
      workflow: { label: "Workflow", desc: "Beslist issue-statusovergangen + follow-ups." },
      intake: { label: "Intake / Triage", desc: "Classificeert inkomende berichten tot issues." },
      support: { label: "Support-antwoord", desc: "Stelt klantgerichte antwoorden op." },
      dev: { label: "Implementatie", desc: "Genereert code-patches (alleen Anthropic tool-use)." },
      chat: { label: "Workspace-chat", desc: "Interactieve codebase-chat (alleen Anthropic)." },
      command_center: { label: "Command Center", desc: "Vertaalt wat je vraagt naar de gecontroleerde stappen die FixControl kan uitvoeren (alleen Anthropic)." },
    },
  },
  adminPosture: {
    loading: "Laden…",
    adminOnly: "Alleen platform-admin.",
    loadFailed: "Posture-overzicht laden mislukt.",
    networkError: "Netwerkfout.",
    bulkFailed: "Bulk-herbeoordeling mislukt",
    eyebrow: "Platform-admin",
    title: "Operationele posture (cross-tenant)",
    subtitle: "Laatste Operational Posture Assessment per (tenant, codebase). High-risk + onvoldoende-bewijs-rijen zijn de operator-aandachtslijst.",
    loadingRollup: "Posture-rollup laden…",
    statCodebases: "Codebases beoordeeld",
    statTenants: "Tenants",
    statRefusing: "Weigert auto-exec",
    statHighRisk: "Hoog risico",
    statInsufficient: "Onvoldoende bewijs",
    filter: "Filter",
    allPostures: "Alle postures",
    rowCount: "{n} rij(en)",
    selectedCount: "{n} geselecteerd (max 10)",
    selectHighRisk: "Selecteer hoog-risico",
    clear: "Wissen",
    reassessing: "Herbeoordelen…",
    reassessSelected: "Selectie herbeoordelen",
    colTenant: "Tenant",
    colCodebase: "Codebase",
    colPosture: "Posture",
    colConfidence: "Vertrouwen",
    colRiskPos: "Risico / Pos",
    colVersion: "v",
    colLatest: "Laatste",
    colAutoExec: "Auto-exec",
    selectAria: "Selecteer {id} voor bulk-herbeoordeling",
    approvalRequired: "Goedkeuring vereist",
    allowed: "Toegestaan",
    noRows: "Geen rijen voor dit filter.",
    bulkResult: "Bulk-herbeoordeling resultaat",
    bulkSucceeded: "{succeeded}/{requested} geslaagd",
    bulkFailedSuffix: " · {failed} mislukt",
    posturePrefix: "posture",
    footer: "Deze weergave omzeilt tenant-RLS via de platform-admin-sessie. Posture die autonome uitvoering weigert is de enterprise-standaard — geen probleem om op te lossen, maar een vastzittende rij die na de volgende schone scan zou moeten verdwijnen is het controleren waard.",
    posture: {
      low_risk: "laag risico", moderate_risk: "matig risico", elevated_risk: "verhoogd risico",
      high_risk: "hoog risico", insufficient_evidence: "onvoldoende bewijs",
    },
  },
  agentBehaviorPage: {
    pickTenant: "Kies eerst een organisatie.",
    title: "Agentgedrag-profielen",
    introLead: "Hoe agents werken wanneer ze mogen handelen. Bind een profiel aan een scope op",
    consoleLink: "de governance-console",
    builtinBadge: "nog geen opgeslagen profielen — ingebouwde worden getoond",
    newProfile: "Nieuw aangepast profiel",
    edit: "Bewerken",
    builtinReadonly: "Ingebouwd (alleen-lezen)",
    cancel: "Annuleren",
    saving: "Opslaan…",
    saveProfile: "Profiel opslaan",
    saveFailed: "Opslaan mislukt: HTTP {status}",
    editTitle: "Profiel bewerken",
    newTitle: "Nieuw aangepast profiel",
    modalSubtitle: "Aangepaste profielen kunnen door elke scope-binding worden gebruikt.",
    rows: {
      planningDepth: "Planningsdiepte",
      verification: "Verificatie",
      retryCap: "Retry-limiet",
      autonomyPreset: "Autonomie-preset",
      contextExpansion: "Contextuitbreiding",
      confidenceThreshold: "Vertrouwensdrempel",
      sandboxAutoReviseMax: "Sandbox auto-revise max",
      sandboxAutoRevision: "Sandbox auto-revisie",
      includeReleaseStep: "Release-stap meenemen",
      haltDevOnGate: "Dev stoppen bij poort",
      resumeAfterRevise: "Hervatten na revisie",
      rejectAborts: "Afwijzen breekt missie af",
      gateApprove: "Poort-goedkeuring",
    },
    val: { on: "aan", off: "uit", yes: "ja", no: "nee" },
    /**
     * De opgeslagen instellingen, uitgesproken. Elk hiervan werd als ruwe
     * waarde getoond (`shallow`, `wide`, `accept`, `builtin`), waardoor een
     * governancepagina die bepaalt hoeveel ruimte een agent krijgt alleen
     * leesbaar was voor wie de enum geschreven had.
     */
    enum: {
      kind: {
        builtin: "Ingebouwd",
        custom: "Van jezelf",
        conservative: "Voorzichtig",
        balanced: "Gebalanceerd",
        autonomous: "Zelfstandig",
        experimental: "Experimenteel",
      },
      planningDepth: { shallow: "Kort plannen", normal: "Normaal plannen", deep: "Grondig plannen" },
      verificationStrictness: { light: "Licht controleren", normal: "Normaal controleren", strict: "Streng controleren" },
      autonomyPreset: {
        conservative: "Vroeg en vaak vragen",
        balanced: "Vragen als het ertoe doet",
        aggressive: "Alleen vragen als het moet",
      },
      contextExpansion: {
        narrow: "Alleen lezen wat is aangewezen",
        moderate: "De omliggende code lezen",
        wide: "Breed door de codebase lezen",
      },
      gateApproveSemantics: {
        revise: "Goedkeuren vraagt eerst om een revisie",
        accept: "Goedkeuren accepteert het werk zoals het is",
      },
    },
    fields: {
      displayName: "Weergavenaam",
      planningDepth: "Planningsdiepte",
      verificationStrictness: "Verificatiestrengheid",
      retryCap: "Retry-limiet",
      autonomyPreset: "Autonomie-preset",
      contextExpansion: "Contextuitbreiding",
      confidenceThreshold: "Vertrouwensdrempel",
      sandboxAutoReviseMaxDepth: "Sandbox auto-revise max diepte",
      sandboxAutoRevisionOnFailure: "Sandbox auto-revisie bij falen",
      includeReleaseStepInPlan: "Release-stap in plan meenemen",
      haltDevWhileGateOpen: "Dev-agent stoppen terwijl poort open is",
      resumeAfterChangesDone: "Doorgaan zodra de gevraagde wijzigingen klaar zijn",
      rejectAbortsMission: "Afwijzen breekt de missie af",
      gateApproveSemantics: "Poort-goedkeuring-semantiek",
    },
  },
  /** Zie de EN-variant voor de regel die deze zinnen eerlijk houdt. */
  approvalsPage: {
    eyebrow: "Governance",
    title: "Goedkeuringen",
    subtitle: "Alles in deze werkruimte dat op de beslissing van een mens wacht.",
    gatesTitle: "Wachtende gates ({count})",
    repliesTitle: "Klantantwoorden die op goedkeuring wachten ({count})",
    openGate: "Openen om te beslissen",
    overdue: "over de tijdslimiet",
    cannotDecide: "Alleen beheerders en managers kunnen klantantwoorden goedkeuren.",
    emptyTitle: "Er wacht niets op een beslissing",
    emptyBody: "Goedkeuringsverzoeken verschijnen hier zodra werk er een nodig heeft.",
    loadError: "Kon de goedkeuringen niet laden. Probeer het zo nog eens.",
    gateType: {
      plan: "AI-plan",
      risky_patch: "Codewijziging",
      security_override: "Beveiligingsuitzondering",
      deployment: "Uitrol",
      retry: "Opnieuw proberen",
      ui_change_approval: "UI-wijziging",
      ux_regression_override: "UX-regressie",
      commit: "Commit",
      pr_merge: "Pull request samenvoegen",
      development_review: "Ontwikkelreview",
      infra_action: "Infrastructuuractie",
    },
  },
  governanceScope: {
    page: {
      pickTenant: "Kies eerst een organisatie.",
      title: "Beleid & bereik",
      intro: "Bekijk welk beleid geldt voor de organisatie, een project of een codebase.",
      authorHint: "Organisatiebreed beleid stel je in op",
      consoleLink: "de governance-console",
      codebaseInherits: "Volgt het beleid van {project} — een codebase heeft geen eigen beleid.",
    },
    tabs: {
      effective: "Effectief beleid",
      overrides: "Afwijkingen",
      behavior: "Agentgedrag",
      simulator: "Simulator",
    },
    kinds: {
      organization: "Organisatie",
      project: "Project",
      codebase: "Codebase",
      environment: "Omgeving",
      workspace: "Werkscope",
    },
    picker: {
      searchPlaceholder: "Zoek projecten en codebases",
      searchLabel: "Zoek scopes",
      navLabel: "Scopes",
      empty: "Nog geen scopes. De organisatiescope wordt aangemaakt bij de eerste beleidsbeslissing.",
      noMatches: "Niets gevonden voor “{query}”.",
      hasOverride: "Deze scope wijkt op één of meer punten af van het organisatiebeleid.",
      overrideBadge: "Afwijking",
      lockCount: "{n} vastgezet veld(en)",
      addChild: "Scope hieronder toevoegen",
      archive: "Deze scope archiveren",
      archiveConfirm: "“{name}” ({kind}) archiveren? Wat eronder hangt blijft bestaan en erft voortaan van het niveau erboven.",
      advancedTitle: "Geavanceerde scopes",
      advancedHint: "Werkscopes per issue en scopes buiten de projectstructuur. Ze gelden gewoon — ze horen alleen niet in de dagelijkse hiërarchie.",
      advancedWithPolicy: "{n} hiervan wijzigen beleid",
    },
    capabilities: {
      title: "Wat mag FixControl in {scope}?",
      subtitle: "Afgeleid uit het beleid dat hier geldt — geen aparte instelling.",
      advisory: "· vastgelegd, niet afgedwongen",
      planWork: {
        label: "Werk plannen",
        always: "Elk plan heeft goedkeuring nodig voordat er werk begint.",
        riskyOrCritical: "Risicovolle of kritieke plannen vereisen goedkeuring; de rest start zelfstandig.",
        never: "Plannen starten zonder goedkeuring.",
      },
      changeCode: {
        label: "Goedkeuringsregel",
        never: "Vraagt nooit om goedkeuring. Codewijzigingen en pull requests worden door deze regel niet tegengehouden: een issue met risicovlag krijgt een CTO-akkoord, en commit- of merge-gates zijn aparte instellingen.",
        always: "Elke deployment van een wijziging die FixControl maakte, moet door een mens worden goedgekeurd. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
        risky_only: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring bij hoog of kritiek risico. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
        production_impact: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring zodra die productie raakt. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
        security_sensitive: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring zodra die authenticatie of securitybestanden raakt. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
        external_integration: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring zodra die een externe integratie raakt. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
        database_change: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring zodra die het databaseschema raakt. Codewijzigingen en pull requests worden door deze regel niet tegengehouden.",
      },
      runSandbox: {
        label: "Verifiëren in sandbox",
        required: "Staat als verplicht op het uitvoeringsplan. Geen gate controleert deze instelling nog.",
        optional: "Toegestaan, en niet verplicht volgens dit beleid.",
      },
      runTests: {
        label: "Tests draaien",
        required: "Staat als verplicht op het uitvoeringsplan. Geen gate controleert deze instelling nog.",
        optional: "Toegestaan, en niet verplicht volgens dit beleid.",
      },
      releaseToProduction: {
        label: "Productie vrijgeven",
        blockedWithoutQa: "Bedoeld om te blokkeren tot QA akkoord geeft, maar geen gate dwingt dit nog af.",
        qaSignoff: "Vraagt om een QA-akkoord, maar geen gate wacht er nog op.",
        approval: "Een deployment van een wijziging die FixControl maakte, moet door een mens worden goedgekeurd.",
        conditional: "Een deployment van een wijziging die FixControl maakte, vereist goedkeuring zodra die onder de goedkeuringsregel van deze scope valt.",
        open: "Wordt vrijgegeven zonder aparte goedkeuringsstap.",
      },
      contactCustomers: {
        label: "Klanten berichten",
        never: "Wordt nooit automatisch verstuurd — een mens verstuurt elk bericht.",
        draftOnly: "Alleen concepten — een mens verstuurt het bericht.",
        highConfidence: "Automatisch verstuurd vanaf {autosend}% zekerheid; tussen {approval}% en {autosend}% keurt eerst een mens goed.",
        lowRisk: "Alleen antwoorden met laag risico gaan automatisch uit; de rest wordt beoordeeld.",
      },
    },
    groups: {
      autonomy: "Autonomie",
      approval: "Goedkeuring",
      verification: "Validatie",
      production: "Productie",
      communication: "Klantcommunicatie",
      audit: "Bewaartermijnen",
    },
    statements: {
      autonomyMode: {
        conservative: "Maximaal toezicht: korte retryketens en een plangoedkeuring bij elke missie.",
        balanced: "De standaardbalans tussen toezicht en doorlooptijd.",
        aggressive: "Maximale doorloop: langere retryketens en geen plangoedkeuring.",
        custom: "Een eigen samenstelling van autonomie-instellingen, per veld ingesteld.",
      },
      approvalRequirement: {
        never: "Acties hebben nooit een aparte goedkeuring nodig.",
        risky_only: "Acties met hoog of kritiek risico vereisen goedkeuring.",
        production_impact: "Acties die productie raken vereisen goedkeuring.",
        security_sensitive: "Acties die authenticatie of securitybestanden raken vereisen goedkeuring.",
        external_integration: "Acties die een externe integratie raken vereisen goedkeuring.",
        database_change: "Acties die het databaseschema wijzigen vereisen goedkeuring.",
        always: "Elke actie vereist goedkeuring.",
      },
      planApprovalPolicy: {
        always: "Elk plan wordt goedgekeurd vóór uitvoering.",
        risky_or_critical: "Risicovolle en kritieke plannen worden goedgekeurd vóór uitvoering.",
        never: "Plannen worden uitgevoerd zonder aparte plangoedkeuring.",
      },
      approvalTimeoutMinutes: {
        value: "Een goedkeuringsverzoek wacht {minutes} minuten voordat de time-outregel geldt.",
        none: "Een goedkeuringsverzoek heeft geen deadline — het wacht tot iemand beslist.",
      },
      requireSandbox: {
        on: "Elke wijziging wordt eerst in een sandbox geverifieerd.",
        off: "Een sandboxrun kan wel, maar is niet verplicht.",
      },
      requireTests: {
        on: "Een wijziging moet tests meebrengen om de verificatie te halen.",
        off: "Tests zijn niet verplicht voor de verificatie.",
      },
      verificationStrictness: {
        lenient: "Verificatie is soepel: minder controles hoeven te slagen.",
        standard: "Verificatie gebruikt de standaardset controles.",
        strict: "Verificatie is streng: elke controle moet slagen.",
      },
      requireQaSignoff: {
        on: "Een QA-akkoord is vereist voordat werk klaar is.",
        off: "Er is geen apart QA-akkoord vereist.",
      },
      blockProdWithoutQa: {
        on: "Productiewerk kan niet afgerond worden zonder QA-akkoord.",
        off: "Productiewerk wacht niet op een QA-akkoord.",
      },
      commsMode: {
        never_autosend: "Klantberichten worden nooit automatisch verstuurd.",
        draft_only: "Klantberichten worden als concept klaargezet zodat een mens ze verstuurt.",
        autosend_high_confidence: "Klantberichten gaan automatisch uit boven de zekerheidsdrempel.",
        autosend_low_risk: "Alleen klantberichten met laag risico gaan automatisch uit.",
      },
      commsEnforced: {
        on: "Het communicatiebeleid wordt tijdens de uitvoering afgedwongen.",
        off: "Het communicatiebeleid is vastgelegd maar wordt tijdens de uitvoering niet afgedwongen.",
      },
      commsAutosendThreshold: {
        value: "Automatisch versturen begint bij {percent}% zekerheid.",
      },
      commsApprovalThreshold: {
        value: "Onder {percent}% zekerheid blijft een bericht een concept.",
      },
      retentionAuditDays: {
        value: "Auditregistraties worden {days} dagen bewaard.",
      },
      retentionTraceDays: {
        value: "Uitvoeringstraces worden {days} dagen bewaard.",
      },
    },
    fields: {
      autonomyMode: "Autonomiemodus",
      approvalRequirement: "Goedkeuringsvereiste",
      planApprovalPolicy: "Plangoedkeuring",
      approvalTimeoutMinutes: "Goedkeuringstime-out",
      requireSandbox: "Sandbox verplicht",
      requireTests: "Tests verplicht",
      requireQaSignoff: "QA-akkoord verplicht",
      blockProdWithoutQa: "Productie blokkeren zonder QA",
      verificationStrictness: "Strengheid validatie",
      commsMode: "Communicatiemodus",
      commsEnforced: "Communicatiebeleid afgedwongen",
      commsAutosendThreshold: "Drempel automatisch versturen",
      commsApprovalThreshold: "Drempel goedkeuring",
      retentionAuditDays: "Bewaartermijn audit",
      retentionTraceDays: "Bewaartermijn traces",
    },
    source: {
      inheritedFrom: "Geërfd van {scope}",
      overriddenAt: "Ingesteld voor {scope}",
      overriddenHere: "Hier ingesteld",
      lockedAt: "Vastgezet door {scope}",
      lockedReason: "Reden: {reason}",
      advisory: "vastgelegd, niet afgedwongen",
    },
    overrides: {
      emptyTitle: "Geen afwijkingen hier.",
      emptyBody: "{scope} volgt het organisatiebeleid volledig.",
      intro: "{n} regel(s) wijken af van het organisatiebeleid voor {scope}.",
      organisationRow: "{scope}",
      effectiveRow: "Geldt hier",
      noValue: "geen eigen waarde",
      noOverrideHere: "{scope} wijzigt deze regel niet — hij komt van hogerop.",
    },
    advanced: {
      title: "Technisch detail",
      chain: "Opgelost via: {chain}",
      colKey: "Beleidssleutel",
      colRaw: "Opgeslagen waarde",
      colResolved: "Betekent",
      colSource: "Ingesteld door",
      fromScope: "{kind}: {name}",
      orgDefault: "organisatiestandaard",
      conflicts: "{n} afwijkingspoging(en) genegeerd omdat het veld hogerop is vastgezet.",
      conflictDetail: "{field} geprobeerd op {at}, vastgezet op {by}; {attempted} geweigerd, {enforced} behouden.",
    },
    values: {
      boolean: { yes: "ja", no: "nee" },
      conservative: "behoudend",
      balanced: "gebalanceerd",
      aggressive: "voortvarend",
      custom: "aangepast",
      never: "nooit",
      always: "altijd",
      risky_only: "alleen risicovolle acties",
      production_impact: "productie-impact",
      security_sensitive: "securitygevoelig",
      external_integration: "externe integratie",
      database_change: "databasewijziging",
      risky_or_critical: "risicovol of kritiek",
      lenient: "soepel",
      standard: "standaard",
      strict: "streng",
      never_autosend: "nooit automatisch versturen",
      draft_only: "alleen concepten",
      autosend_high_confidence: "automatisch versturen boven de zekerheidsdrempel",
      autosend_low_risk: "alleen automatisch bij laag risico",
    },
    gone: {
      title: "Deze scope bestaat niet meer.",
      body: "Het project of de werkscope waar hij bij hoorde is verwijderd of gearchiveerd. De beleidsgeschiedenis blijft bewaard in het auditspoor.",
      action: "Terug naar de organisatie",
    },
    errors: {
      treeLoad: "Kon de scope-boom niet laden (HTTP {status}).",
      bindingSave: "Kon de agentgedrag-binding niet opslaan (HTTP {status}).",
      bindingClear: "Kon de agentgedrag-binding niet wissen (HTTP {status}).",
      archive: "Kon de scope niet archiveren (HTTP {status}).",
    },
  },
  governanceHierarchy: {
    page: {
      pickTenant: "Kies eerst een organisatie.",
      title: "Hiërarchie & gedrag",
      introLead: "Structuurniveau-weergave van de scope-boom, opgeloste beslissingen en agentgedrag-bindingen. Beleid auteuren (Goedkeuring / Risico / Verificatie / …) gebeurt op",
      consoleLink: "de hoofd-governance-console",
    },
    tabs: { effective: "Effectief", behavior: "Agentgedrag", simulator: "Simulator" },
    fields: {
      autonomyMode: "Autonomie-modus",
      approvalRequirement: "Goedkeuringsvereiste",
      planApprovalPolicy: "Plangoedkeuringsbeleid",
      approvalTimeoutMinutes: "Goedkeurings-timeout (minuten)",
      requireSandbox: "Sandbox vereisen",
      requireTests: "Tests vereisen",
      requireQaSignoff: "QA-goedkeuring vereisen",
      blockProdWithoutQa: "Productie blokkeren zonder QA",
      verificationStrictness: "Verificatie-strengheid",
      commsMode: "Communicatie-modus",
      commsEnforced: "Comms-plafond afgedwongen",
      commsAutosendThreshold: "Comms auto-send-drempel (%)",
      commsApprovalThreshold: "Comms goedkeurings-drempel (%)",
      retentionAuditDays: "Audit-retentie (dagen)",
      retentionTraceDays: "Trace-retentie (dagen)",
    },
    bindingFields: {
      planningDepth: "Planning-diepte",
      verificationStrictness: "Verificatie-strengheid",
      retryCap: "Retry-cap",
      autonomyPreset: "Autonomie-preset",
      contextExpansion: "Context-uitbreiding",
      confidenceThreshold: "Confidence-drempel",
      sandboxAutoReviseMaxDepth: "Sandbox auto-revise max diepte",
      sandboxAutoRevision: "Sandbox auto-revisie bij faal",
      includeReleaseStep: "Release-stap in plan opnemen",
      haltDevOnGate: "Dev-agent pauzeren bij open gate",
      resumeReviseOnComplete: "Doorgaan zodra de gevraagde wijzigingen klaar zijn",
      rejectGateAborts: "Reject breekt de missie af",
      gateApproveSemantics: "Gate-approve-semantiek",
    },
    signals: {
      isDeployment: "Deployment",
      environment: "Omgeving",
      touchesDatabaseSchema: "Raakt DB-schema",
      touchesSecurityFiles: "Raakt security-bestanden",
      touchesInfrastructure: "Raakt infrastructuur",
      irreversible: "Onomkeerbaar",
      isCustomerComms: "Klantcommunicatie",
      externalRecipient: "Externe ontvanger",
      hasTests: "Heeft tests",
      sandboxPassed: "Sandbox geslaagd",
    },
    environments: {
      none: "geen",
      production: "productie",
      staging: "staging",
      development: "development",
    },
    common: {
      inherit: "erven",
      cancel: "Annuleren",
      close: "Sluiten",
      true: "true",
      false: "false",
    },
    scopeTree: {
      navLabel: "Scope-boom",
      empty: "Nog geen scope-boom. Wordt automatisch aangemaakt bij de eerste beslissing — open de Effective-view om hem te bootstrappen.",
      addChild: "Child-scope toevoegen",
      archive: "Scope archiveren",
      lockCountOne: "{n} gelockt veld",
      lockCountMany: "{n} gelockte velden",
      archiveConfirm: "“{name}” ({kind}) archiveren? Onderliggende scopes blijven bestaan maar erven van de grootouder.",
      archiveFailed: "Archiveren mislukt: HTTP {status}",
    },
    policyEditor: {
      rootScope: "root-scope",
      fieldsLockedOne: "{n} veld gelockt",
      fieldsLockedMany: "{n} velden gelockt",
      save: "Wijzigingen opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      lockReasonPrompt: "Reden om \"{label}\" te locken:",
      lockedByAncestor: "gelockt door ancestor",
      lockedHere: "hier gelockt",
      lockedAtTooltip: "Gelockt op {scope}: {reason}",
      inherited: "geërfd",
      overriddenHere: "hier overschreven",
      clearOverride: "Override wissen (van parent erven)",
      lockField: "Dit veld locken",
      unlock: "Unlock",
      lockForAllDescendants: "Locken voor alle descendants",
      conflictsOne: "{n} lock-conflict in deze keten — overrides op gelockte velden worden genegeerd.",
      conflictsMany: "{n} lock-conflicten in deze keten — overrides op gelockte velden worden genegeerd.",
    },
    lockManager: {
      title: "Veld-locks · {scope}",
      intro: "Gelockte velden kunnen door geen enkele descendant-scope overschreven worden. Ancestor-locks zijn hier read-only; verwijder ze op de scope die ze zette.",
      addLock: "Lock toevoegen",
      fieldLabel: "Veld",
      pickField: "— kies een veld —",
      fieldOptionWithDomain: "{label} ({domain})",
      reasonLabel: "Reden",
      reasonPlaceholder: "bv. SOC2 — retentie is een compliance-policy, niet per project configureerbaar",
      errPickFieldReason: "Kies een veld en geef een reden op.",
      lockingButton: "Locken…",
      lockButton: "Veld locken",
      removeConfirm: "Lock op \"{field}\" verwijderen? Descendants kunnen het dan weer overschrijven.",
      noLocks: "Geen locks op dit bereik of hoger.",
      valueLabel: "waarde:",
      removeLock: "Lock verwijderen",
      ancestorLocksOne: "{n} ancestor-lock (hier read-only)",
      ancestorLocksMany: "{n} ancestor-locks (hier read-only)",
      atScope: "op",
    },
    scopeCreate: {
      title: "Nieuwe scope",
      intro: "Voeg een workspace, project of environment toe onder een bestaande scope. Org wordt automatisch aangemaakt bij eerste contact en kan hier niet toegevoegd worden.",
      parentLabel: "Parent",
      kindLabel: "Kind",
      slugLabel: "Slug",
      displayNameLabel: "Weergavenaam",
      repositoryLabel: "Repository",
      criticalityLabel: "Criticality",
      pickParent: "— kies een parent —",
      noAllowedChildren: "— deze parent heeft geen toegestane children —",
      slugPlaceholder: "bv. payments",
      displayNamePlaceholder: "bv. Payments",
      repositoryPlaceholder: "optioneel · matcht approval-routes op repo-id",
      errMissingFields: "Parent, slug en weergavenaam zijn vereist",
      errInvalidSlug: "Slug moet lowercase alfanumeriek + streepjes zijn (begin met letter of cijfer)",
      creatingButton: "Aanmaken…",
      createButton: "Scope aanmaken",
      criticality: {
        none: "—",
        low: "laag",
        medium: "gemiddeld",
        high: "hoog",
        critical: "kritiek",
      },
      kinds: {
        workspace: "workspace",
        project: "project",
        environment: "environment",
      },
    },
    bindingEditor: {
      title: "Agent-gedrag · {scope}",
      introBase: "Hoe een agent op dit bereik opereert. Onafhankelijk van \"wat is toegestaan\" (governance).",
      introResolved: "Resolved vanaf {chain}.",
      removeBindingTooltip: "Binding op dit bereik verwijderen",
      clearButton: "Wissen",
      save: "Binding opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      agentRoleLabel: "Agent-rol",
      baseProfileLabel: "Basisprofiel",
      pickProfile: "— kies een profiel —",
      profileOption: "{name} ({kind})",
      pickProfileHint: "Kies een basisprofiel om velden te gaan overschrijven.",
      overriddenHere: "hier overschreven",
      fromProfile: "uit profiel",
      lockedByAncestor: "gelockt door ancestor",
      lockedHere: "hier gelockt",
      lockedAtTooltip: "Gelockt op {scope}: {reason}",
      clearOverride: "Override wissen",
      unlock: "Unlock",
      lockForDescendants: "Locken voor descendants",
      lockReasonPrompt: "Reden om \"{label}\" te locken:",
      conflictsOne: "{n} lock-conflict in de behavior-keten — overrides op gelockte velden worden genegeerd.",
      conflictsMany: "{n} lock-conflicten in de behavior-keten — overrides op gelockte velden worden genegeerd.",
      removeConfirm: "Binding op dit bereik verwijderen (descendants resolven dan vanaf de parent)?",
    },
    bindingReview: {
      title: "Welke gedragskoppelingen op dit bereik gelden",
      hint: "Een koppeling onder de organisatie geldt pas nadat iemand haar heeft beoordeeld en geactiveerd, en alleen in de vorm die ze toen had: wie haar daarna wijzigt, zet haar terug op wachten op beoordeling.",
      hintOff: "Gedrag per project en per agent staat voor deze organisatie uit (ORCH_SCOPED_BEHAVIOR_RESOLUTION), dus ook een geactiveerde koppeling hier verandert niets totdat dat aan staat.",
      roleAll: "Elke agent",
      role: "Rol {role}",
      details: "Profiel {profile} · {patch} afwijkend · {locks} vergrendeld",
      status: {
        applies: "Geldt",
        pendingReview: "Wacht op beoordeling",
        scopedResolutionOff: "Geactiveerd, geldt niet: projectgedrag staat uit",
      },
      activate: "Beoordelen en activeren",
      deactivate: "Niet meer toepassen",
      activateConfirmTitle: "Deze gedragskoppeling activeren?",
      activateConfirmBody: "Agents die in dit bereik werken ({role}) gebruiken dit profiel, met deze afwijkingen en vergrendelingen, precies zoals het nu is opgeslagen. Ze lezen het vanaf hun volgende stap, ook in missies die al lopen. Het bereikt agents alleen zolang gedrag per project en per agent voor de organisatie aan staat. Een latere wijziging wacht opnieuw op beoordeling.",
      activateConfirm: "Activeren",
      deactivateConfirmTitle: "Deze koppeling niet meer toepassen?",
      deactivateConfirmBody: "De koppeling blijft opgeslagen, maar agents in dit bereik ({role}) vallen vanaf hun volgende stap terug op wat ze van de organisatie erven.",
      deactivateConfirm: "Niet meer toepassen",
      cancel: "Annuleren",
      activated: "Geactiveerd.",
      deactivated: "Gestopt. De koppeling blijft bewaard maar geldt niet meer.",
      failed: "Dat lukte niet. Er is niets gewijzigd.",
    },
    simulator: {
      title: "Runtime-simulator",
      intro: "Preview de beslissing voor een scope + actie. Draait de volledige v2 hiërarchie-resolver tegen het live beleid — geen writes.",
      scopeLabel: "Scope",
      scopeOption: "{kind}: {name}",
      agentRoleLabel: "Agent-rol",
      domainLabel: "Domein",
      kindLabel: "Kind",
      signalsLegend: "Signals",
      simulate: "Simuleren",
      simulating: "Simuleren…",
      errorPrefix: "Fout:",
      decisionTitle: "Beslissing",
      riskLabel: "risico:",
      profileLabel: "profiel:",
      chainTitle: "Keten",
      conflictsOne: "{n} lock-conflict — overrides genegeerd.",
      conflictsMany: "{n} lock-conflicten — overrides genegeerd.",
    },
    effective: {
      title: "Effectief beleid",
      resolvedChain: "Resolved langs de keten: {chain}",
      fromScope: "uit {kind}: {name}",
      orgDefault: "org-default",
      lockedTooltip: "Gelockt: {reason}",
      discardedOne: "{n} genegeerde override-poging (lock-conflicten)",
      discardedMany: "{n} genegeerde override-pogingen (lock-conflicten)",
      conflictDetail: "{field} geprobeerd op {at}, gelockt op {by}; waarde {attempted} afgewezen, {enforced} behouden.",
    },
  },

  settingsSystem: {
    eyebrow: "Instellingen",
    title: "Systeem",
    subtitle: "Onderhoud en retentie voor achtergrond-state.",
    loading: "Laden…",
    externalLocale: {
      title: "Taal voor externe berichten",
      sub: "De standaardtaal waarin FixControl berichten buiten de app opstelt.",
      label: "Standaardtaal",
      optionEn: "English",
      optionNl: "Nederlands",
      saved: "Opgeslagen",
      hint: "Geldt voor Slack-goedkeuringskaarten en beslis-updates, e-mail-goedkeuringen en missie-statusberichten. Een ticket dat in een andere taal binnenkomt houdt die taal voor zijn eigen missie; de interface in de app volgt de taalkeuze van elke gebruiker zelf.",
    },
    retention: {
      title: "Retentie",
      sub: "Één ronde over de state die verjaart: afgeronde agent-runs met hun event-historie, en audit-events die ouder zijn dan het audit-retentievenster. Lopende jobs worden nooit aangeraakt, de ronde geldt voor alle organisaties, en de ronde zelf wordt ge-audit.",
      cleanupEnabled: "Automatische opschoning ingeschakeld",
      windowLabel: "Retentie-venster agent-runs (dagen)",
      windowHint: "Runs die meer dan {days}d geleden zijn afgerond worden verwijderd.",
      sweepNote: "De sweep loopt elke 6 uur en kort na server-startup. Gebruik onderstaande knop voor een directe ronde.",
      auditNote: "Audit-events volgen het audit-retentievenster, dat nooit onder 90 dagen komt; zet het op 0 om ze voor altijd te bewaren. Het opschonen loopt de keten op volgorde af en legt een anker vast, zodat de resterende regels verifieerbaar blijven. Je stelt het in onder Governance → retentie, niet hier.",
      save: "Opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      runCleanupNow: "Retentie nu uitvoeren",
      cleaning: "Bezig…",
      runCleanupTitle: "De retentieronde nu uitvoeren",
      confirmTitle: "Retentie nu uitvoeren?",
      confirmBody: "Dit ruimt afgeronde agent-runs met hun event-historie op, en audit-events die ouder zijn dan het audit-retentievenster (minimaal 90 dagen), voor alle organisaties. Goedkeuringsbeslissingen, orchestratie-events en het anker van de auditketen blijven staan, zodat de keten verifieerbaar blijft — en de ronde wordt in het auditspoor vastgelegd.",
      confirmLabel: "Retentie uitvoeren",
      chainLabel: "Auditketen",
      chainVerified: "Geverifieerd {when} · {walked} regels",
      chainBroken: "Breuk bij reeksnummer {seq}",
      chainUnverified: "Nog niet geverifieerd",
      removedSummary: "Verwijderd: {runs} run en {events} eventrij.",
      removedSummaryRunsPlural: "Verwijderd: {runs} runs en {events} eventrij.",
      removedSummaryEventsPlural: "Verwijderd: {runs} run en {events} eventrijen.",
      removedSummaryBothPlural: "Verwijderd: {runs} runs en {events} eventrijen.",
    },
    issueKeys: {
      title: "Issue-key sequences",
      sub: "Bepaal de prefixes waaruit nieuwe issue-keys worden uitgegeven, en stuur specifieke issue-soorten naar een eigen sequence.",
      defaultNote: "Nieuwe issues gebruiken de {prefix}-sequence, tenzij een soort hieronder anders wordt gerouteerd.",
      sequencesHeading: "Sequences",
      nextNumber: "volgende: {n}",
      noSequences: "Nog geen sequences — de standaard wordt bij eerste gebruik aangemaakt.",
      prefixLabel: "Prefix",
      labelLabel: "Label",
      labelPlaceholder: "bijv. Acme-klant",
      addSequence: "Toevoegen",
      routingHeading: "Routering per issue-soort",
      tenantDefault: "Standaard (alle soorten)",
      kind: {
        BUG: "Bug",
        FEATURE: "Feature",
        SUPPORT: "Support",
      },
      useBuiltinDefault: "Ingebouwde standaard ({prefix})",
      useTenantDefault: "Organisatiestandaard gebruiken",
      example: "→ {key}",
      noTenant: "Kies een organisatie om routering per soort in te stellen. De sequences hierboven zijn platform-breed gedeeld.",
      saved: "Opgeslagen",
      errorGeneric: "Opslaan mislukt. Controleer de prefix en probeer opnieuw.",
    },
  },

  settingsUsers: {
    eyebrow: "Instellingen",
    title: "Gebruikers",
    subtitle: "Beheer teamleden en toegangsrollen",
    accessRequired: "Admin-toegang vereist",
    accessRequiredDetail: "Alleen admins kunnen gebruikers beheren.",
    createUser: "Gebruiker toevoegen",
    headers: {
      user: "Gebruiker",
      role: "Rol",
      lastSeen: "Laatst gezien",
      status: "Status",
      twoFactor: "2FA",
      actions: "Acties",
    },
    you: "(jij)",
    statusActive: "Actief",
    statusInactive: "Inactief",
    twoFactorOn: "Aan",
    twoFactorOff: "Uit",
    lastLogin: {
      never: "Nooit",
      justNow: "zojuist",
      minutesAgo: "{n}m geleden",
      hoursAgo: "{n}u geleden",
      daysAgo: "{n}d geleden",
      noSuccessfulLogin: "Geen succesvolle login geregistreerd",
    },
    actions: {
      edit: "Bewerken",
      resetPassword: "Wachtwoord resetten",
      activate: "Activeren",
      deactivate: "Deactiveren",
      toggleActive: "Actief omschakelen",
      delete: "Verwijderen",
    },
    bulk: {
      activate: "Selectie activeren",
      deactivate: "Selectie deactiveren",
      exportSelected: "Selectie exporteren",
      confirmDeactivateTitle: "Geselecteerde gebruikers deactiveren?",
      confirmDeactivateBody: "{n} gebruiker(s) deactiveren? Zij verliezen direct toegang. Reeds inactieve accounts en je eigen account worden overgeslagen.",
      toastActivated: "Geactiveerd",
      toastDeactivated: "Gedeactiveerd",
      toastFailed: "Bulk-actie mislukt",
    },
    filter: {
      allRoles: "Alle rollen",
      allStatus: "Alle statussen",
    },
    searchPlaceholder: "Zoek op naam of e-mail…",
    empty: "Geen gebruikers gevonden",
    toasts: {
      deleted: "{name} verwijderd",
      created: "{name} aangemaakt",
      updated: "{name} bijgewerkt",
      activated: "{name} geactiveerd",
      deactivated: "{name} gedeactiveerd",
      passwordReset: "Wachtwoord gereset voor {name}",
      failed: "Mislukt",
    },
    deleteModal: {
      title: "Gebruiker verwijderen",
      bodyPrefix: "Permanent verwijderen:",
      bodySuffix: "?",
    },
    create: {
      title: "Gebruiker aanmaken",
      sub: "Voeg een nieuw teamlid toe",
      submit: "Gebruiker aanmaken",
      nameLabel: "Naam",
      namePlaceholder: "Volledige naam",
      emailLabel: "E-mail",
      emailPlaceholder: "gebruiker@bedrijf.nl",
      passwordLabel: "Wachtwoord",
      passwordPlaceholder: "Stel een initieel wachtwoord in",
      roleLabel: "Rol",
      roleOperator: "Operator — bekijken en werken",
      roleManager: "Manager — bekijken, toewijzen, goedkeuren",
      roleAdmin: "Admin — volledige toegang",
      requiredMark: "*",
    },
    edit: {
      title: "{name} bewerken",
      submit: "Opslaan",
      nameLabel: "Naam",
      emailLabel: "E-mail",
      roleLabel: "Rol",
      roleOperator: "Operator",
      roleManager: "Manager",
      roleAdmin: "Admin",
    },
    reset: {
      title: "Wachtwoord resetten",
      sub: "Stel een nieuw wachtwoord in voor {name}",
      submit: "Wachtwoord resetten",
      newPasswordLabel: "Nieuw wachtwoord",
      newPasswordPlaceholder: "Minimaal 8 tekens",
      tooShort: "Minimaal 8 tekens",
    },
  },

  postureOperationalRails: {
    allClear: {
      eyebrow: "Operationele rails",
      headline: "Alles in orde — geen execution blockers, autonomy degraders of governance escalations",
      body: "De interpretation engine vond geen oppervlakken met sterk genoeg bewijs om rechten in te trekken. Reguliere governance + blast-radius regels zijn van toepassing.",
    },
    executionBlockers: {
      eyebrow: "Uitvoeringsblokkades",
      headline: "Wat autonome uitvoering vandaag tegenhoudt",
      body: "Oppervlakken waar het bewijs op dossier sterk genoeg is dat autonome uitvoering geweigerd wordt totdat mitigatie plaatsvindt.",
    },
    autonomyDegraders: {
      eyebrow: "Autonomie-degradaties",
      headline: "Rechten die de postuur heeft ingetrokken",
      body: "Specifieke autonomie-rechten beperkt door de interpretation engine. Elke regel traceert terug naar de signal kinds die het veroorzaakten.",
    },
    governanceEscalations: {
      eyebrow: "Governance-escalaties",
      headline: "Wat deployment nu vereist",
      body: "Approver chain-uitbreidingen en deployment-vereisten gedreven door de operationele postuur.",
    },
    releaseConfidence: {
      eyebrow: "Release-vertrouwen",
      headline: "Veiligheid van change-landing",
      scoreLabel: "Score: {score}/100",
    },
    communicationSafety: {
      eyebrow: "Communicatieveiligheid",
      headlineAvailable: "Outbound autonomie beschikbaar",
      headlineDisabled: "Outbound autonomie uitgeschakeld",
    },
    scannerProvenance: {
      eyebrow: "Scanner-provenance",
      headline: "Adapter-runs die deze beoordeling voedden",
      body: "Elke adapter is capability-detected; een \"Niet geïnstalleerd\" status betekent dat de scanner niet kon draaien op deze host — die afwezigheid is zelf bewijs.",
      succeededFooter: "{count} bevinding(en) · {durationMs}ms",
      failedFooter: "Fout: {error}",
      unknownError: "onbekend",
    },
    level: {
      high: "Hoog",
      moderate: "Matig",
      low: "Laag",
      blocked: "Geblokkeerd",
      insufficient_evidence: "Onvoldoende bewijs",
    },
    exploit: {
      demonstrated: "Aangetoond",
      conditional: "Voorwaardelijk",
      theoretical: "Theoretisch",
      insufficient_evidence: "Onvoldoende bewijs",
    },
    scannerStatus: {
      succeeded: "Geslaagd",
      failed: "Mislukt",
      skipped: "Overgeslagen",
      unavailable: "Niet geïnstalleerd",
    },
    confidenceLabel: "Vertrouwen: {confidence}",
    surfaceLabel: "Oppervlak: {surface}",
    appliesAtLabel: "Geldt bij: {appliesAt}",
    approversAddedLabel: "Approvers toegevoegd:",
    anchoredOn: "Verankerd op {count} signal kind(s).",
    anchoredOnSample: "Verankerd op {kinds}.",
    anchoredOnSamplePlus: "Verankerd op {kinds} (+{extra} meer).",
  },

  codebasePosture: {
    // Drilldown-pagina header
    eyebrow: "Operationele posture",
    title: "Codebase-gezondheid",
    subtitle: "Risico, automatiseringsbereidheid, governance-dekking en uitvoeringsvertrouwen — bewijs eerst.",
    reassess: "Opnieuw meten",
    runAssessment: "Meting uitvoeren",
    assessing: "Bezig met meten…",
    loading: "Laatste meting laden…",
    loadError: "Postuurmeting kon niet geladen worden.",
    networkError: "Netwerkfout bij laden van postuur.",
    assessmentFailed: "Postuurmeting mislukt",
    noAssessmentTitle: "Nog geen meting beschikbaar",
    noAssessmentBody: "Voer een postuurmeting uit om risicosignalen, automatiseringsbereidheid en governance-dekking voor deze codebase zichtbaar te maken. Tot die tijd behandelt governance de codebase als onvoldoende bewijs en weigert autonome uitvoering op elk effectief domein.",

    // Overzichtspagina
    landing: {
      subtitle: "Risico, automatiseringsbereidheid, governance-dekking en uitvoeringsvertrouwen — per codebase, bewijs eerst.",
      loadingCodebases: "Codebases laden…",
      noCodebases: "Geen codebases geconfigureerd voor deze organisatie.",
      noCodebasesHint: "Voeg een codebase toe om postuurmeting in te schakelen.",
      openCodebaseSettings: "Open codebase-instellingen",
      panelTitle: "Codebases",
      panelSub: "Klik op een codebase om de postuur-drilldown te openen — overzichtskaarten, signalen, bewijs en versiegeschiedenis.",
      notAssessed: "Niet gemeten",
      rowMeta: "v{version} · {risk} risico · {positive} positief · {when}",
    },

    // Versies vergelijken
    compareTitle: "Versies vergelijken",
    compareHint: "Kies twee scans van deze codebase en zie wat er veranderde: postuur, signalen, governance-dekking en aanbevolen controles.",
    compareAction: "Vergelijk",
    diffing: "Bezig met vergelijken…",
    pickTwoVersions: "Kies twee verschillende versies om te vergelijken.",
    diffFailed: "Vergelijken mislukt",
    versionFrom: "Van",
    versionTo: "Naar",

    // Postuurniveaus
    level: {
      low_risk: "Laag risico",
      moderate_risk: "Matig risico",
      elevated_risk: "Verhoogd risico",
      high_risk: "Hoog risico",
      insufficient_evidence: "Onvoldoende bewijs",
    },
    levelCopy: {
      low_risk: "Automatisering is veilig voor onderzoek, planopstelling en patch-voorstel.",
      moderate_risk: "Onderzoek en planopstelling zijn veilig; uitvoering vereist goedkeuring.",
      elevated_risk: "Gevoelige oppervlakken aanwezig — goedkeuring vereist voor gevoelige wijzigingen.",
      high_risk: "Autonome uitvoering geweigerd op elk effectief domein.",
      insufficient_evidence: "Te weinig signaal om risico vast te stellen — autonome uitvoering geweigerd tot een nieuwe scan.",
    },

    confidence: {
      low: "Laag",
      moderate: "Matig",
      high: "Hoog",
      insufficient_evidence: "Onvoldoende bewijs",
    },

    stage: {
      investigation: "Onderzoek",
      plan_generation: "Planopstelling",
      patch_proposal: "Patch-voorstel",
      validation: "Validatie",
      customer_communication: "Klantcommunicatie",
      deployment: "Deployment",
      infrastructure_mutation: "Infrastructuurwijziging",
    },

    decision: {
      allowed: "Toegestaan",
      approval_required: "Goedkeuring vereist",
      blocked: "Geblokkeerd",
      insufficient_evidence: "Onvoldoende bewijs",
    },

    blast: {
      isolated: "Geïsoleerd",
      module_scoped: "Modulebreed",
      service_scoped: "Servicebreed",
      tenant_scoped: "Organisatiebreed",
      platform_wide: "Platformbreed",
      insufficient_evidence: "Onvoldoende bewijs",
    },

    section: {
      operationalPosture: "Operationeel postuur",
      automationReadiness: "Automatiseringsbereidheid",
      governanceCoverage: "Governance-dekking",
      executionConfidence: "Uitvoeringsvertrouwen",
      blastRadius: "Impactradius",
      perStageReadiness: "Uitvoeringsbereidheid per fase",
      operationalImplications: "Operationele implicaties",
      governanceImplications: "Governance-implicaties",
      riskSignals: "Risicosignalen",
      positiveSignals: "Positieve signalen",
      sensitiveAreas: "Gevoelige gebieden",
      observabilityGaps: "Observability-hiaten",
      recommendedControls: "Aanbevolen controles",
      recurringFailures: "Terugkerende foutgebieden (geheugen)",
      approvalImpact: "Voorbeeld goedkeuringsimpact",
      historicalReliability: "Historische betrouwbaarheid (laatste {n} dagen)",
      aiNarrative: "AI-toelichting (advies) · {model}",
      suggestedControls: "Aanbevolen controles",
    },

    readiness: {
      allowed: "Toegestaan",
      approvalRequired: "Goedkeuring vereist",
      unsafeToggle: "Onveilig voor autonome uitvoering ({n})",
      investigationLabel: "Onderzoek",
      planGenerationLabel: "Planopstelling",
      patchProposalLabel: "Patch-voorstel",
      autonomousExecutionLabel: "Autonome uitvoering",
    },

    coverage: {
      detected: "Gedetecteerd",
      notDetected: "Niet gedetecteerd",
      approvalHooks: "Goedkeuringshooks",
      auditLogging: "Audit-logging",
      policyEnforcement: "Beleidshandhaving",
      permissionChecks: "Rechtencontroles",
      tenantIsolation: "Tenant-isolatie",
    },

    signal: {
      evidenceLabel: "{n} bewijs",
      noExplanation: "Geen aanvullende toelichting.",
      noEvidenceStored: "Geen bewijs opgeslagen voor dit signaal.",
      rowMeta: "{bucket} · {kind} · vertrouwen {n}",
      severity: { info: "Info", notice: "Let op", warning: "Waarschuwing", critical: "Kritiek" },
      bucket: {
        code: "Code", governance: "Governance", operational: "Operationeel",
        memory: "Geheugen", sensitive: "Gevoelig",
      },
    },

    empty: {
      risk: "Geen risicosignalen gedetecteerd.",
      positive: "Geen positieve signalen gedetecteerd — dekking kan onvoldoende zijn.",
      sensitiveAreas: "Niets gedetecteerd via pad-heuristieken.",
      observability: "Geen observability-hiaten gedetecteerd.",
      controls: "Geen aanvullende controles aanbevolen.",
      operationalImplications: "Geen operationele implicaties afgeleid uit het huidige bewijs.",
      governanceImplications: "Geen governance-implicaties afgeleid uit het huidige bewijs.",
      blastRadius: "Onvoldoende bewijs om de impactradius te bepalen.",
      listFallback: "Niet beschikbaar",
      historicalNa: "Niet beschikbaar.",
    },

    blastRadiusSection: {
      contributors: "Bijdragers ({n})",
      subtitle: "Gedetecteerde gevoelige oppervlakken in deze codebase, plus hoe ver een verstoring hier zou reiken. Géén vulnerability scan — dit toont wat aanwezig is en de bijbehorende impactradius.",
    },

    perStage: {
      conf: "vertrouwen: {level}",
      confNone: "—",
      missingEvidence: "Ontbrekend bewijs: {list}",
    },

    implications: {
      anchoredTo: "Verankerd in: {list}",
      domainAnchored: "Domein: {domain} · verankerd in: {list}",
      domainOnly: "Domein: {domain}",
    },

    approvalImpact: {
      sensitiveSystems: "Gevoelige systemen: {list}",
    },

    historical: {
      insufficientMemory: "Onvoldoende geheugen",
      succeeded: "Geslaagd",
      failed: "Mislukt",
      rolledBack: "Teruggedraaid",
      hotspots: "Module-hotspots",
      rollback: "Rollback",
      validationFailures: "Validatiefouten",
      recurringRejections: "Terugkerende afwijzingen",
      countSuffix: "({n}×)",
    },

    evidenceFootnote: "Elke conclusie is verankerd in bewijs — klik op een signaal om de onderliggende bestanden, migraties, audit-events of geheugen­entries uit te klappen.",

    sparkline: {
      historyTitle: "Geschiedenis — {n} scan",
      historyTitlePlural: "Geschiedenis — {n} scans",
      versionRange: "v{from} → v{to}",
      hint: "Klik op een cel om die versie te laden. Hover voor datum + signaal­aantal.",
      hintChips: "Klik een versie om die meting te laden. Hover voor datum + signaal­aantal.",
      cellAria: "Meting v{version} — {posture}",
    },
    trend: {
      insufficient: "Minder dan 2 metingen beschikbaar.",
      improving: "Postuur verbeterde over de laatste {n} scans.",
      deteriorating: "Postuur verslechterde over de laatste {n} scans.",
      stable: "Postuur stabiel over de laatste {n} scans.",
    },

    // Diff-weergave
    diff: {
      sectionLabel: "Postuur-verschil",
      coverageFlips: "Wijzigingen in dekking & bereidheid",
      detectedShort: "✓ gedetecteerd",
      notDetectedShort: "✗ niet gedetecteerd",
      riskAdded: "Risico toegevoegd ({n})",
      riskResolved: "Risico opgelost ({n})",
      positiveAdded: "Positief toegevoegd ({n})",
      positiveLost: "Positief verloren ({n})",
      none: "Geen",
      added: "Toegevoegd ({n})",
      resolved: "Opgelost ({n})",
    },
  },

  applicableGovernance: {
    title: "Toepasselijke governance",
    shortTitle: "Governance",
    refresh: "Vernieuwen",
    noTenant: "Geen actieve organisatie — open eerst de org-switcher.",
    loadError: "Governance laden mislukt ({code}).",
    sourceResolved: "Opgelost scope-pad: {chain}",
    sourceFallback: "Geen specifieke scope gekoppeld — terugvallen op organisatie-default.",
    sourceScopeGone: "Die scope bestaat niet meer — de organisatiestandaard wordt getoond.",
    overrideHere: "Beleid op deze scope",
    addChildScope: "Subscope toevoegen",
    openHierarchy: "Open hiërarchie",
    compactOverrides: "Overrides",
    compactLocks: "Locks",
    collapsedSummary: "{overrides} overrides · {locks} locks",
    editInline: "Beleid hier aanpassen",
    inlineEditorTitle: "Override op {scope}",
    lockedByAncestor: "Gelockt door een hogere scope — override geblokkeerd.",
    lockedByAncestorShort: "Gelockt",
    lockHere: "Lock hier",
    saving: "Opslaan…",
  },

  demoBanner: {
    active: "Demo data actief — Northwind Bank",
    removeCta: "Verwijder alle demo data",
    removing: "Bezig met verwijderen…",
    confirmRemove: "Weet je zeker dat je alle demo data wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.",
    removeError: "Verwijderen is mislukt. Probeer het opnieuw.",
    resetCta: "Reset demo",
    resetTitle: "Verwijder en laad de demo data opnieuw",
    resetting: "Bezig met resetten…",
    confirmReset: "Hiermee wordt de huidige demo data verwijderd en opnieuw aangemaakt. Lopende notities of wijzigingen op demo-issues gaan verloren.",
    resetError: "Reset is mislukt. Probeer het opnieuw.",
    closeTitle: "Banner verbergen — beheer blijft beschikbaar via Instellingen → Demo data",
  },

  // Antwoordstijl — professionalisering van klantgerichte replies (mig 198).
  settingsReplyStyle: {
    title: "Antwoordstijl",
    description: "Geef elke klantgerichte reply een consistente, professionele vorm: een handtekening, aanhef en afsluiting, en een compacte afzenderregel op chatkanalen. Standaard uit — er verandert niets totdat je het aanzet.",
    loadError: "Antwoordstijl-instellingen konden niet worden geladen.",
    saveError: "Antwoordstijl-instellingen konden niet worden opgeslagen.",
    savedNotice: "Opgeslagen.",
    saving: "Opslaan…",
    logoTooLarge: "Logo moet 256 KB of kleiner zijn.",
    logoUploadError: "Logo-upload mislukt — png, jpeg, webp of gif tot 256 KB.",
    enabledLabel: "Klant-replies professionaliseren",
    enabledHint: "Hoofdschakelaar voor deze organisatie. Uitgeschakeld gaan replies exact zoals geschreven de deur uit.",
    removeOverride: "Override verwijderen",
    signatureLabel: "Handtekening",
    signatureHint: "Toegevoegd aan e-mail-, Freshdesk- en Jira Service Management-replies. Gebruik variabelen voor agent- en bedrijfsnaam; voeg een logo toe via een https-afbeeldings-URL.",
    salutationLabel: "Aanhef",
    salutationHint: "Eerste regel van de reply. Wordt automatisch overgeslagen als het concept al met een groet opent.",
    salutationPlaceholder: "Hi {{customer_name}},",
    closingLabel: "Afsluiting",
    closingHint: "Afsluitregel direct boven de handtekening. Wordt overgeslagen als het concept er al mee eindigt.",
    closingPlaceholder: "Met vriendelijke groet,",
    senderLineLabel: "Afzenderregel (chat)",
    senderLineHint: "Compacte attributie onder Slack-, Teams- en websitechat-replies in plaats van een volledige handtekening.",
    senderLinePlaceholder: "— {{agent_name}}, {{company_name}} Support",
    toneLabel: "Standaardtoon",
    toneHint: "Vooraf geselecteerde toon voor AI-conceptreplies. Een expliciete keuze in de composer wint altijd.",
    toneNone: "Geen voorkeur",
    channelsLabel: "Actieve kanalen",
    channelsHint: "Compositie geldt alleen op de kanalen die hier zijn geselecteerd.",
    channels: {
      email: "E-mail",
      freshdesk: "Freshdesk",
      odoo: "Odoo",
      topdesk: "TOPdesk",
      jsm: "Jira Service Management",
      slack: "Slack",
      teams: "Teams",
      website_chat: "Websitechat",
    },
    scopes: {
      organization: "Organisatie",
      project: "Project",
      mailbox: "Mailbox",
      user: "Gebruiker",
    },
    scopeHints: {
      project: "Project-override — velden die je hier invult vervangen de organisatie-standaard voor replies van dit project. Lege velden vallen terug op de organisatiewaarden.",
      mailbox: "Mailbox-override — geldt voor replies die vanuit deze mailbox worden verstuurd (wint veld-voor-veld van project- en organisatiewaarden). Tip: een platte handtekening op de mailbox zelf blijft in de inbox-composer voorrang houden totdat je die daar leegmaakt.",
      user: "Gebruikers-override — geldt voor replies die deze operator verstuurt (sterkste scope). Lege velden vallen terug op mailbox-, project- en organisatiewaarden.",
    },
    preview: {
      heading: "Voorbeeld",
      sampleBody: "Goed nieuws — het probleem dat je meldde is opgelost en uitgerold. Kun je aan jouw kant controleren of alles weer werkt zoals verwacht?",
      sampleCompany: "Acme",
      note: "Gerenderd door exact dezelfde compositiecode die de server bij verzenden gebruikt — wat je hier ziet is wat de klant ontvangt.",
    },
  },

  // Antwoordtemplates — herbruikbare reply-skeletten (mig 199).
  settingsReplyTemplates: {
    title: "Antwoordtemplates",
    description: "Herbruikbare, gecategoriseerde antwoord-skeletten voor de issue-composer. Teksten worden in het Engels en Nederlands naast elkaar beheerd; variabelen vullen klant-, ticket- en agentgegevens in bij invoegen.",
    loadError: "Antwoordtemplates konden niet worden geladen.",
    saveError: "Template kon niet worden opgeslagen.",
    newTemplate: "Nieuwe template",
    newHeading: "Nieuwe template",
    editHeading: "Template bewerken",
    nameLabel: "Naam",
    categoryLabel: "Categorie",
    projectLabel: "Project",
    bodyEnLabel: "Tekst — Engels",
    bodyNlLabel: "Tekst — Nederlands",
    subjectEnPlaceholder: "Onderwerp (Engels, alleen e-mail — optioneel)",
    subjectNlPlaceholder: "Onderwerp (Nederlands, alleen e-mail — optioneel)",
    enabledLabel: "Beschikbaar in de composer",
    enabledHint: "Uitgeschakelde templates blijven hier staan maar verdwijnen uit de picker.",
    disabledBadge: "Uit",
    filterAll: "Alle templates",
    filterTenantWide: "Organisatiebreed",
    empty: "Nog geen templates. Maak er één zodat operators een consistent startpunt hebben.",
    categories: {
      confirmation: "Bevestiging",
      resolution: "Oplossing",
      escalation: "Escalatie",
      closing: "Afsluiting",
      general: "Algemeen",
    },
  },

  // Beperkte rich-text-editor (handtekeningen, antwoordtemplates).
  replyRichEditor: {
    bold: "Vet",
    italic: "Cursief",
    link: "Link",
    linkPrompt: "Link-URL (https:// of mailto:)",
    bulletList: "Opsomming",
    image: "Logo / afbeelding (https-URL)",
    imagePrompt: "Afbeeldings-URL (alleen https)",
    imageVanishes: "Deze afbeelding is licht op een doorzichtige achtergrond en valt bijna weg in een witte e-mail. Gebruik een donkere of gekleurde versie van je logo.",
  },

  // Website chat — publiek intake-kanaal: instellingen + inbox (migratie 180).
  websiteChat: {
    title: "Chat",
    subtitle: "Zet een chat op je website voor bezoekers, of een interne chat voor je eigen collega's. Alles wat binnenkomt zie je terug in je inbox, net als vragen uit Slack, Teams en e-mail.",
    status: { connected: "Verbonden", healthy: "Gezond", error: "Vereist aandacht", disabled: "Uitgeschakeld" },
    /* De drie toestanden waarin een chat kan staan. "Nog niet bereikbaar"
     * geldt alleen voor een publieke chat: die antwoordt niemand zolang de
     * lijst met website-adressen leeg is. */
    state: {
      live: "Actief",
      needs_origins: "Nog niet bereikbaar",
      disabled: "Uitgeschakeld",
    },
    usedFor: "Gebruikt voor: vragen beantwoorden, contactgegevens verzamelen, een melding aannemen, een demoaanvraag opnemen",
    knowledge: "Kennis",
    knowledgeNone: "Geen kennisbundel",
    knowledgeHint: {
      public: "Een publieke chat mag alleen uit een publieke bundel lezen.",
      internal: "Een interne chat mag ook uit een interne bundel lezen. Beperkte bundels nooit.",
    },
    /* De twee soorten chat. Dit is de eerste vraag bij het aanmaken, omdat
     * het bepaalt wie er mag meepraten en welke kennis de chat mag lezen. */
    types: {
      public: {
        label: "Publieke chat",
        description: "Voor bezoekers van je website. Anoniem, en leest alleen uit publieke kennis.",
        defaultName: "Chat op de website",
      },
      internal: {
        label: "Interne chat",
        description: "Voor collega's die zijn ingelogd in FixControl. Mag ook interne kennis lezen.",
        defaultName: "Interne chat",
      },
    },
    /* Waar de chat voor is. Achter elke keuze zit een set instellingen die
     * we zelf invullen; je kunt ze daarna nog los aanpassen. */
    purposes: {
      answer: {
        label: "Vragen beantwoorden",
        description: {
          public: "De chat beantwoordt vragen uit je publieke documentatie en maakt verder niets aan.",
          internal: "De chat beantwoordt vragen van collega's uit je interne documentatie.",
        },
      },
      support: {
        label: "Vragen en meldingen",
        description: {
          public: "De chat beantwoordt vragen en maakt een melding aan wanneer iemand hulp nodig heeft.",
          internal: "De chat beantwoordt vragen van collega's en maakt een melding aan wanneer dat nodig is.",
        },
      },
      sales: {
        label: "Leads en demo-aanvragen",
        description: {
          public: "De chat vraagt om naam, bedrijf en e-mail zodat je contact kunt opnemen.",
          internal: "Niet beschikbaar voor een interne chat.",
        },
      },
    },
    setup: {
      audienceLabel: "Wie praat er met deze chat?",
      purposeLabel: "Waar is de chat voor?",
      nameLabel: "Naam",
      namePlaceholder: "Bijvoorbeeld: Support op de website",
      create: "Chat aanmaken",
      newChat: "Nieuwe chat",
      failed: "Aanmaken is niet gelukt.",
      publicNote: "Een publieke chat staat uit tot je hieronder de adressen van je website invult. Zo kan een half ingestelde chat nooit ongemerkt live staan.",
      internalNote: "Een interne chat staat meteen aan. Alleen collega's die zijn ingelogd in FixControl kunnen hem bereiken.",
    },
    behavior: {
      greeting: "Openingszin",
      greetingHelp: "De eerste zin die de chat zegt. Leeg laten = de standaardzin in de taal van de bezoeker.",
      persona: "Toon en gedrag",
      personaHelp: "Vrije tekst die meegaat in de prompt, bijvoorbeeld: kort en zakelijk, spreek mensen aan met u.",
      allowedTopics: "Waar hij over mag gaan",
      blockedTopics: "Waar hij niet over mag gaan",
      maxQuestions: "Maximum aantal vragen",
      maxQuestionsHelp: "Hoeveel verduidelijkende vragen de chat stelt voordat hij samenvat. Leeg laten = standaard.",
      kbScope: "Kennisbereik",
      kbScopeHelp: "Binnen de bundel die je hierboven koos: bepaalt of bronnen van dit project voorrang krijgen. Dit kan het bereik alleen kleiner maken, nooit groter.",
      kbScopes: {
        project_first: "Dit project eerst",
        project_only: "Alleen dit project",
        tenant: "Hele organisatie",
      },
    },
    card: {
      revokeLink: "Link intrekken",
      shareLink: "Deelbare link",
      shareLinkHelp: "Een link die je kunt versturen of in je helpdocs kunt zetten \u2014 er is geen website voor nodig. Hij opent deze chat op een gebrande pagina. Dit is wat vroeger de klant-intakelink heette; het is nu \u00e9\u00e9n van de twee manieren om deze chat te bereiken.",
      createLink: "Deelbare link maken",
      openLink: "Openen",
      linkNotRenderable: "Deze chat heeft een link, maar de sleutel is destijds \u00e9\u00e9nmalig getoond en kan niet opnieuw worden weergegeven. Maak een nieuwe link als je hem kwijt bent.",
      changeType: "Soort wijzigen",
      changeTypeHint: "Als je dit wijzigt, zetten we de bijbehorende instellingen opnieuw goed. Een gekoppelde kennisbundel laten we los wanneer de nieuwe soort die niet mag lezen.",
      advanced: "Geavanceerde instellingen",
      openChat: "Chat openen",
      hideChat: "Chat sluiten",
      reach: "Waar collega's deze chat vinden",
      directLink: "Directe link:",
      embedHelpInternal: "Plak dit fragment op een intranetpagina. De bezoeker moet zijn ingelogd in FixControl; de pagina vraagt eenmalig toestemming in een klein venster. Vul hieronder ook het adres van die intranetpagina in.",
    },
    panel: {
      greeting: "Hoi! Waar kan ik je mee helpen?",
      placeholder: "Typ een bericht…",
      send: "Versturen",
      thinking: "Bezig…",
      failed: "Er ging iets mis. Probeer het opnieuw.",
      unavailable: "Deze chat is nu niet beschikbaar.",
      file: "Melding aanmaken",
      filed: "Melding aangemaakt:",
    },
    authorize: {
      connecting: "Even verbinden met de chat…",
      refused: "Deze chat kan hier niet worden geopend.",
      refusedHint: "Log in bij FixControl, of vraag een beheerder om het adres van deze pagina toe te voegen aan de chat.",
    },
    conversations: {
      title: "Gesprekken",
      subtitle: "Wat er via de chats van dit project is binnengekomen.",
      empty: "Nog geen gesprekken.",
      emptyHint: "Zodra iemand de chat gebruikt, verschijnt het gesprek hier — met de melding die eruit is gekomen.",
      anonymous: "Anonieme bezoeker",
    },
    projectIntro: "De chats van dit project: op je website, of intern voor collega's. Je maakt ze hier aan en stelt ze hier in.",
    projectEmpty: "Dit project heeft nog geen chat.",
    projectEmptyNoRights: "Vraag een beheerder van de organisatie om er een aan te maken.",
    loadFailed: "De chats van dit project konden niet worden opgehaald.",
    enabled: "Ingeschakeld",
    name: "Naam",
    project: "Project",
    allowedOrigins: "Adressen van je website",
    allowedOriginsHelp: "Eén adres per regel (https://voorbeeld.nl). Verplicht: zonder dit antwoordt de chat niemand.",
    intranetOrigins: "Adressen van je intranet (optioneel)",
    intranetOriginsHelp: "Alleen nodig als je de chat op een eigen intranetpagina zet. Binnen FixControl werkt hij sowieso.",
    localeDefault: "Standaardtaal",
    intakeMode: "Intake-modus",
    intakeModes: { answer_only: "Alleen antwoorden", lead_capture: "Contactgegevens verzamelen", issue_intake: "Melding aannemen", demo_intake: "Demoaanvraag aannemen" },
    defaultIntent: "Standaardsoort vraag",
    knowledgeScope: "Kennisbundel",
    knowledgeMode: "Kennis-modus",
    knowledgeModes: { public_only: "Alleen publiek", project_public: "Publiek van dit project", internal_scope: "Interne bundel", disabled: "Uitgeschakeld" },
    notifyEmail: "Notificatie-e-mail",
    createIssue: "Maak een FixControl-melding aan",
    createFreshdesk: "Maak een Freshdesk-ticket aan bij een gekwalificeerde lead (via de leveringswachtrij)",
    createJira: "Maak een Jira-issue aan bij een gekwalificeerde lead (via de leveringswachtrij)",
    requireEmail: "E-mail verplicht voor follow-up",
    rateLimitProfile: "Limiet op het aantal berichten",
    embedCode: "Embed-code",
    embedHelp: "Plak dit fragment op je publieke site. De chat-id is publiek; organisatie, project en instellingen worden aan onze kant bepaald.",
    copy: "Kopiëren",
    copied: "Gekopieerd",
    testWidget: "Test chat",
    save: "Opslaan",
    saved: "Opgeslagen",
    widgets: "Chats",
    widgetsSubtitle: "Alle chats van deze organisatie. Een chat die aan een project hangt, stel je ook in op de pagina van dat project.",
    newWidget: "Nieuwe chat",
    noWidgets: "Nog geen chats.",
    noWidgetsHint: "Maak er een aan met de knop hierboven, of vanaf de chatpagina van een project.",
    inbox: "Alle gesprekken",
    inboxSubtitle: "Elk chatgesprek in deze organisatie, publiek en intern.",
    inboxEmpty: "Nog geen gesprekken.",
    columns: { visitor: "Wie", kind: "Soort", intent: "Soort vraag", company: "Bedrijf", status: "Status", created: "Aangemaakt" },
    scopes: "Kennisbundels",
    scopesSubtitle: "Een bundel is een set documentatie met een zichtbaarheid. Een publieke chat leest alleen publieke bundels.",
    newScope: "Naam van de nieuwe bundel",
    newPublicScope: "Publieke bundel",
    newInternalScope: "Interne bundel",
    noScopes: "Nog geen kennisbundels.",
    visibility: "Zichtbaarheid",
    visibilities: { public: "Publiek", internal: "Intern", restricted: "Beperkt" },
    publicOnlyWarning: "Alleen PUBLIEKE bundels kunnen aan een publieke chat worden gekoppeld. Stel nooit tickets, Slack-gesprekken, interne documenten of broncode bloot.",
    privacyNote: "Bezoekers worden gewaarschuwd geen wachtwoorden, tokens of gevoelige data te delen. Er wordt geen ruw IP-adres opgeslagen.",

    /* Waarom een actie niet lukte. Eén zin per situatie, want de actie die de
     * lezer moet nemen verschilt per geval: opnieuw inloggen, een beheerder
     * vragen, de pagina herladen of de server starten. */
    errors: {
      offline: "FixControl is niet bereikbaar. Draait de server nog?",
      unauthenticated: "Je sessie is verlopen. Log opnieuw in.",
      two_factor: "Log opnieuw in met je tweefactorcode.",
      forbidden: "Je hebt hier geen rechten voor. Vraag een beheerder van de organisatie.",
      csrf: "Het verzoek is om veiligheidsredenen geblokkeerd. Herlaad de pagina en probeer het opnieuw.",
      invalid: "Het verzoek is geweigerd. Controleer de ingevulde gegevens.",
      rate_limited: "Te veel verzoeken kort na elkaar. Wacht even.",
      server: "Er ging iets mis aan onze kant. Probeer het opnieuw.",
      unknown: "Er ging iets mis. Probeer het opnieuw.",
    },
    /* Kennisbundel-beheer: wat zit erin, en mag dat eruit gelezen worden. */
    scopeEditor: {
      manage: "Inhoud beheren",
      hide: "Inhoud verbergen",
      contents: "Wat er in deze bundel zit",
      count: "{n} bronnen",
      countOne: "1 bron",
      empty: "Deze bundel is leeg. Een chat die hem gebruikt vindt geen enkel document.",
      add: "Bronnen toevoegen",
      searchPlaceholder: "Zoek in je kennis…",
      searchEmpty: "Geen bronnen gevonden.",
      searchFailed: "De kennis kon niet worden opgehaald.",
      addAll: "Alle {n} resultaten toevoegen",
      limitReached: "Een bundel kan maximaal 500 bronnen bevatten.",
      added: "Toegevoegd",
      addOne: "Toevoegen",
      remove: "Verwijderen",
      missing: "Deze bron bestaat niet meer",
      missingCount: "{n} bronnen in deze bundel bestaan niet meer.",
      removeMissing: "Opruimen",
      unsafe: "{n} van deze bronnen beschrijven interne systemen (codebase-analyses, case-learnings, klantdocumentatie). In een PUBLIEKE bundel kan een anonieme bezoeker daar antwoorden uit krijgen.",
      notAutomatic: "Nieuwe kennis komt hier niet vanzelf bij. Voeg een bron toe nadat je hem op de Kennis-pagina hebt gezet.",
      nameLabel: "Naam van de bundel",
      delete: "Bundel verwijderen",
      deleteConfirm: "Deze bundel verwijderen? Chats die hem gebruiken vallen terug op geen kennis.",
      newHeading: "Nieuwe bundel",
      namePlaceholder: "Bijvoorbeeld: Publieke documentatie",
      create: "Bundel aanmaken",
      loadFailed: "De kennisbundels konden niet worden opgehaald.",
    },
    /* Op de chatkaart: wat de gekozen bundel feitelijk bevat. */
    knowledgeEmptyWarning: "Deze bundel is leeg — de chat beantwoordt nog niets uit documentatie.",
    knowledgeNoneWarning: "Zonder bundel doorzoekt deze chat geen enkele documentatie.",
    knowledgeManage: "Bundels beheren",
  },

  // Microsoft Teams → Identiteitskoppelingen.
  teamsIdentities: {
    title: "Teams identiteitskoppelingen",
    subtitle: "Teams-gebruikers die zichzelf aan deze organisatie hebben gekoppeld. Alleen verhoogde koppelingen kunnen governance-poorten vanuit Teams goedkeuren.",
    loading: "Laden…",
    emptyTyping: "Nog geen koppelingen. Gebruikers koppelen zichzelf door",
    emptyToBotAdmin: "naar de bot in Teams te sturen; beheerders verhogen de koppeling hier zodat die kan goedkeuren.",
    emptyToBotMember: "naar de bot in Teams te sturen; vraag een beheerder om een koppeling te verhogen voordat die kan goedkeuren.",
    unknownUser: "(onbekende gebruiker)",
    linkedAt: "gekoppeld",
    elevatedAt: "verhoogd",
    revokedAt: "ingetrokken",
    elevate: "Verhogen",
    revoke: "Intrekken",
    badges: { elevated: "Verhoogd", verified: "Geverifieerd", revoked: "Ingetrokken" },
    errors: {
      load: "Kon koppelingen niet laden.",
      reach: "Kon FixControl niet bereiken.",
      elevate: "Verhogen mislukt.",
      revoke: "Intrekken mislukt.",
    },
  },

  /* Issues- en missies-secties op de projectpagina (src/app/projects/[id]/
   * _components/sections/project-issues.tsx + project-missions.tsx). Compacte
   * read-only lijsten binnen dit project; rijlabels hergebruiken de bestaande
   * issuesDetail.*- en missions.status.*-woordenboeken. */
  projectSections: {
    issues: {
      navLabel: "Issues",
      title: "Issues",
      subtitle: "Alle issues van dit project — hier zoek, filter en triage je ze.",
      newIssue: "Nieuwe issue",
      viewAll: "Openen in de volledige issuelijst",
      empty: "Nog geen issues in dit project.",
      emptyHint: "Issues verschijnen hier zodra er werk binnenkomt via de intakebronnen van dit project — kanalen, mailboxen of een gekoppelde tracker.",
    },
    missions: {
      navLabel: "Missies",
      title: "Missies",
      subtitle: "Alle AI-missies die voor dit project zijn gedraaid — elke externe stap blijft achter goedkeuring.",
      viewAll: "Openen in de volledige missielijst",
      empty: "Nog geen missies voor dit project.",
      emptyHint: "Een missie start vanuit een issue en doorloopt plan → sandbox-verificatie → goedkeuring. Start er een vanuit een issue in dit project.",
    },
    patches: {
      navLabel: "Patches",
      title: "Patches",
      subtitle: "Voorgestelde codewijzigingen voor dit project. Goedkeuren of afwijzen kan hier; de volledige diff staat een klik verderop.",
      searchPlaceholder: "Zoek patches…",
      viewAll: "Openen in de volledige patchwachtrij",
      empty: "Nog geen patches voor dit project.",
      emptyHint: "Een patch ontstaat wanneer een agent in een werksessie een codewijziging voorstelt. Niets gaat naar buiten zonder goedkeuring.",
      bulkApprove: "Goedkeuren",
      bulkReject: "Afwijzen",
      bulkRejectConfirmTitle: "Geselecteerde patches afwijzen?",
      bulkRejectConfirmBody: "{count} patch(es) worden afgewezen. De werksessie blijft bestaan, zodat een agent een nieuwe versie kan voorstellen.",
    },
    workspaces: {
      navLabel: "Werksessies",
      title: "Werksessies",
      subtitle: "De sessies waarin agents aan de code van dit project werken — met de issue waar ze vandaan komen en de patches die ze opleveren.",
      viewAll: "Openen in de volledige sessielijst",
      empty: "Nog geen werksessies voor dit project.",
      emptyHint: "Een werksessie ontstaat zodra een agent aan een issue van dit project begint. Start een missie vanuit een issue om er een te krijgen.",
      renameFailed: "Hernoemen mislukt.",
      deleteFailed: "Verwijderen mislukt.",
    },
    list: {
      exportCsv: "CSV exporteren",
    },
  },

  /* Contextoverzicht op de projectpagina (src/app/projects/[id]/_components/
   * sections/context-summary.tsx). Alleen eerlijke formuleringen: FixControl
   * gebruikt de bronnen die aan DIT project gekoppeld zijn — nooit "begrijpt
   * je organisatie"; geconfigureerd-maar-niet-geverifieerd heet ook zo. */
  projectContextSummary: {
    loadError: "Kon het contextoverzicht van dit project niet laden.",
    purpose: {
      title: "Waarvoor wordt dit project gebruikt?",
      primaryBadge: "Primair doel",
      addLabel: "Extra use case toevoegen",
      addPlaceholder: "Kies een use case…",
      addButton: "Toevoegen",
      setPrimary: "Maak primair",
      remove: "Verwijderen",
      removeAria: "Verwijder {label}",
      primaryRemoveError: "Het primaire doel kan niet worden verwijderd. Maak eerst een andere use case primair.",
      updateError: "Kon de use cases niet bijwerken.",
    },
    context: {
      title: "Welke context gebruikt FixControl?",
      intro: "FixControl gebruikt voor dit project de onderstaande gekoppelde bronnen.",
      counts: {
        intakeSources: "Intakebronnen",
        codebases: "Codebases",
        knowledgeSources: "Kennisbronnen",
        notificationRoutes: "Notificatieroutes",
        deploymentBindings: "Deploymentkoppelingen",
        codeBindings: "Code- en deliverykoppelingen",
        approvalChannels: "Goedkeuringskanalen",
      },
      /* Elk blokje opent de lijst achter zijn eigen getal — een aantal waar je
       * niet "welke dan?" aan kunt vragen, is geen antwoord. */
      detailHint: "Klik op een blokje om te zien welke bronnen erachter zitten.",
      detailEmpty: "Nog niets gekoppeld in deze categorie.",
      manage: "Beheren",
      more: "…en nog {count}",
      sourceStatus: {
        processing: "Wordt geïndexeerd",
        failed: "Indexeren mislukt",
      },
      resourcesHeading: "Overige gekoppelde bronnen",
      empty: "Er zijn nog geen bronnen aan dit project gekoppeld.",
    },
    capabilities: {
      title: "Wat kan FixControl nu?",
      groupAvailable: "Nu beschikbaar",
      groupUnverified: "Geconfigureerd, niet geverifieerd",
      groupNotConfigured: "Niet geconfigureerd",
      approvalChip: "Goedkeuring vereist",
      status: {
        unknown: "Niet geverifieerd",
        degraded: "Verminderd",
        unavailable: "Niet beschikbaar",
      },
    },
    gaps: {
      title: "Wat ontbreekt nog?",
      none: "Er ontbreekt niets — de checks voor de actieve use cases zijn gereed.",
      fix: "Instellen",
      showOptional: "Toon optionele checks ({count})",
      hideOptional: "Verberg optionele checks",
      status: {
        missing: "Ontbreekt",
        degraded: "Verminderd",
        optional: "Optioneel",
        not_applicable: "Niet van toepassing",
      },
    },
    firstSteps: {
      title: "Eerste stappen",
      intro: "Voorgestelde startpunten voor de actieve use-cases van dit project. Elke link opent een bestaande pagina — er start niets automatisch.",
    },
    approvalNote: "Externe acties — antwoorden, pull requests, deployment-promotie — vereisen altijd menselijke goedkeuring.",
  },

  /* De project-actie-/resolutielaag. Zie en.ts. */
  projectActions: {
    more: "Meer projectacties",
    setup: {
      title: "Instellen & status",
      progress: "Instellen: {completed} van {total} stappen klaar",
      nextLabel: "Volgende stap",
      doneLabel: "Klaar",
      laterLabel: "Later",
      allDone: "Alles wat dit project nodig heeft, staat ingesteld.",
      noPermission: "Iemand met configuratierechten moet deze stap doen.",
    },
    steps: {
      workSource: {
        label: "Waar werk binnenkomt",
        hint: "Een Slack-kanaal, servicedesk, issuetracker of mailbox — of een alert uit je eigen systemen. Zo bereikt werk dit project.",
      },
      codebase: {
        label: "De code",
        hint: "De repository die agents lezen, analyseren en waarvoor ze wijzigingen voorstellen.",
      },
      knowledge: {
        label: "Kennis",
        hint: "Documentatie waar agents uit putten — of laat een gekoppelde codebase inspecteren.",
      },
      deployment: {
        label: "Deploymentbron",
        hint: "De pipeline of rollout-controller waarvan dit project de deployments bewaakt.",
      },
      delivery: {
        label: "Aflevering via pull requests",
        hint: "Waar goedgekeurde wijzigingen landen. Zonder dit blijft het project alleen-analyseren.",
      },
      approvals: {
        label: "Wie goedkeurt",
        hint: "Waar goedkeuringsverzoeken landen voordat een risicovolle actie doorgaat.",
      },
      notifications: {
        label: "Waar updates heen gaan",
        hint: "Het kanaal dat voortgang en uitkomsten te zien krijgt.",
      },
    },
    setup_workSource: { label: "Werkbron toevoegen" },
    setup_codebase: { label: "Code koppelen" },
    setup_knowledge: { label: "Kennis toevoegen" },
    setup_deployment: { label: "Deploymentbron koppelen" },
    setup_delivery: { label: "Aflevering koppelen" },
    setup_approvals: { label: "Goedkeuringskanaal toevoegen" },
    setup_notifications: { label: "Updatekanaal toevoegen" },
    operate_support_work: { label: "Issues openen" },
    operate_support_inbox: { label: "Inbox openen" },
    operate_engineering_work: { label: "Projectwerk openen" },
    operate_engineering_missions: { label: "Missies openen" },
    operate_deployment_activity: { label: "Deploymentactiviteit bekijken" },
    operate_security_posture: { label: "Beveiligingsstatus bekijken" },
    operate_knowledge_sources: { label: "Kennis openen" },
    operate_custom_work: { label: "Projectwerk openen" },
    inspect_patchHistory: { label: "Patchgeschiedenis" },
    inspect_developerWork: { label: "Ontwikkelwerk" },
    inspect_activity: { label: "Projectactiviteit" },
    configure_advanced: { label: "Geavanceerde instellingen" },
    remove_project: { label: "Project verwijderen" },
  },

  /* Context-inkijk — de popover achter een telling. Zie en.ts. */

  /* Taakgerichte projectwizard (src/app/projects/_components/
   * create-project-wizard.tsx). De preview.*-sleutels worden gebruikt door
   * de pure planbouwer in src/lib/use-cases/wizard.ts. */
  projectWizard: {
    stepIndicator: "Stap {current} van {total}",
    buttons: {
      back: "Terug",
      next: "Verder",
      cancel: "Annuleren",
    },
    quickCreate: {
      button: "Direct aanmaken",
      hint: "Sla de resterende stappen over en maak het project aan met alleen deze naam en dit doel — bronnen koppel je later op de projectpagina.",
    },
    steps: {
      goal: {
        title: "Wat wil je bereiken?",
        sub: "Geef het project een naam en kies het primaire doel.",
      },
      sources: {
        title: "Welke bronnen horen bij dit project?",
        sub: "Kies uit bronnen die al gekoppeld zijn. Later toevoegen kan altijd.",
      },
      confirm: {
        title: "Projectcontext bevestigen",
        sub: "Dit is de context waarmee het project start.",
      },
      additional: {
        title: "Aanvullende use-cases",
        sub: "Combineer eventueel andere doelen in hetzelfde project.",
      },
      permissions: {
        title: "Wat mag FixControl doen?",
        sub: "Kies hoe ver analyse mag gaan voor dit project.",
      },
      review: {
        title: "Controle",
        sub: "Er wordt niets aangemaakt totdat je bevestigt.",
      },
      firstSteps: {
        title: "Eerste stap",
        sub: "Het project staat klaar — hier begin je.",
      },
    },
    fields: {
      nameLabel: "Projectnaam",
      namePlaceholder: "bijv. Klantsupport webshop",
      descriptionLabel: "Omschrijving (optioneel)",
      descriptionPlaceholder: "Waar gaat dit project over?",
      goalLabel: "Primair doel",
    },
    sources: {
      groupCommunication: "Communicatie",
      groupCode: "Code",
      groupKnowledge: "Kennis",
      slackLabel: "Slack-kanalen",
      teamsLabel: "Teams-kanalen",
      mailboxLabel: "Mailboxen",
      codebasesLabel: "Codebases",
      notConnected: "Niet gekoppeld",
      connectHint: "Koppel deze integratie eerst in Instellingen.",
      connectLink: "Naar integraties",
      noChannels: "Geen kanalen gevonden.",
      teamsNotListable: "Teams-kanalen kunnen hier niet worden opgesomd. Koppel na het aanmaken een Teams-kanaal via de instellingen van het project.",
      noMailboxes: "Nog geen mailboxen ingesteld.",
      noCodebases: "Nog geen codebases gekoppeld.",
      knowledgeCount: "{count} kennisbronnen beschikbaar voor deze organisatie.",
      knowledgeNone: "Nog geen kennisbronnen.",
      knowledgeHint: "Kennisbronnen worden centraal beheerd — hier hoef je niets te selecteren.",
      knowledgeLink: "Kennisbronnen beheren",
      optionalHint: "Bronnen selecteren is optioneel — je kunt ze later op de projectpagina toevoegen of wijzigen.",
    },
    confirm: {
      none: "Geen bronnen geselecteerd. Het project start zonder gekoppelde bronnen; je kunt ze later toevoegen.",
      slackHeading: "Slack-kanalen",
      teamsHeading: "Teams-kanalen",
      mailboxHeading: "Mailboxen",
      codebaseHeading: "Codebases",
    },
    additional: {
      intro: "Selecteer eventuele extra use-cases voor dit project. Ze voegen dashboards en gereedheidschecks toe — nooit rechten.",
    },
    permissions: {
      readOnly: {
        label: "Alleen kijken",
        description: "FixControl leest de gekoppelde bronnen en rapporteert bevindingen. Geen werkkopieën, geen conceptwijzigingen.",
      },
      sandbox: {
        label: "Wijzigingen proberen in een veilige kopie",
        description: "FixControl mag voorgestelde wijzigingen ook uitproberen in een afgeschermde, veilige kopie. Niets verlaat die kopie zonder goedkeuring.",
      },
      approvalNote: "Acties buiten FixControl — antwoorden aan klanten, pull requests, uitrollen naar productie — hebben altijd goedkeuring van een mens nodig, wat je hier ook kiest.",
    },
    review: {
      intro: "Dit is precies wat er wordt aangemaakt. Annuleren past niets toe.",
      newBadge: "nieuw",
      approvalsHeading: "Vereist altijd jouw goedkeuring",
      noApprovals: "De geselecteerde use-cases bevatten geen externe acties.",
      createButton: "Project aanmaken",
      creating: "Aanmaken…",
      failed: "Project aanmaken is niet gelukt.",
    },
    preview: {
      name: "Projectnaam",
      primaryGoal: "Primair doel",
      additionalUseCase: "Extra use-case",
      executionMode: "Uitvoeringsmodus",
      remediationStart: "Start van herstelwerk",
      codebase: "Codebase",
      binding: "Gekoppelde bron",
    },
    modeValue: {
      read_only: "Alleen kijken",
      sandbox: "Wijzigingen proberen in een veilige kopie",
      writable: "Goedgekeurde wijzigingen toepassen",
    },
    remediationValue: {
      manual: "Handmatig — een persoon bepaalt wanneer werk start",
      automatic_on_intake: "Automatisch bij intake",
    },
    firstSteps: {
      createdIntro: "Project “{name}” is aangemaakt.",
      conflictsHeading: "Sommige bronnen zijn al aan een ander project gekoppeld",
      conflictsHint: "Deze bronnen zijn hier niet gekoppeld. Pas ze zo nodig aan op de projectpagina's.",
      links: {
        project: "Open de projectpagina",
        inbox: "Bekijk binnenkomende vragen in de inbox",
        issues: "Bekijk de issue-lijst",
        devops: "Controleer de deployment-instellingen",
        posture: "Bekijk kennis- en security-posture",
        knowledge: "Kennisbronnen beheren",
      },
      toProject: "Naar project",
    },
  },

  /* Use-case registry (src/lib/use-cases/) — context-first productmodel. */
  useCases: {
    availability: { available: "Beschikbaar", beta: "Bèta", preview: "Preview" },
    support: {
      label: "Klantvragen afhandelen",
      short: "Klantvragen begrijpen, relevante kennis en tickets vinden en gecontroleerde antwoorden voorstellen.",
      full: "FixControl ontvangt vragen uit je gekoppelde kanalen, onderbouwt antwoorden met de kennisbronnen van dit project, maakt tickets aan en houdt ieder klantgericht antwoord achter je antwoordbeleid. Escalatie naar engineering is altijd een expliciete menselijke stap.",
      outcome: "Klantvragen beantwoord vanuit je eigen kennis, met ieder antwoord onder controle.",
      prereq: {
        intake: "Minimaal één intakebron (kanaal, mailbox of chatwidget) gekoppeld aan dit project.",
        knowledge: "Minimaal één kennisbron gekoppeld aan dit project, zodat antwoorden onderbouwd zijn.",
      },
    },
    engineering: {
      label: "Softwareproblemen oplossen",
      short: "Issues analyseren met codecontext, patches voorstellen, verifiëren in een sandbox en pull requests voorbereiden.",
      full: "FixControl leest de repositories van dit project, analyseert issues, stelt patches voor en verifieert ze in een sandbox. Levering gaat uitsluitend via pull requests, achter plan- en patchgoedkeuringen — niets wordt gemerged of uitgerold zonder vastgelegde menselijke beslissing.",
      outcome: "Geverifieerde patches als pull requests, achter jouw goedkeuringen.",
      prereq: {
        codebase: "Een gekoppelde codebase die dit project mag lezen.",
        knowledge: "Relevante documentatie of kennis verbetert de analysekwaliteit (aanbevolen).",
      },
    },
    deployment: {
      label: "Deployments controleren",
      short: "Deploymentactiviteit observeren, risico beoordelen en goedkeuringen organiseren. Acties alleen waar ondersteund.",
      full: "FixControl observeert deploymentactiviteit van je gekoppelde providers, beoordeelt risico en organiseert deploymentgoedkeuringen met bewijs. Observeren betekent nooit dat FixControl kan ingrijpen: de enige gecontroleerde actie is vandaag het promoten van een gepauzeerde Argo Rollout na een goedgekeurde deployment-gate. Capability-health toont per project wat observatie is en welke actie beschikbaar is.",
      outcome: "Deploymentbeslissingen met bewijs en goedkeuringen — actie alleen waar aantoonbaar ondersteund.",
      prereq: {
        binding: "Een deploymentkoppeling (environment, applicatie of namespace) gekoppeld aan dit project.",
        approvalRoute: "Een goedkeuringsroute voor deployment-gates.",
        actionSupport: "Gecontroleerde promotie vereist een geconfigureerde Argo Rollouts-verbinding; andere providers zijn alleen-observeren.",
      },
    },
    security: {
      label: "Beveiligingsrisico's onderzoeken",
      short: "De meegeleverde code- en dependencyscanners draaien, posture beoordelen en risico's naar gecontroleerde remediation leiden.",
      full: "FixControl draait de meegeleverde scanners (secret-detectie, dependency-audit, SAST, vulnerability-scanning) op de codebase van dit project en vertaalt de resultaten naar een posture-beoordeling. Remediation volgt de normale gecontroleerde missieflow. Geplande scans en een aparte findings-workflow zijn er nog niet — deze use case is bèta en toont precies wat er draait.",
      outcome: "Een scanner-onderbouwd posturebeeld met risico's richting gecontroleerde remediation.",
      prereq: {
        codebase: "Een gekoppelde codebase om te scannen.",
        scanner: "Minimaal één beschikbare scanner in deze omgeving.",
      },
    },
    knowledge: {
      label: "Organisatiekennis ontsluiten",
      short: "Verspreide kennisbronnen verbinden en per project brongebonden antwoorden krijgen.",
      full: "FixControl verbindt de wiki's, documentatie, tickets, procedures en eerdere beslissingen die je aan dit project koppelt en beantwoordt vragen onderbouwd met die bronnen — met verwijzingen naar waar ieder antwoord vandaan komt. Wat niet in een gekoppelde bron staat, claimt FixControl niet te weten. Een kennisproject heeft geen codebase en geen ticketintegratie nodig.",
      outcome: "Betrouwbare, brongebonden antwoorden over de kennis die jij hebt verbonden.",
      prereq: {
        source: "Minimaal één gekoppelde kennisbron.",
      },
    },
    custom: {
      label: "Zelf samenstellen",
      short: "Combineer capabilities zelf, vertrekkend vanuit wat werkelijk gekoppeld en gezond is.",
      full: "Een eigen project vertrekt vanuit je echte configuratie: welke bronnen gekoppeld zijn, welke capabilities gezond zijn, welke voorwaarden ontbreken en welke acties goedkeuring vereisen. Je voegt stap voor stap use cases en koppelingen toe — er wordt niets geclaimd dat niet is ingesteld.",
      outcome: "Een project samengesteld uit jouw eigen combinatie van gekoppelde bronnen en capabilities.",
      prereq: {
        context: "Minimaal één gekoppelde bron of capability om mee te werken.",
      },
    },
    capabilities: {
      intake_receive: {
        label: "Werk ontvangen",
        description: "Vragen en issues ontvangen uit gekoppelde kanalen, mailboxen en chatwidgets.",
      },
      knowledge_answer: {
        label: "Antwoorden uit kennis",
        description: "Antwoorden onderbouwen met de kennisbronnen van dit project.",
      },
      tickets_read: {
        label: "Tickets lezen",
        description: "Vragen over tickets in FixControl en gekoppelde trackers beantwoorden, alleen-lezen.",
      },
      reply_govern: {
        label: "Antwoorden bewaken",
        description: "Klantantwoorden opstellen achter het antwoordbeleid en de veiligheidsscan.",
      },
      reply_send: {
        label: "Goedgekeurde antwoorden versturen",
        description: "Een goedgekeurd antwoord afleveren via de gekoppelde mail- of ticketprovider. Externe verzending — altijd beleidsgestuurd.",
      },
      code_read: {
        label: "Code lezen",
        description: "De repositories van dit project lezen.",
      },
      sandbox_analyze: {
        label: "Analyseren in sandbox",
        description: "Analyses en tests draaien in een geïsoleerde sandbox.",
      },
      delivery_pr: {
        label: "Pull requests voorbereiden",
        description: "Goedgekeurde patches afleveren als pull requests — nooit een directe push. Externe write — goedkeuring vereist.",
      },
      deployment_observe: {
        label: "Deployments observeren",
        description: "Deploymentactiviteit en rollout-status van gekoppelde providers inlezen. Alleen observatie.",
      },
      deployment_promote: {
        label: "Rollout promoten (gecontroleerd)",
        description: "Een gepauzeerde Argo Rollout promoten na een goedgekeurde deployment-gate. Externe actie — goedkeuring vereist.",
      },
      security_scan: {
        label: "Scannen op risico's",
        description: "De meegeleverde secret-, dependency-, SAST- en vulnerability-scanners draaien.",
      },
      approvals_route: {
        label: "Goedkeuringen routeren",
        description: "Beslissingen naar de juiste beoordelaars leiden en vastleggen in het beslisregister.",
      },
      notifications_post: {
        label: "Updates plaatsen",
        description: "Statusupdates plaatsen in gekoppelde kanalen. Externe write — loopt via de bezorgwachtrij.",
      },
    },
    /** Zie de EN-dictionary voor waar deze sleutels vandaan komen. */
    evidence: {
      intakeSourcesOne: "{count} intakebron",
      intakeSourcesOther: "{count} intakebronnen",
      intakeSourcesNotTestedOne: "{count} bron ingesteld, nog niet getest",
      intakeSourcesNotTestedOther: "{count} bronnen ingesteld, nog geen getest",
      intakeWaitingFirstMail: "Ingesteld, wacht op de eerste e-mail",
      knowledgeSourcesOne: "{count} kennisbron",
      knowledgeSourcesOther: "{count} kennisbronnen",
      codebasesOne: "{count} codebase",
      codebasesOther: "{count} codebases",
      deploymentBindingsOne: "{count} deploymentkoppeling",
      deploymentBindingsOther: "{count} deploymentkoppelingen",
      codeBindingsOne: "{count} code-/leveringskoppeling",
      codeBindingsOther: "{count} code-/leveringskoppelingen",
      approvalChannelsOne: "{count} goedkeuringskanaal",
      approvalChannelsOther: "{count} goedkeuringskanalen",
      notificationRoutesOne: "{count} meldingsroute",
      notificationRoutesOther: "{count} meldingsroutes",
      approvalRoutesLedgerOne: "{count} goedkeuringsroute; beslisregister altijd aan",
      approvalRoutesLedgerOther: "{count} goedkeuringsroutes; beslisregister altijd aan",
      approvalRoutesReplyPolicyOne: "{count} goedkeuringsroute; antwoordbeleid altijd aan",
      approvalRoutesReplyPolicyOther: "{count} goedkeuringsroutes; antwoordbeleid altijd aan",
      approvalRoutesPlanPatchOne: "{count} goedkeuringsroute; plan-/patchpoorten altijd aan",
      approvalRoutesPlanPatchOther: "{count} goedkeuringsroutes; plan-/patchpoorten altijd aan",
      approvalRoutesDeploymentGateOne: "{count} goedkeuringsroute; een deployment die FixControl vanuit een missie start, wacht op goedkeuring als je beleid dat vereist",
      approvalRoutesDeploymentGateOther: "{count} goedkeuringsroutes; een deployment die FixControl vanuit een missie start, wacht op goedkeuring als je beleid dat vereist",
      approverStepsHeldOne: "Er is iemand die de {count} goedkeuringsstap kan goedkeuren",
      approverStepsHeldOther: "Voor elk van de {count} goedkeuringsstappen is er iemand die kan goedkeuren",
      approverStepsUnheldOne: "Nog niemand kan goedkeuren: bij {count} goedkeuringsstap is er niemand die kan goedkeuren",
      approverStepsUnheldOther: "Nog niemand kan goedkeuren: bij {count} goedkeuringsstappen is er niemand die kan goedkeuren",
      approversUnknown: "Kon niet controleren wie goedkeurt. Open Goedkeuringen & regels om het te zien.",
      ticketsBuiltIn: "FixControl-tickets; gekoppelde trackers zodra die gezond zijn",
      replyGovernAlwaysOn: "Antwoordbeleid + veiligheidsscan (altijd aan)",
      replySendConfigured: "Verzendende provider geconfigureerd",
      replySendMissing: "Geen verzendende provider",
      sandboxWithCodebasesOne: "Sandbox-runtime; {count} codebase",
      sandboxWithCodebasesOther: "Sandbox-runtime; {count} codebases",
      sandboxUnavailable: "Sandbox-runtime niet beschikbaar",
      argoPromoteConfigured: "Argo Rollouts-promotie geconfigureerd",
      argoPromoteMissing: "Geen verbinding die kan promoveren (alleen observeren)",
      scannersVerifiedOne: "{count} scanner geverifieerd bij de laatste run",
      scannersVerifiedOther: "{count} scanners geverifieerd bij de laatste run",
      scannersNoneVerified: "Nog geen scanner geverifieerd",
      scannersNoneVerifiedHere: "Nog geen scanner geverifieerd in deze omgeving",
      environmentVerified: "Geverifieerd omgevingscontract van kracht",
      environmentBlockedQuestion: "Geblokkeerd: {question}",
      environmentBlockedFailure: "Geblokkeerd ({failure})",
      environmentBlocked: "Geblokkeerd",
      environmentRunning: "Bezig uit te zoeken wat dit project nodig heeft om te draaien",
      environmentNotVerified: "Omgeving nog niet geverifieerd",
      knowledgeIndexing: "{indexed} geïndexeerd, {processing} in verwerking, {failed} mislukt",
      knowledgePublicScopesOne: "{count} openbare scope; al het overige blijft intern",
      knowledgePublicScopesOther: "{count} openbare scopes; al het overige blijft intern",
      lastAssessment: "Laatste beoordeling {at}",
      noAssessment: "Nog geen postuurbeoordeling vastgelegd",
      unimplementedCheck: "Check {id} heeft geen implementatie",
    },
    readiness: {
      unimplemented: {
        title: "Check niet geïmplementeerd",
        description: "Deze readiness-check heeft nog geen implementatie — behandel dit als niet gereed.",
      },
      support_intake_source: {
        title: "Intakebron",
        description: "Een kanaal, mailbox of chatwidget waar klantvragen voor dit project binnenkomen.",
      },
      support_knowledge_source: {
        title: "Kennisbron",
        description: "Gekoppelde kennis zodat antwoorden onderbouwd zijn in plaats van geraden.",
      },
      support_reply_destination: {
        title: "Antwoordbestemming",
        description: "Een verzendprovider voor het afleveren van goedgekeurde antwoorden. Optioneel voor alleen-voorstellen-projecten.",
      },
      support_approval_policy: {
        title: "Antwoordbeleid",
        description: "Het antwoordbeleid en de veiligheidsscan die ieder klantgericht antwoord bewaken.",
      },
      support_send_health: {
        title: "Verzendgezondheid",
        description: "Geverifieerde bezorggezondheid voor uitgaande antwoorden. Ingesteld is niet hetzelfde als geverifieerd.",
      },
      engineering_codebase: {
        title: "Codebase",
        description: "Een gekoppelde repository die dit project kan analyseren.",
      },
      engineering_code_access: {
        title: "Codetoegang",
        description: "Geverifieerde leestoegang tot de gekoppelde repositories.",
      },
      engineering_sandbox: {
        title: "Sandbox-analyse",
        description: "De geïsoleerde runtime die patches verifieert voordat iemand ze beoordeelt.",
      },
      engineering_environment: {
        title: "Draaibare omgeving",
        description: "FixControl heeft uitgezocht wat deze repository nodig heeft om te draaien en dat bewezen door het één keer te starten.",
      },
      engineering_approval_route: {
        title: "Goedkeuringsroute",
        description: "Plan- en patch-gates met de beoordelaars die beslissen.",
      },
      engineering_pr_provider: {
        title: "Pull-request-levering",
        description: "Een git-providerkoppeling om goedgekeurde patches als pull requests af te leveren. Optioneel voor alleen-analyseprojecten.",
      },
      deployment_binding: {
        title: "Deploymentkoppeling",
        description: "De environment, applicatie of namespace die dit project bewaakt.",
      },
      deployment_observe_health: {
        title: "Observatiegezondheid",
        description: "Geverifieerde ingest van deploymentactiviteit van de gekoppelde provider.",
      },
      deployment_approval_route: {
        title: "Deploymentgoedkeuringen",
        description: "Deployments die FixControl vanuit een missie start, wachten op goedkeuring als je beleid dat vereist. Deployments van je eigen pipelines krijgen alleen een gate met “Gates openen voor je eigen deployments” aan; anders worden ze gevolgd, niet tegengehouden.",
      },
      deployment_action_capability: {
        title: "Gecontroleerde actie",
        description: "Of een gecontroleerde promote-actie beschikbaar is. Een provider observeren betekent nooit dat je erop kunt ingrijpen.",
      },
      security_codebase: {
        title: "Codebase",
        description: "Een gekoppelde repository om te scannen.",
      },
      security_scanner_capability: {
        title: "Scannerbeschikbaarheid",
        description: "Minimaal één scanner die bij de laatste run aantoonbaar werkte in deze omgeving.",
      },
      security_scan_status: {
        title: "Scanstatus",
        description: "Een vastgelegde posture-beoordeling voor de code van dit project.",
      },
      security_remediation_route: {
        title: "Remediationroute",
        description: "Optioneel: pull-request-levering voor gecontroleerde fixes.",
      },
      knowledge_source: {
        title: "Kennisbron",
        description: "Minimaal één gekoppelde bron om uit te antwoorden.",
      },
      knowledge_indexing: {
        title: "Indexeringsstatus",
        description: "Of gekoppelde bronnen geïndexeerd en doorzoekbaar zijn.",
      },
      knowledge_scope: {
        title: "Zichtbaarheidsscope",
        description: "Wat publiek gepubliceerd is en wat intern blijft. Publieke chat kan uitsluitend publieke scopes lezen.",
      },
      knowledge_intake_channel: {
        title: "Vraagkanaal",
        description: "Optioneel: een kanaal of widget waar mensen vragen kunnen stellen.",
      },
      custom_any_context: {
        title: "Projectcontext",
        description: "Minimaal één gekoppelde bron, codebase, kanaal of koppeling om mee te werken.",
      },
    },
    onboardingSteps: {
      support_process_test_question: {
        label: "Verwerk een testvraag",
        description: "Stel iets in het intakekanaal en zie hoe het vanuit jouw kennis wordt beantwoord.",
      },
      support_look_up_ticket: {
        label: "Zoek een ticket op",
        description: "Open de ticketlijst van dit project en bekijk wat FixControl kan zien.",
      },
      support_draft_answer: {
        label: "Bekijk een conceptantwoord",
        description: "Open de inbox en zie hoe bewaakte antwoordconcepten eruitzien.",
      },
      engineering_select_issue: {
        label: "Kies een issue",
        description: "Kies een issue in dit project om als eerste te analyseren.",
      },
      engineering_read_only_analysis: {
        label: "Start een alleen-lezen analyse",
        description: "Laat FixControl de gekoppelde code lezen zonder iets te wijzigen.",
      },
      engineering_codebase_readiness: {
        label: "Controleer codebase-gereedheid",
        description: "Controleer codetoegang en sandboxstatus op het projectoverzicht.",
      },
      deployment_review_recent: {
        label: "Bekijk recente deployments",
        description: "Open het DevOps-overzicht en zie wat er wordt geobserveerd.",
      },
      deployment_assess_risk: {
        label: "Beoordeel een deployment",
        description: "Bekijk het goedkeuringsvlak voor de deployment-gates van dit project.",
      },
      deployment_test_approval_route: {
        label: "Controleer de goedkeuringsroute",
        description: "Bevestig wie beslist over deployment-gates — zonder echte promotie.",
      },
      security_view_scanner_status: {
        label: "Bekijk scannerstatus",
        description: "Zie welke scanners beschikbaar zijn en wat de laatste run opleverde.",
      },
      security_run_read_only_scan: {
        label: "Draai een alleen-lezen scan",
        description: "Start een posture-beoordeling waar ondersteund — alleen analyse.",
      },
      knowledge_ask_test_question: {
        label: "Stel een testvraag",
        description: "Stel iets en controleer dat het antwoord op jouw bronnen is gebaseerd.",
      },
      knowledge_view_sources_used: {
        label: "Bekijk de gebruikte bronnen",
        description: "Open de kennisbank en bekijk waaruit dit project kan antwoorden.",
      },
      knowledge_add_missing_source: {
        label: "Voeg een ontbrekende bron toe",
        description: "Koppel de documentatie die het laatste antwoord miste.",
      },
      custom_advanced_setup: {
        label: "Open geavanceerde setup",
        description: "Stel koppelingen en capabilities stap voor stap samen.",
      },
    },
  },

  // Ontwikkelgovernance (project → Ontwikkelwerk). Zie en.ts: de sleutels voor
  // gebeurtenissen, statussen en soorten werk volgen één-op-één de gesloten
  // unions in src/lib/dev-governance/types.ts.
  devGovernance: {
    eyebrow: "Ontwikkelgovernance",
    title: "Ontwikkelwerk",
    subtitle:
      "Elk vastgelegd stuk ontwikkelwerk in dit project: wie het deed, op welke branch, en op welke beoordeling het nog wacht.",
    navLink: "Ontwikkelwerk",
    loadError: "Het ontwikkelwerk kon niet worden geladen.",
    retry: "Opnieuw proberen",
    noAccess: {
      title: "Je hebt geen toegang tot het ontwikkelwerk van dit project.",
      body: "Ontwikkelrechten komen uit projectlidmaatschap — een organisatierol geeft ze niet. Vraag een projectbeheerder om je als lid toe te voegen.",
    },
    notFound: {
      title: "Dit project bestaat niet meer.",
      body: "Mogelijk is het verwijderd. Ga terug naar de projectenlijst om een ander te kiezen.",
    },
    unset: "—",
    list: {
      empty: "Er is nog geen ontwikkelwerk vastgelegd voor dit project.",
      emptyHint:
        "Zodra een ontwikkelaar werk start vanaf de opdrachtregel, verschijnt het hier.",
      cliCommand: "fixcontrol dev start",
      filterStatus: "Status",
      filterMode: "Soort werk",
      allStatuses: "Alle statussen",
      allModes: "Alle soorten werk",
      columns: {
        status: "Status",
        mode: "Soort werk",
        issueKey: "Issue",
        branch: "Branch",
        initiator: "Gestart door",
        pullRequest: "Pull request",
        created: "Gestart",
      },
    },
    detail: {
      emptyTitle: "Kies een stuk werk",
      emptyHint:
        "Klik een rij aan om de volledige tijdlijn, de checkpoints en de beoordelingsstatus te zien.",
      close: "Sluiten",
      section: {
        timeline: "Tijdlijn",
        checkpoints: "Checkpoints",
      },
      fields: {
        mode: "Soort werk",
        issue: "Issue",
        branch: "Branch",
        commits: "Commits",
        workspace: "Werkplek",
        pullRequest: "Pull request",
        initiator: "Gestart door",
        startedAt: "Gestart",
        submittedAt: "Ingediend",
        completedAt: "Afgerond",
        executionId: "Kenmerk",
      },
      openIssue: "Issue openen",
      openPullRequest: "Pull request openen",
      readOnly:
        "Deze pagina leest het dossier. Ontwikkelaars schrijven eraan vanaf de opdrachtregel.",
    },
    review: {
      title: "Beoordeling",
      requiredYes: "Dit werk moet beoordeeld worden.",
      requiredNo: "Beoordeling is niet verplicht in dit project.",
      requiredUnknown:
        "Of beoordeling verplicht is, blijkt niet uit de historie die hier geladen is.",
      waitingSince: "Wacht op een beslissing sinds",
      stallWarning:
        "Een openstaande beoordeling vervalt niet vanzelf. Dit werk blijft wachten tot iemand beslist.",
      openDecision: "Beslissing openen",
      noDecision: "Voor dit werk is nog geen beslispunt geopend.",
      phase: {
        submitted: "Ingediend, wacht tot de beoordeling opent",
        in_review: "Wacht op de beslissing van een beoordelaar",
        changes_requested: "Wijzigingen gevraagd — terug bij de ontwikkelaar",
      },
    },
    overlap: {
      badge: "Mogelijk overlappend",
      title: "Mogelijk overlappend werk",
      subtitle:
        "Ander werk in deze codebase lijkt dezelfde bestanden te raken. Dit is een signaal, geen conclusie: FixControl vergelijkt de paden die zijn vastgelegd en opgegeven, en ziet niet alles wat een ontwikkelaar openslaat.",
      peer: "Ander werk",
      openPeer: "Dit werk openen",
      matchedPaths: "Gedeelde paden",
      morePaths: "en nog {count}",
      noPaths: "Er zijn geen gedeelde paden vastgelegd.",
      detectedAt: "Opgemerkt",
    },
    staleBase: {
      title: "Beginpunt is mogelijk verouderd",
      body:
        "Er is werk samengevoegd in deze codebase nadat dit werk begon, dus de basiscommit loopt mogelijk achter. Overweeg een rebase op de laatste stand voordat je indient. Of het dit werk echt raakt, kunnen we hier niet zien.",
      baseCommit: "Basiscommit",
      detectedAt: "Opgemerkt",
    },
    timeline: {
      title: "Tijdlijn",
      subtitle: "Elke vastgelegde stap, in de volgorde waarin die gebeurde.",
      empty: "Er zijn nog geen stappen vastgelegd.",
      showOlder: "Toon {count} oudere",
      showLess: "Toon minder",
      loadMore: "Meer laden",
      counter: "{shown} van {total} stappen",
      follows: "volgt op {label}",
    },
    agentSteps: {
      title: "AI-stappen",
      subtitle:
        "Wat een AI-model aan dit werk deed, vastgelegd door FixControl zelf terwijl het model draaide.",
      empty: "Er zijn geen AI-stappen vastgelegd voor dit werk.",
      detailHidden:
        "Welke bestanden en commando's erbij betrokken waren, is alleen zichtbaar met het recht om patches te lezen.",
      modelUnknown: "Model niet vastgelegd",
      filesRead: "{count} bestanden gelezen",
      filesChanged: "{count} bestanden gewijzigd",
      commands: "{count} commando's",
      toolCalls: "{count} tools",
      tests: "{count} testruns",
      patch: "Patch",
      instruction: "Vingerafdruk opdracht",
    },
    worktrees: {
      title: "Geïsoleerde werkmappen",
      subtitle:
        "Een autonome agent werkt nooit in de werkmap van een ontwikkelaar. Hij krijgt een eigen wegwerpkopie van de repository, op een eigen branch, en FixControl legt vast wat daarmee is gebeurd. Het volledige serverpad staat hier bewust niet.",
      branch: "Branch",
      detached: "Geen branch (losgekoppeld)",
      created: "Aangemaakt",
      removed: "Opgeruimd",
      stillOpen: "Staat nog op schijf",
      status: {
        provisioning: "Wordt aangemaakt",
        active: "In gebruik",
        cleaning: "Wordt opgeruimd",
        removed: "Opgeruimd",
        stale: "Blijven staan",
      },
    },
    deployments: {
      title: "Waar deze code heeft gedraaid",
      subtitle:
        "Deployments die FixControl bij dit werk heeft waargenomen. Elke regel is een vastgelegd feit uit het deploymentlogboek, geen live status.",
      artifactDigest: "Artefact",
      digestUnknown: "Niet gemeld",
      run: "Deploymentrun",
      heuristic:
        "Deze deployment is via het issuenummer aan dit werk gekoppeld, niet via de exacte commit. Lees het als een aanwijzing, niet als bewijs dat deze bytes uit dit werk komen.",
      environment: {
        production: "Productie",
        staging: "Staging",
        development: "Ontwikkeling",
      },
      phase: {
        queued: "In wachtrij",
        running: "Bezig",
        succeeded: "Geslaagd",
        failed: "Mislukt",
        cancelled: "Geannuleerd",
        awaiting_approval: "Wacht op goedkeuring",
      },
    },
    checkpoints: {
      title: "Checkpoints",
      subtitle: "Momentopnamen die de ontwikkelaar tijdens het werk vastlegde.",
      empty: "Er zijn geen checkpoints vastgelegd.",
      changedFiles: "{count} gewijzigde bestanden",
      droppedFiles: "{count} weggelaten",
      truncatedFiles: "{count} niet vastgelegd",
      noDescription: "Geen omschrijving",
      diffHash: "Diff",
      testExit: "Afsluitcode {code}",
    },
    status: {
      active: "Bezig",
      submitted: "Ingediend",
      in_review: "In beoordeling",
      changes_requested: "Wijzigingen gevraagd",
      completed: "Afgerond",
      abandoned: "Gestopt",
      failed: "Mislukt",
    },
    mode: {
      human_local: "Handmatig, lokaal",
      ai_assisted_local: "Met AI-hulp, lokaal",
      autonomous_agent: "Zelfstandige AI-agent",
      support_fix: "Supportfix",
      review: "Beoordeling",
      deployment: "Uitrol",
    },
    checkpointKind: {
      checkpoint: "Checkpoint",
      test_run: "Testrun",
      submit_snapshot: "Momentopname bij indienen",
    },
    agentStepRole: {
      planner: "Planner",
      executor: "Bouwer",
      reviewer: "Beoordelaar",
    },
    agentStepStatus: {
      running: "Nog bezig",
      completed: "Klaar",
      failed: "Mislukt",
      cancelled: "Gestopt",
    },
    actor: {
      user: "Persoon",
      agent: "AI-agent",
      system: "Systeem",
      webhook: "Webhook",
    },
    event: {
      execution: {
        started: "Werk gestart",
        submitted: "Ingediend voor beoordeling",
        withdrawn: "Indiening ingetrokken",
        resumed: "Werk hervat",
        review_opened: "Beoordeling geopend",
        changes_requested: "Wijzigingen gevraagd",
        approved: "Goedgekeurd",
        completed: "Afgerond",
        abandoned: "Gestopt",
        failed: "Mislukt",
      },
      checkpoint: { recorded: "Checkpoint vastgelegd" },
      test_run: { recorded: "Testrun vastgelegd" },
      commit: { linked: "Commit gekoppeld" },
      pull_request: {
        linked: "Pull request gekoppeld",
        merged: "Pull request samengevoegd",
      },
      ci: { linked: "CI-uitslag gekoppeld" },
      deployment: { linked: "Uitrol gekoppeld" },
      agent_step: {
        started: "AI-stap gestart",
        completed: "AI-stap afgerond",
        failed: "AI-stap mislukt",
      },
      patch: {
        proposed: "Patch voorgesteld",
        applied: "Patch toegepast",
        rejected: "Patch afgewezen",
      },
      overlap: { detected: "Overlappend werk gesignaleerd" },
      stale_base: { detected: "Verouderd startpunt gesignaleerd" },
      unknown: "Onbekende stap",
    },
    payload: {
      executionMode: "Soort werk",
      issueKey: "Issue",
      branchName: "Branch",
      codebaseId: "Codebase",
      kind: "Soort",
      role: "Rol",
      modelId: "Model",
      reviewRequired: "Beoordeling verplicht",
      decision: "Beslissing",
      reason: "Reden",
      note: "Notitie",
      prNumber: "PR",
      mergedBy: "Samengevoegd door",
      mergeCommitSha: "Merge-commit",
      sha: "Commit",
      state: "Uitslag",
      context: "Controle",
      changedFileCount: "Gewijzigde bestanden",
      droppedFileCount: "Weggelaten bestanden",
      truncatedCount: "Niet vastgelegde bestanden",
      testCommand: "Testcommando",
      testExitCode: "Afsluitcode",
      mergedSha: "Samengevoegde commit",
      matchedPathCount: "Gedeelde paden",
      heuristic: "Signaal, geen bewijs",
    },
  },
};
